import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';  // Import Axios for sending requests to the backend
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import io from 'socket.io-client';
import { toast, ToastContainer } from 'react-toastify';  // Import React Toastify
import 'react-toastify/dist/ReactToastify.css';  // Import styles for Toastify
import Swal from 'sweetalert2';  // Import SweetAlert2
import ClearanceValidator from './ClearanceValidator';
import arrowImage from './assets/arrow.png';
import PDPOverlay from './PDPOverlay';
import PilotProfile from "./PilotProfile";
import PersonalMinimums from "./PersonalMinimums";

// at the top of MapComponent.js
import RouteBubbleBar from './RouteBubbleBar';
import { buildTokenListFromResponse, coordForToken, highlightSegmentFeature } from '../utils/routeTokens';
import SuggestionsInline from "./SuggestionsInline";

import "./MapComponent.css"; // or correct path

import "./OperationalUI.css";

import LeftSidebar from "./LeftSidebar";

import TopToolbar from "./TopToolbar";

import PreflightBriefing from "./PreflightBriefing"; // adjust path if needed

import ChartModal from "./ChartModal"; // new separate file

import resolveRouteWithCoords from "../utils/routeResolver";

import directToFix from "../utils/flightAmendments";

import { Rnd } from "react-rnd"

import DraggablePanel from "./DraggablePanel";

import AuthModal from "./AuthModal";

import InflightRouteBar from "./InflightRouteBar";

import {
  useAuth
} from "../context/AuthContext";

import FlightsModal from "./FlightsModal";


const useAirportWeather = (icao) => {

  const [weather, setWeather] =
    useState(null);

  useEffect(() => {

    // =====================================
    // ✈️ NORMALIZE ICAO
    // =====================================

    const normalizedIcao =
      String(icao || "")
        .trim()
        .toUpperCase();

    // =====================================
    // 🚫 INVALID ICAO
    // =====================================

    if (normalizedIcao.length !== 4) {

      console.warn(
        "⚠️ Invalid ICAO:",
        normalizedIcao
      );

      setWeather(null);

      return;
    }

    let interval;

    // =====================================
    // 🌦 FETCH WEATHER
    // =====================================

    const fetchWeather = async () => {

      try {

        console.log(
          "🌦 Fetching weather:",
          normalizedIcao
        );

        const res = await fetch(

          `https://ficon-x.com/route/pilot-briefing?icao=${normalizedIcao}&eta_hours=0`

        );

        const data =
          await res.json();


        console.log(
          "✅ Weather received:",
          normalizedIcao,
          data
        );

        setWeather(data);

      } catch (err) {

        console.warn(
          "❌ Weather fetch failed:",
          normalizedIcao,
          err
        );
      }
    };

    // 🔥 Initial fetch
    fetchWeather();

    // 🔥 Auto refresh
    interval =
      setInterval(
        fetchWeather,
        30000
      );

    // =====================================
    // 🧹 CLEANUP
    // =====================================

    return () => {

      clearInterval(interval);
    };

  }, [icao]);

  return weather;
};
let lastICAO = null; // ✅ declare it once at component level

function distanceToSegmentNm(
  p,
  a,
  b
) {

  const x = p[0];
  const y = p[1];

  const x1 = a[0];
  const y1 = a[1];

  const x2 = b[0];
  const y2 = b[1];

  const A = x - x1;
  const B = y - y1;

  const C = x2 - x1;
  const D = y2 - y1;

  const dot =
    A * C + B * D;

  const lenSq =
    C * C + D * D;

  let param = -1;

  if (lenSq !== 0) {

    param =
      dot / lenSq;
  }

  let xx, yy;

  if (param < 0) {

    xx = x1;
    yy = y1;

  } else if (param > 1) {

    xx = x2;
    yy = y2;

  } else {

    xx =
      x1 + param * C;

    yy =
      y1 + param * D;
  }

return getDistanceNm(
    [x, y],
    [xx, yy]
);
}

function getDistanceNm(coord1, coord2) {
  const toRad = d => (d * Math.PI) / 180;

  const [lon1, lat1] = coord1;
  const [lon2, lat2] = coord2;

  const R = 3440; // Earth radius in NM

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}


function haversineDistance(lat1, lon1, lat2, lon2) {
  const toRad = deg => deg * Math.PI / 180;
  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getSuffix(rwy) {
  const match = rwy.match(/[LRC]$/);
  return match ? match[0] : '';
}

function mirrorSuffix(suffix) {
  if (suffix === 'L') return 'R';
  if (suffix === 'R') return 'L';
  return suffix; // C stays C
}

function findTrueReciprocalRunway(rwy, runwayMap) {
  const num = parseInt(rwy.replace(/[^\d]/g, ''), 10);
  if (isNaN(num)) return null;

  const suffix = getSuffix(rwy);
  const expectedRecip = (num + 18) > 36 ? (num - 18) : (num + 18);
  const mirroredSuffix = mirrorSuffix(suffix);

  const recipCandidates = Object.keys(runwayMap).filter(k => {
    const kNum = parseInt(k.replace(/[^\d]/g, ''), 10);
    const kSuffix = getSuffix(k);
    return kNum === expectedRecip && kSuffix === mirroredSuffix;
  });

  const source = runwayMap[rwy];
  if (!source || recipCandidates.length === 0) return null;

  let best = null;
  let minDist = Infinity;

  for (const cand of recipCandidates) {
    const target = runwayMap[cand];
    if (!target) continue;

    const dist = haversineDistance(
      source.lat1, source.lon1,
      target.lat1, target.lon1
    );

    if (dist < minDist) {
      minDist = dist;
      best = cand;
    }
  }

  if (best) {
    console.log(`🧭 Best reciprocal for ${rwy} → ${best} (${minDist.toFixed(2)} km)`);
  } else {
    console.warn(`⚠️ No reciprocal match found for ${rwy}`);
  }

  return best;
}




const getBackendURL = (port) => {
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:${port}`;
};

// =====================
// ROUTE PROCESSOR SOCKET (5002)
// =====================
export const routeSocket = io("https://ficon-x.com", {
  path: "/route-socket.io/",
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 5000,
  timeout: 30000,
  pingInterval: 30000,
});

// 🔥 ADD THIS (CRITICAL)
window.routeSocket = routeSocket;

// =====================
// MAIN BACKEND SOCKET (5000)
// =====================
export const socket = io("https://ficon-x.com", {
  path: "/socket.io/",
  transports: ["websocket"],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 5000,
  timeout: 30000,
  pingInterval: 30000,
});

// =====================
// PDP SOCKET (5001)
// =====================
export const pdpSocket = io(getBackendURL(5001), {
  transports: ["websocket"],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 5000,
  timeout: 30000,
  pingInterval: 30000,
});


function getFiconColor(value) {
  // FICON codes typically range from 0 (poor) to 6 (good condition)
  switch (value) {
    case 6: return "#00FF00"; // excellent - bright green
    case 5: return "#E6C6D2";
    case 4: return "#D7A9E3";
    case 3: return "#C964CF"; // yellow - moderate
    case 2: return "#B04ABF"; // orange
    case 1: return "#80276C"; // poor
    case 0: return "#000000"; // nil braking - red
    default: return "#AAAAAA"; // unknown - gray
  }
}



mapboxgl.accessToken = 'pk.eyJ1IjoibnljcGlsb3QiLCJhIjoiY201ZDVwajE3NWhlcjJrcTdrbWZvYnJxdCJ9.205J2BAAuWIgL8sR3-e2lA'; // Update with your actual token


const backendURL = "https://ficon-x.com/api";





const SubmitPIREPForm = ({ mapClickLocation, onClose }) => {
  const [formData, setFormData] = useState({
    callsign: '',
    location: '',
    altitude: '',
    aircraft_type: '',
    turbulence: 'NONE',
    icing_condition: 'NONE',
    sky_condition: '',
    visibility: '',
    remarks: '',
    video: null,
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formPayload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value) formPayload.append(key, value);
    });

    try {
      await axios.post(`${backendURL}/submit_pirep`, formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      Swal.fire('Success!', 'PIREP submitted successfully.', 'success');
      onClose();
    } catch (error) {
      Swal.fire('Error', 'Failed to submit PIREP.', 'error');
    }
  };



return (
    

    
<form
  onSubmit={handleSubmit}
style={{
  backgroundColor: 'white',
  padding: '25px',
  borderRadius: '12px',
  boxShadow: '0px 0px 10px rgba(0,0,0,0.3)',

  width: '500px',        // 🔥 FORCE WIDTH
  maxHeight: '80vh',     // 🔥 CONTROL HEIGHT
  overflowY: 'auto'
}}
>
  <h3 style={{ marginBottom: '10px' }}>Submit PIREP</h3>

  <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
  <select
    name="report_priority"
    onChange={handleChange}
    defaultValue=""
    style={{ width: '50%' }}
  >
    <option value="" disabled>Priority</option>
    <option value="ROUTINE">Routine</option>
    <option value="URGENT">⚠️ Urgent</option>
  </select>
</div>

  {/* ✈️ BASIC INFO */}
  <div style={{ display: 'flex', gap: '5px' }}>

    <input name="aircraft_type" placeholder="Aircraft Type" onChange={handleChange} required style={{ width: '50%' }} />
  </div>

  <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
    <input name="altitude" placeholder="FL" onChange={handleChange} required style={{ width: '50%' }} />
    <input
      name="time_utc"
      value={new Date().toISOString().slice(11,16).replace(":", "") + "Z"}
      readOnly
      style={{ width: '50%', backgroundColor: '#f5f5f5' }}
    />
  </div>

  {/* 📍 LOCATION */}
  <input
    name="location"
    placeholder="Location (ICAO / VOR / FIX)"
    value={formData.location}
    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
    required
    style={{ marginTop: '8px', width: '100%' }}
  />

  {/* 🌪 TURB + ICE */}
  <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
  {/* Frequency */}
  <select name="turbulence_frequency" onChange={handleChange} defaultValue="" style={{ width: "33%" }}>
    <option value="" disabled>Freq</option>
    <option value="OCNL">OCNL</option>
    <option value="INTMT">INTMT</option>
    <option value="CONS">CONS</option>
  </select>

  {/* Intensity */}
  <select name="turbulence_intensity" onChange={handleChange} defaultValue="" style={{ width: "33%" }}>
    <option value="" disabled>Intensity</option>
    <option value="SMOOTH">Smooth / None</option>
    <option value="LIGHT">LGT</option>
    <option value="MODERATE">MOD</option>
    <option value="SEVERE">SEV</option>
  </select>

  {/* Type */}
  <select name="turbulence_type" onChange={handleChange} defaultValue="" style={{ width: "33%" }}>
    <option value="" disabled>Type</option>
    <option value="CHOP">CHOP</option>
    <option value="CAT">CAT</option>
  </select>

    <select name="icing_condition" onChange={handleChange} defaultValue="" style={{ width: '50%' }}>
      <option value="" disabled>Ice</option>
      <option value="NONE">None</option>
      <option value="TRACE">Trace</option>
      <option value="LIGHT">Light</option>
      <option value="MODERATE">Moderate</option>
      <option value="SEVERE">Severe</option>
    </select>
  </div>

  {/* 🌡 TEMP + VIS */}
  <div style={{ display: 'flex', gap: '5px', marginTop: '8px' }}>
    <input name="temperature" placeholder="Temp °C" onChange={handleChange} style={{ width: '50%' }} />
    <input name="visibility" placeholder="Vis (SM)" onChange={handleChange} style={{ width: '50%' }} />
  </div>

  {/* ☁️ CLOUDS */}
  <input
    name="sky_condition"
    placeholder="Sky (BKN / OVC / SCT)"
    onChange={handleChange}
    style={{ marginTop: '8px', width: '100%' }}
  />

  <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
    <input name="cloud_base" placeholder="Base ft" onChange={handleChange} style={{ width: '50%' }} />
    <input name="cloud_tops" placeholder="Tops ft" onChange={handleChange} style={{ width: '50%' }} />
  </div>

  {/* 📝 REMARKS */}
  <textarea
    name="remarks"
    placeholder="Remarks"
    onChange={handleChange}
    style={{ marginTop: '8px', width: '100%' }}
  />

  {/* 🎥 VIDEO */}
  <div style={{ marginTop: '10px' }}>
    <label style={{ fontSize: '12px' }}>Attach Video</label>

    <label
      style={{
        display: 'block',
        marginTop: '5px',
        padding: '8px',
        backgroundColor: '#007bff',
        color: 'white',
        textAlign: 'center',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 'bold'
      }}
    >
      📹 Upload
      <input
        type="file"
        accept="video/*"
        name="video"
        onChange={(e) =>
          setFormData({ ...formData, video: e.target.files[0] })
        }
        style={{ display: 'none' }}
      />
    </label>

    {formData.video && (
      <div style={{ marginTop: '5px', fontSize: '11px' }}>
        {formData.video.name}
      </div>
    )}
  </div>

  {/* 🔘 BUTTONS */}
  <div style={{ marginTop: '12px', display: 'flex', gap: '5px' }}>
    <button
      type="submit"
      style={{
        backgroundColor: '#28a745',
        color: 'white',
        padding: '8px',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 'bold',
        width: '50%'
      }}
    >
      Submit
    </button>

    <button
      type="button"
      onClick={onClose}
      style={{
        backgroundColor: '#dc3545',
        color: 'white',
        padding: '8px',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 'bold',
        width: '50%'
      }}
    >
      Cancel
    </button>
  </div>
</form>
  );
};


const MapComponent = ({ missionId, missionData, mapView, livePosition, hazards = [], notes = [], readOnly = false }) => {

  console.log("🧨 MapComponent MOUNTED");
useEffect(() => {
  console.log("🧱 MapComponent MOUNTED");

  return () => {
    console.log("💀 MapComponent UNMOUNTED");
  };
}, []);



useEffect(() => {

  console.log(
    "🔥 CHART EFFECT RUNNING"
  );

  const storedCharts =
    localStorage.getItem(
      "savedCharts"
    );

  console.log(
    "🔥 RAW savedCharts:",
    storedCharts
  );

  // =====================================
  // 🚫 NO SAVED CHARTS
  // =====================================

  if (!storedCharts) {

    console.log(
      "⚠️ No saved charts found"
    );

    return;
  }

try {

  const charts =
    JSON.parse(storedCharts);

  const repairedCharts =
    charts.map((chart) => ({

      ...chart,

      // ✅ Stable chart identity
      id:

        chart.id ||

        `${chart.airport || "UNK"}_${chart.name}`
    }));

  console.log(
    "🔥 FOUND charts:",
    repairedCharts
  );

  setSavedCharts(
    repairedCharts
  );

} catch (err) {

  console.error(
    "❌ Failed parsing saved charts:",
    err
  );
}

}, []);



  const [showTurbMonitor, setShowTurbMonitor] = useState(false);
  const [turbCallsign, setTurbCallsign] = useState('');
  const [turbAircraft, setTurbAircraft] = useState('');
  const [monitoringStatus, setMonitoringStatus] = useState('Idle');
  const [turbLog, setTurbLog] = useState([]);
  const lastZoomAboveThresholdRef = useRef(false);
  const [runwayData, setRunwayData] = useState({});
  const [selectedIcao, setSelectedIcao] = useState(null);
  const runwayMarkersRef = useRef([]);
  const [showAirportMenu, setShowAirportMenu] = useState(false);
  const [ficonNotams, setFiconNotams] = useState([]);  // Store filtered FICON NOTAMs
  const [modalOpen, setModalOpen] = useState(false);
  const [pdpLogs, setPdpLogs] = useState([]);
  const [pdpCallsignFiled, setPdpCallsignFiled] = useState('');
  const [pdpCallsignSpoken, setPdpCallsignSpoken] = useState('');
  const [pdpPairs, setPdpPairs] = useState([]);
  const [pdpActive, setPdpActive] = useState(false);
  const [pdpTranscript, setPdpTranscript] = useState("");
  const [spokenCallsign, setSpokenCallsign] = useState(null);
  const [showPdpBox, setShowPdpBox] = useState(true);
  const [pdpCallsign, setPdpCallsign] = useState("");
  const [pdpFiledCallsign, setPdpFiledCallsign] = useState("");
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [route, setRoute] = useState('');
  const [altitude, setAltitude] = useState('');
  const [map, setMap] = useState(null);
  const [routeResult, setRouteResult] = useState(null);
  const [logMessages, setLogMessages] = useState([]);
  const [allPireps, setAllPireps] = useState([]); // Store all PIREPs received
  const [pirepMarkers, setPirepMarkers] = useState([]);  // Store PIREP markers
  const aircraftMarkerMap = useRef({});

  // =====================================
  // ✈️ LIVE TRAFFIC
  // Provider-neutral FICON-X traffic feed.
  // Backend currently uses ADS-B Exchange;
  // clients remain independent of provider.
  // =====================================
  const [trafficOn, setTrafficOn] = useState(true);
  const [trafficAircraft, setTrafficAircraft] = useState([]);
  const trafficMarkersRef = useRef({});
  const trafficFetchInProgressRef = useRef(false);
  const trafficLastCenterRef = useRef(null);

  const [liveTracking, setLiveTracking] = useState(false);  // For Live PIREP tracking
  const [runwayColorsOn, setRunwayColorsOn] = useState(true);
  const [ficonOn, setFiconOn] = useState(true);
  const [crosswindOn, setCrosswindOn] = useState(false);
  const [wingspanOn, setWingspanOn] = useState(true);
  const [taxiwayOn, setTaxiwayOn] = useState(true);
  const [showAirportWeather, setShowAirportWeather] = useState(false);
  const showAirportWeatherRef = useRef(showAirportWeather);
  const [airportMarkers, setAirportMarkers] = useState([]); // Store airport markers
  const [obstructionsOn, setObstructionsOn] = useState(true);
  const obstructionMarkersRef = useRef([]);
  const [routeTokens, setRouteTokens] = useState([]);
  const [activeTokenIndex, setActiveTokenIndex] = useState(-1);
  const [routeSuggestions, setRouteSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [routeLegs, setRouteLegs] = useState([]);     // holds array of fixes
  const [showRoutePanel, setShowRoutePanel] = useState(true);  // toggle panel
  const [showRadar, setShowRadar] = useState(true);
  const [radarVisible, setRadarVisible] = useState(true);
  const [sigmetVisible, setSigmetVisible] = useState(false);
  const [airmetVisible, setAirmetVisible] = useState(false);
  const [showSigmets, setShowSigmets] = useState(false);
  const [showAirmets, setShowAirmets] = useState(false);
  const [fixes, setFixes] = useState({});
  const [airports, setAirports] = useState({});
  const [vors, setVors] = useState({});
  const [burnRate, setBurnRate] = useState("");
  const [tasKts, setTasKts] = useState(""); 
  const [selectedRouteString, setSelectedRouteString] = useState('');
  const [routeConfirmed, setRouteConfirmed] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // ✅ Add this
  const [originData, setOriginData] = useState(null);
  const [destinationData, setDestinationData] = useState(null);
  const [hazardData, setHazardData] = useState(null);

  const [originPireps, setOriginPireps] = useState([]);
  const [destinationPireps, setDestinationPireps] = useState([]);
  const [confirmedRouteString, setConfirmedRouteString] = useState("");

  const [taxiOutMinutes, setTaxiOutMinutes] = useState("");
  const [taxiOutBurn, setTaxiOutBurn] = useState("");
  const [taxiInMinutes, setTaxiInMinutes] = useState("");
  const [taxiInBurn, setTaxiInBurn] = useState("");
  const [contingencyMinutes, setContingencyMinutes] = useState("");
  const [contingencyBurn, setContingencyBurn] = useState("");

  const [reserveMinutes, setReserveMinutes] = useState("");
  const [reserveBurn, setReserveBurn] = useState("");
  const [isFlightActive, setIsFlightActive] = useState(false);
  const isFlightActiveRef = useRef(false);
  const [activeRoute, setActiveRoute] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [localFlightActive, setLocalFlightActive] = useState(false);
  const aircraftMarkerRef = useRef(null);
  const [savedCharts, setSavedCharts] = useState(() => {
    // Load from localStorage if available
    const stored = localStorage.getItem("savedCharts");
    return stored ? JSON.parse(stored) : [];
  });

  const DEV_SIM_MODE = false;
  const [aircraftPosition, setAircraftPosition] = useState(null);
  const [aircraftHeading, setAircraftHeading] = useState(0);
  const [aircraftGroundspeed, setAircraftGroundspeed] = useState(null);
  const [simStarted, setSimStarted] = useState(false);
  const simHasStartedRef = useRef(false);
  const [currentLegIndex, setCurrentLegIndex] = useState(0);

  const [routeInput, setRouteInput] = useState("");
  const [alternateInput, setAlternateInput] = useState("");
  const alternateMarkersRef = useRef([]);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [alternates, setAlternates] = useState([]);
  const [loadingAlts, setLoadingAlts] = useState(false);
  const isActiveFlight = sessionStorage.getItem("flightSessionActive") === "true";
  const [showConfirmBox, setShowConfirmBox] = useState(true);
  const [showChartsPanel, setShowChartsPanel] = useState(false);
  const [showChartBrowser, setShowChartBrowser] = useState(false);
  const [chartSearch, setChartSearch] = useState("");
  const [chartResults, setChartResults] = useState([]);
  const cleanAlt = alternateInput?.trim().toUpperCase();
  const [openChartWindows, setOpenChartWindows] = useState([]);
  const AUTH_ENABLED = false;
  const [showPilotProfile, setShowPilotProfile] = useState(false);
  const [showPersonalMinimums, setShowPersonalMinimums] = useState(false);
  const [callsign, setCallsign] = useState("");
  const [mapZoom, setMapZoom] = useState(0);

  const [airportCache, setAirportCache] = useState({});

  // =====================================
// 🐞 SQUAWK ADMIN EDITING
// =====================================

const [
  editingSquawkId,
  setEditingSquawkId
] = useState(null);

const [
  squawkEditStatus,
  setSquawkEditStatus
] = useState("OPEN");

const [
  squawkEditResponse,
  setSquawkEditResponse
] = useState("");

const [
  squawkUpdateSaving,
  setSquawkUpdateSaving
] = useState(false);

  // =====================================
// 🐞 SQUAWK BOARD
// =====================================

const [
  squawkTab,
  setSquawkTab
] = useState("report");

const [
  squawkBoard,
  setSquawkBoard
] = useState([]);

const [
  squawkBoardLoading,
  setSquawkBoardLoading
] = useState(false);

  // =====================================
// 🐞 BETA SQUAWK SYSTEM
// =====================================

const [
  showSquawkPanel,
  setShowSquawkPanel
] = useState(false);

// =====================================
// 🐞 SQUAWK FORM DATA
// =====================================

const [
  squawkCategory,
  setSquawkCategory
] = useState("Routing");

const [
  squawkDescription,
  setSquawkDescription
] = useState("");

const [
  squawkExpected,
  setSquawkExpected
] = useState("");

// =====================================
// ✈️ IN-FLIGHT CAMERA FOLLOW MODE
// =====================================

// true  = camera follows aircraft
// false = pilot has manually panned map
const [followAircraft, setFollowAircraft] = useState(true);

const userInteractingRef = useRef(false);

  const [

  showFlightsModal,

  setShowFlightsModal

] = useState(false);

  const [
  showAuthModal,
  setShowAuthModal
] = useState(false);

  const [
  pendingInsert,
  setPendingInsert
] = useState(null);

  const rubberBandCoordsRef =
  useRef(null);

  const rubberBandModeRef =
  useRef(false);

const dragSegmentRef =
  useRef(null);

  const holdTimerRef =
  useRef(null);

  const [
  rubberBandMode,
  setRubberBandMode
] = useState(false);

const [
  rubberBandCoords,
  setRubberBandCoords
] = useState(null);

const [
  dragSegment,
  setDragSegment
] = useState(null);

  const [
  showSegmentEditor,
  setShowSegmentEditor
] = useState(false);

const [
  segmentInsertFix,
  setSegmentInsertFix
] = useState("");

  const [

  selectedSegment,

  setSelectedSegment

] = useState(null);

  const [

  procedureConfig,

  setProcedureConfig

] = useState({

  departure: {

    runway: "",

    sid: "",

    transition: ""
  },

  arrival: {

    runway: "",

    star: "",

    transition: "",

    approach: "",

    iaf: ""
  }
});

// =====================================
// ✈️ PROCEDURE CONFIG HELPERS
// =====================================
const updateDepartureProcedure = (

  updates

) => {

  console.log(
    "🛫 DEPARTURE UPDATE",
    updates
  );

  setProcedureConfig(prev => ({

    ...prev,

    departure: {

      ...prev.departure,

      ...updates
    }
  }));
};

const updateArrivalProcedure = (

  updates

) => {

  setProcedureConfig(prev => ({

    ...prev,

    arrival: {

      ...prev.arrival,

      ...updates
    }
  }));
};











  const [

  showProcedureOptions,

  setShowProcedureOptions

] = useState(false);

  const [

  availableIAFs,

  setAvailableIAFs

] = useState([]);

const [

  selectedArrivalIAF,

  setSelectedArrivalIAF

] = useState("");

const [

  showProcedureLabels,

  setShowProcedureLabels

] = useState(true);

const [

  showProcedureMissed,

  setShowProcedureMissed

] = useState(true);

const [

  showProcedureHolds,

  setShowProcedureHolds

] = useState(true);

  const [

  selectedArrivalToken,

  setSelectedArrivalToken

] = useState(null);

  const [
  availableStars,
  setAvailableStars
] = useState([]);

const [
  selectedSid,
  setSelectedSid
] = useState("");

const [
  starDetails,
  setStarDetails
] = useState(null);

const [
  starRunways,
  setStarRunways
] = useState([]);

const [
  starTransitions,
  setStarTransitions
] = useState([]);

const [
  selectedStarRunway,
  setSelectedStarRunway
] = useState("");

const [
  availableSids,
  setAvailableSids
] = useState([]);

const [
  sidExits,
  setSidExits
] = useState({});

const [
  sidDetails,
  setSidDetails
] = useState(null);



const [
  sidTransitions,
  setSidTransitions
] = useState([]);


const [
  departureRunways,
  setDepartureRunways
] = useState([]);


 const [
  departureRunway,
  setDepartureRunway
] = useState("");

const [
  arrivalRunway,
  setArrivalRunway
] = useState("");

const [
  sidRunways,
  setSidRunways
] = useState([]);

const [
  selectedSidRunway,
  setSelectedSidRunway
] = useState("");

const [
  selectedSidTransition,
  setSelectedSidTransition
] = useState("");

const [
  selectedStar,
  setSelectedStar
] = useState("");

const [
  selectedStarTransition,
  setSelectedStarTransition
] = useState(""); 

const [

  previousRouteState,

  setPreviousRouteState

] = useState(null);

const openChartWindow = (chart) => {

  console.log(
    "📄 OPEN CHART WINDOW:",
    chart.name
  );

  setOpenChartWindows(prev => [

    ...prev,

    {

      ...chart,

      id:

        Date.now() +

        "_" +

        Math.random(),

      x:
        120 +
        (prev.length * 40),

      y:
        120 +
        (prev.length * 40),

      width: 700,

      height: 900
    }
  ]);
};

  const [
  activeOverlayButtons,
  setActiveOverlayButtons
] = useState([]);

  const [
  aircraftType,
  setAircraftType
] = useState("");

const [
  cruiseMach,
  setCruiseMach
] = useState("");

const [
  zfw,
  setZfw
] = useState("");

const [
  tow,
  setTow
] = useState("");

const [
  ldw,
  setLdw
] = useState("");

const [
  mzfw,
  setMzfw
] = useState("");

const [
  mtow,
  setMtow
] = useState("");

const [
  mlw,
  setMlw
] = useState("");

const [
  payload,
  setPayload
] = useState("");

  const [
  dismissedAirportMenu,
  setDismissedAirportMenu
] = useState(false);

  const [dismissedAirportBox, setDismissedAirportBox] =
  useState(false);


 const [showWxPanel, setShowWxPanel] =
  useState(false);

const [wxSearchIcao, setWxSearchIcao] =
  useState("");

const searchedWeather =
  useAirportWeather(wxSearchIcao); 



  const [
  editingConfirmRoute,
  setEditingConfirmRoute
] = useState(false);

const [
  confirmRouteDraft,
  setConfirmRouteDraft
] = useState("");

  const [flightStage, setFlightStage] =
  useState("planning");

  const [showAirspaces, setShowAirspaces] =
  useState(false);

  const [
  directToPreview,
  setDirectToPreview
] = useState(null);

  const [
  showInflightBrief,
  setShowInflightBrief
] = useState(false);

  const [showVors, setShowVors] =
  useState(false);

  const [showFixes, setShowFixes] =
    useState(false);

  const [showTFRs, setShowTFRs] =
  useState(false);

  const [locationSearch, setLocationSearch] =
  useState("");

const simIndexRef = useRef(0);

const latestDestMinutesRef =
  useRef(null);

const latestAltMinutesRef =
  useRef(null);


// 🔥 Prevent recursive route resolving
const resolvingRef = useRef(false);

const [

  showProfileMenu,

  setShowProfileMenu

] = useState(false);

  const {
  user,
  logout
} = useAuth();

  const operationalAccess =
  user || mapZoom < 5;


  const [
  sharedAircraftState,
  setSharedAircraftState
] = useState(null);




const [
  sharedActiveLegIndex,
  setSharedActiveLegIndex
] = useState(0);

 
const [activeFlight, setActiveFlight] = useState(() => {

  try {

    const flight = JSON.parse(
      localStorage.getItem("activeFlight") || "null"
    );

    console.log(
      "🛬 MAP LOAD ARRIVAL:",
      flight?.routeStructure?.arrival
    );

    console.log(
      "🛬 MAP LOAD WAYPOINTS:",
      flight?.route?.waypoints
    );

    console.log(
      "🛬 MAP LOAD COORD COUNT:",
      flight?.route?.coords?.length
    );

    return flight;

  } catch {

    return null;
  }
});

  const [altRefresh, setAltRefresh] = useState(false);
  const [closedRunways, setClosedRunways] = useState({});
  const [showPirepForm, setShowPirepForm] = useState(false);
  const [drawingMode, setDrawingMode] = useState(false);
  const [routeCollapsed, setRouteCollapsed] = useState(false);
  const [altCollapsed, setAltCollapsed] = useState(false);

  const [enrouteHazards, setEnrouteHazards] = useState(null);

// 🔥 persist size
const [routeSize, setRouteSize] = useState(() => {

  return JSON.parse(
    localStorage.getItem("routeSize")
  ) || {

    width: 280,

    height: 220

  };

});

const [altSize, setAltSize] = useState(() => {

  return JSON.parse(
    localStorage.getItem("altSize")
  ) || {

    width: 280,

    height: 220

  };

});

// 🔥 persist position
const [routePos, setRoutePos] = useState(() => {

  return JSON.parse(
    localStorage.getItem("routePos")
  ) || {

    x:
      window.innerWidth -
      routeSize.width -
      20,

    y:
      window.innerHeight -
      450

  };

});

const [altPos, setAltPos] = useState(() => {

  return JSON.parse(
    localStorage.getItem("altPos")
  ) || {

    x:
      window.innerWidth -
      altSize.width -
      20,

    y:
      window.innerHeight -
      230

  };

});

const canvasRef = useRef(null);
const drawing = useRef(false);

const [flights, setFlights] = useState([]);
const [showFlightsPanel, setShowFlightsPanel] = useState(false);




// =====================================
// 🌦️ STABLE WEATHER ICAOS
// =====================================

const flightSource =
  activeRoute || activeFlight;

// 🔥 STABLE DESTINATION
const destIcao =

  activeRoute?.meta?.destination ||

  activeFlight?.meta?.destination ||

  activeRoute?.destination ||

  activeFlight?.destination ||

  destination ||

  "";

// 🔥 STABLE ORIGIN
const originIcao =

  activeRoute?.meta?.origin ||

  activeFlight?.meta?.origin ||

  activeRoute?.origin ||

  activeFlight?.origin ||

  origin ||

  "";

const altIcao =

  activeRoute?.alternate?.icao ||

  activeFlight?.alternate?.icao ||

  "";

// =====================================
// 🌦️ WEATHER HOOKS
// =====================================



const destWeather =
  useAirportWeather(destIcao);

const originWeather =
  useAirportWeather(originIcao);

const altWeather =
  useAirportWeather(altIcao);

// =====================================
// 🌦️ LIVE IN-FLIGHT ETA WEATHER
// =====================================

const [
  liveDestEtaWeather,
  setLiveDestEtaWeather
] = useState(null);

const [
  liveAltEtaWeather,
  setLiveAltEtaWeather
] = useState(null);

// =====================================
// ⚠️ IN-FLIGHT WEATHER WARNINGS
// =====================================

const [
  inflightWeatherWarnings,
  setInflightWeatherWarnings
] = useState([]);


const [mapReady, setMapReady] = useState(false); 

const bringRouteLayersToFront = () => {

  const map = mapRef.current;

  if (!map) return;

  const layersToPromote = [

    // 🔵 Base route
    "selected-route",
    "main-route-layer",

    // 🏷️ Labels
    "route-fix-labels",

    // ✨ Active leg glow
    "active-leg-glow",

    // 🟣 Main magenta line
    "active-leg-main"

  ];

  layersToPromote.forEach((id) => {

    if (map.getLayer(id)) {

      try {

        map.moveLayer(id);

        console.log(
          "⬆️ Promoted layer:",
          id
        );

      } catch (e) {

        console.warn(
          "⚠️ Failed to promote:",
          id
        );
      }
    }
  });
};

const [

  selectedArrivalRunway,

  setSelectedArrivalRunway

] = useState("");

const [

  selectedArrivalTransition,

  setSelectedArrivalTransition

] = useState("");

const [

  selectedApproach,

  setSelectedApproach

] = useState("");

const [

  arrivalRunways,

  setArrivalRunways

] = useState([]);

const [

  arrivalTransitions,

  setArrivalTransitions

] = useState([]);

const [

  availableApproaches,

  setAvailableApproaches

] = useState([]);

const [

  activeArrivalConfig,

  setActiveArrivalConfig

] = useState({

  star: "",

  runway: "",

  transition: "",

  approach: ""
});

const removeSavedChart = (chartToRemove) => {

  const updated = savedCharts.filter(

    c =>

      (
        c.uniqueId ||

        `${c.airport || ""}_${c.name}_${c.url}`
      )

      !==

      (
        chartToRemove.uniqueId ||

        `${chartToRemove.airport || ""}_${chartToRemove.name}_${chartToRemove.url}`
      )
  );

  // 💾 Update state
  setSavedCharts(updated);

  // 💾 Persist
  localStorage.setItem(

    "savedCharts",

    JSON.stringify(updated)
  );

  console.log(
    "🗑️ Removed chart:",
    chartToRemove.name
  );
};


const plotProcedureOverlay = (
  overlay
) => {

  if (!map) return;

  const fixes =

  overlay.fixes || [];

console.log(
  "🚨 RECEIVED FIX NAMES:",
  Date.now(),
  fixes?.map(f => f.name)
);

console.log(
  "🚨 PLOT CALLED"
);

console.log(
  "🚨 SEGMENT COUNT:",
  overlay?.segments?.length
);

console.log(
  "🚨 FIX COUNT:",
  overlay?.fixes?.length
);

console.log(
  "🚨 STACK TRACE"
);

console.trace();

  console.log(
  "🚨 OVERLAY FIXES:",
  fixes
);

console.log(
  "🚨 OVERLAY FIX NAMES",
  fixes.map(
    f => ({
      name: f.name,
      role: f.legRole,
      type: f.leg_type
    })
  )
);

console.log(
  "🚨 FIX COUNT:",
  fixes.length
);

  const sourceId =
    "procedure-overlay";

  const layerId =
    "procedure-overlay-line";

  const labelLayerId =
    "procedure-overlay-labels";


// =====================================
// CLEANUP
// =====================================

if (
  map.getLayer(
    "procedure-overlay-markers"
  )
) {

  map.removeLayer(
    "procedure-overlay-markers"
  );
}

if (
  map.getLayer(labelLayerId)
) {

  map.removeLayer(
    labelLayerId
  );
}

if (
  map.getLayer(layerId)
) {

  map.removeLayer(
    layerId
  );
}

if (
  map.getSource(sourceId)
) {

  map.removeSource(
    sourceId
  );
}

  // =====================================
  // BUILD FEATURE COLLECTION
  // =====================================

  const features = [];

  const connectorFeatures = [];

  const markerFeatures = []; 

  const labelFeatures = [];

  const restrictionFeatures = [];

const visibleSegments = (
  overlay.segments || []
).filter(segment => {

  // Always draw the common STAR body
  if (segment.name === "ALL")
    return true;

  // Always draw the approach
  if (segment.name === overlay.approach)
    return true;

  // Always draw non-feeder edges
  if (segment.role !== "FEEDER")
    return true;

  // Only draw the selected feeder
  return (
    segment.transition ===
    selectedStarTransition
  );

});

for (const segment of visibleSegments) {

  console.log(
    "🧩 FULL SEGMENT",
    JSON.stringify(
      segment,
      null,
      2
    )
  );

    const coords =
      segment.coordinates || [];

    if (coords.length < 2) {
      continue;
    }

    // =====================================
    // SEMANTIC COLORS
    // =====================================

    const legRole =
      (
        segment.leg_role || ""
      ).toUpperCase();

    const pathType =
      (
        segment.path_type || ""
      ).toUpperCase();

    let branchColor =
      "#00FFFF";

    // =====================================
    // IAF
    // =====================================

    if (
      legRole === "IAF"
    ) {

      branchColor =
        "#33AAFF";
    }

    // =====================================
    // FAF / FINAL
    // =====================================

    if (
      legRole === "FAF"
    ) {

      branchColor =
        "#00FF66";
    }

    // =====================================
    // RUNWAY
    // =====================================

    if (
      legRole === "RUNWAY"
    ) {

      branchColor =
        "#FF66FF";
    }

    // =====================================
    // MISSED APPROACH
    // =====================================

    if (

      legRole === "MISSED"

      ||

      (
        pathType === "DF"
        &&
        segment.from?.startsWith(
          "RW"
        )
      )

    ) {

      branchColor =
        "#FF9900";
    }

    // =====================================
    // HOLD
    // =====================================

    if (
      legRole ===
      "MISSED_HOLD"
    ) {

      branchColor =
        "#AA66FF";
    }

    // =====================================
    // MAIN SEGMENT
    // =====================================

    features.push({

      type: "Feature",

      properties: {

        name:
          segment.name,

        segmentType:
          segment.type,

        color:
          branchColor
      },

      geometry: {

        type: "LineString",

        coordinates:
          coords
      }
    });

    // =====================================
    // SEMANTIC LABELS
    // =====================================

    const endCoord =
      coords[
        coords.length - 1
      ];

    const startCoord =
      coords[0];


    if (endCoord) {

if (

  startCoord &&
  segment.from &&
  legRole === "FAF"

) {

  const startLabelLines = [

    segment.from

  ];

  const startAltitude =
    segment.altitude_constraint;

  if (

    startAltitude?.type ===
    "at_or_above"

  ) {

    startLabelLines.push(

      `${parseInt(
        startAltitude.altitude,
        10
      )}+`

    );
  }

  else if (

    startAltitude?.altitude

  ) {

    startLabelLines.push(

      `${parseInt(
        startAltitude.altitude,
        10
      )}`

    );
  }

  startLabelLines.push(
    "IAF"
  );

  labelFeatures.push({

    type: "Feature",

    properties: {

      label:
        startLabelLines.join("\n"),

      color:
        "#33AAFF"
    },

    geometry: {

      type: "Point",

      coordinates:
        startCoord
    }
  });
}

      const altitude =
        segment.altitude_constraint;

      const speed =
        segment.speed_constraint;

      let labelLines = [];

      // =================================
      // FIX NAME
      // =================================

      if (segment.to) {

        labelLines.push(
          segment.to
        );
      }

            // =================================
      // SEMANTIC MARKERS
      // =================================

      let markerType =
        "fix";

      if (
        legRole === "IAF"
      ) {

        markerType =
          "iaf";
      }

      if (
        legRole === "FAF"
      ) {

        markerType =
          "faf";
      }

      if (
        legRole === "RUNWAY"
      ) {

        markerType =
          "runway";
      }

      if (
        legRole ===
        "MISSED_HOLD"
      ) {

        markerType =
          "hold";
      }

      if (

        pathType === "DF"
        &&
        segment.from?.startsWith(
          "RW"
        )

      ) {

        markerType =
          "missed";
      }

      if (markerType) {

        markerFeatures.push({

          type: "Feature",

          properties: {

            markerType,

            color:
              branchColor
          },

          geometry: {

            type: "Point",

            coordinates:
              endCoord
          }
        });
      }
/*
      // =================================
      // ALTITUDE
      // =================================

      if (altitude) {

        if (
          altitude.type ===
          "at_or_above"
        ) {

          labelLines.push(
            `${parseInt(
              altitude.altitude,
              10
            )}+`
          );
        }

        else if (
          altitude.type ===
          "at_or_below"
        ) {

          labelLines.push(
            `${parseInt(
              altitude.altitude,
              10
            )}-`
          );
        }

        else if (
          altitude.type ===
          "between"
        ) {

          labelLines.push(

            `${parseInt(
              altitude.lower,
              10
            )}-${parseInt(
              altitude.upper,
              10
            )}`
          );
        }

        else if (
          altitude.altitude
        ) {

          labelLines.push(
            `${parseInt(
              altitude.altitude,
              10
            )}`
          );
        }
      }

      // =================================
      // SPEED
      // =================================

      if (speed) {

        labelLines.push(
          `${speed}KT`
        );
      }
*/
      // =================================
      // SEMANTIC LABELS
      // =================================

      if (
        legRole === "FAF"
      ) {

        labelLines.push(
          "FAF"
        );
      }

      if (
        legRole === "IAF"
      ) {

        labelLines.push(
          "IAF"
        );
      }

      if (
        legRole === "RUNWAY"
      ) {

        labelLines.push(
          "RWY"
        );
      }

      if (
        legRole ===
        "MISSED_HOLD"
      ) {

        labelLines.push(
          "HOLD"
        );
      }

      if (

        pathType === "DF"
        &&
        segment.from?.startsWith(
          "RW"
        )

      ) {

        labelLines.push(
          "MISSED"
        );
      }

      // =================================
      // CREATE LABEL
      // =================================

      const labelText =
        labelLines.join("\n");

      labelFeatures.push({

        type: "Feature",

        properties: {

          label:
            labelText,

          color:
            branchColor
        },

        geometry: {

          type: "Point",

          coordinates:
            endCoord
        }
      });
    }

    // =====================================
    // VISUAL CONNECTORS
    // =====================================

    if (
      features.length >= 2
    ) {

      const prev =
        features[
          features.length - 2
        ];

      const curr =
        features[
          features.length - 1
        ];

      const prevCoords =
        prev.geometry.coordinates;

      const currCoords =
        curr.geometry.coordinates;

      const prevEnd =
        prevCoords[
          prevCoords.length - 1
        ];

      const currStart =
        currCoords[0];

      if (
        prevEnd &&
        currStart
      ) {

        connectorFeatures.push({

          type: "Feature",

          properties: {

            connector: true,

            color:
              "#666666"
          },

          geometry: {

            type: "LineString",

            coordinates: [
              prevEnd,
              currStart
            ]
          }
        });
      }
    }
  }

for (const fix of fixes) {

  console.log(
    "🚨 FIX LOOP:",
    fix.name,
    fix.altitude_constraint,
    fix.speed_constraint
  );

  if (
    !fix.altitude_constraint &&
    !fix.speed_constraint
  ) {
    continue;
  }

  if (
    !fix.altitude_constraint &&
    !fix.speed_constraint
  ) {
    continue;
  }

  const labelLines = [];

  const altitude =
    fix.altitude_constraint;

  const speed =
    fix.speed_constraint;

  if (altitude) {

    if (
      altitude.type === "at_or_above"
    ) {

      labelLines.push(
        `${altitude.altitude}+`
      );
    }

    else if (
      altitude.type === "at_or_below"
    ) {

      labelLines.push(
        `${altitude.altitude}-`
      );
    }

    else if (
      altitude.type === "between"
    ) {

      labelLines.push(
        `${altitude.lower}-${altitude.upper}`
      );
    }

    else if (
      altitude.altitude
    ) {

      labelLines.push(
        altitude.altitude
      );
    }
  }

  if (speed) {

    labelLines.push(
      `${speed}KT`
    );
  }

  console.log(
  "🚨 RESTRICTION FEATURE",
  {
    fix: fix.name,
    altitude: fix.altitude_constraint,
    speed: fix.speed_constraint,
    lat: fix.lat,
    lon: fix.lon
  }
);
  

  restrictionFeatures.push({

    type: "Feature",

    properties: {

      label:
        labelLines.join("\n"),

      color:
        "#00FFFF"
    },

    geometry: {

      type: "Point",

      coordinates: [

        fix.lon,

        fix.lat
      ]
    }
  });
}

  // =====================================
  // SOURCE
  // =====================================

  console.log("########################");
console.log("FEATURE COUNT", features.length);

features.forEach((f, i) => {

    if (f.geometry.type === "LineString") {

        console.log(
            i,
            f.properties.name,
            f.properties.color,
            f.geometry.coordinates
        );

    }

});

console.log("########################");

  map.addSource(sourceId, {

    type: "geojson",

    data: {

      type:
        "FeatureCollection",

features: [

  ...features,

  ...connectorFeatures,

  ...labelFeatures,

  ...restrictionFeatures,

  ...markerFeatures
]
    }
  });

  // =====================================
  // MAIN LINE LAYER
  // =====================================

  /*map.addLayer({

    id: layerId,

    type: "line",

    source: sourceId,

    filter: [
      "!=",
      ["geometry-type"],
      "Point"
    ],

    layout: {

      "line-cap":
        "round",

      "line-join":
        "round"
    },

    paint: {

      "line-color": [
        "get",
        "color"
      ],

      //"line-width":
      //  4,

      //"line-dasharray":
        //[2, 2],

      "line-opacity":
        0.95
    }
  });

  */

  // =====================================
// SEMANTIC MARKERS
// =====================================



map.addLayer({

  id:
    "procedure-overlay-markers",

  type:
    "circle",

  source:
    sourceId,

  filter: [
    "has",
    "markerType"
  ],

  paint: {

    "circle-radius": [

      "match",

      ["get", "markerType"],

      "runway", 8,

      "faf", 7,

      "iaf", 7,

      "missed", 7,

      "hold", 8,

      6
    ],

    "circle-color": [
      "get",
      "color"
    ],

    "circle-stroke-color":
      "#000000",

    "circle-stroke-width":
      2,

    "circle-opacity":
      0.95
  }
});

  // =====================================
  // LABEL LAYER
  // =====================================

  map.addLayer({

    id:
      labelLayerId,

    type:
      "symbol",

    source:
      sourceId,

    filter: [
      "has",
      "label"
    ],

layout: {

  "text-field":
    ["get", "label"],

  "text-size":
    14,

  "text-offset":
    [0, 3],

  "text-anchor":
    "top",

  "text-font": [
    "Open Sans Bold",
    "Arial Unicode MS Bold"
  ],

  "text-allow-overlap":
    true
},

 paint: {

    "text-color": [
      "get",
      "color"
    ],

    "text-halo-color":
      "#000000",

    "text-halo-width":
      3,

    "text-halo-blur":
      1
  }
});

  console.log(
    "✅ Segmented procedure overlay plotted"
  );
};

async function cacheAirportData(
  icao,
  airportType,
  etaHours
) {

    if (!icao) return;

    // Prevent duplicate downloads
    if (airportCache[icao]) {
        console.log("📦 Airport already cached:", icao);
        return;
    }

    // Placeholder entry
    setAirportCache(prev => ({
        ...prev,
        [icao]: {
            icao,
            airportType,
            etaHours,
            loading: true
        }
    }));

    console.log("📦 Downloading airport data:", icao);

    try {

        const notamRes = await fetch(
            `${backendURL}/get_relevant_notams?airport=${icao}`
        );

        const notamData = await notamRes.json();

        setAirportCache(prev => ({
            ...prev,
            [icao]: {
                ...prev[icao],
                loading: false,
                notams: notamData.notams || [],
                closedRunways: notamData.closed_runways || [],
                summaries: notamData.summaries || []
            }
        }));

        console.log("✅ Airport cached:", icao);

        // Return the downloaded data immediately so callers
        // don't have to wait for React state to update.
        return {
            notams: notamData.notams || [],
            closedRunways: notamData.closed_runways || [],
            summaries: notamData.summaries || []
        };

    } catch (err) {

        console.error("❌ Airport cache failed:", icao, err);

        setAirportCache(prev => ({
            ...prev,
            [icao]: {
                ...prev[icao],
                loading: false,
                error: true
            }
        }));

        // Return an empty structure on failure.
        return {
            notams: [],
            closedRunways: [],
            summaries: []
        };

    }

}

useEffect(() => {
    console.log("📦 airportCache updated:", airportCache);
}, [airportCache]);

const loadProcedureOverlay = async (

  star,
  airport,

  transition = null,
  runway = null

) => {

  try {

    console.log(
      "🛬 LOADING PROCEDURE OVERLAY",
      {
        star,
        airport,
        transition,
        runway
      }
    );

    const res = await fetch(

      "https://ficon-x.com/route/procedure_overlay",

      {

        method: "POST",

        headers: {
          "Content-Type":
            "application/json"
        },

        body: JSON.stringify({

          star,
          airport,

          transition,
          runway,

          iaf:
            selectedArrivalIAF


        })
      }
    );

const data =
  await res.json();

  console.log(
  "🚨 PROCEDURE OVERLAY RESPONSE:",
  data
);

console.log(
  "🚨 PROCEDURE OVERLAY FIXES:",
  data.fixes
);

console.log(
  "🚨 FULL OVERLAY FIX OBJECTS",
  fixes
);

console.log(
  "🛬 PROCEDURE OVERLAY:",
  data
);

if (
  data.available_iafs
) {

  console.log(
    "🛬 OVERWRITE PROCEDURE OVERLAY:",
    data.available_iafs
  );

  setAvailableIAFs(
    data.available_iafs
  );
}

if (!data.success) {

  console.warn(
    "⚠️ Overlay failed"
  );

  return;
}

console.log(
  "🚨 PROCEDURE OVERLAY RESPONSE",
  data
);

console.log(
  "🚨 SEGMENT COUNT",
  data?.segments?.length
);

console.log(
  "🚨 FIRST 10 SEGMENTS",
  data?.segments?.slice(0, 10)
);

console.log(
  "🚨 PLOT SOURCE = PROCEDURE OVERLAY"
);

//plotProcedureOverlay(data);

} catch (err) {

    console.error(
      "❌ Overlay fetch failed:",
      err
    );
  }
};


window.loadArrivalChain = async (

  star,
  airport,
  transition,
  approach,
  runway,
  iaf = ""

) => {

  console.log(
  "🚨 SENDING IAF:",
  iaf
);

console.log(
  "🚨 ARRIVAL CHAIN REQUEST",
  {
    airport,
    star,
    transition,
    runway,
    approach,
    iaf
  }
);
  const res = await fetch(

    "https://ficon-x.com/route/procedure_overlay",

    {

      method: "POST",

      headers: {

        "Content-Type":
          "application/json"
      },

body: JSON.stringify({

  star,
  airport,
  transition,
  approach,
  runway,
  iaf
})
    }
  );

  const data =
    await res.json();

    console.log(
  "🚨 PROCEDURE OVERLAY FIXES",
  data.fixes?.map(
    f => f.name
  )
);

   console.log(
  "🚨 ARRIVAL CHAIN RAW RESPONSE",
  data
);

console.log(
  "🚨 DEBUG MARKER DIRECT",
  data.debug_marker
);

console.log(
  "🚨 DEBUG FIXES DIRECT",
  data.debug_fix_names
);

console.log(
  "🚨 RESPONSE KEYS",
  Object.keys(data)
);

console.log(
  "🚨 ARRIVAL CHAIN FIX NAMES",
  Date.now(),
  data?.fixes?.map(f => f.name)
);


  console.log(
    "🧠 ARRIVAL CHAIN:",
    data
  );

  console.log(
  "🚨 ARRIVAL CHAIN FIXES:",
  data.fixes
);

  //if (data.available_iafs) {

  //console.log(
  //  "🛬 OVERWRITE ARRIVAL CHAIN:",
  //  data.available_iafs
  //);

  //setAvailableIAFs(
  //  data.available_iafs
  //);
//}

  if (
    !data?.segments
  ) {
    return;
  }

console.log(
  "🚨 BADCA",
  data.fixes.find(
    f => f.name === "BADCA"
  )
);

console.log(
  "🚨 WACHU",
  data.fixes.find(
    f => f.name === "WACHU"
  )
);
console.log(
  "🚨 SENDING FIX NAMES:",
  data.fixes?.map(f => f.name)
);

console.log(
  "🚨 BADCA FROM API",
  data.fixes?.find(
    f => f.name === "BADCA"
  )
);

console.log(
  "🚨 PLOT SOURCE = ARRIVAL CHAIN"
);

console.log("🚨 DATA KEYS:", Object.keys(data));
console.log("🚨 DATA:", data);

plotProcedureOverlay({
 segments: data.segments,
  fixes: data.fixes || []
});

};

useEffect(() => {

  if (
    !pendingInsert
  ) {
    return;
  }

  console.log(
    "🧲 EXECUTING INSERT",
    pendingInsert
  );

  onInsertAfter(
    pendingInsert.token,
    pendingInsert.index,
    pendingInsert.fix
  );

  setPendingInsert(
    null
  );

}, [pendingInsert]);

useEffect(() => {

  console.log(
    "🛬 ARRIVAL CONFIG:",
    procedureConfig.arrival
  );

}, [procedureConfig]);

useEffect(() => {

  console.log(
    "🚀 PROCEDURE CONFIG CHANGED:",
    procedureConfig
  );

}, [

  procedureConfig
]);

/*
useEffect(() => {

  if (
    !activeArrivalConfig
  ) {
    return;
  }

  const {

    runway,
    transition,
    approach

  } = activeArrivalConfig;

  // =====================================
  // ✈️ REQUIRE FULL SELECTION
  // =====================================

  if (

    !selectedArrivalToken ||
    !runway ||
    !approach

  ) {

    console.log(
      "⏸ Waiting for full arrival selection"
    );

    return;
  }

  const rebuildArrival =
    async () => {

      try {

        console.log(
          "🛬 REBUILD ARRIVAL:",
          activeArrivalConfig
        );

        console.log(
          "🛫 DESTINATION ICAO:",
          activeFlight?.meta?.destination
        );

        // =====================================
        // ✈️ FETCH ARRIVAL GEOMETRY
        // =====================================

        const response =
          await fetch(

            "https://ficon-x.com/route/build-arrival-geometry",

            {

              method: "POST",

              headers: {

                "Content-Type":
                  "application/json"
              },

              body: JSON.stringify({

                airport:
                  activeFlight?.meta?.destination,

                star:
                  selectedArrivalToken,

                transition,

                runway,

                approach,

                current_route:
                  activeFlight?.routeStructure?.filedRoute || []
              })
            }
          );

        console.log(
          "🧪 FETCH RETURNED"
        );

        console.log(
          "🧪 RESPONSE STATUS:",
          response.status
        );

        const data =
          await response.json();

        console.log(
          "🧪 RESPONSE JSON PARSED"
        );

        console.log(
          "🛬 ARRIVAL BUILD RESPONSE:",
          data
        );

        if (
          !data.success ||
          !data.coords?.length
        ) {

          console.warn(
            "⚠️ Arrival build failed"
          );

          return;
        }

        // =====================================
        // ✈️ MAP INSTANCE
        // =====================================

        const map =
          mapRef.current;

        if (!map) {
          return;
        }

        // =====================================
        // 🧹 REMOVE OLD PREVIEW
        // =====================================

        if (
          map.getLayer(
            "arrival-preview-line"
          )
        ) {

          map.removeLayer(
            "arrival-preview-line"
          );
        }

        if (
          map.getSource(
            "arrival-preview-line"
          )
        ) {

          map.removeSource(
            "arrival-preview-line"
          );
        }

        // =====================================
        // ✈️ BUILD GEOJSON
        // =====================================

        const geojson = {

          type: "Feature",

          geometry: {

            type: "LineString",

            coordinates:
              data.coords
          }
        };

        // =====================================
        // ✈️ ADD PREVIEW SOURCE
        // =====================================

        map.addSource(

          "arrival-preview-line",

          {

            type: "geojson",

            data: geojson
          }
        );

        // =====================================
        // ✈️ ADD PREVIEW LAYER
        // =====================================

        map.addLayer({

          id:
            "arrival-preview-line",

          type: "line",

          source:
            "arrival-preview-line",

          layout: {

            "line-join":
              "round",

            "line-cap":
              "round"
          },

          paint: {

            "line-color":
              "#00BFFF",

            "line-width": 5,

            "line-opacity": 0.92
          }
        });

        console.log(
          "✅ Arrival preview plotted"
        );

        // =====================================
// ✈️ ARRIVAL FIX LABELS
// =====================================

if (
  map.getLayer(
    "arrival-preview-labels"
  )
) {

  map.removeLayer(
    "arrival-preview-labels"
  );
}

if (
  map.getSource(
    "arrival-preview-labels"
  )
) {

  map.removeSource(
    "arrival-preview-labels"
  );
}

const labelFeatures =
  data.waypoints

    .filter(wp =>

      wp.name &&
      wp.lat != null &&
      wp.lon != null
    )

    .map(wp => ({

      type: "Feature",

      geometry: {

        type: "Point",

        coordinates: [

          wp.lon,
          wp.lat
        ]
      },

      properties: {

        name: wp.name
      }
    }));

map.addSource(

  "arrival-preview-labels",

  {

    type: "geojson",

    data: {

      type:
        "FeatureCollection",

      features:
        labelFeatures
    }
  }
);

map.addLayer({

  id:
    "arrival-preview-labels",

  type: "symbol",

  source:
    "arrival-preview-labels",

  layout: {

    "text-field":
      ["get", "name"],

    "text-size": 12,

    "text-offset":
      [0, 1.2],

    "text-anchor":
      "top",

    "text-allow-overlap":
      false
  },

  paint: {

    "text-color":
      "#00FFFF",

    "text-halo-color":
      "#000000",

    "text-halo-width":
      1.5
  }
});

console.log(
  "✅ Arrival labels added"
);

        // =====================================
        // ✈️ EXISTING MAIN ROUTE
        // =====================================

        const existingCoords =
          activeFlight?.route?.coords || [];

        // =====================================
        // ✈️ EXISTING WAYPOINTS
        // =====================================

        const existingWaypoints =
          activeFlight?.route?.waypoints || [];

        console.log(
          "🧪 EXISTING COORDS:",
          existingCoords
        );
        console.log(
          "🧪 EXISTING WAYPOINTS:",
          existingWaypoints
        );

        // =====================================
        // ✈️ ARRIVAL WAYPOINTS
        // =====================================

        const arrivalWaypoints =
          data.waypoints || [];

        console.log(
          "🛬 ARRIVAL WAYPOINTS:",
          arrivalWaypoints
        );

        // =====================================
        // ✈️ REMOVE INVALID ARRIVAL FIXES
        // =====================================

        const drawableArrival =
          arrivalWaypoints.filter(

            wp =>

              wp.lat !== null &&
              wp.lon !== null
          );

        console.log(
          "🛬 DRAWABLE ARRIVAL:",
          drawableArrival
        );

        // =====================================
        // ✈️ NORMALIZE EXISTING FIXES
        // =====================================

        const existingFixNames =
          existingWaypoints.map(

            wp => (

              wp.name ||
              wp.id ||
              ""

            )

              .trim()
              .toUpperCase()
          );

        console.log(
          "🧪 EXISTING FIX NAMES:",
          existingFixNames
        );

        // =====================================
        // ✈️ FIND FIRST COMMON FIX
        // =====================================

        let spliceIndex = -1;

        for (
          const arrivalWp
          of drawableArrival
        ) {

          const arrivalName = (

            arrivalWp.name ||
            arrivalWp.id ||
            ""

          )

            .trim()
            .toUpperCase();

          const idx =
            existingFixNames.indexOf(
              arrivalName
            );

          if (idx >= 0) {

            spliceIndex = idx;

            console.log(
              "✅ COMMON FIX FOUND:",
              arrivalName
            );

            break;
          }
        }

        console.log(
          "⭐ FINAL SPLICE INDEX:",
          spliceIndex
        );

        // =====================================
        // ✈️ PRESERVE UPSTREAM
        // =====================================

        let preservedWaypoints = [];

        if (spliceIndex >= 0) {

          preservedWaypoints =
            existingWaypoints.slice(

              0,
              spliceIndex
            );
        }
        else {

          console.warn(
            "⚠️ NO COMMON FIX FOUND"
          );

          preservedWaypoints =
            existingWaypoints;
        }

        console.log(
          "🧪 PRESERVED WAYPOINTS:",
          preservedWaypoints
        );

        // =====================================
        // ✈️ FINAL MERGED ROUTE
        // =====================================

        const mergedWaypoints = [

          ...preservedWaypoints,

          ...drawableArrival
        ];

        console.log(
          "✈️ MERGED WAYPOINTS:",
          mergedWaypoints
        );

        // =====================================
        // ✈️ BUILD CLEAN COORDS
        // =====================================

        const mergedCoords =
          mergedWaypoints.map((wp) => {

            // existing coord array

            if (

              Array.isArray(
                wp.coordinates
              ) &&

              wp.coordinates.length === 2

            ) {

              return wp.coordinates;
            }

            // lat/lon object

            if (

              wp.lon !== undefined &&
              wp.lat !== undefined

            ) {

              return [
                wp.lon,
                wp.lat
              ];
            }

            return null;

          })

          .filter(Boolean);

        console.log(
          "🧪 CLEAN MERGED COORDS:",
          mergedCoords
        );

        // =====================================
        // ✈️ UPDATE ACTIVE FLIGHT
        // =====================================

        const updatedFlight = {

          ...activeFlight,

          route: {

            ...activeFlight.route,

            coords:
              mergedCoords,

            waypoints:
              mergedWaypoints
          }
        };

        console.log(
          "✈️ Updating activeFlight"
        );

        setActiveFlight(
          updatedFlight
        );

        // =====================================
        // ✈️ REPLOT MAIN ROUTE
        // =====================================

        console.log(
          "🧪 Calling plotRoute()"
        );

        plotRoute({

          routeText:
            updatedFlight
              ?.routeStructure
              ?.filedRoute
              ?.join(" "),

          coords:
            mergedCoords,

          waypoints:
            mergedWaypoints
        });

        console.log(
          "✈️ Tactical arrival route applied"
        );

      } catch (err) {

        console.error(
          "❌ Arrival rebuild failed:",
          err
        );
      }
    };

  rebuildArrival();

}, [

  activeArrivalConfig,
  selectedArrivalToken
]);

*/

useEffect(() => {
console.log(
  "🚨 START FLIGHT ARRIVAL:",
  activeFlight?.routeStructure?.arrival
);

console.log(
  "🚨 PROCEDURE CONFIG ARRIVAL:",
  procedureConfig?.arrival
);

console.log(
  "🚨 SELECTED ARRIVAL RUNWAY:",
  selectedArrivalRunway
);


  console.log(
    "🛬 ARRIVAL EFFECT STATE",
    {
      procedureArrival:
        procedureConfig?.arrival,

      savedArrival:
        activeFlight?.routeStructure?.arrival
    }
  );



const arrivalConfig =

  procedureConfig?.arrival?.approach

    ? procedureConfig.arrival

    : activeFlight?.routeStructure?.arrival;

      console.log(
  "🚨 USING ARRIVAL CONFIG:",
  arrivalConfig
);

const {

  star,
  transition,
  approach,
  iaf

} = arrivalConfig || {};

const runway =

  arrivalConfig?.runway ||

  selectedArrivalRunway ||

  (
    approach?.match(/RW(\d+[LRC]?)/)?.[1]
  ) ||

  "";

  console.log(
    "🛬 ACTIVE ARRIVAL CONFIG:",
    arrivalConfig
  );

  console.log(
    "🛬 ACTIVE APPROACH:",
    approach
  );

  console.log(
    "🛬 ACTIVE IAF:",
    iaf
  );

  console.log(
    "🧪 procedureConfig.arrival:",
    procedureConfig.arrival
  );
// =====================================
// VALID ARRIVAL CONFIG
//
// STAR is OPTIONAL now
// =====================================

if (

  !approach

) {

  return;
}

  const rebuildArrival =
    async () => {

      try {

        console.log(
          "🛬 REBUILD ARRIVAL:",
          procedureConfig.arrival
        );

        console.log(
          "🛫 DESTINATION ICAO:",
          activeFlight?.meta?.destination
        );

console.log(
  "🚀 BUILD ARRIVAL REQUEST",
  {

airport:

  destination ||

  activeFlight?.meta?.destination ||

  "",

    star,

    transition,

    runway,

    approach,

    iaf,
    

    current_route:
      activeFlight?.routeStructure?.filedRoute || []
  }
);
console.log(
  "🚀 BUILD ARRIVAL REQUEST"
);

console.log(
  "🚨 DEPARTURE VALUES",
  {
    departureRunway,
    selectedSid,
    selectedSidTransition
  }
);

console.log(
  "🚨 SAVED DEPARTURE",
  activeFlight?.routeStructure?.departure
);

console.log(
  "🚨 ARRIVAL DROPDOWN VALUES",
  {
    selectedArrivalRunway,
    selectedApproach,
    selectedArrivalIAF,
    selectedStar,
    selectedStarTransition
  }
);

console.log(
  "🚨 ARRIVAL SOURCE",
  {
    procedureArrival:
      procedureConfig?.arrival,

    savedArrival:
      activeFlight?.routeStructure?.arrival,

    arrivalConfig
  }
);
        const response =
          await fetch(

            "https://ficon-x.com/route/build-arrival-geometry",

            {

              method: "POST",

              headers: {

                "Content-Type":
                  "application/json"
              },

              

body: JSON.stringify({

  airport:
    destination ||
    activeFlight?.meta?.destination ||
    "",

  star:
    arrivalConfig?.star || "",

  transition:
    arrivalConfig?.transition || "",

  runway:
    arrivalConfig?.runway || "",

  approach:
    arrivalConfig?.approach || "",

  iaf:
    arrivalConfig?.iaf || "",

  current_route:
    activeFlight?.routeStructure?.filedRoute || []

})
            }
          );

        const data =
          await response.json();

          console.log(
  "🚨 DEBUG MARKER",
  data.debug_marker
);

console.log(
  "🚨 DEBUG FIX NAMES",
  data.debug_fix_names
);

          console.log(
  "🚨 ARRIVAL RESPONSE SUMMARY",
  {
    success: data.success,
    coords: data.coords?.length,
    waypoints: data.waypoints?.length,
    route: data.route?.length
  }
);

          console.log(
          "🛬 ARRIVAL GEOMETRY RESPONSE:",
          data
        );

        console.log(
          "🚨 BUILD-ARRIVAL FIXES:",
          data.fixes
        );

        console.log(
          "🛬 ARRIVAL RESPONSE:",
          data
        );

        if (data.available_iafs) {

          console.log(
            "🛬 OVERWRITE BUILD-ARRIVAL IAFS:",
            data.available_iafs
          );


          setAvailableIAFs(
            data.available_iafs
          );
        }

        console.log(
          "🛬 ARRIVAL GEOMETRY RESPONSE:",
          data
        );

        console.log(
          "🛬 SUCCESS:",
          data.success
        );

        console.log(
          "🛬 COORD COUNT:",
          data.coords?.length
        );

        console.log(
          "🛬 COORDS:",
          data.coords
        );

        if (

          !data.success ||
          !data.coords?.length

        ) {

          console.log(
            "❌ ARRIVAL GEOMETRY REJECTED"
          );

          return;
        }

        console.log(
          "✅ ARRIVAL GEOMETRY ACCEPTED"
        );

        if (

  window.loadArrivalChain

) {

  console.log(
    "🧪 TESTING UNIFIED OVERLAY"
  );


window.loadArrivalChain(

  star || "",

  destination ||
  activeFlight?.meta?.destination ||
  "",

  transition || "",

  approach || "",

  runway || "",

  iaf || ""

);
}

console.log(
  "🚨 ARRIVAL PREVIEW COORDS:",
  data.coords
);

//return;

        const geojson = {

          type: "Feature",

          geometry: {

            type: "LineString",

            coordinates:
              data.coords
          }
        };

        const map =
          mapRef.current;

        if (!map) return;

        if (
          map.getLayer(
            "arrival-preview-line"
          )
        ) {

          map.removeLayer(
            "arrival-preview-line"
          );
        }

        if (
          map.getSource(
            "arrival-preview-line"
          )
        ) {

          map.removeSource(
            "arrival-preview-line"
          );
        }
        console.log(
  "🛬 ABOUT TO PLOT ARRIVAL"
);

console.log(
  "🛬 MAP EXISTS:",
  !!map
);

console.log(
  "🛬 ACTIVE ROUTE EXISTS:",
  !!activeRoute
);

console.log(
  "🛬 ACTIVE FLIGHT EXISTS:",
  !!activeFlight
);

        map.addSource(

          "arrival-preview-line",

          {

            type: "geojson",

            data: geojson
          }
        );
        map.addLayer({

          id:
            "arrival-preview-line",

          type: "line",

          source:
            "arrival-preview-line",

          paint: {

            "line-color":
              "#00BFFF",

            "line-width": 5,

            "line-opacity": 0.92
          }
        });

        console.log(
          "🛬 LAYER EXISTS:",
          map.getLayer(
            "arrival-preview-line"
          )
        );

        console.log(
          "🛬 SOURCE EXISTS:",
          map.getSource(
            "arrival-preview-line"
          )
        );

        console.log(
          "🛬 STYLE LAYER COUNT:",
          map.getStyle()?.layers?.length
        );

        console.log(
          "✅ Arrival preview plotted"
        );

      } catch (err) {

        console.error(
          "❌ Arrival rebuild failed:",
          err
        );
      }
    };

  rebuildArrival();

}, [

  procedureConfig.arrival,
  activeFlight
]);


// =====================================
// 🛬 LOAD ARRIVAL OPTIONS
// =====================================

useEffect(() => {

  if (!selectedArrivalToken)
    return;

  console.log(
    "⭐ Syncing arrival token -> selectedStar:",
    selectedArrivalToken
  );

  setSelectedStar(
    selectedArrivalToken
  );

  updateArrivalProcedure({

    star:
      selectedArrivalToken
  });

}, [

  selectedArrivalToken
]);

useEffect(() => {

  console.log(
    "⭐ selectedStar CHANGED:",
    selectedStar
  );

}, [

  selectedStar
]);

async function rebuildAndResolveRoute({

  structure,

  origin,

  destination,

  altitude,

  setActiveRoute,

  setActiveFlight,

  setConfirmedRouteString,

  setRouteTokens,

  setRouteResult,

  setHazardData,

  plotRoute
}) {

  try {

    // =====================================
    // 🧠 REBUILD ROUTE STRING
    // =====================================

    const rebuiltRoute =

      buildRouteStringFromStructure(
        structure
      );

    console.log(
      "🧠 REBUILT ROUTE:",
      rebuiltRoute
    );

    // =====================================
    // 🔥 RESOLVE ROUTE
    // =====================================

    const resolved =

      await resolveRouteWithCoords(

        rebuiltRoute,

        origin,

        destination,

        altitude
      );

    console.log(
      "🧪 RE-RESOLVED ROUTE:",
      resolved
    );

    if (
      !resolved?.coords ||
      resolved.coords.length < 2
    ) {

      console.warn(
        "❌ Rebuild failed"
      );

      return;
    }

    // =====================================
    // 🔥 UPDATE TOKENS
    // =====================================

    setRouteTokens(
      resolved.names || []
    );

    // =====================================
    // 🔥 UPDATE HAZARDS
    // =====================================

    if (
      resolved?.hazardAnalysis
    ) {

      setHazardData(
        resolved.hazardAnalysis
      );
    }

    // =====================================
    // 🔥 BUILD NEW ROUTE OBJECT
    // =====================================
    // =====================================
// 🎯 STORE DESTINATION FOR ACTIVE FLIGHT
// =====================================

destinationRef.current =
  resolved.coords[
    resolved.coords.length - 1
  ];

    const routeData = {

      origin:
        resolved.coords[0],

      destination:
        resolved.coords[
          resolved.coords.length - 1
        ],

      routeText:
        rebuiltRoute,

       coordObjects:
  resolved.coordObjects || [], 

      routeStructure: {

        ...(structure || {}),

        filedRoute:

          rebuiltRoute
            .split(/\s+/)
            .filter(Boolean)
      },

      routeSegments: [

        {

          type: "ENROUTE",

          name: "MAIN",

          coords:
            resolved.coords,

          waypoints:
            resolved.waypoints || []
        }
      ],

route: {

  coords:
    resolved.coords,

  waypoints:
    resolved.waypoints || [],

  coordObjects:
    resolved.coordObjects || []
},

      waypoints:
        resolved.waypoints || [],

      meta: {

        origin,

        destination,

        altitude,

        routeTextOriginal:
          rebuiltRoute,

        routeTextCurrent:
          rebuiltRoute
      }
    };

    // =====================================
    // 🔥 SAVE RESULT
    // =====================================

    setRouteResult(
      resolved
    );

    setConfirmedRouteString(
      rebuiltRoute
    );

    // =====================================
    // 🔥 UPDATE LIVE ROUTE
    // =====================================

    if (
      typeof setActiveRoute ===
      "function"
    ) {

      setActiveRoute(
        routeData
      );
    }

    // =====================================
    // 🔥 UPDATE PERSISTED FLIGHT
    // =====================================

    if (
      typeof setActiveFlight ===
      "function"
    ) {

      setActiveFlight(
        routeData
      );
    }

    // =====================================
    // 💾 PERSIST TO STORAGE
    // =====================================

    localStorage.setItem(

      "activeFlight",

      JSON.stringify(routeData)
    );

    // =====================================
    // 🔥 REPLOT
    // =====================================

    if (
      typeof plotRoute ===
      "function"
    ) {

      plotRoute(routeData);
    }

    console.log(
      "✅ Route rebuild complete"
    );

  } catch (err) {

    console.error(
      "❌ rebuildAndResolveRoute failed:",
      err
    );
  }
}

function buildRouteStringFromStructure(
  structure
) {

  if (!structure) {
    return "";
  }

  const parts = [];

  // =====================================
  // 🛫 SID
  // =====================================

  if (
    structure.departure?.sid
  ) {

    let sidPart =
      structure.departure.sid;

    if (
      structure.departure
        ?.transition
    ) {

      sidPart +=
        `.${structure.departure.transition}`;
    }

    parts.push(
      sidPart
    );
  }

  // =====================================
  // 🛣 ENROUTE
  // =====================================

  if (
    structure.enroute
      ?.routeText
  ) {

    parts.push(
      structure.enroute
        .routeText
    );
  }

  // =====================================
  // 🛬 STAR
  // =====================================

  if (
    structure.arrival?.star
  ) {

    let starPart =
      structure.arrival.star;

    if (
      structure.arrival
        ?.transition
    ) {

      starPart +=
        `.${structure.arrival.transition}`;
    }

    parts.push(
      starPart
    );
  }

  return parts
    .filter(Boolean)
    .join(" ");
}

function buildStarRouteString() {

  if (!selectedStar)
    return route;

  let out =
    route.trim();

  if (
    selectedStarTransition
  ) {

    out +=
      ` ${selectedStarTransition}`;
  }

  out +=
    ` ${selectedStar}`;

  return out.trim();
}

function buildSidRouteString() {

  if (!selectedSid)
    return route;

  let out = selectedSid;

  if (
    selectedSidTransition
  ) {

    out +=
      `.${selectedSidTransition}`;
  }

  if (
    route &&
    route.trim()
  ) {

    out +=
      ` ${route.trim()}`;
  }

  return out;
}

useEffect(() => {

  const arrivalAirport =
  activeFlight?.meta?.destination ||
  destination;

  console.log("🧪 arrivalAirport =", arrivalAirport);
console.log("🧪 selectedApproach =", selectedApproach);
console.log("🧪 selectedArrivalRunway =", selectedArrivalRunway);

  if (
    !arrivalAirport ||
    !selectedApproach
  ) {
    return;
  }

  fetch(
    "https://ficon-x.com/route/get_approach_iafs",
    {
      method: "POST",
      headers: {
        "Content-Type":
          "application/json"
      },
      body: JSON.stringify({
        airport: arrivalAirport,
        approach: selectedApproach,
        runway: selectedArrivalRunway
      })
    }
  )

  .then(res => res.json())

.then(data => {

  console.log(
    "🛬 IAFS RECEIVED:",
    data
  );

  console.log(
    "🛬 OVERWRITE GET APPCH AVAILABLE IAFS:",
    data.available_iafs
  );

  setAvailableIAFs(
    data.available_iafs || []
  );

  console.log(
    "🛬 SETTING IAFS:",
    data.available_iafs || []
  );

  setTimeout(() => {

  console.log(
    "🚨 availableIAFs AFTER SET:",
    data.available_iafs
  );

}, 100);

});

}, [

  selectedApproach,
  selectedArrivalRunway,
  destination
]);

useEffect(() => {

  console.log(
    "🛬 FRONTEND availableIAFs:",
    availableIAFs
  );

}, [availableIAFs]);

useEffect(() => {

  console.log(
    "🧹 DESTINATION CHANGED:",
    destination
  );

  setSelectedStar("");
  setSelectedStarTransition("");

  setSelectedArrivalRunway("");
  setSelectedApproach("");
  setSelectedArrivalIAF("");

  setArrivalRunways([]);
  setArrivalTransitions([]);
  setAvailableApproaches([]);
  setAvailableIAFs([]);

}, [destination]);

useEffect(() => {

const arrivalAirport =

  activeFlight?.meta?.destination ||

  destination;

console.log(
  "⭐ STAR EFFECT destination:",
  arrivalAirport
);


console.log(
  "⭐ EFFECT RUNNING FOR:",
  arrivalAirport
);

console.log(
  "⭐ STAR EFFECT selectedStar:",
  selectedStar
);

// =====================================
// NO AIRPORT
// =====================================

if (!arrivalAirport) {

  console.warn(
    "⚠️ No arrival airport"
  );

  return;
}

// =====================================
// NO STAR
// LOAD APPROACHES DIRECTLY
// =====================================
// =====================================
// NO STAR
// LOAD APPROACHES + RUNWAYS DIRECTLY
// =====================================

if (!selectedStar) {

  //setArrivalRunways([]);
  setArrivalTransitions([]);
  //setAvailableIAFs([]);
  setAvailableApproaches([]);

  //setSelectedApproach("");
  //setSelectedArrivalRunway("");
  //setSelectedArrivalIAF("");

  console.log(
    "⭐ No STAR selected - loading airport approaches directly"
  );

  // =====================================
  // APPROACHES
  // =====================================

  fetch(
    `https://ficon-x.com/route/debug-approaches/${arrivalAirport}`
  )
    .then(res => res.json())

    .then(data => {

      console.log(
        "⭐ DIRECT APPROACH LOAD:",
        data
      );

      console.log(
        "🚨 APPROACHES RECEIVED:",
        data.approaches
      );

      console.log(
        "🚨 APPROACH COUNT:",
        data.approaches?.length
      );

      setAvailableApproaches(
        data.approaches || []
      );

    })

    .catch(err => {

      console.error(
        "❌ Direct approach fetch failed:",
        err
      );

    });

// =====================================
// RUNWAYS
// =====================================

console.log(
  "🚨 ABOUT TO FETCH RUNWAYS:",
  arrivalAirport
);

fetch(
  `https://ficon-x.com/route/list_cifp_runways/${arrivalAirport}`
)
  .then(res => res.json())

  .then(data => {

    console.log(
      "🚨 RUNWAY FETCH RAW:",
      data
    );

    console.log(
      "🛬 RUNWAYS RECEIVED:",
      data
    );

    console.log(
      "🛬 RUNWAY COUNT:",
      data.runways?.length
    );

console.log(
  "🚨 SET ARRIVAL RUNWAYS",
  "SOURCE=CIFP AIRPORT",
  data.runways
);

setArrivalRunways(
  data.runways || []
);

  })

  .catch(err => {

    console.error(
      "❌ Runway fetch failed:",
      err
    );

  });
  return;
}

const normalizedStar =
  (selectedStar || "")
    .replace(
      /\d+[A-Z]?$/,
      ""
    );

console.log(
  "⭐ RAW STAR:",
  selectedStar
);

console.log(
  "⭐ NORMALIZED STAR:",
  normalizedStar
);

console.log(
  "⭐ ARRIVAL AIRPORT RAW:",
  arrivalAirport
);

console.log(
  "⭐ ARRIVAL AIRPORT TYPE:",
  typeof arrivalAirport
);

console.log(
  "⭐ FETCH URL:",
  `https://ficon-x.com/route/debug-approaches/${arrivalAirport}`
);

console.log(
  "🚨 STAR DETAIL REQUEST",
  {
    star: normalizedStar,
    transition: selectedStarTransition
  }
);

fetch(

  `https://ficon-x.com/route/get_star_details/${arrivalAirport}/${normalizedStar}/${selectedStarTransition || ""}`

)
  .then(res => res.json())

  .then(data => {

    console.log(
      "⭐ STAR DETAILS:",
      data
    );

    if (!data.success) return;

      // =====================================
      // 🛬 POPULATE ARRIVAL PANEL
      // =====================================

      setStarTransitions(
        data.available_transitions || []
      );

      setArrivalTransitions(
        data.available_transitions || []
      );

console.log(
  "🚨 SET ARRIVAL RUNWAYS",
  "SOURCE=STAR DETAILS",
  data.available_runways
);

setArrivalRunways(
  data.available_runways || []
);

      setAvailableApproaches(
        data.available_approaches || []
      );

      // =====================================
      // ⭐ STAR PREVIEW
      // =====================================

      if (
        map &&
        data.coords &&
        data.coords.length > 1
      ) {

        if (
          map.getLayer(
            "star-preview-layer"
          )
        ) {
          map.removeLayer(
            "star-preview-layer"
          );
        }

        if (
          map.getSource(
            "star-preview-src"
          )
        ) {
          map.removeSource(
            "star-preview-src"
          );
        }

        map.addSource(
          "star-preview-src",
          {
            type: "geojson",

            data: {

              type: "Feature",

              geometry: {

                type: "LineString",

                coordinates:
                  data.coords
              }
            }
          }
        );

        map.addLayer({

          id: "star-preview-layer",

          type: "line",

          source:
            "star-preview-src",

          layout: {

            "line-join":
              "round",

            "line-cap":
              "round"
          },

          paint: {

            "line-color":
              "#00d2ff",

            "line-width": 5,

            "line-opacity": 0.85
          }
        });

        console.log(
          "⭐ STAR preview plotted"
        );
      }

    })

    .catch(err => {

      console.error(
        "❌ STAR detail fetch failed:",
        err
      );
    });

}, [

  activeFlight,
  destination,
  selectedStar,
  selectedStarTransition
]);


useEffect(() => {

  const icao =
    (origin || "")
      .trim()
      .toUpperCase();

  if (
    icao.length !== 4
  ) {

    setAvailableSids([]);
    setSidExits({});

    return;
  }

  fetch(
    `https://ficon-x.com/route/list_cifp_sids/${origin}`
  )

    .then(res => res.json())

    .then(data => {

      console.log(
        "🛫 AVAILABLE SIDS:",
        data
      );

setAvailableSids(
  data.sids || []
);

setSidExits(
  data.sid_exits || {}
);
// =====================================
// 🛫 LOAD SID LIST ONLY
// USER CHOOSES SID
// =====================================

console.log(
  "🛫 SID LIST LOADED:",
  data.sids
);
    })

    .catch(err => {

      console.error(
        "❌ SID fetch failed:",
        err
      );
    });

}, [origin]);

useEffect(() => {
  console.log(
  "🧪 SID RUNWAY EFFECT FIRED",
  {
    origin,
    selectedSid
  }
);

  if (
    !origin ||
    !selectedSid
  ) return;

  fetch(

    `https://ficon-x.com/route/list_cifp_sid_runways/${origin}/${selectedSid}`

  )

    .then(res => res.json())

    .then(data => {

      console.log(
        "🛫 SID RUNWAYS:",
        data
      );

      setSidRunways(
        data.runways || []
      );

      console.log(
  "🛫 SID RUNWAYS STATE",
  sidRunways
);

    })

    .catch(err => {

      console.error(
        "❌ SID RUNWAY LOAD FAILED",
        err
      );

    });

}, [

  origin,

  selectedSid

]);

useEffect(() => {

  const icao =
    (destination || "")
      .trim()
      .toUpperCase();

  if (
    icao.length !== 4
  ) {

    setAvailableStars([]);

    return;
  }

  console.log(
    "🛬 DESTINATION EFFECT:",
    destination
  );

  fetch(
    `https://ficon-x.com/route/list_cifp_stars/${destination}`
  )

    .then(res => res.json())

    .then(data => {

      console.log(
        "🛬 AVAILABLE STARS:",
        data
      );

      const stars =
        data.stars || [];

      setAvailableStars(
        stars
      );

      console.log(
        "⭐ CURRENT selectedStar:",
        selectedStar
      );

      console.log(
        "⭐ CURRENT selectedStar:",
        selectedStar
      );

      // =====================================
      // LOAD STARS ONLY
      // ROUTE ENGINE CHOOSES STAR LATER
      // =====================================

      if (stars.length) {

        console.log(
          "🛬 STAR LIST LOADED:",
          stars
        );

      }

    })

    .catch(err => {

      console.error(
        "❌ STAR fetch failed:",
        err
      );

    });

}, [destination]);



useEffect(() => {

  const fetchRunways = async () => {

    try {

      // =====================================
      // 🛫 DEPARTURE RUNWAYS
      // =====================================

      if (origin?.trim()) {

        const res = await fetch(

          `https://ficon-x.com/route/get_airport_runways/${origin.trim().toUpperCase()}`
        );

        const data = await res.json();

        console.log(
          "🛫 Departure runways:",
          data
        );

        setDepartureRunways(
          data.runways || []
        );
      }

      // =====================================
      // 🛬 ARRIVAL RUNWAYS
      // =====================================

      if (destination?.trim()) {

        const res = await fetch(

          `https://ficon-x.com/route/get_airport_runways/${destination.trim().toUpperCase()}`
        );

        const data = await res.json();

        console.log(
          "🛬 Arrival runways:",
          data
        );

console.log(
  "🚫 AIRPORT METADATA RUNWAYS IGNORED",
  data.runways
);

// setArrivalRunways(
//   data.runways || []
// );
      }

    } catch (err) {

      console.error(
        "❌ Failed to fetch runways:",
        err
      );
    }
  };

  fetchRunways();

}, [origin, destination]);

// =====================================
// 🟣 TRUE ACTIVE LEG DETECTION
// =====================================

useEffect(() => {

  const coords =

    activeRoute?.route?.coords ||

    activeFlight?.route?.coords;

  const aircraft =

    aircraftPosition ||

    sharedAircraftState?.position;

      console.log(
    "🚨 ACTIVE ROUTE FIRST 10",
    activeRoute?.route?.waypoints
      ?.slice(0,10)
      ?.map(w => w.id)
  );

  console.log(
    "🚨 ACTIVE FLIGHT FIRST 10",
    activeFlight?.waypoints
      ?.slice(0,10)
      ?.map(w => w.id)
  );

  if (
    !coords ||
    coords.length < 2 ||
    !aircraft
  ) {
    return;
  }

  let bestIndex = 0;

  let bestDistance =
    Infinity;

  for (
    let i = 0;
    i < coords.length - 1;
    i++
  ) {

    const dist =
      distanceToSegmentNm(

        aircraft,

        coords[i],

        coords[i + 1]
      );

    if (
      dist < bestDistance
    ) {

      bestDistance =
        dist;

      bestIndex = i;
    }
  }

  console.log(
    "🟣 TRUE LEG DETECTED:",
    bestIndex
  );

  console.log(
    "🟣 Segment distance:",
    bestDistance
  );

  setCurrentLegIndex(
    bestIndex
  );

  setSharedActiveLegIndex(
    bestIndex
  );

}, [

  aircraftPosition,

  sharedAircraftState,

  activeRoute,

  activeFlight
]);

// =====================================
// ✈️ MOVING MAP FOLLOW MODE
// =====================================

// =====================================
// ✈️ MOVING MAP FOLLOW MODE
// =====================================

useEffect(() => {

  const map = mapRef.current;

  if (
    !map ||
    !aircraftPosition
  ) return;

  // =====================================
  // ✋ PILOT HAS PANNED AWAY
  // =====================================
  //
  // Continue updating the aircraft itself,
  // but DO NOT move the camera.
  //

  if (!followAircraft) {

    return;
  }

  // =====================================
  // ✈️ SMOOTH AVIATION FOLLOW
  // =====================================

  map.easeTo({

    center: aircraftPosition,

    // ✈️ Aircraft lower on screen
    offset: [0, 180],

    // 🧭 Track-up map
    bearing: aircraftHeading,

    // 🔥 Slight 3D feel
    pitch: 45,

    // Keep pilot-selected zoom.
    // Only enforce minimum operational zoom.
    zoom: Math.max(
      map.getZoom(),
      7
    ),

    duration: 250,

    essential: true
  });

}, [

  aircraftPosition,

  aircraftHeading,

  followAircraft
]);

// =====================================
// 📡 VORS
// =====================================

useEffect(() => {

  const map = mapRef.current;

  if (!map) return;

  const sourceId = "vors-source";

  const layerId = "vors-layer";

  const labelLayerId =
    "vors-labels";

  // =====================================
  // REMOVE
  // =====================================

  if (!showVors) {

    if (
      map.getLayer(labelLayerId)
    ) {
      map.removeLayer(
        labelLayerId
      );
    }

    if (
      map.getLayer(layerId)
    ) {
      map.removeLayer(
        layerId
      );
    }

    if (
      map.getSource(sourceId)
    ) {
      map.removeSource(
        sourceId
      );
    }

    return;
  }

  // =====================================
  // FETCH VORS
  // =====================================

  fetch(
    "https://ficon-x.com/route/get_vors"
  )

    .then(res => res.json())

    .then(data => {

      console.log(
        `📡 Loaded ${data.features.length} VORs`
      );

      // =====================================
      // REMOVE OLD
      // =====================================

      if (
        map.getLayer(labelLayerId)
      ) {
        map.removeLayer(
          labelLayerId
        );
      }

      if (
        map.getLayer(layerId)
      ) {
        map.removeLayer(
          layerId
        );
      }

      if (
        map.getSource(sourceId)
      ) {
        map.removeSource(
          sourceId
        );
      }

      // =====================================
      // SOURCE
      // =====================================

if (!map.getSource(sourceId)) {

  map.addSource(sourceId, {

    type: "geojson",

    data
  });

} else {

  map
    .getSource(sourceId)
    .setData(data);
}
      // =====================================
      // VOR DOTS
      // =====================================

      map.addLayer({

        id: layerId,

        type: "circle",

        source: sourceId,

        minzoom: 5,

        paint: {

          "circle-radius": 4,

          "circle-color":
            "#00d5ff",

          "circle-stroke-width":
            1,

          "circle-stroke-color":
            "#003344",

          "circle-opacity":
            0.9
        }
      });

      // =====================================
      // VOR LABELS
      // =====================================

      map.addLayer({

        id: labelLayerId,

        type: "symbol",

        source: sourceId,

        minzoom: 7,

        layout: {

          "text-field": [
            "get",
            "name"
          ],

          "text-size": 11,

          "text-offset": [
            0,
            1.4
          ],

          "text-anchor": "top"
        },

        paint: {

          "text-color":
            "#7ee8ff",

          "text-halo-color":
            "#001018",

          "text-halo-width":
            1
        }
      });

    })

    .catch(err => {

      console.error(
        "❌ VOR LOAD ERROR:",
        err
      );
    });

}, [showVors]);

// =====================================
// ✳️ FIXES
// =====================================

useEffect(() => {

  const map = mapRef.current;

  if (!map) return;

  const sourceId =
    "fixes-source";

  const layerId =
    "fixes-layer";

  const labelLayerId =
    "fixes-labels";

  // =====================================
  // REMOVE
  // =====================================

  if (!showFixes) {

    if (
      map.getLayer(labelLayerId)
    ) {
      map.removeLayer(
        labelLayerId
      );
    }

    if (
      map.getLayer(layerId)
    ) {
      map.removeLayer(
        layerId
      );
    }

    if (
      map.getSource(sourceId)
    ) {
      map.removeSource(
        sourceId
      );
    }

    return;
  }

  // =====================================
  // FETCH FIXES
  // =====================================

  fetch(
    "https://ficon-x.com/route/get_fixes"
  )

    .then(res => res.json())

    .then(data => {

      console.log(
        `✳️ Loaded ${data.features.length} FIXES`
      );

      // =====================================
      // REMOVE OLD
      // =====================================

      if (
        map.getLayer(labelLayerId)
      ) {
        map.removeLayer(
          labelLayerId
        );
      }

      if (
        map.getLayer(layerId)
      ) {
        map.removeLayer(
          layerId
        );
      }

      if (
        map.getSource(sourceId)
      ) {
        map.removeSource(
          sourceId
        );
      }

      // =====================================
      // SOURCE
      // =====================================

if (!map.getSource(sourceId)) {

  map.addSource(sourceId, {

    type: "geojson",

    data
  });

} else {

  map
    .getSource(sourceId)
    .setData(data);
}

      // =====================================
      // FIX DOTS
      // =====================================

      map.addLayer({

        id: layerId,

        type: "circle",

        source: sourceId,

        minzoom: 5,

        paint: {

          "circle-radius": 3.5,

          "circle-color":
            "#7ee8ff",

          "circle-stroke-width":
            1,

          "circle-stroke-color":
            "#001018",

          "circle-opacity":
            0.9
        }
      });

      // =====================================
      // FIX LABELS
      // =====================================

      map.addLayer({

        id: labelLayerId,

        type: "symbol",

        source: sourceId,

        minzoom: 7,

        layout: {

          "text-field": [
            "get",
            "name"
          ],

          "text-size": 11,

          "text-offset": [
            0,
            1.3
          ],

          "text-anchor": "top"
        },

        paint: {

          "text-color":
            "#dff8ff",

          "text-halo-color":
            "#000000",

          "text-halo-width":
            1.2
        }
      });

    })

    .catch(err => {

      console.error(
        "❌ FIX LOAD ERROR:",
        err
      );
    });

}, [showFixes]);

useEffect(() => {

  const map = mapRef.current;

  if (!map) return;

  // =====================================
  // REMOVE TFRS
  // =====================================

  if (!showTFRs) {

    if (map.getLayer("tfr-fill")) {
      map.removeLayer("tfr-fill");
    }

    if (map.getLayer("tfr-outline")) {
      map.removeLayer("tfr-outline");
    }

    if (map.getSource("tfrs")) {
      map.removeSource("tfrs");
    }

    return;
  }

  // =====================================
  // LOAD TFRS
  // =====================================

  fetch("https://ficon-x.com/api/get_tfrs")

    .then(res => res.json())

    .then(data => {

      console.log(
        "🛑 TFR DATA:",
        data
      );

      console.log(
        `🛑 Loaded ${data.features?.length || 0} TFRs`
      );

      // REMOVE OLD

      if (map.getLayer("tfr-fill")) {
        map.removeLayer("tfr-fill");
      }

      if (map.getLayer("tfr-outline")) {
        map.removeLayer("tfr-outline");
      }

      if (map.getSource("tfrs")) {
        map.removeSource("tfrs");
      }

      // =====================================
      // SOURCE
      // =====================================

if (!map.getSource("tfrs")) {

  map.addSource("tfrs", {

    type: "geojson",

    data
  });

} else {

  map
    .getSource("tfrs")
    .setData(data);
}

      // =====================================
      // FILL
      // =====================================

      map.addLayer({
        id: "tfr-fill",
        type: "fill",
        source: "tfrs",
        paint: {
          "fill-color": "#ff0000",
          "fill-opacity": 0.18
        }
      });

      // =====================================
      // OUTLINE
      // =====================================

      map.addLayer({
        id: "tfr-outline",
        type: "line",
        source: "tfrs",
        paint: {
          "line-color": "#ff0000",
          "line-width": 2
        }
      });

      console.log(
        "✅ TFRs rendered"
      );

      // =====================================
      // CLICK POPUP
      // =====================================

      map.on(
        "click",
        "tfr-fill",
        (e) => {

          const props =
            e.features?.[0]?.properties || {};

          console.log(
            "🛑 TFR CLICK:",
            props
          );

          new mapboxgl.Popup()

            .setLngLat(e.lngLat)

            .setHTML(`
              <div style="color:white;">
                <strong>
                  TFR
                </strong><br/>

                ${
                  props.NAME || ""
                }<br/>

                ${
                  props.TYPE_CODE || ""
                }
              </div>
            `)

            .addTo(map);
        }
      );

    })

    .catch(err => {

      console.error(
        "❌ TFR LOAD ERROR:",
        err
      );
    });

}, [showTFRs]);

useEffect(() => {

  const map = mapRef.current;

  if (!map) return;

  // =====================================
  // REMOVE AIRSPACES
  // =====================================

  if (!showAirspaces) {

    [
      "airspace-fill",
      "airspace-outline",
      "airspace-labels"
    ].forEach(layer => {

      if (map.getLayer(layer)) {
        map.removeLayer(layer);
      }
    });

    if (map.getSource("airspaces")) {
      map.removeSource("airspaces");
    }

    return;
  }

  // =====================================
  // LOAD AIRSPACES
  // =====================================

  fetch(
    "https://ficon-x.com/route/get_airspaces"
  )

    .then(res => res.json())

    .then(data => {

      console.log(
        "🛫 AIRSPACE DATA:",
        data
      );

      console.log(
        `🛫 Loaded ${
          data.features?.length || 0
        } airspaces`
      );

      // =====================================
      // CLEAN OLD
      // =====================================

      [
        "airspace-fill",
        "airspace-outline",
        "airspace-labels"
      ].forEach(layer => {

        if (map.getLayer(layer)) {
          map.removeLayer(layer);
        }
      });

      if (map.getSource("airspaces")) {
        map.removeSource("airspaces");
      }

      // =====================================
      // SOURCE
      // =====================================

if (!map.getSource("airspaces")) {

  map.addSource("airspaces", {

    type: "geojson",

    data
  });

} else {

  map
    .getSource("airspaces")
    .setData(data);
}

      // =====================================
      // FILLS
      // =====================================

      map.addLayer({

        id: "airspace-fill",

        type: "fill",

        source: "airspaces",

        paint: {

          "fill-color": [
            "match",
            ["get", "class"],

            "B", "#0048ff",
            "C", "#9b59ff",
            "D", "#00bcd4",

            "#666666"
          ],

          "fill-opacity": 0.10
        }
      });

      // =====================================
      // OUTLINES
      // =====================================

      map.addLayer({

        id: "airspace-outline",

        type: "line",

        source: "airspaces",

        paint: {

          "line-color": [
            "match",
            ["get", "class"],

            "B", "#2d6cff",
            "C", "#c77dff",
            "D", "#4dd0e1",

            "#999999"
          ],

          "line-width": 2
        }
      });

// =====================================
// LABELS
// =====================================

map.addLayer({

  id: "airspace-labels",

  type: "symbol",

  source: "airspaces",

  minzoom: 7,

  layout: {

    "text-field": [

      "format",

      "Class ",
      {},

      ["get", "CLASS"],
      {},

      " ",
      {},

      [
        "case",

        ["==", ["get", "LOWER_CODE"], "SFC"],

        "SFC",

        ["get", "LOWER_VAL"]
      ],
      {},

      "/",
      {},

      ["get", "UPPER_VAL"],
      {}
    ],

    "text-size": 11,

    "text-offset": [0, 1.2],

    "text-anchor": "center"

  },

  paint: {

    "text-color": "#ffffff",

    "text-halo-color": "#000000",

    "text-halo-width": 1.2
  }
});

      // =====================================
      // POPUP
      // =====================================

      map.on(
        "click",
        "airspace-fill",
        (e) => {

          const props =
            e.features?.[0]?.properties || {};

          new mapboxgl.Popup()

            .setLngLat(e.lngLat)

            .setHTML(`
<div style="color:white;">
  <strong>
    ${props.NAME || "Airspace"}
  </strong><br/>

  Class:
  ${props.CLASS || "?"}<br/>

  Floor:
  ${
    props.LOWER_CODE === "SFC"
      ? "SFC"
      : (props.LOWER_VAL || "?")
  }<br/>

  Ceiling:
  ${props.UPPER_VAL || "?"} ${props.UPPER_UOM || ""}
</div>
            `)

            .addTo(map);
        }
      );

      console.log(
        "✅ AIRSPACES RENDERED"
      );

    })

    .catch(err => {

      console.error(
        "❌ AIRSPACE LOAD ERROR:",
        err
      );
    });

}, [showAirspaces]);

/*
useEffect(() => {

  console.log("🟢 DEV SIM EFFECT FIRED");

  console.log(
    "🧪 DEV_SIM_MODE:",
    DEV_SIM_MODE
  );

  console.log(
    "🧪 simStarted:",
    simStarted
  );

  console.log(
    "🧪 simHasStartedRef:",
    simHasStartedRef.current
  );

  console.log(
    "🧪 simIndexRef:",
    simIndexRef.current
  );

  console.log(
    "🧪 activeRoute:",
    activeRoute
  );

  console.log(
    "🧪 coords:",
    activeRoute?.route?.coords
  );

console.log("🧪 DEV_SIM_MODE =", DEV_SIM_MODE);

if (!DEV_SIM_MODE) {

  console.log(
    "❌ BLOCKED: DEV_SIM_MODE false"
  );

  return;
}

console.log("🧪 simStarted =", simStarted);

if (!simStarted) {

  console.log(
    "❌ BLOCKED: simStarted false"
  );

  return;
}

console.log(
  "🧪 activeRoute =",
  activeRoute
);

const coords =
  activeRoute?.route?.coords;

console.log(
  "🧪 coords =",
  coords
);

console.log(
  "🧪 coords length =",
  coords?.length
);

if (!coords || coords.length < 2) {

  console.log(
    "❌ BLOCKED: invalid coords"
  );

  return;
}

console.log(
  "✅ DEV SIM PASSED ALL GATES"
);

  // 🔥 HARD RESET IF SIM FINISHED
  if (
    simHasStartedRef.current &&
    simIndexRef.current >= (
      coords.length - 1
    )
  ) {

    console.log(
      "♻️ RESETTING COMPLETED SIM"
    );

    simHasStartedRef.current = false;

    simIndexRef.current = 0;
  }

  // ⛔ ACTIVE SIM STILL RUNNING
  if (simHasStartedRef.current) {

    console.log(
      "⛔ sim already started"
    );

    return;
  }

  console.log(
    "✅ ALL DEV SIM CHECKS PASSED"
  );

  const groundSpeed =
    activeRoute?.meta?.tas || 0;

  console.log(
    "📦 activeRoute:",
    activeRoute
  );

  console.log(
    "📦 coords length:",
    coords?.length
  );

  console.log(
    "🛫 Ground Speed (TAS):",
    groundSpeed
  );

  simHasStartedRef.current = true;

  console.log(
    "🚀 DEV SIM STARTED"
  );

  let index =
    simIndexRef.current;

  const interval = setInterval(() => {

    console.log(
      "🔁 LOOP TICK",
      index
    );

    // 🛑 END OF ROUTE
    if (
      index >= coords.length - 1
    ) {

      console.log(
        "🛑 DEV SIM COMPLETE"
      );

      clearInterval(interval);

      console.log(
        "✅ SIM FINISHED"
      );

      simHasStartedRef.current = false;

      simIndexRef.current = 0;

      setSimStarted(false);

      return;
    }

    const position =
      coords[index];

    console.log(
      "✈️ Moving:",
      index,
      position
    );

    setAircraftPosition(
      position
    );

    let heading = 0;

    // 🧭 Heading
    if (coords[index + 1]) {

      const [lon1, lat1] =
        coords[index];

      const [lon2, lat2] =
        coords[index + 1];

      heading =
        Math.atan2(
          lon2 - lon1,
          lat2 - lat1
        ) * (180 / Math.PI);

      console.log(
        "🧭 Heading:",
        heading
      );

      setAircraftHeading(
        heading
      );
    }

    // 🌐 SHARED AIRCRAFT STATE
const nextLegIndex =
  Math.min(
    index,
    coords.length - 2
  );

const sharedAircraft = {

  lat: position[1],

  lon: position[0],

  position: [
    position[0],
    position[1]
  ],

  heading,

  activeLegIndex:
    nextLegIndex,

      speed:
        groundSpeed,

      altitude:
        activeRoute?.meta?.altitude || 0,

      timestamp:
        Date.now()
    };

    const role =

      window.__missionRole ||

      sessionStorage.getItem(
        "missionRole"
      );

    // 👥 ONLY CREW USES
    if (role !== "captain") {

      setSharedAircraftState(
        sharedAircraft
      );
    }

    // 📡 MULTIPLAYER EMIT
    const missionId =

      window.__activeMissionId ||

      localStorage.getItem(
        "activeMissionId"
      );

    console.log(
      "🧪 CURRENT ACTIVE MISSION:",
      missionId
    );

    if (missionId) {

      console.log(
        "📡 EMITTING AIRCRAFT STATE →",
        missionId
      );

      window.routeSocket?.emit(
        "update_mission",
        {

          mission_id:
            String(missionId),

          state: {

            aircraftOnly: true,

            aircraft:
              sharedAircraft
          }
        }
      );

      // =====================================
      // 🌐 PERIODIC FULL FLIGHT RE-SYNC
      // =====================================

      if (index % 5 === 0) {

        console.log(
          "📡 Periodic fullFlight sync"
        );

        window.routeSocket?.emit(
          "update_mission",
          {

            mission_id:
              String(missionId),

            state: {

              fullFlight: {

                ...activeFlight,

                aircraft: {
                  ...sharedAircraft
                },

                timestamp:
                  Date.now()
              }
            }
          }
        );
      }

    } else {

      console.warn(
        "❌ No active mission ID during aircraft emit"
      );
    }

    // =====================================
    // 🟣 SIMPLE LEG TRACKING (FIXED)
    // =====================================

    setCurrentLegIndex(
      nextLegIndex
    );

    console.log(
  "🟣 SIM nextLegIndex:",
  nextLegIndex
);

console.log(
  "🟣 SIM index:",
  index
);

console.log(
  "🟣 SIM coords length:",
  coords.length
);

    console.log(
      "🟣 Current leg index:",
      nextLegIndex
    );

    // 🔥 ADVANCE SIM
    index++;

    simIndexRef.current =
      index;

  }, 2000);
  return () => {

    console.log(
      "🔴 DEV SIM CLEANUP (interval cleared)"
    );

    clearInterval(
      interval
    );

    simHasStartedRef.current = false;
  };

}, [
  simStarted,
  activeRoute,
  mapReady
]);

*/

useEffect(() => {

  const storedRole =

    sessionStorage.getItem(
      "missionRole"
    );

  if (storedRole) {

    window.__missionRole =
      storedRole;

    console.log(
      "♻️ Restored global mission role:",
      storedRole
    );
  }

}, []); 

useEffect(() => {

  console.log(
    "🟢 DEV MODE - operational gating bypassed"
  );

}, [operationalAccess]);
// 🧪 DEBUG
useEffect(() => {

  console.log(
    "🧪 WEATHER SOURCE activeFlight:",
    activeFlight
  );

  console.log(
    "🧪 destIcao:",
    destIcao
  );

  console.log(
    "🧪 altIcao:",
    altIcao
  );

  console.log(
    "🧪 destWeather:",
    destWeather
  );

  console.log(
    "🧪 altWeather:",
    altWeather
  );

}, [

  activeFlight,

  destIcao,

  altIcao,

  destWeather,

  altWeather
]);

const fetchFlights = async () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return;

    const res = await fetch("https://ficon-x.com/auth/get-flights", {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    const data = await res.json();

    console.log("📦 Flights fetched:", data);

    setFlights(data);
    setShowFlightsPanel(true);

  } catch (err) {
    console.error("❌ Failed to fetch flights:", err);
  }
};

const handleSaveFlight = async () => {
  try {
    const flight = JSON.parse(localStorage.getItem("activeFlight"));
    const token = localStorage.getItem("token");

    if (!flight || !token) {
      console.warn("❌ Missing flight or token");
      return;
    }

    const res = await fetch("https://ficon-x.com/auth/save-flight", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(flight)
    });

    const data = await res.json();

    console.log("💾 Flight saved:", data);

    // 🔥 refresh list immediately
    fetchFlights();

  } catch (err) {
    console.error("❌ Save failed:", err);
  }
};

const [
  pendingRouteProposal,
  setPendingRouteProposal
] = useState(null);

useEffect(() => {

  console.log(
    "👂 route_change_requested listener mounted"
  );

  const handler = (proposal) => {

    console.log(
      "🚨 CAPTAIN RECEIVED PROPOSAL:",
      proposal
    );

    // 🔥 TAB-ISOLATED ROLE
    const role =
      sessionStorage.getItem(
        "missionRole"
      );

    console.log(
      "🧪 Current role:",
      role
    );

    // 👨‍✈️ ONLY CAPTAIN GETS UI
    if (role !== "captain") {

      console.log(
        "⛔ Ignoring proposal — not captain"
      );

      return;
    }

    console.log(
      "✅ Setting pending proposal"
    );

    setPendingRouteProposal(
      proposal
    );
  };

  // 🔌 GLOBAL SOCKET
  window.routeSocket.on(
    "route_change_requested",
    handler
  );

  return () => {

    window.routeSocket.off(
      "route_change_requested",
      handler
    );
  };

}, []);

useEffect(() => {

  const map = mapRef.current;

  if (!map) return;

  // =====================================
  // 🧹 REMOVE OVERLAY IF NO PROPOSAL
  // =====================================

  if (!pendingRouteProposal) {

    // ✅ REMOVE LAYER ONLY
    if (
      map.getLayer(
        "proposal-route-layer"
      )
    ) {

      map.removeLayer(
        "proposal-route-layer"
      );
    }

    // ✅ REMOVE SOURCE ONLY IF EXISTS
    if (
      map.getSource(
        "proposal-route-source"
      )
    ) {

      map.removeSource(
        "proposal-route-source"
      );
    }

    return;
  }

  // =====================================
  // 📡 GET COORDS
  // =====================================

  const coords =
    pendingRouteProposal
      ?.proposedFlight
      ?.coords;

  if (!coords?.length) {

    console.warn(
      "❌ Proposal has no coords"
    );

    return;
  }

  console.log(
    "🛫 Plotting proposed route overlay"
  );

  // =====================================
  // 📦 GEOJSON
  // =====================================

  const proposalGeojson = {

    type: "Feature",

    geometry: {

      type: "LineString",

      coordinates: coords
    }
  };

  // =====================================
  // 📡 CREATE SOURCE ONCE
  // =====================================

  if (
    !map.getSource(
      "proposal-route-source"
    )
  ) {

    console.log(
      "📡 Creating proposal source"
    );

    map.addSource(
      "proposal-route-source",
      {
        type: "geojson",
        data: proposalGeojson
      }
    );

  } else {

    console.log(
      "♻️ Updating proposal source"
    );

    map
      .getSource(
        "proposal-route-source"
      )
      .setData(
        proposalGeojson
      );
  }

  // =====================================
  // 🛫 CREATE LAYER ONCE
  // =====================================

  if (
    !map.getLayer(
      "proposal-route-layer"
    )
  ) {

    console.log(
      "🛫 Creating proposal layer"
    );

    map.addLayer({

      id: "proposal-route-layer",

      type: "line",

      source:
        "proposal-route-source",

      layout: {

        "line-cap": "round",

        "line-join": "round"
      },

      paint: {

        "line-color":
          "#00FFFF",

        "line-width":
          4,

        "line-opacity":
          0.9,

        "line-dasharray":
          [2, 2]
      }
    });
  }

  // =====================================
  // 🔥 BRING TO FRONT
  // =====================================

  if (
    typeof bringRouteLayersToFront ===
    "function"
  ) {

    bringRouteLayersToFront();
  }

  // =====================================
  // 🧹 CLEANUP
  // =====================================

  return () => {

    // 🚨 DO NOT REMOVE SOURCE/LAYER
    // DURING NORMAL RERENDERS

    console.log(
      "♻️ Proposal effect cleanup"
    );
  };

}, [pendingRouteProposal]);

function approveRouteProposal() {

  if (!pendingRouteProposal) {
    return;
  }

  console.log(
    "✅ Captain approved proposal"
  );

  const proposed =
    pendingRouteProposal;

  const currentFlight =
    activeFlight || activeRoute;

  if (!currentFlight) {
    console.warn(
      "❌ No active flight"
    );
    return;
  }

  // =====================================
  // 🛫 BUILD UPDATED FLIGHT
  // =====================================

  const updatedFlight = {

    ...currentFlight,

    routeText:
      proposed.proposedRoute,

    meta: {

      ...currentFlight.meta,

      routeTextCurrent:
        proposed.proposedRoute
    },

    route: {

      ...currentFlight.route,

      coords:
        proposed.proposedFlight.coords,

      waypoints:
        proposed.proposedFlight.waypoints || [],

      amendedHistory: [

        ...(currentFlight.route
          ?.amendedHistory || []),

        proposed.proposedRoute
      ]
    },

    aircraft: {

      ...currentFlight.aircraft,

      currentLegIndex: 0
    }
  };

  // =====================================
  // 💾 SAVE LOCALLY
  // =====================================

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  // =====================================
  // 🔥 FORCE FULL STATE UPDATE
  // =====================================

  setActiveFlight(
    updatedFlight
  );

  setActiveRoute(
    updatedFlight
  );

  // =====================================
  // 🔥 RESET ROUTE UI
  // =====================================

  setCurrentLegIndex(0);

  setRouteLegs(
    updatedFlight.route
      ?.waypoints || []
  );

  // =====================================
  // 📡 SEND AUTHORITATIVE UPDATE
  // =====================================

  sendRouteUpdate({

    route:
      proposed.proposedRoute,

    coords:
      proposed.proposedFlight.coords,

    waypoints:
      proposed.proposedFlight.waypoints || []
  });

  // =====================================
  // 🧹 CLEANUP
  // =====================================

  setPendingRouteProposal(null);

  console.log(
    "✅ Proposal fully applied"
  );
}


function denyRouteProposal() {

  console.log(
    "❌ Captain denied proposal"
  );

  const currentFlight =
    activeFlight || activeRoute;

  // 🔄 Restore actual route text
  if (currentFlight) {

    const actualRoute =

      currentFlight.routeText ||

      currentFlight.meta
        ?.routeTextCurrent ||

      "";

    setRouteInput(
      actualRoute
    );
  }

  setPendingRouteProposal(null);
}

useEffect(() => {
  async function loadFlights() {
    try {
      console.log("📡 Fetching saved flights...");

      const token = localStorage.getItem("token");

      if (
        !token ||
        token === "null" ||
        token === "undefined"
      ) {
        console.log("🔐 Guest mode - skipping flight load");
        return;
      }

      const res = await fetch("https://ficon-x.com/auth/get-flights", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();

      console.log("🧪 flights RAW:", data);

      // 🔥 normalize here (cleanest approach)
      const flightList = Array.isArray(data)
        ? data
        : data.flights || [];

      setFlights(flightList);

    } catch (err) {
      console.error("❌ Failed to load flights:", err);
      setFlights([]);
    }
  }

  loadFlights();
}, []);


const insertFixAfter = async (

  token,

  index,

  insertedFix

) => {

  const currentRoute =

    activeFlight
      ?.routeStructure
      ?.activeTokens || [];

  setPreviousRouteState({

    activeTokens:
      activeFlight
        ?.routeStructure
        ?.activeTokens,

    activeObjects:
      activeFlight
        ?.routeStructure
        ?.activeObjects,

    route:
      activeFlight?.route,

    waypoints:
      activeFlight?.waypoints

  });

  const newTokens = [
    ...currentRoute
  ];

  newTokens.splice(

    index + 1,

    0,

    insertedFix
      .toUpperCase()

);

  console.log(
    "➕ INSERT FIX:",
    insertedFix
  );

const insertRoute =

  newTokens.join(" ");

console.log(
  "➕ INSERT ROUTE STRING",
  insertRoute
);

const resolved =
  await resolveRouteWithCoords(

    insertRoute,

    activeFlight.meta.origin,

    activeFlight.meta.destination,

    activeFlight.meta.altitude,

    null,
    null,
    null,

    null,
    null,
    null,

    null,
    null
  );

console.log(
  "➕ INSERT RESOLVED",
  resolved
);

console.log(
  "➕ INSERT RESOLVED NAMES",
  resolved?.names?.slice(0,20)
  );
setActiveFlight(prev => {

  console.log(
    "🗑 DELETE RESOLVED NAMES",
    resolved.names?.slice(0,10)
  );

  console.log(
    "🗑 BEFORE ACTIVE TOKENS",
    prev.routeStructure?.activeTokens?.slice(0,10)
  );

  const updatedFlight = {

    ...prev,

    routeStructure: {

      ...prev.routeStructure,

      activeTokens:
        resolved.names || [],

      activeObjects:
        resolved.coordObjects || []

    },

    route: {

      ...prev.route,

      coords:
        resolved.coords || [],

      waypoints:
        resolved.waypoints || [],

      coordObjects:
        resolved.coordObjects || []

    },

    waypoints:
      resolved.waypoints || []

  };

  setActiveRoute(
    updatedFlight
  );

  console.log(
    "🗑 FIRST TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  console.log(
    "🗑 AFTER ACTIVE TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  return updatedFlight;

});

plotRoute({

  coords:
    resolved.coords,

  coordObjects:
    resolved.coordObjects,

  waypoints:
    resolved.waypoints,

  routeText:
    insertRoute,

  origin:
    resolved.origin,

  destination:
    resolved.destination

});
// =====================================
// 🧹 CLEAR RUBBER BAND HIGHLIGHT
// =====================================

if (
  map.getLayer(
    "selected-segment"
  )
) {

  try {

    map.removeLayer(
      "selected-segment"
    );

  } catch {}

}

if (
  map.getSource(
    "selected-segment"
  )
) {

  try {

    map.removeSource(
      "selected-segment"
    );

  } catch {}

}

dragSegmentRef.current =
  null;

setSelectedSegment(
  null
);

setDragSegment(
  null
);

};

useEffect(() => {

  // 🔗 FIRST check URL
  const urlMissionId =
    new URLSearchParams(window.location.search)
      .get("mission");

  // 💾 fallback to localStorage
  const storedMissionId =
    localStorage.getItem(
      "activeMissionId"
    );

  const finalMissionId =
    urlMissionId || storedMissionId;

  if (!finalMissionId) {

    console.warn(
      "⚠️ No mission ID available"
    );

    return;
  }

  // 💾 persist mission ID globally
  localStorage.setItem(
    "activeMissionId",
    finalMissionId
  );

  console.log(
    "🔌 AUTO JOIN:",
    finalMissionId
  );

  // 🔥 Use sessionStorage for roles
  // so each tab has isolated permissions
  const existingRole =
    sessionStorage.getItem(
      "missionRole"
    );

  // 👥 ONLY assign crew role
  // if this tab has no role yet
  if (
    urlMissionId &&
    !existingRole
  ) {

    sessionStorage.setItem(
      "missionRole",
      "crew"
    );

    console.log(
      "👥 Joined mission as crew"
    );
  }

  console.log(
    "🧪 Current mission role:",
    sessionStorage.getItem(
      "missionRole"
    )
  );

  // 🔌 JOIN MISSION ROOM
  routeSocket.emit(
    "join_mission",
    {
      mission_id: String(
        finalMissionId
      )
    }
  );

}, []);

useEffect(() => {

  // 🔗 URL mission (shared link)
  const urlMissionId =
    new URLSearchParams(
      window.location.search
    ).get("mission");

  // 💾 Previous stored mission
  const storedMissionId =
    localStorage.getItem(
      "activeMissionId"
    );

  // 🔥 TAB-ISOLATED ROLE
  const existingRole =
    sessionStorage.getItem(
      "missionRole"
    );

  let finalMissionId = null;

  // 👥 CREW joins from URL ONLY
  if (urlMissionId) {

    finalMissionId =
      urlMissionId;

    // 👥 Shared link always means crew
    sessionStorage.setItem(
      "missionRole",
      "crew"
    );

    console.log(
      "👥 Joined mission as crew"
    );
  }

  // 💾 ONLY crew can restore old mission
  else if (
    existingRole === "crew" &&
    storedMissionId
  ) {

    // 🔥 Reject legacy numeric IDs
    if (
      !storedMissionId.includes("-")
    ) {

      console.warn(
        "❌ Rejecting legacy mission ID:",
        storedMissionId
      );

      localStorage.removeItem(
        "activeMissionId"
      );

      return;
    }

    finalMissionId =
      storedMissionId;

    console.log(
      "👥 Restoring crew mission:",
      finalMissionId
    );
  }

  // 🚫 NOTHING TO JOIN
  if (!finalMissionId) {

    console.log(
      "🧪 No valid mission auto-join"
    );

    return;
  }

  // 🌐 AUTHORITATIVE RUNTIME ID
  window.__activeMissionId =
    finalMissionId;

  // 💾 Persist latest valid ID
  localStorage.setItem(
    "activeMissionId",
    finalMissionId
  );

  console.log(
    "🔌 AUTO JOIN:",
    finalMissionId
  );

  console.log(
    "🧪 Current mission role:",
    sessionStorage.getItem(
      "missionRole"
    )
  );

  // 🔌 JOIN ROOM
  routeSocket.emit(
    "join_mission",
    {
      mission_id:
        String(finalMissionId)
    }
  );

}, []);

useEffect(() => {

  console.log(
    "👂 mission_update listener mounted"
  );

  const handler = (mission) => {

    console.log(
      "🔥 LIVE MISSION UPDATE:",
      mission
    );

    const state =
      mission?.state;

    if (!state) {
      return;
    }

    // 🔥 TAB ROLE
    const role =
      sessionStorage.getItem(
        "missionRole"
      );

    console.log(
      "🧪 Mission role:",
      role
    );

    // =====================================
    // ✈️ AIRCRAFT-ONLY PATCHES
    // =====================================

    if (
      state.aircraftOnly &&
      state.aircraft
    ) {

      console.log(
        "✈️ Aircraft-only patch received"
      );

      console.log(
        state.aircraft
      );

      // 👨‍✈️ CAPTAIN IGNORES
      if (role === "captain") {
        return;
      }

      // 👥 CREW AIRCRAFT
      setSharedAircraftState(
        state.aircraft
      );

      // 🔥 LIVE LEG SYNC
      setSharedActiveLegIndex(
        state.aircraft.activeLegIndex || 0
      );

      console.log(
        "🟣 LIVE SHARED LEG:",
        state.aircraft.activeLegIndex
      );

      return;
    }

    // =====================================
    // 🛫 FULL FLIGHT STATE
    // =====================================

    if (state.fullFlight) {

      console.log(
        "🛫 Full flight state received"
      );

      // 👨‍✈️ CAPTAIN NEVER RESTORES
      if (role === "captain") {

        console.log(
          "👨‍✈️ Captain ignoring restore"
        );

        return;
      }

      const restoredFlight =
        state.fullFlight;

      console.log(
        "🧪 RESTORED FULL FLIGHT:",
        restoredFlight
      );

      console.log(
        "🛫 RESTORE COORDS:",
        restoredFlight?.route?.coords?.length
      );

      // 👥 CREW RESTORE
      setActiveRoute(
        restoredFlight
      );

      setActiveFlight(
        restoredFlight
      );

      console.log(
        "🛫 CREW ROUTE RESTORED:",
        restoredFlight?.route?.coords?.length
      );

      // 👥 RESTORE AIRCRAFT
      if (
        restoredFlight.aircraft
      ) {

        setSharedAircraftState(
          restoredFlight.aircraft
        );

        // 🔥 AUTHORITATIVE SHARED LEG
        setSharedActiveLegIndex(
          restoredFlight.aircraft.activeLegIndex || 0
        );

        // 🔥 OPTIONAL:
        // local visual sync
        setCurrentLegIndex(
          restoredFlight.aircraft.activeLegIndex || 0
        );

        console.log(
          "🟣 RESTORED SHARED LEG:",
          restoredFlight.aircraft.activeLegIndex
        );
      }
    }
  };

  // 🔌 SOCKET LISTENER
  window.routeSocket.on(
    "mission_update",
    handler
  );

  return () => {

    window.routeSocket.off(
      "mission_update",
      handler
    );
  };

}, []);



const sendRouteUpdate = (payload) => {

  const missionId =

    window.__activeMissionId ||

    localStorage.getItem(
      "activeMissionId"
    );

  if (!missionId) {

    console.warn(
      "❌ No mission ID"
    );

    return;
  }

  console.log(
    "📡 Sending route payload:",
    payload
  );

  console.log(
    "🧭 Using mission:",
    missionId
  );

  routeSocket.emit(
    "update_mission",
    {

      mission_id:
        String(missionId),

      state: {

        fullFlight: {

          ...activeFlight,

          meta: {

            ...activeFlight?.meta,

            routeTextCurrent:
              payload.route || ""
          },

          route: {

            ...activeFlight?.route,

            coords:
              payload.coords || [],

            waypoints:
              payload.waypoints || []
          }
        }
      }
    }
  );

  console.log(
    "✅ Full flight mission update sent"
  );
};

useEffect(() => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const ctx = canvas.getContext("2d");

  const resizeCanvas = () => {
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
  };

  resizeCanvas();

  let lastX = 0;
  let lastY = 0;

  const startDrawing = (e) => {
    if (!drawingMode) return;
    drawing.current = true;

    const rect = canvas.getBoundingClientRect();
    lastX = e.clientX - rect.left;
    lastY = e.clientY - rect.top;
  };

  const draw = (e) => {
    if (!drawing.current || !drawingMode) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    ctx.strokeStyle = "#ff0000";
    ctx.lineWidth = 3;
    ctx.lineCap = "round";

    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(x, y);
    ctx.stroke();

    lastX = x;
    lastY = y;
  };

  const stopDrawing = () => {
    drawing.current = false;
  };

  canvas.addEventListener("pointerdown", startDrawing);
  canvas.addEventListener("pointermove", draw);
  canvas.addEventListener("pointerup", stopDrawing);
  canvas.addEventListener("pointerleave", stopDrawing);

  return () => {
    canvas.removeEventListener("pointerdown", startDrawing);
    canvas.removeEventListener("pointermove", draw);
    canvas.removeEventListener("pointerup", stopDrawing);
    canvas.removeEventListener("pointerleave", stopDrawing);
  };
}, [drawingMode]);



const fetchChartsForAirport = async () => {
  try {
    const res = await fetch(
      `https://ficon-x.com/route/get_available_charts?icao=${chartSearch}`
    );
    const data = await res.json();

    console.log("🧪 Chart results:", data);

    let allCharts = [];

    Object.values(data).forEach(val => {
      if (Array.isArray(val)) {
        allCharts.push(...val);
      }
    });

    // 🔥 PIN AIRPORT DIAGRAM TO TOP
    allCharts.sort((a, b) => {
      const aIsAD = (a.name || "").toUpperCase().includes("AIRPORT DIAGRAM");
      const bIsAD = (b.name || "").toUpperCase().includes("AIRPORT DIAGRAM");

      if (aIsAD && !bIsAD) return -1;
      if (!aIsAD && bIsAD) return 1;
      return 0;
    });

    setChartResults(allCharts);

  } catch (err) {
    console.error("Chart fetch failed:", err);
  }
};

const saveChart = (chart) => {

  const existing =
    JSON.parse(
      localStorage.getItem("savedCharts") || "[]"
    );

  // =====================================
  // 🚫 Prevent ONLY identical URLs
  // =====================================

  const already =
    existing.some(
      c => c.url === chart.url
    );

  if (already) {

    console.log(
      "⚠️ Chart already saved:",
      chart.name
    );

    return;
  }

  // =====================================
  // ✅ Save chart
  // =====================================

  const updated = [

    ...existing,

    {
      ...chart,

      id:
        Date.now() +
        Math.random()
    }
  ];

  localStorage.setItem(
    "savedCharts",
    JSON.stringify(updated)
  );

  setSavedCharts(updated);

  console.log(
    "💾 Saved chart:",
    chart.name
  );
};


const onToggleIfrHigh = () => {
  const map = mapRef.current;
  if (!map) return;

  console.log("🟣 IFR HIGH toggle");

  if (!map.getSource("ifr-high")) {
    map.addSource("ifr-high", {
      type: "raster",
      tiles: [
        "https://tile.openaviationdata.com/ifr_high/{z}/{x}/{y}.png"
      ],
      tileSize: 256
    });

    map.addLayer({
      id: "ifr-high-layer",
      type: "raster",
      source: "ifr-high",
      layout: { visibility: "visible" },
      paint: { "raster-opacity": 0.9 }
    });

    console.log("✅ IFR HIGH ADDED");
  } else {
    const vis = map.getLayoutProperty("ifr-high-layer", "visibility");

    map.setLayoutProperty(
      "ifr-high-layer",
      "visibility",
      vis === "visible" ? "none" : "visible"
    );

    console.log("👁 IFR HIGH toggled");
  }
};

  const styles = {
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0, 0, 0, 0.35)",
    backdropFilter: "blur(4px)", // 🔥 modern glass effect
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  loadingBox: {
    background: "rgba(20,20,20,0.9)",
    padding: "20px 30px",
    borderRadius: "14px",
    color: "#fff",
    textAlign: "center",
    boxShadow: "0 0 30px rgba(0,0,0,0.5)",
  },
};
 
  const loadingMessages = [
    "Evaluating weather...",
    "Checking hazards...",
    "Optimizing fuel...",
    "Building routes...",
  ];

 function cancelDirectTo() {
  setDirectToPreview(null);
} 

 function confirmDirectTo() {
  if (!directToPreview) return;

  handleDirectTo(directToPreview.fixName);

  setDirectToPreview(null);
} 

  function previewDirectTo(fixName) {
  const raw = localStorage.getItem("activeFlight");
  if (!raw) return;

  const flight = JSON.parse(raw);
  const simulated = directToFix(flight, fixName);
  if (!simulated) return;

  const oldDistance = flight.route.distanceRemainingNm || 0;
  const newDistance = simulated.route.distanceRemainingNm || 0;

  const oldETE = flight.timing?.eteRemainingMinutes || 0;
  const newETE = simulated.timing?.eteRemainingMinutes || 0;

  const oldFuel = flight.fuel?.projectedFuelAtDestination || 0;
  const newFuel = simulated.fuel?.projectedFuelAtDestination || 0;

  setDirectToPreview({
    fixName,
    distanceDelta: newDistance - oldDistance,
    eteDelta: newETE - oldETE,
    fuelDelta: newFuel - oldFuel
  });
}

const loadFlight = async (flightData) => {

  console.log(
    "🛫 Loading flight (no reload):",
    flightData
  );

  // 🧹 CLEAR OLD MISSION STATE
  localStorage.removeItem(
    "activeMissionId"
  );

  window.__activeMissionId =
    null;

  console.log(
    "🧹 Cleared previous mission ID"
  );

  // 💾 Persist active flight
  localStorage.setItem(
    "activeFlight",
    JSON.stringify(flightData)
  );

  sessionStorage.setItem(
    "flightSessionActive",
    "true"
  );

  // 👨‍✈️ AUTHORITATIVE ROLE
  sessionStorage.setItem(
    "missionRole",
    "captain"
  );

  window.__missionRole =
    "captain";

  console.log(
    "👨‍✈️ GLOBAL ROLE SET:",
    window.__missionRole
  );

  console.log(
    "👨‍✈️ This tab is Captain"
  );

  setActiveFlight(
    flightData
  );

  setActiveRoute(
    flightData
  );

  // 🚀 START DEV SIM
  setSimStarted(true);

  // 🔁 Notify restore listeners
  window.dispatchEvent(
    new Event("storage")
  );

  // =========================
  // 🧭 CREATE SHARED MISSION
  // =========================

  try {

    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {

      console.warn(
        "❌ No token — skipping mission creation"
      );

      return;
    }

    const res = await fetch(
      "https://ficon-x.com/route/create-mission",
      {

        method: "POST",

        headers: {

          Authorization:
            `Bearer ${token}`,

          "Content-Type":
            "application/json"
        },

        body: JSON.stringify({

          name:
            `${flightData.meta.origin}-${flightData.meta.destination}`,

          origin:
            flightData.meta.origin,

          destination:
            flightData.meta.destination,

          aircraft:
            flightData.meta.aircraft ||
            flightData.meta.default_aircraft ||
            "UNKNOWN"
        })
      }
    );

    const mission =
      await res.json();

    console.log(
      "🧭 CREATED:",
      mission
    );

    // 💾 Persist mission ID
    localStorage.setItem(
      "activeMissionId",
      mission.id
    );

    // 🌐 AUTHORITATIVE GLOBAL MISSION
    window.__activeMissionId =
      mission.id;

    console.log(
      "🧭 ACTIVE MISSION:",
      mission.id
    );

    // 🔌 Join realtime room
    if (window.routeSocket) {

      window.routeSocket.emit(
        "join_mission",
        {

          mission_id:
            String(mission.id)
        }
      );

      // 🔥 RE-BROADCAST FULL ROUTE
setTimeout(() => {

  console.log(
    "📡 Re-broadcasting full flight"
  );

  window.routeSocket.emit(
    "update_mission",
    {

      mission_id:
        String(mission.id),

      state: {

        fullFlight: {

          ...flightData,

          timestamp:
            Date.now()
        }
      }
    }
  );

}, 250);

      // 📡 SEND INITIAL FULL FLIGHT STATE
      window.routeSocket.emit(
        "update_mission",
        {

          mission_id:
            String(mission.id),

          state: {

            fullFlight: {

              ...flightData,

              timestamp:
                Date.now()
            }
          }
        }
      );

      console.log(
        "📡 Initial mission state broadcasted"
      );
    }

  } catch (err) {

    console.error(
      "❌ Mission creation failed:",
      err
    );
  }
};
 
function handleDirectTo(fixName) {
  const raw = localStorage.getItem("activeFlight");
  if (!raw) return;

  const flight = JSON.parse(raw);

const updated = directToFix(
  flight,
  fixName,
  aircraftPosition
);
  if (!updated) return;

  // 🔥 CRITICAL: preserve routeText
  const updatedWithText = {
    ...updated,
    routeText: updated.routeText || flight.routeText || ""
  };

  // Persist updated flight
  localStorage.setItem("activeFlight", JSON.stringify(updatedWithText));

  console.log("✈️ Direct-To applied:", fixName);
  console.log("🧪 Updated flight:", updatedWithText);

  // 🔥 Trigger route redraw
  setActiveRoute({
    ...updatedWithText
  });
}

const generateAlternateRoute = async (alternateIcao) => {

  const raw =
    localStorage.getItem(
      "activeFlight"
    );

  if (!raw) return;

  const flight =
    JSON.parse(raw);

  const icao =
    alternateIcao
      ?.toUpperCase()
      ?.trim();

  if (!icao) {

    console.warn(
      "⚠️ Invalid alternate ICAO"
    );

    return;
  }

  // =====================================
  // ✅ BLOCK DUPLICATE ALTERNATE
  // =====================================

  if (

    flight.alternate?.icao === icao &&

    flight.alternate?.route?.coords?.length > 0

  ) {

    console.log(
      "⚠️ Alternate already generated:",
      icao
    );

    return;
  }

  // =====================================
  // ✈️ LIVE AIRCRAFT POSITION
  // =====================================

  const start =

    aircraftPosition ||

    flight.aircraft?.position ||

    flight.route?.coords?.[0];

  console.log(
    "✈️ Alternate start position:",
    start
  );

  if (
    !start ||
    start.length !== 2
  ) {

    console.error(
      "❌ No valid start position for alternate"
    );

    return;
  }

  try {

    console.log(
      "✈️ Generating alternate:",
      icao
    );

    const resolved =
      await resolveRouteWithCoords(

        icao,

        flight.meta?.origin || "",

        icao,

        flight.meta?.altitude || ""
      );

    if (

      !resolved?.coords ||

      resolved.coords.length === 0

    ) {

      console.error(
        "❌ Resolver returned no coords",
        resolved
      );

      return;
    }

    // =====================================
    // 🔥 BUILD ROUTE
    // =====================================

    const coords = [

      start,

      ...resolved.coords.slice(1)
    ];

    // =====================================
    // 🧮 TRUE ROUTE DISTANCE
    // =====================================

    let distanceNm = 0;

    for (
      let i = 0;
      i < coords.length - 1;
      i++
    ) {

      distanceNm += getDistanceNm(

        coords[i],

        coords[i + 1]
      );
    }

    console.log(
      "🟠 Alternate distance:",
      distanceNm
    );

    // =====================================
    // ✈️ LIVE GS ONLY
    // NO FALLBACKS
    // =====================================

    const currentGs =
      Number(
        sharedAircraftState?.speed
      );

    console.log(
      "✈️ LIVE GS:",
      currentGs
    );

    // =====================================
    // 🚫 PARKED / INVALID
    // =====================================

    let eteMinutes = null;

    if (

      currentGs &&

      currentGs >= 50 &&

      currentGs < 700

    ) {

      eteMinutes =
        (distanceNm / currentGs) * 60;
    }

    console.log(
      "🟠 Alternate ETE:",
      eteMinutes
    );

    // =====================================
    // 🛫 ROUTE OBJECT
    // =====================================

    const route = {

      coords,

      waypoints:
        resolved.waypoints || [],

      routeText:
        `DCT ${icao}`
    };

    // =====================================
    // 💾 SAVE
    // =====================================

    flight.alternate = {

      ...flight.alternate,

      icao,

      route,

      visible: true,

      distanceNm,

      eteMinutes,

      mode:
        flight.alternate?.mode ||
        "direct"
    };

    localStorage.setItem(

      "activeFlight",

      JSON.stringify(flight)
    );

    // =====================================
    // 🔥 FORCE REFRESH
    // =====================================

    setAltRefresh(
      prev => !prev
    );

    window.dispatchEvent(
      new Event("storage")
    );

    // =====================================
    // 🟠 PLOT
    // =====================================

    plotAlternateRoute(route);

    console.log(
      `🟠 Alternate generated: ${icao} | ${distanceNm.toFixed(1)} NM | ${
        eteMinutes != null
          ? eteMinutes.toFixed(1) + " min"
          : "--"
      }`
    );

  } catch (err) {

    console.error(
      "❌ Alternate generation failed:",
      err
    );
  }
};

async function applyRouteAmendment() {

  try {

    if (!routeInput.trim()) return;

    const raw =
      localStorage.getItem(
        "activeFlight"
      );

    if (!raw) return;

    const flight =
      JSON.parse(raw);

    console.log(
      "✏️ Applying route amendment:",
      routeInput
    );

    // 🔥 Resolve proposed route
    const resolved =
      await resolveRouteWithCoords(
        routeInput,
        flight.meta.origin,
        flight.meta.destination,
        flight.meta.altitude
      );

    if (!resolved?.coords?.length) {

      console.warn(
        "❌ Route resolution failed"
      );

      return;
    }

    const currentPosition =
      aircraftPosition ||
      flight.aircraft?.position ||
      flight.route?.coords?.[0];
// =====================================
// ✈️ BUILD TRUE AMENDED ROUTE
// Aircraft → first fix → remaining route
// =====================================

// 🔥 KEEP PURE OPERATIONAL ROUTE
const newCoords =
  resolved.coords;
    const updatedFlight = {

      ...flight,

      routeText: routeInput,

      meta: {
        ...flight.meta,

        routeTextCurrent:
          routeInput
      },

      route: {
        ...flight.route,

        coords: newCoords,

waypoints: [


  ...(resolved.waypoints || [])
],

        amendedHistory: [
          ...(flight.route
            .amendedHistory || []),

          routeInput
        ]
      },

      aircraft: {
        ...flight.aircraft,

        currentLegIndex: 0
      }
    };

        // 🔥 TAB-ISOLATED ROLE
    const missionRole =

      window.__missionRole ||

      sessionStorage.getItem(
        "missionRole"
      );

    console.log(
      "🧪 FINAL RESTORE ROLE:",
      missionRole
    );

    // 👥 CREW REQUEST FLOW
    if (missionRole === "crew") {

      console.log(
        "📝 Crew requesting route amendment"
      );

      if (window.routeSocket) {

        window.routeSocket.emit(
          "request_route_change",
          {

            mission_id:
              localStorage.getItem(
                "activeMissionId"
              ),

            proposedRoute:
              routeInput,

            proposedFlight: {

              routeText:
                routeInput,

              coords:
                newCoords,

              waypoints:
                resolved.waypoints || []
            },

            requestedBy:
              sessionStorage.getItem(
                "missionRole"
              ) || "crew",

            timestamp:
              Date.now()
          }
        );

        console.log(
          "✅ Route proposal sent to Captain"
        );

        alert(
          "Route change request sent to Captain"
        );
      }

      return;
    }

    // 👨‍✈️ CAPTAIN FLOW
    console.log(
      "👨‍✈️ Captain applying route amendment"
    );

    // 🔥 SEND AUTHORITATIVE MISSION UPDATE
    sendRouteUpdate({
      route: routeInput,

      coords: newCoords,

      waypoints:
        resolved.waypoints || []
    });

    // 🔥 Persist locally
    localStorage.setItem(
      "activeFlight",
      JSON.stringify(
        updatedFlight
      )
    );

    // 🚨 FORCE FULL ROUTE REDRAW
    setActiveRoute({
      ...updatedFlight
    });

        setActiveFlight({
      ...updatedFlight
    });

        console.log(
          "✅ Route amendment applied"
        );

  } catch (err) {

    console.error(
      "❌ Route amendment failed:",
      err
    );
  }
}

useEffect(() => {

  // 🔥 MAIN BACKEND SOCKET (5000)
  const liveSocket = socket;

  if (!liveSocket) {

    console.warn(
      "❌ No main socket found"
    );

    return;
  }

  console.log(
    "🛰️ VATSIM listener mounted"
  );

  const handleVatsimPosition =
    (data) => {

      console.log(
        "🛫 RAW VATSIM POSITION:",
        data
      );

      if (
        data.lat == null ||
        data.lon == null
      ) {

        console.warn(
          "❌ Missing coords:",
          data
        );

        return;
      }

      // =====================================
      // ✈️ AIRCRAFT POSITION
      // =====================================

      const position = [

        Number(data.lon),

        Number(data.lat)
      ];

      console.log(
        "🛰️ FINAL POSITION:",
        position
      );

      setAircraftPosition(
        position
      );

      setAircraftHeading(
        Number(data.heading) || 0
      );

      // =====================================
      // ✈️ LIVE GROUNDSPEED
      // FR24 + VATSIM both normalize this
      // as "groundspeed"
      // =====================================

      const liveGroundspeed =
        Number(data.groundspeed);

      if (
        Number.isFinite(liveGroundspeed) &&
        liveGroundspeed >= 0
      ) {
        setAircraftGroundspeed(
          liveGroundspeed
        );
      }

      console.log(
        "✈️ LIVE GROUNDSPEED:",
        liveGroundspeed,
        "KT",
        "SOURCE:",
        data.source
      );

      updateAircraftMarker(

        position,

        Number(data.heading) || 0
      );

      // =====================================
      // 🟣 LIVE LEG TRACKING
      // =====================================

      const coords =
        activeRoute?.route?.coords;

      if (
        coords &&
        coords.length > 1
      ) {

const nextIndex =

  Math.min(

    currentLegIndex + 1,

    coords.length - 1
  );

const nextFix =
  coords[nextIndex];

        if (nextFix) {

          const distNm =
            getDistanceNm(

              position,

              nextFix
            );

          console.log(
            "🟣 Distance to next fix:",
            distNm.toFixed(2)
          );

          console.log(
            "🟣 Current leg:",
            currentLegIndex
          );

          console.log(
            "🟣 Next leg:",
            nextIndex
          );

          // =====================================
          // FIX PASSAGE DETECTION
          // =====================================

          if (distNm < 3) {

            console.log(
              "🟣 ADVANCING LEG:",
              nextIndex
            );

setCurrentLegIndex(
  prev =>
    Math.min(
      prev + 1,
      coords.length - 1
    )
);
          }
        }
      }
    };

  // =====================================
  // 🔌 SOCKET LISTENER
  // =====================================

  liveSocket.on(
    "live_aircraft_position",
    handleVatsimPosition
  );

  return () => {

    liveSocket.off(
      "live_aircraft_position",
      handleVatsimPosition
    );
  };

}, [

  activeRoute,

  currentLegIndex
]);




useEffect(() => {

  if (!aircraftPosition) {
    return;
  }

  console.log(
    "✈️ Updating aircraft marker:",
    aircraftPosition
  );

  updateAircraftMarker(
    aircraftPosition,
    aircraftHeading
  );

}, [

  aircraftPosition,
  aircraftHeading
]);

useEffect(() => {
  const loadLastFlight = async () => {
    try {
      console.log("📡 Attempting backend restore...");

      const missionId = localStorage.getItem("activeMissionId");

      const sessionActive =
        sessionStorage.getItem(
          "flightSessionActive"
        ) === "true";

      // 🚫 No active operational session
      if (!sessionActive) {

        console.log(
          "🧹 No active flight session — skipping restore"
        );

        return;
      }

      // 🚫 CRITICAL: If we're in a mission, DO NOT restore old flights
      if (missionId) {
        console.log("🧠 Mission active — skipping backend restore");
        return;
      }

      const token = localStorage.getItem("token");
      if (
        !token ||
        token === "null" ||
        token === "undefined"
      ) {
        console.log("🔐 Guest mode");
        return;
      }

      const res = await fetch("https://ficon-x.com/auth/get-flights", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const flights = await res.json();

      if (!Array.isArray(flights) || flights.length === 0) {

          console.log("✈️ No active flights found");

          return;
      }

      const latest = flights[0].data;

      console.log("✈️ Restoring ACTIVE flight:", latest.meta);

      // 🔒 Only restore if nothing active already
      if (!localStorage.getItem("activeFlight")) {
        console.log("✈️ Restoring flight from backend → React state");

        sessionStorage.setItem("flightSessionActive", "true");

        setIsFlightActive(true);
        setActiveRoute(latest);
        setActiveFlight(latest);
        setSimStarted(true);

        localStorage.setItem("activeFlight", JSON.stringify(latest));
      }

    } catch (err) {
      console.error("❌ Backend restore failed:", err);
    }
  };

  loadLastFlight();
}, []);

useEffect(() => {
  if (!destination || destination.length !== 4) return;

  const timeout = setTimeout(() => {
    fetchAlternates(destination.toUpperCase());
  }, 300);

  return () => clearTimeout(timeout);
}, [destination]);

const handleSelectAlternate = (icao) => {
  console.log("🟠 Selected alternate:", icao);

  setAlternateInput(icao);

  // 🔥 OPTIONAL (recommended later)
  // generateAlternateRoute(icao);
};

const fetchAlternates = async (icao) => {
  try {
    setLoadingAlts(true);

 console.log(
  "🟠 ALTERNATE PERSONAL MINIMUMS:",
  {
    runway:
      user?.preferred_runway_length_ft,

    ceiling:
      user?.alternate_ceiling_min_ft,

    visibility:
      user?.alternate_visibility_min_sm
  }
);   

const res = await fetch("https://ficon-x.com/route/suggest-alternates", {
  method: "POST",

  headers: {
    "Content-Type": "application/json"
  },

body: JSON.stringify({

    destination: icao,

    tas: Number(tasKts),

    burnRate: Number(burnRate),

    preferred_runway_length_ft:
      user?.preferred_runway_length_ft ?? 5000,

    alternate_ceiling_min_ft:
      user?.alternate_ceiling_min_ft ?? 2000,

    alternate_visibility_min_sm:
      user?.alternate_visibility_min_sm ?? 5

})
});

    const data = await res.json();
    setAlternates(data);

    console.log("🟢 setAlternates received:", data);

    // 🔥 AUTO-SELECT BEST
    if (data.length > 0) {
      setAlternateInput(data[0].icao);
    }

  } catch (err) {
    console.error("Alt fetch error:", err);
  } finally {
    setLoadingAlts(false);
  }
};

useEffect(() => {

  if (!mapReady) {
    return;
  }

  const map =
    mapRef.current;

  if (!map) {
    return;
  }

  console.log(
    "🔐 Zoom gate attached"
  );

const updateZoom = () => {

  const z = map.getZoom();

  console.log(
    "🧪 CURRENT ZOOM:",
    z
  );

  setMapZoom(z);

};

  map.on(
    "zoom",
    updateZoom
  );

  // 🔥 run immediately
  updateZoom();

  return () => {

    map.off(
      "zoom",
      updateZoom
    );
  };

}, [mapReady]);

// 🔄 ROTATING UI MESSAGES
useEffect(() => {
  if (!suggestionsLoading) return;

  const interval = setInterval(() => {
    setMessageIndex((prev) => (prev + 1) % loadingMessages.length);
  }, 1200);

  return () => clearInterval(interval);
}, [suggestionsLoading]);

useEffect(() => {

  const missionRole =

    window.__missionRole ||

    sessionStorage.getItem(
      "missionRole"
    );

  // =====================================
  // ✈️ LIVE AIRCRAFT POSITION
  // =====================================

  const livePosition =

    missionRole === "crew"

      ? sharedAircraftState?.position

      : aircraftPosition;

  if (!livePosition) {

    console.log(
      "⚠️ No live aircraft position"
    );

    return;
  }

  // =====================================
  // 💾 LOAD ACTIVE FLIGHT
  // =====================================

  const raw =
    localStorage.getItem(
      "activeFlight"
    );

  if (!raw) {

    console.log(
      "⚠️ No activeFlight found"
    );

    return;
  }

  const flight =
    JSON.parse(raw);

  const alt =
    flight.alternate;

  // =====================================
  // 🟠 VALIDATE ALTERNATE
  // =====================================

  if (
    !alt?.visible ||

    !alt?.route?.coords?.length
  ) {

    console.log(
      "⚠️ No alternate route"
    );

    return;
  }

  // =====================================
  // 🎯 END OF ALT ROUTE
  // =====================================

  const end =

    alt.route.coords[
      alt.route.coords.length - 1
    ];

  // =====================================
  // 🧮 LIVE DISTANCE
  // =====================================

  const distanceNm =
    getDistanceNm(
      livePosition,
      end
    );

  // =====================================
  // ✈️ SPEED
  // =====================================

  const tas =
    flight.meta?.tas || 450;

  // =====================================
  // ⏱️ ETE
  // =====================================

  const eteMinutes =

    tas > 0

      ? (distanceNm / tas) * 60

      : 0;

  // =====================================
  // 💾 BUILD NEW FLIGHT OBJECT
  // =====================================

  const updatedFlight = {

    ...flight,

    alternate: {

      ...flight.alternate,

      distanceNm,

      eteMinutes
    }
  };

  // =====================================
  // 💾 SAVE TO STORAGE
  // =====================================

  localStorage.setItem(

    "activeFlight",

    JSON.stringify(updatedFlight)
  );

  // =====================================
  // 🔥 FORCE REACT UPDATE
  // =====================================

  setActiveFlight(
    updatedFlight
  );



  // =====================================
  // 🧪 DEBUG
  // =====================================

  console.log(
    "🟠 LIVE ALT UPDATE:",
    distanceNm.toFixed(1),
    "NM",
    "|",
    eteMinutes.toFixed(1),
    "MIN"
  );

}, [

  aircraftPosition,

  sharedAircraftState?.position
]);
useEffect(() => {
  if (activeRoute?.meta?.routeTextCurrent) {
    setRouteInput(activeRoute.meta.routeTextCurrent);
  }
}, [activeRoute]);





useEffect(() => {
  console.log("🧱 MapComponent MOUNTED");

  return () => {
    console.log("💥 MapComponent UNMOUNTED");
  };
}, []);

useEffect(() => {

  console.log("🔥 MAP MOUNTED");

  const storedCharts =
    localStorage.getItem("savedCharts");

  console.log(
    "🔥 RAW savedCharts:",
    storedCharts
  );

  if (storedCharts) {

    const charts =
      JSON.parse(storedCharts);

    console.log(
      "🔥 PARSED charts:",
      charts
    );

    // ✅ ONLY restore saved chart list
    // 🚫 DO NOT auto-open windows
    setSavedCharts(charts);

  } else {

    console.log(
      "🔥 NO CHARTS FOUND"
    );
  }

}, []);


useEffect(() => {

  const role =
    sessionStorage.getItem(
      "missionRole"
    );

  if (role === "captain") {
    return;
  }

  if (!sharedAircraftState) {
    return;
  }

  const position =

    sharedAircraftState.position ||

    [

      sharedAircraftState.lon,

      sharedAircraftState.lat
    ];

  console.log(
    "👥 Rendering shared aircraft:",
    position
  );

  // 👥 CREW ONLY RENDERS
  updateAircraftMarker(
    position,
    sharedAircraftState.heading || 0
  );

}, [sharedAircraftState]);

useEffect(() => {

  const role =
    sessionStorage.getItem(
      "missionRole"
    );

  // 👨‍✈️ CAPTAIN ONLY
  if (role !== "captain") {
    return;
  }

  if (!aircraftPosition) {
    return;
  }

  console.log(
    "👨‍✈️ Rendering captain aircraft:",
    aircraftPosition
  );

  updateAircraftMarker(
    aircraftPosition,
    aircraftHeading || 0
  );

}, [

  aircraftPosition,

  aircraftHeading
]);

useEffect(() => {

  console.log(
    "🟣 MAGENTA EFFECT RUNNING"
  );

  const map =
    mapRef.current;

  if (!map) {

    console.log(
      "❌ No map ref"
    );

    return;
  }

  if (!mapReady) {

    console.log(
      "⏳ Style not loaded yet"
    );

    return;
  }

const activeMissionId =

  window.__activeMissionId ||

  localStorage.getItem(
    "activeMissionId"
  );

  const missionRole =

  activeMissionId

    ? (
        window.__missionRole ||

        sessionStorage.getItem(
          "missionRole"
        )
      )

    : "captain";

  const coords =
    activeRoute?.route?.coords;

  console.log(
    "👥 missionRole:",
    missionRole
  );

  console.log(
    "🟣 currentLegIndex:",
    currentLegIndex
  );

  console.log(
    "👥 sharedActiveLegIndex:",
    sharedActiveLegIndex
  );



  console.log(
    "👥 coords used for magenta:",
    coords
  );

  if (!coords || coords.length < 2) {

    console.log(
      "❌ Invalid coords"
    );

    return;
  }

  const displayLegIndex =

    missionRole === "captain"

      ? currentLegIndex

      : (
          sharedActiveLegIndex || 0
        );

     console.log(
  "🟣 displayLegIndex:",
  displayLegIndex
);

console.log(
  "🟣 MAGENTA start:",
  coords?.[displayLegIndex]
);

console.log(
  "🟣 MAGENTA end:",
  coords?.[displayLegIndex + 1]
);     

  console.log(
    "🟣 displayLegIndex:",
    displayLegIndex
  );

  const start =
    coords[displayLegIndex];

  const end =
    coords[displayLegIndex + 1];

  console.log(
    "🟣 start:",
    start
  );

  console.log(
    "🟣 end:",
    end
  );

  if (!start || !end) {

    console.log(
      "❌ Invalid active leg"
    );

    return;
  }

  console.log(
    "🟣 DRAWING ACTIVE LEG:",
    displayLegIndex
  );

  const geojson = {

    type: "Feature",

    geometry: {

      type: "LineString",

      coordinates: [
        start,
        end
      ]
    }
  };

// =====================================
// 🧹 REMOVE OLD LAYERS FIRST
// =====================================

if (
  map.getLayer("active-leg-glow")
) {

  try {

    map.removeLayer(
      "active-leg-glow"
    );

  } catch (e) {

    console.warn(e);
  }
}

if (
  map.getLayer("active-leg-main")
) {

  try {

    map.removeLayer(
      "active-leg-main"
    );

  } catch (e) {

    console.warn(e);
  }
}

// =====================================
// 🧹 REMOVE OLD SOURCE
// =====================================

if (
  map.getSource("active-leg")
) {

  try {

    map.removeSource(
      "active-leg"
    );

  } catch (e) {

    console.warn(
      "⚠️ Failed removing source",
      e
    );
  }
}

// =====================================
// 🆕 CREATE SOURCE
// =====================================

map.addSource(
  "active-leg",
  {

    type: "geojson",

    data: geojson
  }
);

// =====================================
// 🆕 GLOW LAYER
// =====================================

map.addLayer({

  id: "active-leg-glow",

  type: "line",

  source: "active-leg",

  layout: {

    "line-cap": "round",

    "line-join": "round"
  },

  paint: {

    "line-color": "#ffffff",

    "line-width": 12,

    "line-opacity": 0.35,

    "line-blur": 2
  }
});

// =====================================
// 🆕 MAIN LAYER
// =====================================

map.addLayer({

  id: "active-leg-main",

  type: "line",

  source: "active-leg",

  layout: {

    "line-cap": "round",

    "line-join": "round"
  },

  paint: {

    "line-color": "#FF00FF",

    "line-width": 6,

    "line-opacity": 1
  }
});

}, [

  currentLegIndex,

  sharedActiveLegIndex,

  sharedAircraftState,

  activeRoute,

  mapReady
]);

// ======================================
// 🛫 ROUTE PLOT + ALT GENERATION
// ======================================

useEffect(() => {

  if (!mapReady) {
    return;
  }

  if (!activeRoute) {
    return;
  }

  console.log(
  "🚨 ACTIVE ROUTE REPLOT"
);

console.log(
  "🚨 ACTIVE ROUTE WAYPOINTS",
  activeRoute?.waypoints?.map(
    w => w.id || w.name
  )
);

console.log(
  "🚨 ACTIVE ROUTE OBJECT",
  activeRoute
);

  plotRoute(
    activeRoute
  );

  // 🟠 GENERATE ALT AFTER ROUTE READY
  const flight =
    activeRoute;

  if (

    flight.alternate?.icao &&

    !flight.alternate?.route &&

    !window.__altGenerating

  ) {

    console.log(
      "🟠 Generating ALT after route ready"
    );

    window.__altGenerating =
      true;

    generateAlternateRoute(
      flight.alternate.icao
    ).finally(() => {

      window.__altGenerating =
        false;
    });
  }

}, [

  activeRoute,

  mapReady
]);

const mapInstanceId = useRef(
  Math.random().toString(36).substring(2, 8)
);

useEffect(() => {

  console.log(
    "🚨 MAP INSTANCE:",
    mapInstanceId.current,
    destinationData
  );

}, [destinationData]);

useEffect(() => {

  console.log(
    "🧨 MAP MOUNTED",
    mapInstanceId.current
  );

  return () => {

    console.log(
      "💀 MAP UNMOUNTED",
      mapInstanceId.current
    );

  };

}, []);

useEffect(() => {
  isFlightActiveRef.current = isFlightActive;
}, [isFlightActive]);

useEffect(() => {

  const loadFlightFromStorage = () => {

    const raw =
      localStorage.getItem(
        "activeFlight"
      );

    const session =
      sessionStorage.getItem(
        "flightSessionActive"
      );

    // 🔒 Only restore if flight exists
    if (!raw) {

      console.log(
        "🧹 No activeFlight — skipping restore"
      );

      return;
    }

try {

  const flight =
    JSON.parse(raw);

  if (
    flight?.route?.coords?.length > 1
  ) {

    console.log(
      "🛫 Restoring active flight into state"
    );

    console.log(
      "🚨 RESTORED WAYPOINTS",
      flight.route?.waypoints?.map(
        w => w.id || w.name
      )
    );

    console.log(
      "🚨 RESTORED DEPARTURE",
      flight.routeStructure?.departure
    );

    // ✈️ ALWAYS restore route state
    setActiveRoute(flight);

    setActiveFlight(flight);

        // 🔥 DETERMINE ROLE
        const missionRole =

          window.__missionRole ||

          sessionStorage.getItem(
            "missionRole"
          );

        console.log(
          "🧪 Restore role:",
          missionRole
        );

                console.log(
          "🧪 WILL START SIM:",
          missionRole === "captain"
        );

        // 👨‍✈️ ONLY CAPTAIN RUNS SIM
        if (
          missionRole === "captain"
        ) {

          console.log(
            "👨‍✈️ Captain restoring live simulation"
          );

          setIsFlightActive(
            true
          );

          // 🔥 CRITICAL RESET
          simHasStartedRef.current =
            false;

          console.log(
            "🚀 FORCING DEV SIM RESTART"
          );

          // 🔥 FORCE STATE CHANGE
          setSimStarted(false);

          setTimeout(() => {

            console.log(
              "🚀 DEV SIM TRUE"
            );

            setSimStarted(
              true
            );

          }, 0);

        } else {

          console.log(
            "👥 Crew restore mode — tactical sync only"
          );
        }

        // 🟠 AUTO-GENERATE PLANNED ALT (SAFE)
        if (

          flight.alternate?.icao &&

          !flight.alternate?.route &&

          !window.__altGenerating

        ) {

          console.log(
            "🟠 Auto-generating planned alternate on load:",
            flight.alternate.icao
          );

          window.__altGenerating = true;

          generateAlternateRoute(
            flight.alternate.icao
          ).finally(() => {

            window.__altGenerating = false;
          });
        }

      } else {

        console.warn(
          "⚠️ No valid coords in stored flight"
        );
      }

    } catch (err) {

      console.error(
        "❌ Failed to parse activeFlight:",
        err
      );
    }
  };

  // 🔥 Run immediately
  loadFlightFromStorage();

  // 🔥 Listen for updates
  const handleStorageUpdate = () => {

    console.log(
      "🔁 Storage event detected → reloading flight"
    );

    loadFlightFromStorage();
  };

  window.addEventListener(
    "storage",
    handleStorageUpdate
  );

  return () => {

    window.removeEventListener(
      "storage",
      handleStorageUpdate
    );
  };

}, [altRefresh]);



async function handleEndFlight() {

  // =====================================
  // ⚠️ CONFIRM END FLIGHT
  // =====================================

  const confirmed = window.confirm(

    "Are you sure you want to end the flight?\n\nThis will clear:\n• Route\n• Aircraft state\n• Saved charts\n• Mission session\n• Inflight briefing"

  );

  if (!confirmed) {

    console.log(
      "❌ End flight cancelled"
    );

    return;
  }

 // =====================================
// 💾 MARK FLIGHT COMPLETED
// =====================================

try {

  const token =
    localStorage.getItem("token");

  const flight =
    JSON.parse(
      localStorage.getItem("activeFlight")
    );

  if (

    token &&

    flight?.meta?.flightId

  ) {

    const res = await fetch(

      `https://ficon-x.com/auth/end-flight/${flight.meta.flightId}`,

      {

        method: "POST",

        headers: {

          Authorization:
            `Bearer ${token}`

        }

      }

    );

    const data =
      await res.json();

    console.log(
      "✅ Flight completed:",
      data
    );

  }

} catch (err) {

  console.warn(
    "⚠️ Failed to complete flight:",
    err
  );

} 

const map = mapRef.current;

if (!map) return;

console.log(
  "🧹 PROCEDURE LAYERS:",
  map.getStyle()?.layers
    ?.map(l => l.id)
    ?.filter(id =>
      id.includes("arrival") ||
      id.includes("procedure") ||
      id.includes("overlay")
    )
);

console.log(
  "🧹 PROCEDURE SOURCES:",
  Object.keys(
    map.getStyle()?.sources || {}
  ).filter(id =>
    id.includes("arrival") ||
    id.includes("procedure") ||
    id.includes("overlay")
  )
);

  // =====================================
  // 🛑 STOP DEV SIM
  // =====================================

  simHasStartedRef.current = false;

  simIndexRef.current = 0;

  setSimStarted(false);

  

  // =====================================
  // 🔥 REMOVE INFLIGHT BRIEF
  // =====================================

  setShowInflightBrief(false);

  // =====================================
  // 🔥 REMOVE ROUTE LAYERS
  // =====================================

  const style =
    map.getStyle?.();

  const layers =
    style?.layers || [];

  layers.forEach((layer) => {

    if (

      layer.id.includes("route") ||

      layer.id.includes("selected") ||

      layer.id.includes("suggestion")

    ) {

      if (map.getLayer(layer.id)) {

        try {

          map.removeLayer(layer.id);

          console.log(
            `🧹 Removed layer: ${layer.id}`
          );

        } catch (e) {

          console.warn(
            `⚠️ Failed removing layer ${layer.id}`,
            e
          );
        }
      }
    }
  });

  // =====================================
  // 🔥 REMOVE ROUTE SOURCES
  // =====================================

  const sources =
    Object.keys(style?.sources || {});

  sources.forEach((src) => {

    if (

      src.includes("route") ||

      src.includes("selected") ||

      src.includes("suggestion")

    ) {

      if (map.getSource(src)) {

        try {

          map.removeSource(src);

          console.log(
            `🧹 Removed source: ${src}`
          );

        } catch (e) {

          console.warn(
            `⚠️ Failed removing source ${src}`,
            e
          );
        }
      }
    }
  });

  // =====================================
// 🛬 REMOVE PROCEDURE OVERLAYS
// =====================================

[
  "procedure-overlay",
  "procedure-connectors",
  "procedure-labels",
  "procedure-markers",
  "arrival-preview-line"
].forEach((layerId) => {

  if (map.getLayer(layerId)) {

    try {

      map.removeLayer(layerId);

      console.log(
        `🧹 Removed procedure layer: ${layerId}`
      );

    } catch (e) {

      console.warn(
        `⚠️ Failed removing procedure layer ${layerId}`,
        e
      );
    }
  }
});

[
  "procedure-overlay",
  "procedure-connectors",
  "procedure-labels",
  "procedure-markers",
  "arrival-preview-line"
].forEach((sourceId) => {

  if (map.getSource(sourceId)) {

    try {

      map.removeSource(sourceId);

      console.log(
        `🧹 Removed procedure source: ${sourceId}`
      );

    } catch (e) {

      console.warn(
        `⚠️ Failed removing procedure source ${sourceId}`,
        e
      );
    }
  }
});

  // =====================================
  // 🟣 REMOVE ACTIVE LEG
  // =====================================

  [
    "active-leg-main",
    "active-leg-glow",
    "active-leg"
  ].forEach((layerId) => {

    if (map.getLayer(layerId)) {

      try {

        map.removeLayer(layerId);

        console.log(
          `🧹 Removed layer: ${layerId}`
        );

      } catch (e) {

        console.warn(
          `⚠️ Failed removing ${layerId}`,
          e
        );
      }
    }
  });

  // 🔥 REMOVE ACTIVE LEG SOURCE
  if (map.getSource("active-leg")) {

    try {

      map.removeSource("active-leg");

      console.log(
        "🧹 Removed source: active-leg"
      );

    } catch (e) {

      console.warn(
        "⚠️ Failed removing source active-leg",
        e
      );
    }
  }

// =====================================
// 🛬 REMOVE PROCEDURE OVERLAY
// =====================================

[
  "procedure-overlay-line",
  "procedure-overlay-markers",
  "procedure-overlay-labels"
].forEach((layerId) => {

  if (map.getLayer(layerId)) {

    try {

      map.removeLayer(layerId);

      console.log(
        `🧹 Removed layer: ${layerId}`
      );

    } catch (e) {

      console.warn(
        `⚠️ Failed removing ${layerId}`,
        e
      );
    }
  }
});

if (
  map.getSource(
    "procedure-overlay"
  )
) {

  try {

    map.removeSource(
      "procedure-overlay"
    );

    console.log(
      "🧹 Removed source: procedure-overlay"
    );

  } catch (e) {

    console.warn(
      "⚠️ Failed removing procedure-overlay",
      e
    );
  }
}
  // =====================================
  // 🟠 REMOVE ALTERNATE ROUTE
  // =====================================

  removeAlternateRoute();

  // =====================================
  // ✈️ REMOVE PRIMARY AIRCRAFT
  // =====================================

  if (aircraftMarkerRef.current) {

    try {

      aircraftMarkerRef.current.remove();

    } catch {}

    aircraftMarkerRef.current = null;
  }

  // =====================================
  // ✈️ REMOVE ALL AIRCRAFT MARKERS
  // =====================================

  if (aircraftMarkerMap?.current) {

    Object.values(
      aircraftMarkerMap.current
    ).forEach((marker) => {

      if (
        marker &&
        typeof marker.remove === "function"
      ) {

        try {

          marker.remove();

        } catch {}
      }
    });

    aircraftMarkerMap.current = {};
  }

  // =====================================
  // 🧠 CLEAR MAP CACHE
  // =====================================

  map._lastAltCoords = null;

  // =====================================
  // 🔥 RESET FLIGHT STATE
  // =====================================

  setLocalFlightActive(false);

  if (
    typeof setIsFlightActive === "function"
  ) {

    setIsFlightActive(false);
  }

  if (
    typeof setActiveRoute === "function"
  ) {

    setActiveRoute(null);
  }

  setProcedureConfig({

  departure: {},

  arrival: {}

  

});

setPreviousRouteState(null);

setRouteTokens([]);

setRouteConfirmed(false);

setConfirmedRouteString("");

setHazardData(null);

setSelectedSid("");

setSelectedSidRunway("");

setSelectedSidTransition("");

setSelectedArrivalToken(null);

setActiveArrivalConfig(null);

setAvailableIAFs([]);

setSelectedArrivalIAF("");

setSelectedApproach("");

setSelectedArrivalRunway("");

setSelectedStar("");

setSelectedStarTransition("");

  if (
    typeof setActiveFlight === "function"
  ) {

    setActiveFlight(null);
  }

  if (
    typeof setAlternateRoute === "function"
  ) {

    setAlternateRoute(null);
  }

  if (
    typeof setCurrentLegIndex === "function"
  ) {

    setCurrentLegIndex(0);
  }

  if (
    typeof setSharedActiveLegIndex === "function"
  ) {

    setSharedActiveLegIndex(0);
  }

  if (
    typeof setSharedAircraftState === "function"
  ) {

    setSharedAircraftState(null);
  }

  if (
    typeof setAircraftPosition === "function"
  ) {

    setAircraftPosition(null);
  }

  // =====================================
  // 🔥 CLEAR UI INPUT STATE
  // =====================================

  if (
    typeof setRouteInput === "function"
  ) {

    setRouteInput("");
  }

  if (
    typeof setAlternateInput === "function"
  ) {

    setAlternateInput("");
  }

  // =====================================
  // 🔥 CLEAR ALTERNATE FROM STORAGE
  // =====================================

  try {

    const raw =
      localStorage.getItem(
        "activeFlight"
      );

    if (raw) {

      const flight =
        JSON.parse(raw);

      if (flight.alternate) {

        delete flight.alternate;

        localStorage.setItem(
          "activeFlight",
          JSON.stringify(flight)
        );
      }
    }

  } catch (e) {

    console.warn(
      "⚠️ Failed to clear alternate from storage"
    );
  }

  // =====================================
  // 🔥 CLEAR SESSION STATE
  // =====================================

  sessionStorage.removeItem(
    "flightSessionActive"
  );

  sessionStorage.removeItem(
    "missionRole"
  );

  // =====================================
  // 🔥 CLEAR LOCAL STORAGE
  // =====================================

  localStorage.removeItem(
    "activeFlight"
  );

  localStorage.removeItem(
    "activeMissionId"
  );

  localStorage.removeItem(
    "activeMission"
  );

  localStorage.removeItem(
    "savedCharts"
  );

  localStorage.removeItem(
  "previousRouteState"
);

  // =====================================
  // 🌐 CLEAR GLOBALS
  // =====================================

  window.__activeMissionId =
    null;

  window.__missionRole =
    null;

  // =====================================
  // 🔥 CLEAR CHARTS
  // =====================================

  setSavedCharts([]);

  setShowChartsPanel(false);

  // =====================================
  // 🔍 CLOSE CHART BROWSER
  // =====================================

  if (
    typeof setShowChartBrowser === "function"
  ) {

    setShowChartBrowser(false);
  }

  // =====================================
  // 📄 CLEAR SEARCH RESULTS
  // =====================================

  if (
    typeof setChartResults === "function"
  ) {

    setChartResults([]);
  }


  // =====================================
  // 🔎 CLEAR SEARCH FIELD
  // =====================================

  if (
    typeof setChartSearch === "function"
  ) {

    setChartSearch("");
  }

  console.log(
    "✅ Flight fully cleared"
  );
}


const plotAlternateRoute = (route) => {
  const map = mapRef.current;
  if (!map || !route?.coords || route.coords.length < 2) {
    console.warn("⚠️ Invalid alternate route coords");
    return;
  }

  const sourceId = "alternate-route-src";
  const layerId = "alternate-route-line";

  try {
    // 🧹 HARD RESET (always remove first)

    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }

    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    // 🔥 REMOVE ALL EXISTING ALT MARKERS (not just one)
    if (alternateMarkersRef.current.length > 0) {
      alternateMarkersRef.current.forEach(marker => {
        try { marker.remove(); } catch {}
      });
      alternateMarkersRef.current = [];
    }

  } catch (err) {
    console.warn("⚠️ Cleanup error (safe to ignore):", err);
  }

  // 🧠 Prevent duplicate plotting
  if (map._lastAltCoords) {
    const same =
      JSON.stringify(map._lastAltCoords) === JSON.stringify(route.coords);

    if (same) {
      console.log("⚠️ Skipping duplicate ALT plot");
      return;
    }
  }

  // 💾 Store last plotted route
  map._lastAltCoords = route.coords;

  // 📡 Add source
  map.addSource(sourceId, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: route.coords,
      },
    },
  });

  // 🟠 Add dashed line
  map.addLayer({
    id: layerId,
    type: "line",
    source: sourceId,
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#FFA500",
      "line-width": 3,
      "line-dasharray": [2, 2],
      "line-opacity": 0.9,
    },
  });

  // 🟠 ADD MARKER AT ALTERNATE AIRPORT (END POINT)
  const lastCoord = route.coords.at(-1);

  const marker = new mapboxgl.Marker({ color: "#FFA500" })
    .setLngLat(lastCoord)
    .addTo(map);

  // 💾 STORE MARKER (ARRAY VERSION — CRITICAL FIX)
  alternateMarkersRef.current.push(marker);

  console.log("🟠 Alternate plotted:", route.coords[0], "→", lastCoord);
};
const removeAlternateRoute = () => {
  const map = mapRef.current;
  if (!map) return;

  if (map.getLayer("alternate-route-line")) {
    try { map.removeLayer("alternate-route-line"); } catch {}
  }

  if (map.getSource("alternate-route-src")) {
    try { map.removeSource("alternate-route-src"); } catch {}
  }

  // 🔥 REMOVE ALL ALT MARKERS
  alternateMarkersRef.current.forEach(marker => {
    try { marker.remove(); } catch {}
  });
  alternateMarkersRef.current = [];

  // 🔥 RESET CACHE
  map._lastAltCoords = null;

  console.log("🧹 Alternate fully removed");
};
const toggleAlternate = () => {

  const map =
    mapRef.current;

  if (!map) return;

  const raw =
    localStorage.getItem(
      "activeFlight"
    );

  if (!raw) return;

  const flight =
    JSON.parse(raw);

  if (
    !flight.alternate?.route?.coords?.length
  ) {

    console.warn(
      "⚠️ No alternate route to toggle"
    );

    return;
  }

  // =====================================
  // 🟠 USE ACTUAL MAP STATE
  //
  // Do NOT trust stored alternate.visible.
  // The Mapbox layer is the truth.
  // =====================================

  const alternateIsDisplayed =
    !!map.getLayer(
      "alternate-route-line"
    );

  // =====================================
  // 🧹 ALREADY DISPLAYED → HIDE
  // =====================================

  if (alternateIsDisplayed) {

    removeAlternateRoute();

    flight.alternate.visible =
      false;

    localStorage.setItem(
      "activeFlight",
      JSON.stringify(flight)
    );

    return;
  }

  // =====================================
  // 🟠 NOT DISPLAYED → SHOW
  //
  // CURRENT AIRCRAFT → ALTERNATE AIRPORT
  // =====================================

  const livePosition =

    aircraftPosition ||

    sharedAircraftState?.position ||

    flight.aircraft?.position;

  const alternateCoord =
    flight.alternate.route.coords.at(-1);

  if (
    !livePosition ||
    livePosition.length !== 2 ||
    !alternateCoord ||
    alternateCoord.length !== 2
  ) {

    console.warn(
      "⚠️ Cannot plot live direct-to alternate",
      {
        livePosition,
        alternateCoord
      }
    );

    return;
  }

  const directRoute = {

    ...flight.alternate.route,

    coords: [

      [...livePosition],

      [...alternateCoord]

    ]

  };

  console.log(
    "🟠 LIVE DIRECT TO ALTERNATE:",
    livePosition,
    "→",
    alternateCoord
  );

  plotAlternateRoute(
    directRoute
  );

  flight.alternate.visible =
    true;

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(flight)
  );

};


const plotRoute = (routeData) => {
  console.log("📍 plotRoute() triggered!");
  console.log("ROUTE TEXT:", routeData.routeText);


  
console.log(
  "🚨 PLOT ROUTE CALLED"
);

console.trace(
  "🚨 PLOT ROUTE STACK"
);

console.log(
  "🚨 ROUTE DATA:",
  routeData
);

console.log(
  "🚨 COORD COUNT:",
  routeData?.coords?.length
);

console.log(
  "🚨 COORDOBJECTS ROOT:",
  routeData?.coordObjects
);

console.log(
  "🚨 COORDOBJECTS ROUTE:",
  routeData?.route?.coordObjects
);

console.log(
  "🚨 WAYPOINT COUNT:",
  routeData?.waypoints?.length
);

console.log(
  "🚨 ROUTE TEXT:",
  routeData?.routeText
);
console.log(
  routeData?.routeText
);

  const map = mapRef.current;
  if (!routeData || !map) {
    console.warn("⚠️ No route data or map instance.");
    return;
  }

[
  "active-leg-main",
  "active-leg-glow",
  "active-leg"
].forEach((layerId) => {

  if (map.getLayer(layerId)) {

    try {

      map.removeLayer(layerId);

      console.log(
        "🧹 REMOVED",
        layerId
      );

    } catch {}
  }
});

if (map.getSource("active-leg")) {

  try {

    map.removeSource("active-leg");

    console.log(
      "🧹 REMOVED ACTIVE LEG SOURCE"
    );

  } catch {}
}

  // 🧹 CLEAR PREVIOUS ROUTE LAYERS / SOURCES
  try {
    const style = map.getStyle();
    if (!style) return;

    style.layers?.forEach((layer) => {
      if (
        layer.id.startsWith("route-") ||
        layer.id.startsWith("suggested-route-") ||
        layer.id === "selected-route" 
        
      ) {
        if (map.getLayer(layer.id)) {
          try { map.removeLayer(layer.id); } catch {}
        }
      }
    });

    Object.keys(style.sources || {}).forEach((sourceId) => {
      if (
        sourceId.startsWith("route-") ||
        sourceId.startsWith("suggested-route-") ||
        sourceId === "selected-route" 
        
      ) {
        if (map.getSource(sourceId)) {
          try { map.removeSource(sourceId); } catch {}
        }
      }
    });

  if (map.getLayer("route-fix-labels")) {
    map.removeLayer("route-fix-labels");
  }

  if (map.getSource("route-fix-labels")) {
    map.removeSource("route-fix-labels");
  }

  console.log("🧹 Removed fix labels");
} catch (e) {
  console.warn("⚠️ Failed to remove fix labels:", e);
}

// 🧹 THEN CLEAR ROUTE LAYERS
try {
  const style = map.getStyle();
  if (!style) return;

  style.layers?.forEach((layer) => {
    if (
      layer.id.startsWith("route-") ||
      layer.id.startsWith("suggested-route-") ||
      layer.id === "selected-route" 
      
    ) {
      if (map.getLayer(layer.id)) {
        try { map.removeLayer(layer.id); } catch {}
      }
    }
  });

  Object.keys(style.sources || {}).forEach((sourceId) => {
    if (
      sourceId.startsWith("route-") ||
      sourceId.startsWith("suggested-route-") ||
      sourceId === "selected-route" 
      
    ) {
      if (map.getSource(sourceId)) {
        try { map.removeSource(sourceId); } catch {}
      }
    }
  });

  console.log("🧹 Cleared route layers/sources");
} catch (e) {
  console.warn("⚠️ Cleanup failed:", e);
}

  const { origin, destination, waypoints = [] } = routeData;

  if (!origin || !destination) {
    console.error("❌ Missing origin or destination.");
    return;
  }

  const originCoordinates = [origin[0], origin[1]];
  const destinationCoordinates = [destination[0], destination[1]];

  if (
    !isValidCoordinates(originCoordinates) ||
    !isValidCoordinates(destinationCoordinates)
  ) {
    console.error("❌ Invalid origin/destination coordinates.");
    return;
  }

  // 🚀 Use FMS coords if they exist (Direct-To safe)
// =====================================
// ✈️ TACTICAL ROUTE PRIORITY
// =====================================

let coordinates = [];

// =====================================
// ✈️ USE PROVIDED COORDS FIRST
// =====================================

if (
  routeData?.coords &&
  routeData.coords.length > 1
) {

  console.log(
    "🚀 USING PROVIDED TACTICAL COORDS"
  );

  coordinates =
    routeData.coords.map((c) => {

      if (Array.isArray(c)) {
        return c;
      }

      if (
        c &&
        typeof c === "object" &&
        c.lat !== undefined &&
        c.lon !== undefined
      ) {

        return [
          c.lon,
          c.lat
        ];
      }

      return null;

    }).filter(Boolean);

}

// =====================================
// ✈️ FALLBACK TO route.coords
// =====================================

else if (
  routeData.route?.coords?.length
) {

  console.log(
    "⚠️ Falling back to route.coords"
  );

  coordinates =
    routeData.route.coords.map((c) => {

      if (Array.isArray(c)) {
        return c;
      }

      if (
        c &&
        typeof c === "object" &&
        c.lat !== undefined &&
        c.lon !== undefined
      ) {

        return [
          c.lon,
          c.lat
        ];
      }

      return null;

    }).filter(Boolean);

}

// =====================================
// ✈️ FINAL FALLBACK
// =====================================

else {

  console.log(
    "⚠️ Falling back to waypoint reconstruction"
  );

  const validWaypoints =
    waypoints.filter(

      (wp) =>

        wp?.coordinates &&
        isValidCoordinates(
          wp.coordinates
        )
    );

  coordinates = [

    originCoordinates,

    ...validWaypoints.map(
      (wp) => wp.coordinates
    ),

    destinationCoordinates,
  ];
}

// =====================================
// ✈️ FINAL DEBUG
// =====================================

console.log(
  "🗺️ Coordinates to plot:",
  coordinates
);

console.log(
  "🚨 WAYPOINT IDS:",
  waypoints.map(w => w.id)
);

console.log(
  "🚨 COORDOBJECT IDS:",
  (routeData.coordObjects || []).map(
    o => o.name
  )
);


console.log(
  "🚨 FINAL COORDINATES TO DRAW"
);

coordinates.forEach((c, i) => {

  console.log(
    i,
    c
  );

});

  // 🔵 FULL ROUTE (Blue)
  try {
    map.addSource("selected-route", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates,
        },
      },
    });

    map.addLayer({
      id: "selected-route",
      type: "line",
      source: "selected-route",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "#0078FF",
        "line-width": 4,
        "line-opacity": 0.85,
      },
    });

    // =====================================
// 🧲 ROUTE SEGMENT TEST
// =====================================

if (
  map._routeSegmentHandler
) {

  map.off(
    "click",
    "selected-route",
    map._routeSegmentHandler
  );

}

const routeSegmentHandler = (e) => {

  console.log(
    "🧲 ROUTE CLICKED"
  );

  const clickPoint = [

    e.lngLat.lng,

    e.lngLat.lat

  ];

  const coords =

    activeFlight
      ?.route
      ?.coords || [];

  console.log(
    "🧲 COORD COUNT",
    coords.length
  );

  console.log(
    "🧲 ROUTE FIXES",
    activeFlight
      ?.routeStructure
      ?.activeTokens
  );

  if (
    coords.length < 2
  ) {

    console.warn(
      "⚠️ NO ROUTE COORDS"
    );

    return;
  }

  const routeFixes =

    activeFlight
      ?.routeStructure
      ?.activeTokens || [];

  let closestSegment =
    null;

  let closestDistance =
    Infinity;

  for (
    let i = 0;
    i < coords.length - 1;
    i++
  ) {

    const a = coords[i];

    const b = coords[i + 1];

    const midLon =
      (a[0] + b[0]) / 2;

    const midLat =
      (a[1] + b[1]) / 2;

    const dx =
      clickPoint[0] -
      midLon;

    const dy =
      clickPoint[1] -
      midLat;

    const dist =
      Math.sqrt(
        dx * dx +
        dy * dy
      );

    if (
      dist <
      closestDistance
    ) {

      closestDistance =
        dist;

      closestSegment = {

        index: i,

        start:
          routeFixes[i],

        end:
          routeFixes[i + 1],

        distance:
          dist

      };
    }
  }

  console.log(
    "🧲 SEGMENT SELECTED",
    closestSegment
  );

  setSelectedSegment(
    closestSegment
  );

setDragSegment(
  closestSegment
);

dragSegmentRef.current =
  closestSegment;


  setShowSegmentEditor(
  true
);

  // =====================================
  // 🧲 SEGMENT HIGHLIGHT
  // =====================================

  const segmentCoords = [

    coords[
      closestSegment.index
    ],

    coords[
      closestSegment.index + 1
    ]

  ];

  if (
    map.getLayer(
      "selected-segment"
    )
  ) {

    try {

      map.removeLayer(
        "selected-segment"
      );

    } catch {}

  }

  if (
    map.getSource(
      "selected-segment"
    )
  ) {

    try {

      map.removeSource(
        "selected-segment"
      );

    } catch {}

  }

  map.addSource(
    "selected-segment",
    {

      type: "geojson",

      data: {

        type: "Feature",

        geometry: {

          type: "LineString",

          coordinates:
            segmentCoords

        }

      }

    }
  );

  map.addLayer({

    id:
      "selected-segment",

    type:
      "line",

    source:
      "selected-segment",

    paint: {

      "line-color":
        "#00FFFF",

      "line-width":
        8,

      "line-opacity":
        0.9

    }

  });

  console.log(
    "🧲 HIGHLIGHT SEGMENT",
    closestSegment.start,
    "→",
    closestSegment.end
  );

};

map._routeSegmentHandler =
  routeSegmentHandler;
  
map.on(
  "click",
  "selected-route",
  routeSegmentHandler
);

// =====================================
// 🧲 RUBBER BAND LONG PRESS
// =====================================

const routeMouseDownHandler =
  (e) => {

    console.log(
      "🧲 HOLD START"
    );

    console.count(
      "🧲 HOLD START COUNT"
    );

    holdTimerRef.current =
      setTimeout(() => {

        console.log(
          "🧲 RUBBER BAND ACTIVE"
        );

        setRubberBandMode(
          true
        );

        rubberBandModeRef.current =
          true;

        map.dragPan.disable();

      }, 500);

  };
  
const routeMouseUpHandler =
  () => {

    if (
      holdTimerRef.current
    ) {

      clearTimeout(
        holdTimerRef.current
      );

      holdTimerRef.current =
        null;

    }

    // =====================================
    // 🧲 RUBBER BAND RELEASE
    // =====================================

if (
  rubberBandModeRef.current &&
  rubberBandCoordsRef.current
) {

  const selectedSegment =
    dragSegmentRef.current;

  const [lon, lat] =
    rubberBandCoordsRef.current;

  

      console.log(
        "🧲 RELEASE",
        lon,
        lat
      );

      fetch(
        "https://ficon-x.com/route/nearest_navpoint",
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json"
          },

          body: JSON.stringify({
            lat,
            lon
          })
        }
      )
        .then(r => r.json())

.then(data => {

  console.log(
    "🧲 NEAREST NAVPOINT",
    data
  );

  console.log(
    "🧲 INSERT CALL",
    {
      token:
        selectedSegment?.start,

      index:
        selectedSegment?.index,

      fix:
        data.ident
    }
  );



insertFixAfter(

  selectedSegment?.start,

  selectedSegment?.index,

  data.ident

);

})

.catch(console.error);

    }

    // =====================================
    // 🧹 EXIT RUBBER BAND MODE
    // =====================================

    rubberBandModeRef.current =
      false;

    setRubberBandMode(
      false
    );

    map.dragPan.enable();

  };

if (
  map._routeMouseDownHandler
) {

  map.off(
    "mousedown",
    "selected-route",
    map._routeMouseDownHandler
  );

}

map._routeMouseDownHandler =
  routeMouseDownHandler;

map.on(
  "mousedown",
  "selected-route",
  routeMouseDownHandler
);

if (
  map._routeMouseUpHandler
) {

  map.off(
    "mouseup",
    map._routeMouseUpHandler
  );

}

map._routeMouseUpHandler =
  routeMouseUpHandler;

map.on(
  "mouseup",
  routeMouseUpHandler
);

const routeDragHandler =
  (e) => {

console.log(
  "🟢 RUBBER MODE",
  rubberBandModeRef.current
);

console.log(
  "🟢 DRAG SEGMENT",
  dragSegmentRef.current
);

if (
  !rubberBandModeRef.current
) {
  return;
}

if (
  !dragSegmentRef.current
) {
  return;
}

    const coords =

      activeFlight
        ?.route
        ?.coords || [];

const startCoord =

  coords[
    dragSegmentRef.current.index
  ];

if (
  !startCoord
) {
  return;
}

const endCoord =

  coords[
    dragSegmentRef.current
      .index + 1
  ];

if (
  !endCoord
) {
  return;
}

const warpedCoords = [

  startCoord,

  [

    e.lngLat.lng,

    e.lngLat.lat

  ],

  endCoord

];

rubberBandCoordsRef.current = [

  e.lngLat.lng,

  e.lngLat.lat

];

console.log(
  "🟢 WARPED SEGMENT",
  warpedCoords
);

// =====================================
// UPDATE SELECTED SEGMENT
// =====================================

if (
  map.getSource(
    "selected-segment"
  )
) {

  map.getSource(
    "selected-segment"
  ).setData({

    type:
      "Feature",

    geometry: {

      type:
        "LineString",

      coordinates:
        warpedCoords

    }

  });

}



  };

if (
  map._routeDragHandler
) {

  map.off(
    "mousemove",
    map._routeDragHandler
  );

}

map._routeDragHandler =
  routeDragHandler;

map.on(
  "mousemove",
  routeDragHandler
);

    console.log(
  "🚨 SELECTED ROUTE SOURCE DATA",
  map.getSource("selected-route")
);

    // 🔥 FORCE ROUTE TO TOP OF MAPBOX STACK
if (map.getLayer("selected-route")) {
  map.moveLayer("selected-route");
}

    console.log("✅ Route plotted.");
  } catch (e) {
    console.error("❌ Route plotting failed:", e);
  }
  setTimeout(() => {
  bringRouteLayersToFront();
}, 200);

// 🔥 SYNC WAYPOINTS WITH COORDS (FINAL — CORRECT PRIORITY)
const routeFixes = routeData.routeText
  ? routeData.routeText.split(" ").filter(Boolean)
  : (routeData.meta?.routeTextCurrent || "").split(" ").filter(Boolean);

  console.log(
  "🚨 ROUTEDATA WAYPOINTS RAW",
  routeData?.waypoints
);

console.log(
  "🚨 ROUTEDATA ROUTE WAYPOINTS",
  routeData?.route?.waypoints
);

const baseWaypoints =
  Array.isArray(routeData?.waypoints) &&
  routeData.waypoints.length > 0
    ? routeData.waypoints
    : (routeData?.route?.waypoints || []);


    console.log(
  "🚨 BASE WAYPOINT IDS",
  baseWaypoints.map(w => w.id)
);

// 🔥 Build fresh every time (NO mutation)
// 🔥 BUILD CLEAN OPERATIONAL WAYPOINTS
const syncedWaypoints = coordinates
  .map((coord, i) => {

    if (
      !coord ||
      !isValidCoordinates(coord)
    ) {
      return null;
    }

    // 🔍 Match real operational waypoint
const match = baseWaypoints.find((wp) => {

  let wpCoord = null;

  // =====================================
  // ✈️ coordinate ARRAY
  // =====================================

  if (
    Array.isArray(wp.coordinates) &&
    wp.coordinates.length === 2
  ) {

    wpCoord = wp.coordinates;
  }

  // =====================================
  // ✈️ lat/lon OBJECT
  // =====================================

  else if (

    wp.lon !== undefined &&
    wp.lat !== undefined

  ) {

    wpCoord = [
      wp.lon,
      wp.lat
    ];
  }

  if (!wpCoord)
    return false;

const dist = getDistanceNm(
  coord,
  wpCoord
);

if (dist < 5) {

  console.log(
    "🚨 CLOSE MATCH",
    wp.id,
    dist,
    coord,
    wpCoord
  );

}

return dist < 0.1;
});

// =====================================
// ✈️ AUTHORITATIVE LABEL
// =====================================

const finalId =

  match?.name ||

  match?.id ||

  null;

const altitudeConstraint =
  match?.altitude_constraint;


// =====================================
// 🧪 SID DEBUG
// =====================================

if (
  finalId === "KIWIE" ||
  finalId === "VOBOZ"
) {

  console.log(
    "🚨 SID MATCH",
    match
  );

}


// =====================================
// ✈️ BUILD ALTITUDE RESTRICTION LABEL
// =====================================

let restrictionText = "";

if (
  altitudeConstraint &&
  altitudeConstraint.altitude != null
) {

  const alt =
    altitudeConstraint.altitude;

  const type =
    altitudeConstraint.type;


  // =====================================
  // ✈️ NORMALIZE ALTITUDE
  //
  // Preserve flight levels:
  // FL290 -> FL290
  //
  // Preserve normal altitudes:
  // 14000 -> 14000
  // =====================================

  const formattedAlt = (() => {

    if (alt == null) {
      return null;
    }

    const value =
      String(alt)
        .trim()
        .toUpperCase();

    // Flight level
    if (/^FL\d+$/.test(value)) {

      return value;

    }

    // Normal numeric altitude
    const numeric =
      parseInt(
        value,
        10
      );

    if (
      Number.isNaN(numeric)
    ) {

      return null;

    }

    return String(numeric);

  })();


  // =====================================
  // ✈️ FORMAT RESTRICTION TYPE
  // =====================================

  if (
    formattedAlt !== null
  ) {

    if (
      type === "at_or_above"
    ) {

      restrictionText =
        `≥${formattedAlt}`;

    }

    else if (
      type === "at_or_below"
    ) {

      restrictionText =
        `≤${formattedAlt}`;

    }

    else if (
      type === "at"
    ) {

      restrictionText =
        `${formattedAlt}`;

    }

  }


  console.log(
    "🚨 RESTRICTION BUILT",
    finalId,
    restrictionText
  );

}
if (
  match?.id === "ALNZO"
) {

  console.log(
    "🚨 ALNZO MATCH:",
    match
  );

}
    // 🚫 Skip unlabeled endpoints
    if (
      !finalId &&
      (
        i === 0 ||
        i === coordinates.length - 1
      )
    ) {
      return null;
    }

  console.log(
  "🚨 SYNC MATCH",
  i,
  finalId,
  match?.name,
  match?.id
);  

return {

  id: finalId,

  coordinates: coord,

  altitudeConstraint:
    altitudeConstraint
};

  })

  .filter(Boolean);
// 🏷️ FIX LABELS (FINAL — CLEAN, SINGLE SOURCE)
try {
  const validWaypoints = syncedWaypoints.filter(
    (wp) =>
      wp?.coordinates &&
      isValidCoordinates(wp.coordinates)
  );

  const restrictionFeatures = [];

  const routeFixes = routeData.routeText
    ? routeData.routeText.split(" ").filter(Boolean)
    : [];

  const usedNames = new Set();

  const coordLookup = new Map();

  console.log(
  "🚨 COORDOBJECTS ROUTE:",
  routeData?.route?.coordObjects
);

console.log(
  "🚨 PLOT ROUTE DATA:",
  routeData
);

console.log(
  "🚨 PLOT ROUTE KEYS:",
  Object.keys(routeData || {})
);

(routeData.coordObjects || []).forEach((obj) => {

  coordLookup.set(
    (obj.name || "").toUpperCase(),
    obj
  );

});

console.log(
  "🚨 ROUTEDATA COORDOBJECTS",
  routeData.coordObjects
);

  const features = validWaypoints.map((wp, i) => {
let label = wp.id || null;

    const isPlaceholder =
      !label ||
      label.startsWith("FIX") ||
      label.startsWith("WP");

    // 🔥 ONLY replace placeholders (FIX/WP) using routeText
    if (isPlaceholder && routeFixes.length) {

      // 🔥 CRITICAL: never assign placeholder to origin
      if (i === 0) {
        label = null;
      } else {
        const nextFix = routeFixes.find((f) => !usedNames.has(f));

        if (nextFix) {
          label = nextFix;
          usedNames.add(nextFix);
        } else {
          label = `WP${i}`;
        }
      }

    } else if (label) {
      usedNames.add(label);
    }

    // 🔥 FINAL SAFETY: eliminate FIX0 if it slipped through
    if (typeof label === "string" && label.toUpperCase() === "FIX0") {
      label = "FIX1";
    }

let restrictionText = "";

if (
  wp.altitudeConstraint &&
  wp.altitudeConstraint.altitude != null
) {

  const alt =
    wp.altitudeConstraint.altitude;

  const type =
    wp.altitudeConstraint.type;

  // =====================================
  // ✈️ FORMAT ALTITUDE
  //
  // FL290 stays FL290
  // FL240 stays FL240
  // 14000 stays 14000
  // =====================================

  const formattedAlt = (() => {

    const value =
      String(alt)
        .trim()
        .toUpperCase();

    // Preserve flight levels
    if (/^FL\d+$/.test(value)) {

      return value;

    }

    // Normal numeric altitude
    const numeric =
      parseInt(
        value,
        10
      );

    if (
      Number.isNaN(numeric)
    ) {

      return null;

    }

    return String(numeric);

  })();


  // =====================================
  // ✈️ BUILD DISPLAY RESTRICTION
  // =====================================

  if (
    formattedAlt !== null
  ) {

    if (
      type === "at_or_above"
    ) {

      restrictionText =
        `${formattedAlt}+`;

    }

    else if (
      type === "at_or_below"
    ) {

      restrictionText =
        `${formattedAlt}-`;

    }

    else if (
      type === "at"
    ) {

      restrictionText =
        `${formattedAlt}`;

    }

  }

}

console.log(
  "🚨 FEATURE",
  label,
  restrictionText,
  restrictionText
    ? `${(label || "").toUpperCase()}\n${restrictionText}`
    : (label || "").toUpperCase()
);

if (restrictionText) {

  restrictionFeatures.push({

    

    type: "Feature",

    properties: {

      label:
        restrictionText,

      color:
        "#00FFFF"
    },

    geometry: {

      type: "Point",

      coordinates:
        wp.coordinates
    }
  });

  console.log(
    "🚨 PUSHING RESTRICTION",
    label,
    restrictionText,
    wp.coordinates
);

}


return {

  type: "Feature",

  geometry: {
    type: "Point",
    coordinates: wp.coordinates,
  },

  properties: {

    title:
      (label || "").toUpperCase(),

displayText:
  (label || "").toUpperCase()
  }

    };
  });

  console.log("WAYPOINT COUNT (SYNCED):", validWaypoints.length);

  if (features.length > 0) {
    // 🧹 Clean existing layer/source (prevents duplication bugs)
    if (map.getLayer("route-fix-points")) {
  try { map.removeLayer("route-fix-points"); } catch {}
}
    if (map.getLayer("route-fix-labels")) {
      try { map.removeLayer("route-fix-labels"); } catch {}
    }
    if (map.getSource("route-fix-labels")) {
      try { map.removeSource("route-fix-labels"); } catch {}
    }

// =====================================
// 🧹 REMOVE LABEL LAYER FIRST
// =====================================

if (
  map.getLayer(
    "route-fix-labels-layer"
  )
) {

  try {

    map.removeLayer(
      "route-fix-labels-layer"
    );

  } catch (e) {

    console.warn(e);
  }
}

// =====================================
// 🧹 REMOVE SOURCE
// =====================================

if (
  map.getSource(
    "route-fix-labels"
  )
) {

  try {

    map.removeSource(
      "route-fix-labels"
    );

  } catch (e) {

    console.warn(e);
  }
}

// =====================================
// 🆕 ADD SOURCE
// =====================================

map.addSource(
  "route-fix-labels",
  {
    type: "geojson",
    data: {
      type: "FeatureCollection",

      features: [

        ...features,

        ...restrictionFeatures

      ]
    }
  }
);

    // =====================================
// 🔴 FIX MARKERS
// =====================================

map.addLayer({

  id: "route-fix-points",

  type: "symbol",

  source: "route-fix-labels",

  layout: {

    "text-field": "◆",

    "text-size": 12,

    "text-allow-overlap": true,

    "text-ignore-placement": true,
  },

  paint: {

    "text-color": "#00E5FF",

    "text-halo-color": "#000000",

    "text-halo-width": 1.5,
  },
});

    map.addLayer({
      id: "route-fix-labels",
      type: "symbol",
      source: "route-fix-labels",
      layout: {
        "text-field": ["get", "displayText"],
        "text-size": 14,
        "text-offset": [0, 1.2],
        "text-anchor": "top",
        "text-allow-overlap": true,
      },
      paint: {
        "text-color": "#FF0000",
        "text-halo-color": "#ffffff",
        "text-halo-width": 1,
      },
    });
    if (map.getLayer("route-fix-labels")) {
  map.moveLayer("route-fix-labels");
}

if (
  map.getLayer(
    "route-fix-restrictions"
  )
) {

  try {

    map.removeLayer(
      "route-fix-restrictions"
    );

  } catch (e) {

    console.warn(e);
  }
}

map.addLayer({

  id: "route-fix-restrictions",

  type: "symbol",

  source: "route-fix-labels",

  filter: [
    "has",
    "label"
  ],

  layout: {

    "text-field":
      ["get", "label"],

    "text-size":
      14,

    "text-offset":
      [0, 3],

    "text-anchor":
      "top",

    "text-font": [
      "Open Sans Bold",
      "Arial Unicode MS Bold"
    ],

    "text-allow-overlap":
      true,

    "text-ignore-placement":
      true
  },

  paint: {

    "text-color": [
      "get",
      "color"
    ],

    "text-halo-color":
      "#000000",

    "text-halo-width":
      3,

    "text-halo-blur":
      1
  }
});

    // 🧹 Remove old click handler
    if (map._directToHandler) {
      map.off("click", "route-fix-labels", map._directToHandler);
    }

const clickHandler = (e) => {

  console.log(
    "🖱 ROUTE LABEL CLICKED"
  );

  console.log(
    "🧪 FEATURE:",
    e.features
  );

  const fixName =
    e.features?.[0]?.properties?.title;

  console.log(
    "🧪 FIX:",
    fixName
  );

  console.log(
    "🧪 FLIGHT ACTIVE:",
    isFlightActiveRef.current
  );

  if (!fixName) {

    console.warn(
      "❌ No fix name"
    );

    return;
  }

  previewDirectTo(fixName);
};

    map._directToHandler = clickHandler;
    map.on("click", "route-fix-labels", clickHandler);
  }
} catch (e) {
  console.warn("⚠️ Fix label plotting error:", e);
}
};

const replotActiveRouteIfExists = async (mapInstance) => {
  const isSharedMission = window.location.pathname.startsWith("/m/");

  // ✅ SOLO MODE CHECK
  if (!isSharedMission) {
    const sessionActive = sessionStorage.getItem("flightSessionActive");
    if (sessionActive !== "true") {
      console.log("🧹 No active solo flight — skipping route replot");
      return;
    }
  }

  const raw = localStorage.getItem("activeFlight");
  if (!raw) {
    console.log("🧹 No activeFlight found in localStorage");
    return;
  }

  let flight;
  try {
    flight = JSON.parse(raw);
  } catch (err) {
    console.error("❌ Failed to parse activeFlight:", err);
    return;
  }

  // ✅ Ensure alternate structure exists (SAFE INIT)
  if (!flight.alternate) {
    flight.alternate = {
      icao: null,
      route: null,
      visible: false
    };
    localStorage.setItem("activeFlight", JSON.stringify(flight));
  }

  // ✅ Ensure stored geometry exists
  if (!flight?.route?.coords || flight.route.coords.length < 2) {
    console.log("⚠️ No stored route geometry found");
    return;
  }

  try {
    const coords = flight.route.coords;

    // 🔵 PRIMARY ROUTE (UNCHANGED)
    plotRoute({
      origin: coords[0],
      destination: coords[coords.length - 1],
      waypoints: flight.route?.waypoints || [],
      routeText: flight.routeText || ""
    });

    console.log("🔁 Route replotted from stored geometry");

    // 🟠 ALTERNATE ROUTE (NEW — SAFE ADD)
    if (flight.alternate?.visible && flight.alternate?.route?.coords) {
      plotAlternateRoute(flight.alternate.route);
      console.log("🟠 Alternate route replotted");
    }

    // ✈️ Restore aircraft marker
    if (flight.aircraft?.position) {
      updateAircraftMarker(
        flight.aircraft.position,
        flight.aircraft.heading || 0
      );
      console.log("✈️ Aircraft restored from session");
    }

  } catch (err) {
    console.error("❌ Failed to replot stored route:", err);
  }
};

function handleSaveChart(chart) {

const alreadyExists =
  savedCharts.some(
    c => c.url === chart.url
  );

  if (alreadyExists) {

    console.log(
      "⚠️ Chart already saved:",
      chart.name
    );

    return;
  }

  const updated = [

    ...savedCharts,

    {

      ...chart,

      id:
        chart.id ||

        Date.now() +
        "_" +
        Math.random()
    }
  ];

  setSavedCharts(updated);

  localStorage.setItem(

    "savedCharts",

    JSON.stringify(updated)
  );

  console.log(
    "💾 Saved chart:",
    chart.name
  );
}


function handleUnsaveChart(chart) {

  const updated =
    savedCharts.filter(
      c => c.id !== chart.id
    );

  setSavedCharts(updated);

  localStorage.setItem(
    "savedCharts",
    JSON.stringify(updated)
  );

  console.log(
    "🗑 Removed chart:",
    chart.name
  );
}

  const fetchAndPlotAirmets = async (map) => {
    try {
      const res = await fetch('https://ficon-x.com/api/proxy/airmets');
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
  
      const features = json.features || [];
      const gAirmetGeojson = { type: "FeatureCollection", features };
  
      if (!map.getSource('airmet-source')) {
        map.addSource('airmet-source', {
          type: 'geojson',
          data: gAirmetGeojson,
        });
  
        map.addLayer({
          id: 'airmet-layer',
          type: 'fill',
          source: 'airmet-source',
          paint: {
            'fill-color': [
              'match',
              ['get', 'hazard'],
              'IFR', '#2c3e50',
              'MT_OBSC', '#7f8c8d',
              'ICE', '#3498db',
              'TURB', '#f39c12',
              'FZLVL', '#e67e22',
              '#95a5a6'
            ],
            'fill-opacity': 0.35,
          },
        });
  
        map.addLayer({
          id: 'airmet-outline',
          type: 'line',
          source: 'airmet-source',
          paint: {
            'line-color': '#333',
            'line-width': 1,
          },
        });
      } else {
        map.getSource('airmet-source').setData(gAirmetGeojson);
      }
     
  
      console.log('✅ G‑AIRMETs plotted');
      bringRouteLayersToFront();
    } catch (err) {
      console.error('❌ Failed to fetch AIRMETs:', err);
    }
  };
  

  const fetchAndPlotSigmets = async (map) => {
    const SIGMET_URL = 'https://ficon-x.com/api/proxy/sigmets';  // ✅ Proxy endpoint
  
    try {
      const res = await fetch(SIGMET_URL);
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const geojson = await res.json();
  
      if (!map.getSource('sigmet-source')) {
        map.addSource('sigmet-source', {
          type: 'geojson',
          data: geojson,
        });
  
        map.addLayer({
          id: 'sigmet-layer',
          type: 'fill',
          source: 'sigmet-source',
          paint: {
            'fill-color': [
              'match',
              ['get', 'phenomena'],
              'T', '#f39c12',        // Turbulence
              'I', '#3498db',        // Icing
              'C', '#e74c3c',        // Convective
              '#9b59b6'              // Fallback color
            ],
            'fill-opacity': 0.35,
          },
        });
  
        map.addLayer({
          id: 'sigmet-outline',
          type: 'line',
          source: 'sigmet-source',
          paint: {
            'line-color': '#333',
            'line-width': 1,
          },
        });
      } else {
        map.getSource('sigmet-source').setData(geojson);
      }
  
      console.log('✅ SIGMETs plotted');
    } catch (err) {
      console.error('❌ Failed to fetch SIGMETs:', err);
    }
  };
  
  
  const removeSigmetLayer = (map) => {
    if (map.getLayer('sigmet-layer')) map.removeLayer('sigmet-layer');
    if (map.getLayer('sigmet-outline')) map.removeLayer('sigmet-outline');
    if (map.getSource('sigmet-source')) map.removeSource('sigmet-source');
  };
  
  const removeAirmetLayer = (map) => {
    if (map.getLayer('airmet-layer')) map.removeLayer('airmet-layer');
    if (map.getLayer('airmet-outline')) map.removeLayer('airmet-outline');
    if (map.getSource('airmet-source')) map.removeSource('airmet-source');
  };
  
const toggleRadar = () => {
  if (!map || !map.getLayer('noaa-radar-layer')) {
    loadNOAARadarLayer(map); // add once if missing
    setRadarVisible(true);
    return;
  }

  const visibility = map.getLayoutProperty('noaa-radar-layer', 'visibility');
  const nextVisibility = visibility === 'visible' ? 'none' : 'visible';
  map.setLayoutProperty('noaa-radar-layer', 'visibility', nextVisibility);
  setRadarVisible(nextVisibility === 'visible');
};

  
  const toggleSigmets = () => {
    setSigmetVisible((prev) => {
      const next = !prev;
      if (next) fetchAndPlotSigmets(map); 
      else removeSigmetLayer(map);
      return next;
    });
  };
  
  const toggleAirmets = () => {
    setAirmetVisible((prev) => {
      const next = !prev;
      if (next) fetchAndPlotAirmets(map); 
      else removeAirmetLayer(map);
      return next;
    });
  };
  


  const handleBubbleSelect = (idx, tok) => {
    setActiveTokenIndex(idx);
  
    const c = coordForToken(routeResult, tok);
    if (map && c) {
      map.easeTo({ center: c, zoom: 9, duration: 800 });
    }
  
    // Highlight the segment (idx to idx+1)
    const feat = highlightSegmentFeature(routeResult, routeTokens, idx);
    if (!map) return;
  
    const srcId = 'route-highlight-src';
    const layerId = 'route-highlight-layer';
  
    if (map.getSource(srcId)) {
      map.getSource(srcId).setData(feat || { type: 'FeatureCollection', features: [] });
    } else {
      map.addSource(srcId, {
        type: 'geojson',
        data: feat || { type: 'FeatureCollection', features: [] }
      });
      map.addLayer({
        id: layerId,
        type: 'line',
        source: srcId,
        paint: {
          'line-width': 5,
          'line-color': '#1e88e5',
          'line-opacity': 0.9
        }
      });
    }
  
    // If no segment (e.g., last token), clear highlight
    if (!feat && map.getSource(srcId)) {
      map.getSource(srcId).setData({ type: 'FeatureCollection', features: [] });
    }
  };

useEffect(() => {

  const raw =
    localStorage.getItem(
      "activeFlight"
    );

  if (!raw) {

    setLocalFlightActive(false);

    return;
  }

  try {

    const parsed =
      JSON.parse(raw);

    if (

      parsed?.route?.coords &&

      Array.isArray(
        parsed.route.coords
      ) &&

      parsed.route.coords.length > 1 &&

      sessionStorage.getItem(
        "flightSessionActive"
      ) === "true"

    ) {

      console.log(
        "🚀 RESTORED ACTIVE FLIGHT"
      );

      setLocalFlightActive(true);

      // 🔥 THIS WAS MISSING
      setSimStarted(true);

      console.log(
        "🚀 SIM RE-ARMED"
      );

    } else {

      setLocalFlightActive(false);
    }

  } catch {

    setLocalFlightActive(false);
  }

}, []);

useEffect(() => {
  // 🟡 START PROCESSING
  routeSocket.on("route_processing_started", () => {
    console.log("🧠 Backend started processing...");
    setSuggestionsLoading(true);
  });

  // 🟢 FINISH PROCESSING
  routeSocket.on("route_suggestions", (data) => {
    console.log("🛰️ Received route suggestions:", data);

    setSuggestions(Array.isArray(data) ? data : data.routes);

    // 🔥 STOP LOADING HERE
    setSuggestionsLoading(false);
  });

  // 🧠 DEBUG LOGS
  routeSocket.on("route_console_log", (msg) => {
    console.log("🧠 Route Processor:", msg.message);
  });

  // 🧹 CLEANUP
  return () => {
    routeSocket.off("route_processing_started");
    routeSocket.off("route_suggestions");
    routeSocket.off("route_console_log");
  };
}, []);




useEffect(() => {
  const fetchNearbyPireps = async () => {
    if (!origin || !destination) return;

    try {
      const response = await fetch(`/fetch_nearby_pireps?origin=${origin}&destination=${destination}`);
      const data = await response.json();

      console.log("📡 Fetched PIREPs:", data);

      setOriginPireps(data.originPireps || []);
      setDestinationPireps(data.destinationPireps || []);
    } catch (err) {
      console.error("Failed to fetch nearby PIREPs:", err);
    }
  };

  fetchNearbyPireps();
}, [origin, destination]);

const getEtaHours = () => {
  try {
    const raw = localStorage.getItem("activeFlight");
    if (!raw) return 0;

    const flight = JSON.parse(raw);
    return flight?.timeEnroute ? flight.timeEnroute / 60 : 0;
  } catch {
    return 0;
  }
};


useEffect(() => {

  const originIcao =
    (origin || "")
      .trim()
      .toUpperCase();

  const destinationIcao =
    (destination || "")
      .trim()
      .toUpperCase();

  // =====================================
  // ONLY FETCH VALID COMPLETE ICAOS
  // Prevent stale K / KM / KMY requests
  // from overwriting KMYR data.
  // =====================================

  if (
    originIcao.length !== 4 ||
    destinationIcao.length !== 4
  ) {
    return;
  }

  const flight =
    JSON.parse(
      localStorage.getItem("activeFlight")
    );

  const eta =
    flight?.timeEnroute
      ? flight.timeEnroute / 60
      : 0;

async function loadAllData() {
    try {

        await cacheAirportData(
            originIcao,
            "departure",
            0
        );

        // -----------------------------
        // 🔥 CORE DATA
        // -----------------------------
        console.log(
          "🚨 BRIEFING FETCH VALUES:",
          {
            origin,
            destination,
            eta,
            destinationUrl:
              `https://ficon-x.com/route/pilot-briefing?icao=${destination}&type=arrival&eta_hours=${eta}`
          }
        );
const [originRes, destRes] = await Promise.all([

  fetch(
    `https://ficon-x.com/route/pilot-briefing?icao=${originIcao}&type=departure&eta_hours=0`
  ),

  fetch(
    `https://ficon-x.com/route/pilot-briefing?icao=${destinationIcao}&type=arrival&eta_hours=${eta}`
  )

]);

      const originCore = await originRes.json();
      const destCore = await destRes.json();

      console.log(
  "🚨 RAW DEST CORE BEFORE STATE:",
  JSON.stringify(destCore, null, 2)
);

      console.log("✅ ETA DATA:", destCore);

      setOriginData(originCore);
      setDestinationData(destCore);


// -----------------------------
// NOTAMS (FINAL CLEAN VERSION)
// -----------------------------
// -----------------------------
// NOTAMS (FROM AIRPORT CACHE)
// -----------------------------
try {

// -------------------------------------
// Get airport data directly
// (also populates the cache)
// -------------------------------------

const originNotamData =
    await cacheAirportData(
        originIcao,
        "departure",
        0
    );

const destNotamData =
    await cacheAirportData(
        destinationIcao,
        "arrival",
        eta
    );

console.log(
    "📦 Airport data loaded:",
    {
        origin: originIcao,
        destination: destinationIcao
    }
);

console.log("🧪 ORIGIN NOTAM RAW:", originNotamData);
console.log("🧪 DEST NOTAM RAW:", destNotamData);

  // -----------------------------
  // 🔥 SUMMARY
  // -----------------------------
const originSummary =
    Object.values(originNotamData.summaries || {})[0] || "";

const destSummary =
    Object.values(destNotamData.summaries || {})[0] || "";
  // -----------------------------
  // 🔥 FORCE ARRAY (THIS IS THE FIX)
  // -----------------------------
  // -----------------------------
  // NOTAMS
  // -----------------------------
  const originNotams =
    originNotamData.notams || [];

  const destNotams =
    destNotamData.notams || [];

  // -----------------------------
  // CLOSED RUNWAYS
  // -----------------------------
const originClosed =
    originNotamData.closedRunways || [];

const destClosed =
    destNotamData.closedRunways || [];

  console.log("🧪 originNotams FINAL:", originNotams);
  console.log("🧪 destNotams FINAL:", destNotams);

  // -----------------------------
  // 🔥 STATE UPDATE (CRITICAL)
  // -----------------------------
  setOriginData(prev => ({
    ...prev,
    notams: originNotams,   // MUST be array
    closedRunways: originClosed,
    summary: originSummary
  }));

  setDestinationData(prev => ({
    ...prev,
    notams: destNotams,     // MUST be array
    closedRunways: destClosed,
    summary: destSummary
  }));

} catch (e) {
  console.warn("⚠️ NOTAM fetch failed:", e);
}

    } catch (err) {
      console.error("❌ CRITICAL FAILURE:", err);
    }
  }

  loadAllData();
}, [origin, destination]);


useEffect(() => {
  if (!routeTokens?.length) return;

  if (resolvingRef.current) {
    console.log("⚠️ Route resolve already in progress — skipping");
    return;
  }

  resolvingRef.current = true;

const updateRoute = async () => {

  const cleanedRoute =
    routeTokens.filter(Boolean).join(" ");

  try {

    console.log(
      "🚨 EFFECT RE-RESOLVE",
      {
        selectedSidRunway,
        selectedSid,
        selectedSidTransition,

        selectedArrivalRunway,
        selectedStar,
        selectedStarTransition,

        selectedApproach,
        selectedArrivalIAF
      }
    );

    const resolved =
      await resolveRouteWithCoords(

        cleanedRoute,

        origin.trim().toUpperCase(),

        destination.trim().toUpperCase(),

        altitude,

        selectedSidRunway,

        selectedSid,
        selectedSidTransition,

        selectedArrivalRunway,

        selectedStar,
        selectedStarTransition,

        selectedApproach,
        selectedArrivalIAF
      );

      if (!resolved || !resolved.coords || resolved.coords.length < 2) {
        console.error("❌ Could not resolve edited route:", resolved);
        return;
      }

      // 🔥 IMPORTANT: ONLY update tokens if they actually changed
      const newTokens = resolved.expanded_tokens || resolved.names || [];

      const currentKey = JSON.stringify(routeTokens);
      const newKey = JSON.stringify(newTokens);

if (currentKey !== newKey) {

  console.log(
    "🚨 NOT UPDATING ROUTE TOKENS FOR TEST",
    {
      old: routeTokens,
      new: newTokens
    }
  );

  // setRouteTokens(newTokens);
}


      //setRouteResult(resolved);

      if (resolved?.hazardAnalysis) {
        setHazardData(resolved.hazardAnalysis);
      }

      plotRoute(resolved);
      setActivePanel?.(null);

    } catch (err) {
      console.error("❌ Error resolving route:", err);
    } finally {
      resolvingRef.current = false;
    }
  };

  updateRoute();

}, [origin, destination, altitude]); // 🔥 REMOVE routeTokens from dependency


  useEffect(() => {
    const joined = routeTokens.join(' ');
    setSelectedRouteString(joined);
    setRoute(joined);  // <-- Add this line to keep main route updated
  }, [routeTokens]);

  useEffect(() => {
    if (!map) return;
  
    if (showAirmets) {
      fetchAndPlotAirmets(map);  // Load and display layer
    } else {
      if (map.getLayer("airmet-layer")) map.removeLayer("airmet-layer");
      if (map.getLayer("airmet-outline")) map.removeLayer("airmet-outline");
      if (map.getSource("airmet-source")) map.removeSource("airmet-source");
    }
  }, [showAirmets, map]);
  
  
  useEffect(() => {
    if (!mapRef.current) return;
  
    if (showSigmets) {
      fetchAndPlotSigmets(mapRef.current);
    } else {
      removeSigmetLayer(mapRef.current);
    }
  }, [showSigmets]);
  
  useEffect(() => { showAirportWeatherRef.current = showAirportWeather; }, [showAirportWeather]);

  async function resolveRouteCoords(route) {
  const fixes = await fetch("/fixes.json").then(r => r.json());
  const airports = await fetch("/airports.json").then(r => r.json());
  const vors = await fetch("/vors.json").then(r => r.json());

  const resolveFix = id => {
    id = id.toUpperCase();
    return (
      fixes[id] ||
      airports[id] ||
      vors[id] ||
      null
    );
  };

  const coords = [];
  for (const id of route) {
    const wp = resolveFix(id);
    if (wp) coords.push([wp.lon, wp.lat]);
  }
  return coords;
}

 
function applyObstructionsVisibility(show) {
  const markers = obstructionMarkersRef.current;
  if (!markers || markers.length === 0) return;

  console.log("🧪 Applying obstruction visibility:", {
    show,
    markerCount: markers.length,
    mapZoom
  });

  markers.forEach(m => {
    try {
      const el = m.getElement();
      if (el) {
        // 🔥 REMOVE ZOOM FROM THIS FUNCTION
        el.style.display = show ? 'block' : 'none';
      }
    } catch {}
  });
}

// put this ABOVE MapComponent (or in its own file and import it)
function KissBox({ activeInstruction, pdpCallsignSpoken }) {
  if (!activeInstruction) return null;

  const { instruction = {}, readback = {} } = activeInstruction;

  return (
    <div
      style={{
        position: 'absolute',
        top: 20,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 3000,
        backgroundColor: '#fff',
        padding: '16px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,.25)',
        width: 520,
        fontFamily: 'Arial, sans-serif'
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: 8 }}>
        {pdpCallsignSpoken || '—'}
      </div>
      <table style={{ width: '100%', fontSize: '0.95em' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Field</th>
            <th style={{ textAlign: 'left' }}>ATC</th>
            <th style={{ textAlign: 'left' }}>Pilot</th>
            <th style={{ textAlign: 'left' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {['altitude','heading','turn','speed'].map((k) => {
            const atc = (instruction[k] ?? '').toString();
            const pilot = (readback[k] ?? '').toString();
            const match = atc && pilot && atc.toUpperCase() === pilot.toUpperCase();
            return (
              <tr key={k}>
                <td>{k}</td>
                <td>{atc}</td>
                <td>{pilot}</td>
                <td style={{ color: match ? 'green' : 'red' }}>
                  {match ? '✅ Match' : '❌ Mismatch'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


 

// one place to nuke all airport condition markers
function clearAirportMarkers() {
  // remove mapbox markers we tracked
  airportMarkers.forEach(m => { try { m.remove(); } catch {} });
  setAirportMarkers([]);

  // belt & suspenders: remove any stray DOM we created
  document.querySelectorAll('.airport-marker').forEach(el => {
    try { el.remove(); } catch {}
  });
}

function applyTaxiwayVisibility(show) {
  const map = mapRef.current;
  if (!map || !map.getStyle()) return;

  console.log("🧪 Applying taxiway visibility:", {
    show,
    mapZoom
  });

  map.getStyle().layers.forEach(layer => {
    const id = layer.id;

    if (id.startsWith('twy-closures-')) {
      try {
        // 🔥 NO ZOOM LOGIC HERE
        map.setLayoutProperty(
          id,
          'visibility',
          show ? 'visible' : 'none'
        );
      } catch {}
    }
  });
}

const applyWingspanVisibility = (on) => {
  const map = mapRef.current;
  if (!map || !map.getStyle()) return;

  console.log("🧪 Wingspan visibility:", { on });

  map.getStyle().layers.forEach(layer => {
    const id = layer.id;

    if (id.startsWith('twy-wingspan-')) {
      try {
        map.setLayoutProperty(
          id,
          'visibility',
          on ? 'visible' : 'none'
        );
      } catch {}
    }
  });
};

const applyCrosswindVisibility = (on) => {
  const map = mapRef.current;

  console.log("🧪 Crosswind visibility:", { on });

  // A) DOM labels
  document.querySelectorAll('.runway-inline-label').forEach(el => {
    el.style.display = on ? 'block' : 'none';
  });

  // B) Mapbox layers
  if (!map || !map.getStyle()) return;

  map.getStyle().layers.forEach(layer => {
    const id = layer.id;

    if (id.startsWith('crosswind-center-')) {
      try {
        map.setLayoutProperty(
          id,
          'visibility',
          on ? 'visible' : 'none'
        );
      } catch {}
    }
  });
};
const applyFiconVisibility = (on) => {

  const map = mapRef.current;

  if (!map || !map.getStyle()) return;

  console.log(
    "🧪 FICON visibility:",
    { on }
  );

  map.getStyle().layers.forEach(layer => {

    const id = layer.id;

    if (id.startsWith("ficon-")) {

      try {

        map.setLayoutProperty(
          id,
          "visibility",
          on ? "visible" : "none"
        );

      } catch (e) {

        console.log(
          "⚠️ FICON visibility error:",
          id
        );

      }

    }

  });

};

const applyRunwayColorsVisibility = (on) => {

  const map = mapRef.current;

  if (!map || !map.getStyle()) return;

  console.log(
    "🧪 Runway colors visibility:",
    { on }
  );

  map.getStyle().layers.forEach(layer => {

    const id = layer.id;

    if (id.startsWith("runway-segment-")) {

      try {

        map.setLayoutProperty(
          id,
          "visibility",
          on ? "visible" : "none"
        );

        if (on) {
          map.moveLayer(id);
        }

      } catch (e) {

        console.log(
          "⚠️ Runway visibility error:",
          id
        );

      }

    }

  });

};
  const [weatherData, setWeatherData] = useState([]); // Store received weather data
 

  const [filterMenuOpen, setFilterMenuOpen] = useState(false); // To toggle the filter menu visibility

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

// Destination coordinates for active-flight
// "Zoom to Destination" control
  const destinationRef = useRef(null);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);  // Live Camera Modal
  const [cameraFeedUrl, setCameraFeedUrl] = useState(null);
  

  const [clickedPoint, setClickedPoint] = useState(null); // optional if map click fills lat/lng


  // Toggle modal visibility for flight input form
const handleOpenFlightPlan = () => {
    setModalOpen(true);  
    setCameraModalOpen(false);  // ✅ Ensure the camera modal is closed when opening flight plan
};

 
const startPdpMonitoring = () => {
  fetch("http://localhost:5001/start_pdp_monitoring", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      spoken_callsign: pdpCallsign,
      flight_plan_callsign: pdpFiledCallsign
    })
  })
    .then(response => response.json())
    .then(data => {
      console.log("[✅] PDP monitoring started:", data);
    })
    .catch(err => {
      console.error("[❌] PDP start error:", err);
    });
};

  


  // Function to determine marker color based on METAR/TAF weather data
  const determineWeatherColor = (metarData) => {
    const visibilityMatch = metarData.match(/(\d+SM)/);
    const visibility = visibilityMatch ? parseInt(visibilityMatch[1].replace('SM', '')) : null;

    const cloudMatch = metarData.match(/BKN(\d{3})|OVC(\d{3})/);
    const cloudCeiling = cloudMatch ? parseInt(cloudMatch[1] || cloudMatch[2]) : 9999;

	if (visibility >= 3 && cloudCeiling >= 1000) {
	  return 'green'; // VFR
	} else if ((visibility >= 3 && visibility < 5) || (cloudCeiling >= 1000 && cloudCeiling < 2500)) {
	  return 'yellow'; // MVFR
	} else {
	  return 'red'; // IFR
    }
  };



// Function to plot airport markers on the map
const plotAirportMarkers = (weatherData) => {
  console.log("🛰️ plotAirportMarkers called.");

  if (!map) {
    console.warn("❌ Map is not initialized.");
    return;
  }

  if (!Array.isArray(weatherData)) {
    console.error("❌ weatherData is not an array:", weatherData);
    return;
  }

  if (weatherData.length === 0) {
    console.warn("⚠️ weatherData array is empty.");
    return;
  }



  // Clear old markers
  airportMarkers.forEach(marker => marker.remove());
  setAirportMarkers([]);

  // Create markers
  const newMarkers = weatherData.map((airport, idx) => {
    const { latitude, longitude, icao, metar, taf } = airport;

    if (
      typeof latitude !== "number" ||
      typeof longitude !== "number" ||
      !icao ||
      isNaN(latitude) ||
      isNaN(longitude)
    ) {
      console.warn(`⚠️ Skipping invalid airport at index ${idx}:`, airport);
      return null;
    }

    const color = determineWeatherColor(metar);
    console.log(`📍 Creating airport marker for ${icao} at [${longitude}, ${latitude}] color: ${color}`);

    // Create a small round DOM element as custom marker
    const el = document.createElement('div');
    el.className = 'airport-marker';
    el.style.width = '10px';
    el.style.height = '10px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = color;
    el.style.opacity = '0.65';
    el.style.border = '1px solid #444';

    try {
      const marker = new mapboxgl.Marker(el)
        .setLngLat([longitude, latitude])
        .setPopup(new mapboxgl.Popup().setHTML(
          `<strong>${icao}</strong><br>METAR: ${metar || "N/A"}<br>TAF: ${taf || "N/A"}`
        ))
        .addTo(map);

      return marker;
    } catch (err) {
      console.error(`❌ Failed to create airport marker for ${icao}:`, err);
      return null;
    }
  }).filter(Boolean);

  setAirportMarkers(newMarkers);
  console.log(`✅ Plotted ${newMarkers.length} airport markers.`);
};



// put near other state hooks if you want a default mode:
// const [planMode, setPlanMode] = useState('preview');

const submitFlightPlan = async (mode = "preview") => {
  if (!origin || !destination) {
    console.error("Origin and destination are required.");
    Swal.fire("Please provide both origin and destination.");
    return;
  }

  const normOrigin = origin.trim().toUpperCase();
  const normDestination = destination.trim().toUpperCase();
  const normRoute = (route || "").trim().toUpperCase();
  const normAltitude = altitude;

  const payload = {
  origin: normOrigin,
  destination: normDestination,
  route: normRoute,
  cruise_alt_ft: Array.isArray(normAltitude) ? Number(normAltitude[0]) : Number(normAltitude),
  tas: Number(normTAS),            // <-- if user input exists
  burn_rate: Number(normBurnRate), // <-- if applicable
  mode,
};


  console.log("📡 Sending payload to backend:", payload);

  try {
    const response = await axios.post(`${backendURL}/submit-flight-plan`, payload);
    if (!response?.data) {
      Swal.fire("No response from server.");
      return;
    }

    const data = response.data;
    const serverMode = data?.debug?.mode || mode;

console.log(
  "🚨 WAYPOINT OBJECTS",
  data.waypoints
);    

    // Build route bubbles based on mode
const expandedTokens = Array.isArray(data.waypoints)
  ? data.waypoints
      .map((wp) => wp?.id?.toUpperCase())
      .filter(Boolean)
  : [];

console.log("🧩 Expanded tokens for RouteBubbleBar:", expandedTokens);
    setRouteTokens(expandedTokens);

    setRouteLegs(data?.waypoints || []);
    setRouteResult(data);
    plotRoute(data);

    setModalOpen(false);
    setFlightPlanSubmitted(true);
    setAlertedPireps(new Set());
 
    // NOTAMs
    try {
      const notamResponse = await axios.get(`${backendURL}/get-notams`, {
        params: { origin: normOrigin, destination: normDestination },
      });

      if (Array.isArray(notamResponse.data)) {
        const ficonFilteredNotams = notamResponse.data.filter(
          (n) =>
            n?.properties?.coreNOTAMData?.notam?.text &&
            n.properties.coreNOTAMData.notam.text.includes("FICON")
        );
        setFiconNotams(ficonFilteredNotams);
      } else {
        setFiconNotams([]);
      }
    } catch (notamError) {
      console.error("🚨 Error fetching NOTAMs:", notamError);
      setFiconNotams([]);
    }

    // Live camera feed
    try {
      const liveFeedResponse = await axios.get(`${backendURL}/get-live-feed/${normDestination}`);
      setCameraFeedUrl(liveFeedResponse.data?.available ? liveFeedResponse.data.url : null);
    } catch (feedError) {
      console.error("🚨 Error fetching live feed:", feedError);
      setCameraFeedUrl(null);
    }
  } catch (error) {
    console.error("🚨 Error submitting flight plan:", error);
    const msg =
      error?.response?.data?.error ||
      error?.message ||
      "An error occurred while submitting the flight plan. Please try again.";
    Swal.fire("Submit failed", msg, "error");
  }
};







const plotSuggestionRoute = (routeData, index) => {
  if (!map) return;
  console.log(`🗺️ Plotting suggestion ${index} via plotRoute()`);

  // Give each suggestion unique source/layer IDs
  const uniqueId = `route-suggestion-${index}`;

  // Clone and modify plotRoute to use unique IDs
  const { origin, destination, waypoints } = routeData;
  const coordinates = [origin, ...waypoints.map(wp => wp.coordinates), destination];

  // Remove previous instance of this suggestion
  try {
    if (map.getLayer(uniqueId)) map.removeLayer(uniqueId);
    if (map.getSource(uniqueId)) map.removeSource(uniqueId);
    if (map.getLayer(`${uniqueId}-labels`)) map.removeLayer(`${uniqueId}-labels`);
    if (map.getSource(`${uniqueId}-labels`)) map.removeSource(`${uniqueId}-labels`);
  } catch (e) {}

  // Add main line
  map.addSource(uniqueId, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: { type: "LineString", coordinates },
    },
  });

  map.addLayer({
    id: uniqueId,
    type: "line",
    source: uniqueId,
    layout: { "line-join": "round", "line-cap": "round" },
    paint: {
      "line-color": ["#0055FF", "#8A2BE2", "#20C997", "#FF69B4", "#A0522D", "#708090"][index % 6],
      "line-width": 2,
      "line-opacity": 0.75,
    },
  });

  // Label fixes
  const features = waypoints.map((wp, i) => ({
    type: "Feature",
    geometry: { type: "Point", coordinates: wp.coordinates },
    properties: { title: (wp.name || wp.id || `WP${i}`).toUpperCase() },
  }));

  map.addSource(`${uniqueId}-labels`, {
    type: "geojson",
    data: { type: "FeatureCollection", features },
  });

  map.addLayer({
    id: `${uniqueId}-labels`,
    type: "symbol",
    source: `${uniqueId}-labels`,
    layout: {
      "text-field": ["get", "title"],
      "text-size": 11,
      "text-offset": [0, 1.2],
      "text-anchor": "top",
      "text-allow-overlap": true,
    },
    paint: {
      "text-color": "#0055FF",
      "text-halo-color": "#fff",
      "text-halo-width": 1.2,
    },
  });

  console.log(`✅ Finished plotting suggestion ${index}`);
};

  
  // Function to plot the route when there are only origin and destination points
  const plotSingleRoute = (coordinates) => {
    if (map.getLayer('route')) {
      map.removeLayer('route');
      map.removeSource('route');
    }

    map.addSource('route', {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'LineString', coordinates } },
    });

    map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#FF5733', 'line-width': 4 },
    });

    const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[1]);
    map.fitBounds(bounds, { padding: 50 });
  };

  // Function to plot the route when there are multiple waypoints
  const plotMultiRoute = (coordinates) => {
    if (map.getLayer('route')) {
      map.removeLayer('route');
      map.removeSource('route');
    }

    map.addSource('route', {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'LineString', coordinates } },
    });

    map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#FF5733', 'line-width': 4 },
    });

    const bounds = new mapboxgl.LngLatBounds(coordinates[0], coordinates[coordinates.length - 1]);
    map.fitBounds(bounds, { padding: 50 });
  };

  // Helper function to validate coordinates
  const isValidCoordinates = (coords) => {
    return Array.isArray(coords) && coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1]);
  };

  // Function to calculate perpendicular distance from a point to a line segment
  const calculatePerpendicularDistance = (lat1, lon1, lat2, lon2, lat3, lon3) => {
    const toRad = (deg) => deg * (Math.PI / 180);
    const R = 3958.8; // Radius of Earth in miles
    const lat1Rad = toRad(lat1);
    const lon1Rad = toRad(lon1);
    const lat2Rad = toRad(lat2);
    const lon2Rad = toRad(lon2);
    const lat3Rad = toRad(lat3);
    const lon3Rad = toRad(lon3);

    const d1 = Math.sqrt(Math.pow(lat2Rad - lat1Rad, 2) + Math.pow(lon2Rad - lon1Rad, 2));
    const d2 = Math.sqrt(Math.pow(lat3Rad - lat2Rad, 2) + Math.pow(lon3Rad - lon2Rad, 2));
    const d3 = Math.sqrt(Math.pow(lat3Rad - lat1Rad, 2) + Math.pow(lon3Rad - lon1Rad, 2));

    const s = (d1 + d2 + d3) / 2;
    const area = Math.sqrt(s * (s - d1) * (s - d2) * (s - d3));
    const distance = (2 * area) / d1;
    return distance * R; // Return distance in miles
  };

  // Function to alert when within 50 miles of the route
const [flightPlanSubmitted, setFlightPlanSubmitted] = useState(false);
const [alertedPireps, setAlertedPireps] = useState(new Set());

const checkAlertForProximity = (lat, lon, routeCoordinates) => {
    const alertDistance = 50;  // 50 miles
    let newAlertsTriggered = new Set(alertedPireps);

    for (let i = 0; i < routeCoordinates.length - 1; i++) {
        const start = routeCoordinates[i];
        const end = routeCoordinates[i + 1];

        const distance = calculatePerpendicularDistance(lat, lon, start[1], start[0], end[1], end[0]);

        if (distance <= alertDistance) {
            const alertKey = `${lat},${lon}`;  // Unique identifier for alerts

            // ✅ Only trigger the alert if:
            // - It's the **first time** a flight plan is submitted.
            // - The PIREP **has not been alerted yet** (new data).
            if ((flightPlanSubmitted && !newAlertsTriggered.has(alertKey)) || !flightPlanSubmitted) {
                Swal.fire({
                    title: 'Proximity Alert!',
                    text: 'Weather or PIREP detected within 50 miles of the route!',
                    icon: 'warning',
                    confirmButtonText: 'OK'
                });

                newAlertsTriggered.add(alertKey);  // Track this PIREP as "already alerted"
            }
            break;
        }
    }

    setAlertedPireps(newAlertsTriggered);
};



function createRunwaySegmentPolygon(threshold, heading, lengthMeters = 300, widthMeters = 30) {
  const R = 6378137; // Earth radius in meters

  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;

  const lat = threshold.lat;
  const lon = threshold.lon;

  const destinationPoint = (lat, lon, distance, bearing) => {
    const δ = distance / R;
    const θ = toRad(bearing);

    const φ1 = toRad(lat);
    const λ1 = toRad(lon);

    const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
    const λ2 = λ1 + Math.atan2(
      Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
      Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2)
    );

    return [toDeg(λ2), toDeg(φ2)];
  };

  // Front edge is threshold, back edge is down the runway direction
  const backCenter = destinationPoint(lat, lon, lengthMeters, heading);

  const frontLeft = destinationPoint(lat, lon, widthMeters / 2, heading - 90);
  const frontRight = destinationPoint(lat, lon, widthMeters / 2, heading + 90);
  const backLeft = destinationPoint(backCenter[1], backCenter[0], widthMeters / 2, heading - 90);
  const backRight = destinationPoint(backCenter[1], backCenter[0], widthMeters / 2, heading + 90);

  const corners = [
    frontLeft,
    frontRight,
    backRight,
    backLeft,
    frontLeft // close the polygon
  ];

  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [corners]
    },
    properties: {
      color: "red"
    }
  };
}




function addRunwayPolygonLayer(runway, color) {
  const id = `runway-segment-${runway.runway}-${Math.random().toString(36).substring(2, 9)}`;
  const map = mapRef.current;

  const polygon = createRunwaySegmentPolygon(runway.threshold, runway.heading);
  polygon.properties.color = color;

  if (map.getLayer(id)) map.removeLayer(id);
  if (map.getSource(id)) map.removeSource(id);

  map.addSource(id, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: [polygon]
    }
  });

  map.addLayer({
    id,
    type: "fill",
    source: id,
    layout: {
      visibility: runwayColorsOn ? 'visible' : 'none'   // ⬅️ add this
    },
    paint: {
      "fill-color": color,
      "fill-opacity": 0.7
    }
  });
  

  // 🛑 Add proper cleanup tracking
  runwayMarkersRef.current.push({ id, type: 'polygon' });
}

function getTopWindLayerId() {
  const map = mapRef.current;
  if (!map || !map.getStyle()) return undefined;

  const allLayers = map.getStyle().layers;
  if (!Array.isArray(allLayers)) return undefined;

  const windLayers = allLayers
    .map(layer => layer.id)
    .filter(id => id.startsWith('wind-'));

  return windLayers.length > 0 ? windLayers[windLayers.length - 1] : undefined;
}



const plotRunwayMarkers = (mapInstance, runwayData) => {
  // ✅ Clear previously drawn runway segments
  if (runwayMarkersRef.current.length > 0) {
    runwayMarkersRef.current.forEach(obj => {
      if (obj?.type === 'line' || obj?.type === 'polygon') {
        if (mapInstance.getLayer(obj.id)) mapInstance.removeLayer(obj.id);
        if (mapInstance.getSource(obj.id)) mapInstance.removeSource(obj.id);
      } else if (typeof obj.remove === 'function') {
        obj.remove(); // For markers
      }
    });
    runwayMarkersRef.current = [];
  }

  if (!Array.isArray(runwayData)) return;

  runwayData.forEach(runway => {
    const end = runway.threshold;
    const heading = parseFloat(runway.heading);
    const isReciprocal = runway.reciprocal === true;
    const color = isReciprocal ? 'red' : (runway.color || 'gray');

    if (!end || isNaN(heading) || typeof end.lat !== 'number' || typeof end.lon !== 'number') {
      console.warn('⚠️ Invalid runway data:', runway);
      return;
    }

    // ✅ Add colored polygon rectangle over the threshold
    addRunwayPolygonLayer(runway, color);
  });
};

function addWindArrow(lat, lon, direction, speed, gust) {
  const map = mapRef.current;
  if (!map) return;

  // Remove previous wind markers
  runwayMarkersRef.current = runwayMarkersRef.current.filter(obj => {
    if (obj?.type === 'wind' && typeof obj.remove === 'function') {
      obj.remove();
      return false;
    }
    return true;
  });

  // Create arrow container
  const windEl = document.createElement("div");
  windEl.style.display = "flex";
  windEl.style.flexDirection = "column";
  windEl.style.alignItems = "center";

  // Create arrow image
  const arrow = document.createElement("img");
  arrow.src = arrowImage;
  arrow.style.width = "40px";
  arrow.style.height = "40px";
  arrow.style.transform = `rotate(${(direction + 180) % 360}deg)`;
  arrow.style.transformOrigin = "center center";
  arrow.style.pointerEvents = "none";
  arrow.style.display = "block";

  // Create speed label
  const label = document.createElement("div");
  label.textContent = gust ? `${speed}G${gust} KT` : `${speed} KT`;
  label.className = "wind-label";  // Uses your existing CSS for styling
  label.style.marginTop = "2px";

  // Combine elements
  windEl.appendChild(arrow);
  windEl.appendChild(label);

  // Add to map
  const marker = new mapboxgl.Marker(windEl)
    .setLngLat([lon, lat])
    .addTo(map);

  runwayMarkersRef.current.push({ type: 'wind', remove: () => marker.remove() });
}


const fetchRunwayColors = async (icao) => {
  try {
    const map = mapRef.current;
    if (!map) return;

    const zoom = map.getZoom();

    // 🔥 HARD BLOCK — NOTHING RENDERS WHEN ZOOMED OUT
    if (zoom < 10) {
      console.log("⛔ Skipping runway rendering — zoom too low");
      return;
    }

    const res = await axios.get(`${backendURL}/get_runway_colors?airport=${icao}`);

    console.log("✅ Full backend response:", res.data);

    if (!res.data || !Array.isArray(res.data.runways)) {
      console.warn(`⚠️ Invalid or missing 'runways' array from backend for ${icao}:`, res.data);
      return;
    }

    res.data.runways.forEach(runway => {
      console.log(`🛬 Runway ${runway.runway} – Heading: ${runway.heading} – Color: ${runway.color}`);
      console.log(`   ↪️ Headwind: ${runway.headwind} kt, Crosswind: ${runway.crosswind} kt`);
    });

    // ✅ Plot colored runway segments
    plotRunwayMarkers(map, res.data.runways);

    // 🔥 REMOVE ALL OLD MARKERS (wind + crosswind)
    runwayMarkersRef.current.forEach(obj => {
      if (obj?.remove) {
        try { obj.remove(); } catch {}
      }
    });
    runwayMarkersRef.current = [];

    // ✅ Add wind arrow ONLY if zoom OK
    if (res.data.wind && res.data.latitude && res.data.longitude && zoom >= 10) {
      const { direction, speed, gust } = res.data.wind;
      addWindArrow(res.data.latitude, res.data.longitude, direction, speed, gust);
    }

    // ---- pair runway ends ----
    const ends = res.data.runways.filter(r => r?.threshold && typeof r.heading === 'number');
    const used = new Set();
    const pairs = [];

    const headingDiffTo180 = (h1, h2) => {
      const target = (h1 + 180) % 360;
      let d = Math.abs(((h2 - target + 540) % 360) - 180);
      return d;
    };

    for (let i = 0; i < ends.length; i++) {
      const a = ends[i];
      if (used.has(a.runway)) continue;

      let best = null;
      let bestDelta = 999;

      for (let j = 0; j < ends.length; j++) {
        if (i === j) continue;
        const b = ends[j];
        if (used.has(b.runway)) continue;

        const d = headingDiffTo180(a.heading, b.heading);
        if (d < bestDelta) {
          bestDelta = d;
          best = b;
        }
      }

      if (best && bestDelta <= 15) {
        used.add(a.runway);
        used.add(best.runway);
        pairs.push([a, best]);
      }
    }

    // ✅ CROSSWIND LABELS (ZOOM SAFE)
    pairs.forEach(([r1, r2]) => {
      // 🔥 BLOCK CREATION IF NOT ALLOWED
      if (!crosswindOn || zoom < 10) return;

      const midLon = (r1.threshold.lon + r2.threshold.lon) / 2;
      const midLat = (r1.threshold.lat + r2.threshold.lat) / 2;

      const avgHead = Math.round(((r1.headwind ?? 0) + (r2.headwind ?? 0)) / 2);
      const avgCross = Math.round(((r1.crosswind ?? 0) + (r2.crosswind ?? 0)) / 2);

      const el = document.createElement('div');
      el.className = 'runway-inline-label';
      el.textContent = `↔ ${avgCross} kt  |  ↑ ${avgHead} kt`;

      el.style.padding = '2px 6px';
      el.style.backgroundColor = 'white';
      el.style.color = 'black';
      el.style.borderRadius = '4px';
      el.style.fontSize = '20px';
      el.style.fontWeight = 'bold';
      el.style.whiteSpace = 'nowrap';
      el.style.boxShadow = '0 1px 4px rgba(0, 0, 0, 0.25)';
      el.style.pointerEvents = 'none';

      el.style.transform = `translate(-50%, -50%) rotate(${r1.heading}deg)`;

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center'
      })
        .setLngLat([midLon, midLat])
        .addTo(map);

      runwayMarkersRef.current.push({
        type: 'crosswind',
        remove: () => marker.remove()
      });
    });

  } catch (err) {
    console.error(`❌ Error fetching runway colors for ${icao}:`, err);
  }
};

function plotCenteredCrosswindLabels(map, runways, icao) {
  const sourceId = `crosswind-center-${icao}`;
  const layerId = `crosswind-center-${icao}`;

  // Clean previous
  if (map.getLayer(layerId)) map.removeLayer(layerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);

  // Build a map of ident -> runway end
  const byIdent = {};
  runways.forEach(r => { byIdent[r.runway] = r; });

  // Try to pair ends using your existing helper if available
  const used = new Set();
  const features = [];

  // Fallback reciprocal finder if you don't have a helper handy
  const findReciprocal = (r1) => {
    // Look for an end that's ~180° different
    const target = (r1.heading + 180) % 360;
    let best = null, bestDiff = 999;
    for (const r2 of runways) {
      if (r2 === r1 || used.has(r2.runway)) continue;
      const diff = Math.abs((((r2.heading - target) % 360) + 540) % 360 - 180);
      if (diff < bestDiff) { bestDiff = diff; best = r2; }
    }
    // Tolerance 10°
    return bestDiff <= 10 ? best : null;
  };

  for (const r of runways) {
    if (used.has(r.runway) || !r.threshold) continue;
    const mate = findReciprocal(r);
    if (!mate || !mate.threshold) continue;

    used.add(r.runway);
    used.add(mate.runway);

    // Midpoint between thresholds
    const midLon = (r.threshold.lon + mate.threshold.lon) / 2;
    const midLat = (r.threshold.lat + mate.threshold.lat) / 2;

    // Magnitudes + arrows: use one end’s sign for head/tail arrow
    const headMag = Math.round(Math.abs(r.headwind ?? 0));
    const crossMag = Math.round(Math.abs(r.crosswind ?? 0));
    const hdArrow = (r.headwind ?? 0) >= 0 ? '↑' : '↓';

    // Crosswind side: if you added crosswind_side in backend, prefer it.
    // Otherwise infer from wind vs heading if available (skipping here).
    // Default to left/right neutrality if unknown:
    const cxArrow = '↔';

    const label = `${cxArrow} ${crossMag} kt  |  ${hdArrow} ${headMag} kt`;

    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: [midLon, midLat] },
      properties: {
        label,
        rotate: r.heading // align with runway heading
      }
    });
  }

  map.addSource(sourceId, {
    type: "geojson",
    data: { type: "FeatureCollection", features }
  });

  map.addLayer({
    id: layerId,
    type: "symbol",
    source: sourceId,
    layout: {
      "text-field": ["get", "label"],
      "text-size": 14,                 // bump if you want larger
      "text-font": ["Open Sans Bold"],
      "text-rotate": ["get", "rotate"],
      "text-rotation-alignment": "map",
      "text-keep-upright": false,      // don't auto-flip
      "text-offset": [0, 1.2],         // nudge off the centerline a bit
      "text-anchor": "center",
      "text-allow-overlap": true
    },
    paint: {
      "text-color": "#000000",
      "text-halo-color": "#FFFFFF",
      "text-halo-width": 1.2
    },
    minzoom: 10
  });
}

// -------------------------
// FAA NOTAM TIME PARSER
// -------------------------
const parseNotamTime = (str) => {
  try {
    const year = 2000 + parseInt(str.slice(0, 2));
    const month = parseInt(str.slice(2, 4)) - 1;
    const day = parseInt(str.slice(4, 6));
    const hour = parseInt(str.slice(6, 8));
    const min = parseInt(str.slice(8, 10));

    return new Date(Date.UTC(year, month, day, hour, min));
  } catch {
    return null;
  }
};


// -------------------------
// ACTIVE NOTAM CHECK
// -------------------------
const isNotamActive = (text) => {
  if (!text) return false;

  // Match FAA time block
  const match = text.match(/(\d{10})-(\d{10})/);

  // If no time → assume active (important)
  if (!match) return true;

  const start = parseNotamTime(match[1]);
  const end = parseNotamTime(match[2]);

  if (!start || !end) return true;

  const now = new Date();

  return now >= start && now <= end;
};


const plotObstructionMarkers = (obstructions) => {
  const map = mapRef.current;
  if (!map) return;

  obstructionMarkersRef.current.forEach(m => {
    try { m.remove(); } catch {}
  });

  obstructionMarkersRef.current = [];

  (obstructions || []).forEach(obs => {

    if (!obs.longitude || !obs.latitude) return;

    const el = document.createElement("div");
    el.className = "obstruction-marker";

    el.style.width = "12px";
    el.style.height = "12px";
    el.style.backgroundColor = "red";
    el.style.borderRadius = "50%";

    el.title = `${obs.text}\nHeight: ${obs.height || "Unknown"}`;

    const heightLabel = document.createElement("div");
    heightLabel.textContent = obs.height || "Unknown";

    heightLabel.style.position = "absolute";
    heightLabel.style.color = "black";
    heightLabel.style.fontWeight = "bold";
    heightLabel.style.fontSize = "12px";
    heightLabel.style.transform = "translate(-50%, -120%)";
    heightLabel.style.backgroundColor = "white";
    heightLabel.style.borderRadius = "4px";
    heightLabel.style.padding = "1px 4px";

    el.appendChild(heightLabel);

    const marker = new mapboxgl.Marker(el)
      .setLngLat([obs.longitude, obs.latitude])
      .setPopup(
        new mapboxgl.Popup().setHTML(`
          <strong>Obstacle Info</strong><br/>
          ${obs.text}<br/>
          <strong>Height:</strong> ${obs.height || "Unknown"}
        `)
      )
      .addTo(map);

    marker.getElement().style.display = obstructionsOn ? "block" : "none";

    obstructionMarkersRef.current.push(marker);

  });
};


const fetchObstructions = async (icao) => {
  try {
    const res = await fetch(`${backendURL}/get_relevant_notams?airport=${icao}`);
    const data = await res.json();

    const notams = data.notams?.[icao] || [];

    const obstructions = parseObstructionNotams(notams);

    plotObstructionMarkers(obstructions);

  } catch (error) {
    console.error("Error fetching NOTAM obstructions:", error);
  }

  console.log("USING NOTAM OBSTRUCTIONS");
};
const parseObstructionNotams = (notams) => {
  const results = [];

  (notams || []).forEach(n => {
    const text = n.text?.toUpperCase();
    if (!text || !text.includes("OBST")) return;

    // 🔥 Improved regex:
    // Handles:
    // 415355.70N0875351.50W 960.0FT
    // 415355N0875351W 960FT
    const match = text.match(
      /(\d{6}(\.\d+)?[NS])\s*(\d{7}(\.\d+)?[EW]).*?(\d+(\.\d+)?)\s*FT/
    );

    if (!match) {
      console.log("❌ NO OBST MATCH:", text);
      return;
    }

    const latRaw = match[1];
    const lonRaw = match[3];
    const height = Math.round(parseFloat(match[5]));

    const latitude = convertDMSToDD(latRaw);
    const longitude = convertDMSToDD(lonRaw);

    if (latitude == null || longitude == null) {
      console.log("❌ BAD COORD CONVERSION:", latRaw, lonRaw);
      return;
    }

    results.push({
      latitude,
      longitude,
      height,
      text
    });
  });

  console.log("🧱 Parsed OBST:", results);

  return results;
};
const convertDMSToDD = (dms) => {
  if (!dms) return null;

  const dir = dms.slice(-1);
  const val = dms.slice(0, -1);

  let deg, min, sec;

  try {
    if (dir === "N" || dir === "S") {
      // Latitude: DDMMSS.SS
      deg = parseInt(val.slice(0, 2));
      min = parseInt(val.slice(2, 4));
      sec = parseFloat(val.slice(4));
    } else {
      // Longitude: DDDMMSS.SS
      deg = parseInt(val.slice(0, 3));
      min = parseInt(val.slice(3, 5));
      sec = parseFloat(val.slice(5));
    }

    if (isNaN(deg) || isNaN(min) || isNaN(sec)) return null;

    let dd = deg + (min / 60) + (sec / 3600);

    if (dir === "S" || dir === "W") dd *= -1;

    // 🔥 HARD VALIDATION (prevents your crash)
    if (dd < -180 || dd > 180) return null;

    return dd;

  } catch {
    return null;
  }
};

const detectNearestAirport = async () => {
  try {
    const map = mapRef.current;
    if (!map) return null;

    const zoom = map.getZoom();

    // 🚫 Do NOT detect if zoomed out too far
    if (zoom < 10) {
      return null;
    }

    const center = map.getCenter();
    if (!center?.lat || !center?.lng) return null;

    const res = await axios.get(`${backendURL}/get_nearest_airport`, {
      params: { lat: center.lat, lon: center.lng }
    });

    if (!res.data?.icao) return null;

    return res.data;
  } catch (err) {
    console.error("❌ Failed to detect nearest airport:", err);
    return null;
  }
};


const loadNOAARadarLayer = (mapInstance) => {
  // Avoid double-adding the source or layer
  if (mapInstance.getSource('noaa-radar')) return;
  if (mapInstance.getLayer('noaa-radar-layer')) return;

  mapInstance.addSource('noaa-radar', {
    type: 'raster',
    tiles: [
      'https://opengeo.ncep.noaa.gov/geoserver/conus/conus_bref_qcd/ows?service=WMS&request=GetMap&format=image/png&transparent=true&version=1.1.1&layers=conus_bref_qcd&styles=&width=256&height=256&srs=EPSG:3857&bbox={bbox-epsg-3857}'
    ],
    tileSize: 256,
  });

  mapInstance.addLayer({
    id: 'noaa-radar-layer',
    type: 'raster',
    source: 'noaa-radar',
    paint: {
      'raster-opacity': 0.6,
    },
  });
};

function plotFiconSegments(ficonData, runwayData) {
  const map = mapRef.current;

  if (!map) {
    console.warn("🛑 mapRef.current is null in plotFiconSegments");
    return;
  }

  console.log("🟢 Plotting FICON numbers dynamically...");

  const normalize = (r) =>
    r.toUpperCase().replace(/^0/, "").trim();

  // -----------------------------
  // GROUP BY AIRPORT
  // -----------------------------
  const groupedByAirport = {};

  ficonData.forEach(entry => {
    const icao = entry.icao;
    if (!groupedByAirport[icao]) groupedByAirport[icao] = [];
    groupedByAirport[icao].push(entry);
  });

  // -----------------------------
  // LOOP AIRPORTS
  // -----------------------------
Object.keys(groupedByAirport).forEach(icao => {

  const ficonEntries =
    groupedByAirport[icao];

  console.log(
    "🚨 ICAO:",
    icao
  );

  console.log(
    "🚨 RUNWAY DATA RECEIVED:",
    runwayData
  );

  const airportRunways =
    runwayData;

  console.log(
    "🚨 AIRPORT RUNWAYS:",
    airportRunways
  );

console.log(
  "🚨 AIRPORT RUNWAYS:",
  airportRunways
);

    if (!airportRunways) {
      console.warn(`❌ No runway data for ${icao}`);
      return;
    }

    // -----------------------------
    // CLEAN OLD FICON LAYERS
    // -----------------------------
    Object.keys(map.style._layers || {}).forEach(layerId => {
      if (layerId.startsWith(`ficon-${icao}-`)) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(layerId)) map.removeSource(layerId);
      }
    });

    const runwayNames = Object.keys(airportRunways);
    const matchedPairs = [];

    // -----------------------------
    // BUILD RUNWAY PAIRS
    // -----------------------------
    runwayNames.forEach(name1 => {
      const name2 = findReciprocalRunway(name1, runwayNames);

      if (name2 && !matchedPairs.some(([a, b]) => a === name2 && b === name1)) {
        matchedPairs.push([name1, name2]);
      }
    });

    // -----------------------------
    // PLOT EACH RUNWAY
    // -----------------------------
    matchedPairs.forEach(([rwy1, rwy2]) => {

      const data1 = airportRunways[rwy1];
      const data2 = airportRunways[rwy2];

      if (!data1 || !data2) return;

      const ficonEntry = ficonEntries.find(entry => {
        // Prefer the structured runway list supplied by the backend.
        // Example:
        // runway:  "10R/28L"
        // runways: ["10R", "28L"]
        //
        // Fall back to splitting entry.runway so older API responses
        // remain compatible.
        const entryRunways =
          Array.isArray(entry.runways) && entry.runways.length
            ? entry.runways
            : String(entry.runway || "").split("/");

        const normalizedEntryRunways =
          entryRunways.map(rwy => normalize(rwy));

        return (
          normalizedEntryRunways.includes(normalize(rwy1)) ||
          normalizedEntryRunways.includes(normalize(rwy2))
        );
      });

      console.log(
  "🚨 CHECKING PAIR:",
  rwy1,
  rwy2
);

console.log(
  "🚨 MATCHED FICON:",
  ficonEntry
);

      if (!ficonEntry || !Array.isArray(ficonEntry.ficon_values) || ficonEntry.ficon_values.length !== 3) {
        return;
      }

      const { ficon_values } = ficonEntry;

      const start = [data1.lon1, data1.lat1];
      const end = [data2.lon1, data2.lat1];

      const deltaLat = (end[1] - start[1]) / 3;
      const deltaLon = (end[0] - start[0]) / 3;

      // -----------------------------
      // DRAW 3 SEGMENTS
      // -----------------------------
      for (let i = 0; i < 3; i++) {

        const segmentStart = [
          start[0] + i * deltaLon,
          start[1] + i * deltaLat
        ];

        const segmentEnd = [
          start[0] + (i + 1) * deltaLon,
          start[1] + (i + 1) * deltaLat
        ];

        const center = [
          (segmentStart[0] + segmentEnd[0]) / 2,
          (segmentStart[1] + segmentEnd[1]) / 2
        ];

        const value = ficon_values[2 - i]; // reversed logic

        const layerId = `ficon-${icao}-${rwy1}-${i}`;

try {

  console.log(
    "🚨 ADDING FICON:",
    layerId,
    center,
    value
  );

  map.addSource(layerId, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: center
      },
      properties: {
        value: value.toString()
      }
    }
  });

  map.addLayer({
    id: layerId,
    type: "symbol",
    source: layerId,
    layout: {
      "text-field": ["get", "value"],
      "text-size": 30,
      "text-font": [
        "DIN Offc Pro Medium",
        "Arial Unicode MS Bold"
      ],
      "text-anchor": "center",

      // FORCE ON FOR TESTING
      "visibility": "visible"
    },
    paint: {

      // FORCE RED FOR TESTING
      "text-color": "#ff0000",

      "text-halo-color": "#ffffff",
      "text-halo-width": 3
    },
    minzoom: 0,
    maxzoom: 24
  });

  console.log(
    "🟢 ADDED FICON:",
    layerId
  );

}
catch (e) {

  console.error(
    "❌ FICON ADD ERROR:",
    layerId,
    e
  );

}
      }
    });
  });
}


function findReciprocalRunway(name, runwayList) {
  // Match by numeric reciprocal + L/R if needed
  const match = name.match(/(\d{2})([LRC]?)/);
  if (!match) return null;

  const number = parseInt(match[1], 10);
  const reciprocalNumber = ((number + 18) % 36) || 36;
  const reciprocalStr = reciprocalNumber.toString().padStart(2, '0');

  // Try all possible combinations of L/R/C
  const suffixes = ["", "L", "R", "C"];
  for (const suffix of suffixes) {
    const candidate = reciprocalStr + suffix;
    if (runwayList.includes(candidate)) return candidate;
  }

  return null;
}




const updateAircraftMarker = (position, heading = 0) => {

  const map = mapRef.current;

  if (
    !map ||
    !position ||
    position.length !== 2
  ) {
    return;
  }

  // =====================================
  // ✈️ CREATE AIRCRAFT MARKER
  // =====================================

  if (!aircraftMarkerRef.current) {

    // Outer container belongs to Mapbox.
    // DO NOT apply transforms to this element.
    const markerEl =
      document.createElement("div");

    markerEl.style.width = "36px";
    markerEl.style.height = "36px";

    markerEl.style.display = "flex";
    markerEl.style.alignItems = "center";
    markerEl.style.justifyContent = "center";

    // Inner element is ours to rotate.
    const planeEl =
      document.createElement("div");

    planeEl.className =
      "live-aircraft-icon";

    planeEl.innerHTML = "✈";

planeEl.style.fontSize = "25px";
planeEl.style.lineHeight = "1";

planeEl.style.color = "#ffffff";

planeEl.style.width = "34px";
planeEl.style.height = "34px";

planeEl.style.display = "flex";
planeEl.style.alignItems = "center";
planeEl.style.justifyContent = "center";

planeEl.style.background =
  "rgba(20, 20, 20, 0.88)";

planeEl.style.border =
  "2px solid #ffffff";

planeEl.style.borderRadius =
  "50%";

planeEl.style.boxShadow =
  "0 0 0 2px rgba(0,0,0,0.65), 0 2px 6px rgba(0,0,0,0.8)";

planeEl.style.textShadow =
  "0 1px 2px #000";

    // Important: rotation happens on INNER element
    planeEl.style.transform =
      `rotate(${Number(heading) || -90}deg)`;

    planeEl.style.transformOrigin =
      "50% 50%";

    planeEl.style.transition =
      "transform 0.35s linear";

    planeEl.style.pointerEvents =
      "none";

    markerEl.appendChild(
      planeEl
    );

    aircraftMarkerRef.current =
      new mapboxgl.Marker({

        element: markerEl,

        rotationAlignment: "map",

        pitchAlignment: "map"

      })
        .setLngLat(position)
        .addTo(map);

    console.log(
      "✈️ Aircraft marker created",
      position,
      heading
    );

  } else {

    // =====================================
    // 📍 UPDATE LIVE POSITION
    // =====================================

    aircraftMarkerRef.current
      .setLngLat(position);
  }


  // =====================================
  // 🧭 UPDATE LIVE HEADING
  // Rotate INNER icon only
  // =====================================

  const markerEl =
    aircraftMarkerRef.current
      .getElement();

  const planeEl =
    markerEl?.querySelector(
      ".live-aircraft-icon"
    );

  if (planeEl) {

    const liveHeading =
      Number(heading) || 0;

planeEl.style.transform =
  `rotate(${liveHeading - 90}deg)`;

    console.log(
      "🧭 AIRCRAFT HEADING:",
      liveHeading
    );
  }
};
async function plotTaxiwayClosures(icao, taxiwayOn = true) {
  try {
    const map = mapRef.current;

    if (!operationalAccess) {
      console.log("🔒 Operational access blocked");
      return;
    }

    if (!map) {
      console.warn("⚠️ plotTaxiwayClosures: map not ready");
      return;
    }

    if (!icao || typeof icao !== "string") {
      console.warn(
        "⚠️ plotTaxiwayClosures invalid ICAO:",
        icao
      );
      return;
    }

    console.log(
      "🚧 Loading centralized taxiway closures for:",
      icao
    );

    const layerId = `twy-closures-${icao}`;
    const sourceId = `twy-closures-${icao}`;

    // Remove previous depiction.
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }

    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    // -------------------------------------------------
    // CENTRAL TAXIWAY STATUS ENGINE — PORT 5002
    // -------------------------------------------------

    const response = await axios.get(
      `https://ficon-x.com/route/taxiway-status?airport=${icao}`
    );

    const resolved =
      response.data?.closures || [];

    console.log(
      `📡 CENTRAL TAXIWAY CLOSURES ${icao}:`,
      resolved.length
    );

    // Backend has already selected the correct geometry.
    const closureFeatures = resolved
      .map((item) => {
        if (!item?.feature) {
          return null;
        }

        const clone = JSON.parse(
          JSON.stringify(item.feature)
        );

        clone.properties = {
          ...(clone.properties || {}),
          icao,
          notamText:
            item.notam?.text || "",
          taxiway:
            item.taxiway || "",
          closureStart:
            item.start || null,
          closureEnd:
            item.end || null,
        };

        return clone;
      })
      .filter(Boolean);

    if (!closureFeatures.length) {
      console.log(
        `🟢 No active taxiway closures for ${icao}`
      );
      return;
    }

    // -------------------------------------------------
    // DRAW
    // -------------------------------------------------

    map.addSource(sourceId, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: closureFeatures,
      },
    });

    map.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      paint: {
        "line-color": "#FF0000",
        "line-width": 3,
        "line-dasharray": [1.5, 1.5],
      },
      layout: {
        visibility:
          taxiwayOn
            ? "visible"
            : "none",
      },
      minzoom: 10,
    });

    console.log(
      `✅ Plotted ${closureFeatures.length} centralized taxiway closure segments`
    );

  } catch (err) {
    console.error(
      `❌ Failed to plot centralized taxiway closures for ${icao}:`,
      err
    );
  }
}

async function plotRunwayClosures(map, icao) {
  try {
    if (!map || !icao) return;

    console.log("🛬 plotRunwayClosures called for:", icao);

    // -----------------------------
    // CLEAN OLD RUNWAY CLOSURES ONLY WHEN AIRPORT CHANGES
    // -----------------------------
    if (window._lastRwyClosureAirport !== icao) {
      window._lastRwyClosureAirport = icao;

      const style = map.getStyle();

      if (style?.layers) {
        style.layers.forEach(layer => {
          if (layer.id.startsWith("rwy-closed-")) {
            if (map.getLayer(layer.id)) {
              map.removeLayer(layer.id);
            }
          }
        });
      }

      if (style?.sources) {
        Object.keys(style.sources).forEach(sourceId => {
          if (sourceId.startsWith("rwy-closed-")) {
            if (map.getSource(sourceId)) {
              map.removeSource(sourceId);
            }
          }
        });
      }
    }

    // -----------------------------
    // FETCH DATA
    // -----------------------------
    const [rwyColorRes, notamRes] = await Promise.all([
      axios.get(`${backendURL}/get_runway_colors?airport=${icao}`),
      axios.get(`${backendURL}/get_relevant_notams?airport=${icao}`),
    ]);

    const runwayArray = rwyColorRes.data?.runways || [];
    const closedRunways = notamRes.data?.closed_runways?.[icao] || [];

    console.log("🚫 ACTIVE CLOSED RUNWAYS:", icao, closedRunways);

    if (!runwayArray.length) {
      console.warn("⚠️ No runway data returned.");
      return;
    }

    if (!closedRunways.length) {
      console.log(`🟢 No ACTIVE closed runways for ${icao}`);
      return;
    }

    // -----------------------------
    // BUILD RUNWAY MAP
    // -----------------------------
    const rwyMap = {};

    for (const rwy of runwayArray) {
      const key = rwy.runway?.toUpperCase();
      if (!key) continue;

      rwyMap[key] = {
        lat1: rwy.threshold.lat,
        lon1: rwy.threshold.lon,
        lat2: rwy.final_point.lat,
        lon2: rwy.final_point.lon,
      };
    }

    const plotted = new Set();

    const normalize = (r) =>
      r.toUpperCase().replace(/^0/, "").trim();

    // -----------------------------
    // PLOT CLOSED RUNWAYS (SMOOTH UPDATE)
    // -----------------------------
    for (const raw of closedRunways) {
      const rwy1 = normalize(raw);

      if (plotted.has(rwy1)) continue;

      const data1 = rwyMap[rwy1];
      if (!data1) {
        console.warn("⚠️ Missing runway geometry for:", rwy1);
        continue;
      }

      const rwy2 = findTrueReciprocalRunway(rwy1, rwyMap);
      const data2 = rwy2 ? rwyMap[rwy2] : null;

      if (!data2) continue;

      const layerId = `rwy-closed-${icao}-${rwy1}`;

      const geojson = {
        type: "FeatureCollection",
        features: [{
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [
              [data1.lon1, data1.lat1],
              [data2.lon1, data2.lat1],
            ],
          },
          properties: {},
        }],
      };

      // 🔥 NO MORE REMOVE/ADD → JUST UPDATE
      if (map.getSource(layerId)) {
        map.getSource(layerId).setData(geojson);
      } else {
        map.addSource(layerId, {
          type: "geojson",
          data: geojson,
        });

        map.addLayer({
          id: layerId,
          type: "line",
          source: layerId,
          paint: {
            "line-color": "#FF0000",
            "line-width": 4,
            "line-dasharray": [2, 2],
          },
          layout: { visibility: "visible" },
          minzoom: 10,
        });
      }

      plotted.add(rwy1);
      if (rwy2) plotted.add(rwy2);

      console.log(`✅ Plotted ACTIVE closed runway: ${rwy1} ↔ ${rwy2}`);
    }

  } catch (err) {
    console.error(`❌ Failed to plot runway closures for ${icao}:`, err);
  }
}
async function plotWingspanRestrictions(icao, wingspanOn = true) {
  try {
    const map = mapRef.current;

    if (!map) {
      console.warn("⚠️ plotWingspanRestrictions: map not ready");
      return;
    }

    if (!icao || typeof icao !== "string") {
      console.warn(
        "⚠️ plotWingspanRestrictions called with invalid ICAO:",
        icao
      );
      return;
    }

    const sourceId = `twy-wingspan-${icao}`;
    const layerId = `twy-wingspan-${icao}`;

    // Always remove the previous depiction first.
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }

    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    if (!wingspanOn) {
      console.log("🟡 Wingspan layer disabled by state");
      return;
    }

    console.log(
      `🟠 Loading centralized wingspan restrictions for ${icao}`
    );

    // -------------------------------------------------
    // CENTRAL TAXIWAY STATUS ENGINE — PORT 5002
    // -------------------------------------------------

    const response = await axios.get(
      `https://ficon-x.com/route/taxiway-status?airport=${icao}`
    );

    const resolved =
      response.data?.wingspan_restrictions || [];

    console.log(
      `📡 CENTRAL WINGSPAN RESTRICTIONS ${icao}:`,
      resolved.length
    );

    const features = [];

    for (const item of resolved) {
      if (!item?.feature) {
        continue;
      }

      const clone = JSON.parse(
        JSON.stringify(item.feature)
      );

      clone.properties = {
        ...(clone.properties || {}),
        icao,
        twy: item.taxiway || "",
        notamText: item.notam?.text || "",
        wingspanLimit:
          item.max_wingspan_ft != null
            ? `${item.max_wingspan_ft}FT`
            : "",
      };

      // Preserve the behavior of the existing web layer.
      if (clone.geometry?.type === "MultiLineString") {
        for (const line of clone.geometry.coordinates) {
          features.push({
            type: "Feature",
            geometry: {
              type: "LineString",
              coordinates: line,
            },
            properties: clone.properties,
          });
        }
      } else {
        features.push(clone);
      }
    }

    if (!features.length) {
      console.log(
        `🛑 No active wingspan restriction segments for ${icao}`
      );
      return;
    }

    // -------------------------------------------------
    // DRAW
    // -------------------------------------------------

    map.addSource(sourceId, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features,
      },
    });

    map.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      paint: {
        "line-color": "#FFA500",
        "line-width": 4,
        "line-dasharray": [2, 2],
      },
      layout: {
        visibility: "visible",
      },
      minzoom: 10,
    });

    console.log(
      `🟠 Plotted ${features.length} centralized wingspan segments for ${icao}`
    );

  } catch (err) {
    console.error(
      `❌ Failed to plot centralized wingspan restrictions for ${icao}:`,
      err
    );
  }
}

const [pdpInstructions, setPdpInstructions] = useState([]);

const appendPdpLog = (msg) => {
  setPdpLogs((prev) => [...prev.slice(-50), msg]);  // Limit to last 50 entries
};

const updatePdpInstructionDisplay = (callsign, instruction, readback) => {
  setPdpInstructions((prev) => [
    ...prev,
    { callsign, instruction, readback, time: new Date() },
  ]);
};

const [activeInstruction, setActiveInstruction] = useState(null);
const [kissData, setKissData] = useState({
  heading: { value: null, stale: false },
  altitude: { value: null, stale: false },
  speed: { value: null, stale: false },
});


useEffect(() => {
  console.log("🔥 ZOOM EFFECT TRIGGERED:", mapZoom);

  // 🚨 CRITICAL FIX — ignore React-triggered zoom glitch
  if (mapZoom === 0) {
    console.warn("🚫 Ignoring fake zoom=0 in zoom effect");
    return;
  }

  const visible = mapZoom >= 10;

  applyRunwayColorsVisibility(visible && runwayColorsOn);
  applyFiconVisibility(visible && ficonOn);
  applyTaxiwayVisibility(visible && taxiwayOn);
  applyWingspanVisibility(visible && wingspanOn);
  applyObstructionsVisibility(visible && obstructionsOn);
  applyCrosswindVisibility(visible && crosswindOn);

}, [
  mapZoom,
  runwayColorsOn,
  ficonOn,
  taxiwayOn,
  wingspanOn,
  obstructionsOn,
  crosswindOn
]);

useEffect(() => {
  const handleRouteHazardScore = (data) => {
    console.log("📡 Received route_hazard_score", data);
    if (data.coords && data.coords.length >= 2) {
      plotRoute(data); // ✅ Same as used for manual route plotting
    }
  };

  socket.on("route_hazard_score", handleRouteHazardScore);

  return () => {
    // 🔌 Clean up on component unmount or re-render
    socket.off("route_hazard_score", handleRouteHazardScore);
  };
}, []);




useEffect(() => {
  // ✅ Confirm connection
  pdpSocket.on("connect", () => {
    console.log("🟢 PDP Socket connected");
  });

// 🎙️ PDP Console Transcript Log (handles both string and object formats)
pdpSocket.on("pdp_console_log", (data) => {
  console.log("🎧 PDP CONSOLE LOG RECEIVED:", data);

  const msg = typeof data === "string" ? data : data.message;
  console.log("🪵 Parsed message value:", JSON.stringify(msg));

  setPdpLogs((prev) => {
    const updated = [...prev, msg];
    return updated.slice(-100); // ✅ keep only the last 100 log entries
  });

  setPdpTranscript(msg);
});


  let lastInstruction = null;

  pdpSocket.on("pdp_instruction_pair", (data) => {
    console.log("📦 PDP Instruction Pair received:", data);
    const { callsign, instruction, readback } = data;
  
    setPdpCallsignSpoken(callsign);
  
    if (instruction && Object.keys(instruction).length > 0) {
      lastInstruction = instruction;
    }
  
    setActiveInstruction({
      instruction: lastInstruction || {},
      readback: readback || {}
    });
  
    setKissData((prev) => {
      const updated = { ...prev };
  
      ["heading", "altitude", "speed"].forEach((key) => {
        const instrVal = (lastInstruction || {})[key];
        const readVal = (readback || {})[key];
  
        if (instrVal && readVal) {
          if (instrVal === readVal) {
            // ✅ Match — update and mark as fresh
            updated[key] = { value: instrVal, stale: false };
          } else {
            // ❌ Mismatch — keep previous value, mark as stale
            updated[key] = { ...prev[key], stale: true };
          }
        }
      });
  

    return updated;
  });
});


  // 💓 PDP Heartbeat
  pdpSocket.on("pdp_heartbeat", (data) => {
    console.log("💓 PDP Heartbeat from 5001:", data);
  });

  // 🔌 Cleanup handlers on unmount
  return () => {
    pdpSocket.off("connect");
    pdpSocket.off("pdp_console_log");
    pdpSocket.off("pdp_instruction_pair");
    pdpSocket.off("pdp_heartbeat");
  };
}, []); // ✅ Run once on component mount

useEffect(() => {

  console.log(
    "🚨 FETCH FICON EFFECT",
    selectedIcao
  );

  const fetchFiconData = async () => {

    try {

      console.log(
        "🚨 ENTERED fetchFiconData()"
      );

      const [notamRes, runwayRes] =
        await Promise.all([

          axios.get(
            `${backendURL}/get_relevant_notams?airport=${selectedIcao}`
          ),

          axios.get(
            `${backendURL}/get_runway_coords?airport=${selectedIcao}`
          )

        ]);

      console.log(
        "🚨 GOT NOTAM RESPONSE"
      );

      console.log(
        "🚨 GOT RUNWAY RESPONSE"
      );

      const runwayJson =
        runwayRes.data;

      const ficonData =
        notamRes.data?.ficon_data?.[selectedIcao] || [];

      console.log(
        "🚨 FICON DATA:",
        ficonData
      );

      console.log(
        "🚨 RUNWAY DATA:",
        runwayJson
      );

      setRunwayData(runwayJson);

      if (
        ficonData &&
        ficonData.length > 0
      ) {

        console.log(
          "🟢 Plotting FICON numbers dynamically..."
        );

        plotFiconSegments(
          ficonData,
          runwayJson
        );

      } else {

        console.log(
          `⚠️ No FICON entries returned for ${selectedIcao}`
        );

      }

    } catch (error) {

      console.error(
        "❌ Failed to fetch FICON data or runway data:",
        error
      );

    }

  };

  if (selectedIcao) {

    console.log(
      "🚨 CALLING fetchFiconData()",
      selectedIcao
    );

    fetchFiconData();

  } else {

    console.log(
      "🚨 selectedIcao is empty"
    );

  }

}, [selectedIcao]);


const toggleEchoTops = () => {
  const map = mapRef.current;
  if (!map) return;

  const visibility = map.getLayoutProperty("echoTops-layer", "visibility");
  map.setLayoutProperty("echoTops-layer", "visibility", visibility === "none" ? "visible" : "none");
};

const fetchAndShowAirportChart = async (icao) => {
  try {
    const res = await fetch("/airportcharts/airport_chart_bounds.json");
    const boundsData = await res.json();
    const entry = boundsData[icao];
    if (!entry || !entry.image || !entry.bounds) return;

    const map = mapRef.current;
    if (!map) return;

    const imageId = `chart-${icao}`;

    if (map.getLayer(imageId)) map.removeLayer(imageId);
    if (map.getSource(imageId)) map.removeSource(imageId);

    map.addSource(imageId, {
      type: "image",
      url: entry.image,
      coordinates: [
        [entry.bounds[0][0], entry.bounds[1][1]],
        [entry.bounds[1][0], entry.bounds[1][1]],
        [entry.bounds[1][0], entry.bounds[0][1]],
        [entry.bounds[0][0], entry.bounds[0][1]],
      ],
    });

    map.addLayer({
      id: imageId,
      source: imageId,
      type: "raster",
      paint: { "raster-opacity": 0.75 },
    });

    console.log(`🗺️ Airport chart overlay loaded for ${icao}`);
  } catch (err) {
    console.error("❌ Failed to load chart overlay:", err);
  }
};

useEffect(() => {

  if (
    !mapRef.current &&
    mapContainerRef.current
  ) {

    const mapInstance =
      new mapboxgl.Map({

        container:
          mapContainerRef.current,

        style:
          "mapbox://styles/mapbox/streets-v11",

        center:
          [-95.7129, 37.0902],

        zoom: 5,
      });

    mapRef.current =
      mapInstance;

    setMap(mapInstance);

    // =====================================
// 🧪 MAPBOX SOURCE / TILE ERROR DEBUG
// =====================================

mapInstance.on("error", (e) => {

  console.error(
    "🚨 MAPBOX ERROR EVENT:",
    e
  );

  console.error(
    "🚨 SOURCE ID:",
    e?.sourceId
  );

  console.error(
    "🚨 ERROR:",
    e?.error
  );

  console.error(
    "🚨 TILE:",
    e?.tile
  );

});

    // =====================================
    // ✋ USER MAP INTERACTION
    // =====================================

// =====================================
// ✋ PILOT MANUAL MAP PAN
// =====================================

mapInstance.on(
  "dragstart",
  () => {

    userInteractingRef.current =
      true;

    // Permanently detach camera until
    // pilot presses RECENTER.
    setFollowAircraft(false);

    console.log(
      "✋ Manual map pan — aircraft follow paused"
    );
  }
);

mapInstance.on(
  "dragend",
  () => {

    userInteractingRef.current =
      false;

    console.log(
      "🗺️ Manual map exploration active"
    );
  }
);

    mapInstance.on(
      "load",
      () => {

        console.log(
          "🗺️ Map fully loaded"
        );

        setMapReady(true);
      }
    );
  

    let lastICAO = null;
    setShowAirportMenu(mapInstance.getZoom() >= 12);

    /* ======================================================
       MAP LOAD — CLEAN SINGLE PIPELINE
       ====================================================== */
    mapInstance.on("load", async () => {
      console.log("🗺️ Map fully loaded");

      // Rehydrate route first
      await replotActiveRouteIfExists(mapInstance);

      // Run detection once
      await handleAirportChange();

      /* ---------- Echo Tops ---------- */
      mapInstance.addSource("echoTops", {
        type: "raster",
        tiles: [
          "https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0r/{z}/{x}/{y}.png",
        ],
        tileSize: 256,
      });

      mapInstance.addLayer({
        id: "echoTops-layer",
        type: "raster",
        source: "echoTops",
        paint: { "raster-opacity": 0.65 },
        layout: { visibility: "visible" },
      });
    });

mapInstance.on("mousedown", () => {
  userInteractingRef.current = true;
});

mapInstance.on("wheel", () => {
  userInteractingRef.current = true;
});

mapInstance.on("touchstart", () => {
  userInteractingRef.current = true;
});   

mapInstance.on("moveend", async () => {
  const zoom = mapInstance.getZoom();

  console.log("🧪 moveend zoom:", zoom);

  // 🚨 CRITICAL FIX — ignore bogus zoom events
  if (zoom === 0) {
    console.warn("🚫 Ignoring fake zoom=0 event (UI interaction)");
    return;
  }

// 🔐 OPERATIONAL ACCESS GATE
if (zoom < 10) {

  console.log(
    "🔒 Airport operational mode blocked"
  );

  removeAirportLayers();

  setShowAirportMenu(false);

  // 🔥 RESET DISMISS STATE
  setDismissedAirportMenu(false);

  return;
}

// ✅ AUTHORIZED OPERATIONAL MODE
// ✅ AUTHORIZED OPERATIONAL MODE

if (!dismissedAirportMenu) {

  setShowAirportMenu(true);
}

await handleAirportChange();
});
    /* ======================================================
       STYLE LOAD SAFE REHYDRATION
       ====================================================== */
    mapInstance.on("style.load", async () => {
      console.log("🎨 Map style reloaded");

      await replotActiveRouteIfExists(mapInstance);
      await handleAirportChange();
      loadNOAARadarLayer(mapInstance);
    });

    /* ======================================================
       AIRPORT CHANGE HANDLER
       ====================================================== */
    async function handleAirportChange() {
      const zoom = mapInstance.getZoom();
      const nearest = await detectNearestAirport();

      if (!operationalAccess) {

  console.log(
    "🔒 Operational access blocked"
  );

  return;
}

      if (!nearest || typeof nearest.icao !== "string" || nearest.icao.length !== 4) {
        console.warn("⚠️ Invalid nearest airport:", nearest);
        return;
      }

      const icao = nearest.icao.toUpperCase();

      console.log(
  "🛬 SETTING SELECTED ICAO:",
  icao
);

setSelectedIcao(icao);

      const airportChanged = icao !== lastICAO;

      if (airportChanged) {
        lastICAO = icao;
        console.log("✈️ Airport changed to:", icao);

        fetchRunwayColors(icao);
        fetchObstructions(icao);

        if (zoom >= 10) {
          await fetchAndShowAirportChart(icao);
        }
      }

      // 🔥 ALWAYS replot overlays (even if ICAO didn’t change)
      plotRunwayClosures(mapInstance, icao);
      plotWingspanRestrictions(icao, true);
      plotTaxiwayClosures(icao, true);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }
}, []);

// =====================================
// ✈️ LIVE TRAFFIC MARKERS
// =====================================
useEffect(() => {

  const mapInstance = mapRef.current;

  if (!mapInstance || !mapReady) {
    return;
  }

  // Traffic switched off — remove all markers.
  if (!trafficOn) {

    Object.values(
      trafficMarkersRef.current
    ).forEach(entry => {
      try {
        entry.marker.remove();
      } catch {}
    });

    trafficMarkersRef.current = {};
    return;
  }

  const activeIds = new Set();

  trafficAircraft.forEach(ac => {

    if (
      !ac?.id ||
      !Number.isFinite(Number(ac.lat)) ||
      !Number.isFinite(Number(ac.lon))
    ) {
      return;
    }

    const id = String(ac.id);
    activeIds.add(id);

    const callsign =
      ac.callsign ||
      ac.registration ||
      id.toUpperCase();

    const altitudeLabel =
      ac.on_ground
        ? "GND"
        : Number.isFinite(Number(ac.altitude_ft))
        ? `${Math.round(Number(ac.altitude_ft) / 100)}`
            .padStart(3, "0")
        : "---";

    const track =
      Number.isFinite(Number(ac.track_deg))
        ? Number(ac.track_deg)
        : 0;

    let entry =
      trafficMarkersRef.current[id];

    if (!entry) {

      const container =
        document.createElement("div");

      container.className =
        "ficonx-traffic-marker";

      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.alignItems = "center";
      container.style.cursor = "pointer";
      container.style.userSelect = "none";

      const aircraftEl =
        document.createElement("div");

      aircraftEl.textContent = "✈";
      aircraftEl.style.fontSize = "20px";
      aircraftEl.style.lineHeight = "20px";
      aircraftEl.style.color = "#00e5ff";
      aircraftEl.style.textShadow =
        "0 0 3px #000, 0 0 3px #000";
      aircraftEl.style.transformOrigin =
        "50% 50%";

      const label =
        document.createElement("div");

      label.style.marginTop = "1px";
      label.style.padding = "1px 3px";
      label.style.borderRadius = "3px";
      label.style.background =
        "rgba(0,0,0,0.72)";
      label.style.color = "#ffffff";
      label.style.fontSize = "10px";
      label.style.fontWeight = "700";
      label.style.lineHeight = "12px";
      label.style.whiteSpace = "nowrap";
      label.style.pointerEvents = "none";

      container.appendChild(aircraftEl);
      container.appendChild(label);

      const popup =
        new mapboxgl.Popup({
          offset: 18,
          closeButton: true,
          closeOnClick: true,
        });

      const markerObject =
        new mapboxgl.Marker({
          element: container,
          anchor: "center",
        })
          .setLngLat([
            Number(ac.lon),
            Number(ac.lat),
          ])
          .setPopup(popup)
          .addTo(mapInstance);

      entry = {
        marker: markerObject,
        aircraftEl,
        label,
        popup,
      };

      trafficMarkersRef.current[id] =
        entry;
    }

    entry.marker.setLngLat([
      Number(ac.lon),
      Number(ac.lat),
    ]);

    // Unicode airplane points roughly east by default.
    // Convert aviation track (0 = north)
    // to screen rotation.
    entry.aircraftEl.style.transform =
      `rotate(${track - 90}deg)`;

    entry.label.textContent =
      `${callsign} ${altitudeLabel}`;

    const verticalRate =
      Number.isFinite(
        Number(ac.vertical_rate_fpm)
      )
        ? Number(ac.vertical_rate_fpm)
        : null;

    const verticalText =
      verticalRate === null
        ? "N/A"
        : verticalRate > 100
        ? `↑ ${Math.round(verticalRate)} fpm`
        : verticalRate < -100
        ? `↓ ${Math.abs(
            Math.round(verticalRate)
          )} fpm`
        : "Level";

    entry.popup.setHTML(`
      <div style="min-width:150px">
        <strong>${callsign}</strong><br>
        Type: ${ac.aircraft_type || "N/A"}<br>
        Registration: ${ac.registration || "N/A"}<br>
        Altitude: ${
          ac.on_ground
            ? "Ground"
            : ac.altitude_ft != null
            ? `${ac.altitude_ft} ft`
            : "N/A"
        }<br>
        Groundspeed: ${
          ac.groundspeed_kt != null
            ? `${Math.round(ac.groundspeed_kt)} kt`
            : "N/A"
        }<br>
        Track: ${
          ac.track_deg != null
            ? `${Math.round(ac.track_deg)}°`
            : "N/A"
        }<br>
        Vertical: ${verticalText}<br>
        Squawk: ${ac.squawk || "N/A"}
      </div>
    `);

  });

  // Remove aircraft no longer present.
  Object.keys(
    trafficMarkersRef.current
  ).forEach(id => {

    if (activeIds.has(id)) {
      return;
    }

    try {
      trafficMarkersRef.current[
        id
      ].marker.remove();
    } catch {}

    delete trafficMarkersRef.current[id];
  });

}, [
  trafficAircraft,
  trafficOn,
  mapReady
]);

// =====================================
// ✈️ LIVE TRAFFIC MARKERS
// =====================================
useEffect(() => {

  const mapInstance = mapRef.current;

  if (!mapInstance || !mapReady) {
    return;
  }

  // Traffic switched off — remove all markers.
  if (!trafficOn) {

    Object.values(
      trafficMarkersRef.current
    ).forEach(entry => {
      try {
        entry.marker.remove();
      } catch {}
    });

    trafficMarkersRef.current = {};
    return;
  }

  const activeIds = new Set();

  trafficAircraft.forEach(ac => {

    if (
      !ac?.id ||
      !Number.isFinite(Number(ac.lat)) ||
      !Number.isFinite(Number(ac.lon))
    ) {
      return;
    }

    const id = String(ac.id);
    activeIds.add(id);

    const callsign =
      ac.callsign ||
      ac.registration ||
      id.toUpperCase();

    const altitudeLabel =
      ac.on_ground
        ? "GND"
        : Number.isFinite(Number(ac.altitude_ft))
        ? `${Math.round(Number(ac.altitude_ft) / 100)}`
            .padStart(3, "0")
        : "---";

    const track =
      Number.isFinite(Number(ac.track_deg))
        ? Number(ac.track_deg)
        : 0;

    let entry =
      trafficMarkersRef.current[id];

    if (!entry) {

      const container =
        document.createElement("div");

      container.className =
        "ficonx-traffic-marker";

      container.style.display = "flex";
      container.style.flexDirection = "column";
      container.style.alignItems = "center";
      container.style.cursor = "pointer";
      container.style.userSelect = "none";

      const aircraftEl =
        document.createElement("div");

      aircraftEl.textContent = "✈";
      aircraftEl.style.fontSize = "20px";
      aircraftEl.style.lineHeight = "20px";
      aircraftEl.style.color = "#00e5ff";
      aircraftEl.style.textShadow =
        "0 0 3px #000, 0 0 3px #000";
      aircraftEl.style.transformOrigin =
        "50% 50%";

      const label =
        document.createElement("div");

      label.style.marginTop = "1px";
      label.style.padding = "1px 3px";
      label.style.borderRadius = "3px";
      label.style.background =
        "rgba(0,0,0,0.72)";
      label.style.color = "#ffffff";
      label.style.fontSize = "10px";
      label.style.fontWeight = "700";
      label.style.lineHeight = "12px";
      label.style.whiteSpace = "nowrap";
      label.style.pointerEvents = "none";

      container.appendChild(aircraftEl);
      container.appendChild(label);

      const popup =
        new mapboxgl.Popup({
          offset: 18,
          closeButton: true,
          closeOnClick: true,
        });

      const markerObject =
        new mapboxgl.Marker({
          element: container,
          anchor: "center",
        })
          .setLngLat([
            Number(ac.lon),
            Number(ac.lat),
          ])
          .setPopup(popup)
          .addTo(mapInstance);

      entry = {
        marker: markerObject,
        aircraftEl,
        label,
        popup,
      };

      trafficMarkersRef.current[id] =
        entry;
    }

    entry.marker.setLngLat([
      Number(ac.lon),
      Number(ac.lat),
    ]);

    // Unicode airplane points roughly east by default.
    // Convert aviation track (0 = north)
    // to screen rotation.
    entry.aircraftEl.style.transform =
      `rotate(${track - 90}deg)`;

    entry.label.textContent =
      `${callsign} ${altitudeLabel}`;

    const verticalRate =
      Number.isFinite(
        Number(ac.vertical_rate_fpm)
      )
        ? Number(ac.vertical_rate_fpm)
        : null;

    const verticalText =
      verticalRate === null
        ? "N/A"
        : verticalRate > 100
        ? `↑ ${Math.round(verticalRate)} fpm`
        : verticalRate < -100
        ? `↓ ${Math.abs(
            Math.round(verticalRate)
          )} fpm`
        : "Level";

    entry.popup.setHTML(`
      <div style="min-width:150px">
        <strong>${callsign}</strong><br>
        Type: ${ac.aircraft_type || "N/A"}<br>
        Registration: ${ac.registration || "N/A"}<br>
        Altitude: ${
          ac.on_ground
            ? "Ground"
            : ac.altitude_ft != null
            ? `${ac.altitude_ft} ft`
            : "N/A"
        }<br>
        Groundspeed: ${
          ac.groundspeed_kt != null
            ? `${Math.round(ac.groundspeed_kt)} kt`
            : "N/A"
        }<br>
        Track: ${
          ac.track_deg != null
            ? `${Math.round(ac.track_deg)}°`
            : "N/A"
        }<br>
        Vertical: ${verticalText}<br>
        Squawk: ${ac.squawk || "N/A"}
      </div>
    `);

  });

  // Remove aircraft no longer present.
  Object.keys(
    trafficMarkersRef.current
  ).forEach(id => {

    if (activeIds.has(id)) {
      return;
    }

    try {
      trafficMarkersRef.current[
        id
      ].marker.remove();
    } catch {}

    delete trafficMarkersRef.current[id];
  });

}, [
  trafficAircraft,
  trafficOn,
  mapReady
]);

// =====================================
// ✈️ LIVE TRAFFIC FETCH / POLLING
// =====================================
useEffect(() => {

  const mapInstance = mapRef.current;

  if (!mapInstance || !mapReady || !trafficOn) {
    return;
  }

  let cancelled = false;

  const fetchTraffic = async () => {

    if (
      cancelled ||
      trafficFetchInProgressRef.current
    ) {
      return;
    }

    const center = mapInstance.getCenter();

    if (
      !center ||
      !Number.isFinite(center.lat) ||
      !Number.isFinite(center.lng)
    ) {
      return;
    }

    trafficFetchInProgressRef.current = true;

    try {

      const lat = center.lat;
      const lon = center.lng;
      const radiusNm = 50;

      const response = await fetch(
        `/route/traffic?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lon)}&radius_nm=${radiusNm}`
      );

      if (!response.ok) {
        throw new Error(
          `Traffic request failed: HTTP ${response.status}`
        );
      }

      const data = await response.json();

      if (cancelled) return;

      const aircraft =
        Array.isArray(data?.aircraft)
          ? data.aircraft
          : [];

      setTrafficAircraft(aircraft);

      trafficLastCenterRef.current = {
        lat,
        lon,
      };

      console.log(
        `✈️ LIVE TRAFFIC: ${aircraft.length} aircraft`,
        {
          center: {
            lat: lat.toFixed(4),
            lon: lon.toFixed(4),
          },
          radiusNm,
          cacheHit: data?.cache_hit,
        }
      );

    } catch (err) {

      if (!cancelled) {
        console.error(
          "❌ Live traffic fetch failed:",
          err
        );
      }

    } finally {

      trafficFetchInProgressRef.current = false;
    }
  };

  const handleTrafficMoveEnd = () => {
    void fetchTraffic();
  };

  // Initial traffic load
  void fetchTraffic();

  // Refresh after map movement
  mapInstance.on(
    "moveend",
    handleTrafficMoveEnd
  );

  // Normal live refresh
  const intervalId = window.setInterval(
    fetchTraffic,
    5000
  );

  return () => {

    cancelled = true;

    window.clearInterval(intervalId);

    mapInstance.off(
      "moveend",
      handleTrafficMoveEnd
    );
  };

}, [mapReady, trafficOn]);

// =====================================
// 🎯 ZOOM TO DESTINATION
// =====================================

// =====================================
// 🎯 ZOOM TO DESTINATION
// =====================================

// =====================================
// 🎯 ZOOM TO DESTINATION AIRPORT
// =====================================

const handleZoomToDestination = async () => {

  const map = mapRef.current;

  const destinationIcao =
    activeFlight?.meta?.destination
      ?.trim()
      ?.toUpperCase();

  if (!map || !destinationIcao) return;

  try {

    const airports =
      await fetch("/airports.json")
        .then(res => res.json());

    const airport =
      airports.find(a =>
        (
          a.icao ||
          a.ident ||
          a.id ||
          ""
        ).toUpperCase() === destinationIcao
      );

    if (!airport) {
      console.warn(
        "🎯 Destination airport not found:",
        destinationIcao
      );
      return;
    }

    const lon =
      airport.lon ??
      airport.longitude;

    const lat =
      airport.lat ??
      airport.latitude;

    if (
      !Number.isFinite(Number(lon)) ||
      !Number.isFinite(Number(lat))
    ) return;

    map.easeTo({
      center: [
        Number(lon),
        Number(lat)
      ],
      zoom: 12,
      duration: 1200
    });

  } catch (err) {

    console.error(
      "🎯 Destination zoom failed:",
      err
    );

  }

};
// =====================================
// ✈️ RECENTER AIRCRAFT
// =====================================

const handleRecenterAircraft = () => {

  const map = mapRef.current;

  if (
    !map ||
    !aircraftPosition
  ) return;

  console.log(
    "✈️ RECENTER — aircraft follow resumed"
  );

  // Resume automatic aircraft follow
  setFollowAircraft(true);

  // Immediately return camera to aircraft
  map.easeTo({

    center: aircraftPosition,

    // Aircraft lower on screen
    offset: [0, 180],

    // Restore track-up
    bearing: aircraftHeading,

    pitch: 45,

    // Preserve pilot zoom,
    // but maintain minimum operational zoom
    zoom: Math.max(
      map.getZoom(),
      7
    ),

    duration: 600,

    essential: true
  });
};

function removeAirportLayers() {
  const map = mapRef.current;
  if (!map || !map.getStyle()) return;

  console.log("🧹 Removing airport visuals");

  // 🔥 REMOVE MAPBOX LAYERS
  map.getStyle().layers.forEach(layer => {
    const id = layer.id;

    if (
      id.startsWith("runway-") ||
      id.startsWith("twy-") ||
      id.startsWith("ficon-") ||
      id.startsWith("crosswind-")
    ) {
      try {
        if (map.getLayer(id)) map.removeLayer(id);
      } catch {}
    }
  });

  // 🔥 REMOVE MAPBOX SOURCES
  Object.keys(map.getStyle().sources || {}).forEach(src => {
    if (
      src.startsWith("runway-") ||
      src.startsWith("twy-") ||
      src.startsWith("ficon-") ||
      src.startsWith("crosswind-")
    ) {
      try {
        if (map.getSource(src)) map.removeSource(src);
      } catch {}
    }
  });

  // 🔥 REMOVE CROSSWIND LABELS (DOM — THIS WAS MISSING)
  document.querySelectorAll('.runway-inline-label').forEach(el => {
    try { el.remove(); } catch {}
  });

  // 🔥 REMOVE RUNWAY / CROSSWIND MARKERS (CRITICAL)
  if (runwayMarkersRef.current) {
    runwayMarkersRef.current.forEach(obj => {
      if (obj?.remove) {
        try { obj.remove(); } catch {}
      }
    });
    runwayMarkersRef.current = [];
  }

  // 🔥 REMOVE OBSTRUCTION MARKERS
  if (obstructionMarkersRef.current) {
    obstructionMarkersRef.current.forEach(m => {
      try { m.remove(); } catch {}
    });
    obstructionMarkersRef.current = [];
  }

  console.log("✅ Airport layers cleared");
  setTimeout(() => {
  bringRouteLayersToFront();
}, 100);
}

useEffect(() => {
  if (!map) return;

  if (showRadar) {
    if (map.isStyleLoaded()) {
      // ✅ Style is ready — safe to load radar
      loadNOAARadarLayer(map);
      bringRouteLayersToFront();
    } else {
      // ⏳ Wait for style to load
      map.once('styledata', () => {
        loadNOAARadarLayer(map);
        bringRouteLayersToFront();
      });
    }
  } else {
    // 🔄 Radar turned off — remove source and layer if they exist
    if (map.getLayer('noaa-radar-layer')) {
      map.removeLayer('noaa-radar-layer');
    }
    if (map.getSource('noaa-radar')) {
      map.removeSource('noaa-radar');
    }
  }
}, [showRadar, map]);


useEffect(() => {
  if (!map) return;

  // 🔥 MERGE + STORE DATA — DO NOT CREATE MARKERS HERE
  const handlePirepData = (locations, source = 'UNKNOWN') => {
    console.log(`📡 Received ${source} PIREPs:`, locations);

    if (!locations || !Array.isArray(locations)) {
      console.warn("⚠️ Invalid PIREP data received");
      return;
    }

    setAllPireps(prev => {
      const prevSafe = prev || [];

      // 🔥 tag incoming data with source
      const tagged = locations.map(p => ({
        ...p,
        source: p.source || source
      }));

      // 🔥 merge old + new
      const combined = [...prevSafe, ...tagged];

      // 🔥 dedupe
      const unique = [];
      const seen = new Set();

      combined.forEach(p => {
        const key = JSON.stringify([
          p.source,
          p.icao || p.callsign || "",
          p.latitude,
          p.longitude,
          p.time || p.report_time || p.timestamp || "",
          p.turbulence || "",
          p.icing_condition || "",
          p.sky_condition || "",
          p.remarks || ""
        ]);

        if (!seen.has(key)) {
          seen.add(key);
          unique.push(p);
        }
      });

      console.log("📦 MERGED TOTAL PIREPS:", unique.length);

      return unique;
    });
  };

  // 🔥 SOCKET EVENTS

  socket.on('pirep_locations', (data) => {
    handlePirepData(data.locations, 'FAA');
  });

  socket.on('parsed_pirep_locations', (data) => {
    handlePirepData(data.locations, 'PARSED');
  });

  // 🔥 WEATHER (UNCHANGED)
  socket.on('weather_data', (data) => {
    if (!data || !Array.isArray(data.locations)) return;

    setWeatherData(data.locations);

    if (showAirportWeatherRef.current) {
      plotAirportMarkers(data.locations);
    }
  });

  // 🔥 HAZARD SCORE (UNCHANGED)
  socket.on("hazard_score", (data) => {
    console.log("📦 ROUTES FROM BACKEND:", data);

    if (!data || !data.data || !Array.isArray(data.data)) {
      console.warn("⚠️ route_hazard_score received invalid data:", data);
      return;
    }

    console.log("✅ Hazard score received:", data.data);
  });

  // 🔥 PDP EVENTS (UNCHANGED)
  socket.on('pdp_console_log', (data) => {
    appendPdpLog(data.message);
  });

  socket.on('pdp_instruction_pair', (data) => {
    const { callsign, instruction, readback } = data;
    updatePdpInstructionDisplay(callsign, instruction, readback);
  });

  // 🔥 CLEANUP
  return () => {
    socket.off('pirep_locations');
    socket.off('parsed_pirep_locations');
    socket.off('weather_data');
    socket.off('pdp_console_log');
    socket.off('pdp_instruction_pair');
    socket.off('hazard_score');

    Object.values(aircraftMarkerMap.current).forEach(marker => marker.remove());
    aircraftMarkerMap.current = {};

    // filter system will rebuild markers
    pirepMarkers.forEach(marker => marker.remove());
    setPirepMarkers([]);
  };

}, [map]);

// 🔥 NORMALIZATION (FIXED ORDER)
const normalizeSeverity = (value) => {
    if (!value) return "NONE";

    const v = value.toUpperCase().trim();

    if (v === "" || v === "NEG") return "NONE";

    // descriptors → MODERATE
    if (
        v.includes("OCNL") ||
        v.includes("INTMT") ||
        v.includes("CONS") ||
        v.includes("CONT")
    ) return "MODERATE";

    if (v.includes("SEV")) return "SEVERE";
    if (v.includes("MOD")) return "MODERATE";

    if (
        v.includes("LGT") ||
        v.includes("LIGHT") ||
        v.includes("TRACE")
    ) return "LIGHT";

    return "NONE";
};


const [allPirepsEnabled, setAllPirepsEnabled] = useState(() => {
    const saved = localStorage.getItem("pirepFilters");
    return saved ? JSON.parse(saved).allPirepsEnabled : true;
});

const [selectedCategories, setSelectedCategories] = useState(() => {
    const saved = localStorage.getItem("pirepFilters");
    return saved
        ? JSON.parse(saved).selectedCategories
        : { turbulence: [], icing: [] };
});

// 🔥 FILTER TRIGGER
useEffect(() => {
    applyFilters();
}, [selectedCategories, allPirepsEnabled]);

useEffect(() => {
    if (allPireps && allPireps.length > 0) {
        console.log("📡 New PIREPs received → applying filter");
        applyFilters();
    }
}, [allPireps]);

useEffect(() => {
    console.log("📡 allPireps UPDATED:", allPireps.length);
}, [allPireps]);


// 🔥 MAIN FILTER (DEBUG VERSION)
const applyFilters = () => {
    console.log("🚀 APPLY FILTERS TRIGGERED");

    if (!allPireps) {
        console.warn("⚠️ allPireps is null/undefined");
        return;
    }


    // 🧹 Clear markers
    console.log("🧹 Removing existing markers:", pirepMarkers.length);
    pirepMarkers.forEach(marker => marker.remove());

    const filteredPireps = allPireps.filter((pirep, index) => {

        const rawTurb = pirep.turbulence;
        const rawIce = pirep.icing_condition;

        const turb = normalizeSeverity(rawTurb);
        const ice = normalizeSeverity(rawIce);

        const selectedTurb = selectedCategories.turbulence || [];
        const selectedIce = selectedCategories.icing || [];

        // ✅ ALL ON → show everything
        if (allPirepsEnabled) {
            console.log("   ✅ PASS (allPirepsEnabled)");
            return true;
        }

        // ✅ NEW RULE
        if (!allPirepsEnabled && selectedTurb.length === 0 && selectedIce.length === 0) {
            return false; // show NOTHING when no filters selected
        }
        let match = false;

        if (selectedTurb.length > 0) {
            const turbMatch = selectedTurb.includes(turb);
            console.log("   Turb match:", turbMatch);
            if (turbMatch) match = true;
        }

        if (selectedIce.length > 0) {
            const iceMatch = selectedIce.includes(ice);
            console.log("   Ice match:", iceMatch);
            if (iceMatch) match = true;
        }



        return match;
    });



    // 🛠 Rebuild markers
    const newMarkers = [];

    filteredPireps.forEach((p, i) => {
        const m = createPirepMarker(p);
        if (m) {
            newMarkers.push(m);
        } else {
            console.warn(`⚠️ Marker failed for PIREP index ${i}`, p);
        }
    });



    setPirepMarkers(newMarkers);
};

const handleAllPirepsChange = (event) => {
    const checked = event.target.checked;

    setAllPirepsEnabled(checked);

    if (checked) {
        setSelectedCategories({
            turbulence: [],
            icing: []
        });
    }
};

// ✅ HANDLE CATEGORY CHECKBOX
const handleCategoryChange = (event, category) => {
    const { value, checked } = event.target;

    setSelectedCategories((prev) => {
        const updated = { ...prev };

        if (checked) {
            updated[category] = [...prev[category], value];
        } else {
            updated[category] = prev[category].filter(v => v !== value);
        }

        return updated;
    });

    // turn OFF "All"
    setAllPirepsEnabled(false);
};


// ✅ KEEP (unchanged)
useEffect(() => {
    showAirportWeatherRef.current = showAirportWeather;
    if (!showAirportWeather) {
        clearAirportMarkers();
    }
}, [showAirportWeather]);


// ✅ FORMAT ALTITUDE
const formatAltitude = (altitude) => {
    if (!altitude) return '';
    const alt = altitude.toString().toUpperCase().replace(/FL/gi, '');
    return alt.length === 3 ? `FL${alt}` : alt;
};

useEffect(() => {
    localStorage.setItem("pirepFilters", JSON.stringify({
        allPirepsEnabled,
        selectedCategories
    }));
}, [allPirepsEnabled, selectedCategories]);

const isUsefulPirep = (pirep) => {
  const normalize = val => (val ? val.toUpperCase().trim() : "");

  const turb = normalize(pirep.turbulence);
  const icing = normalize(pirep.icing_condition);
  const sky = normalize(pirep.sky_condition);
  const temp = normalize(pirep.temperature);
  const aircraft = normalize(pirep.aircraft_type);

  const isNegTurb = ['NONE', 'NEG', 'NEGATIVE'].includes(turb);
  const isNegIcing = ['NONE', 'NEG', 'NEGATIVE'].includes(icing);
  const isSkyClear = ['SKC', 'SKY CLEAR'].includes(sky);
  const hasClouds = !!(sky && !isSkyClear);

  // ✅ We do not count altitude or aircraft type alone as useful
  const hasUsefulExtra = !!(temp || hasClouds);

  // ❌ Skip if ONLY negative turb + negative icing + no other useful info
  if (isNegTurb && isNegIcing && !hasUsefulExtra) {
    return false;
  }

  return true;
};



const getMarkerColor = (turbulence, icing_condition, sky_condition = "") => {
  const normalize = val => (val ? val.toUpperCase().trim() : "");

  const turb = normalize(turbulence);
  const ice = normalize(icing_condition);
  const sky = normalize(sky_condition);

  const isNegTurb = ['NONE', 'NEG', 'NEGATIVE'].includes(turb);
  const isNegIce = ['NONE', 'NEG', 'NEGATIVE'].includes(ice);

  // ⛅ 1. If sky condition is *anything* except SKC or SKY CLEAR, force gray
  if (sky && !["SKC", "SKY CLEAR"].includes(sky)) {
    return 'gray';
  }

  // 🌪️ 2. Turbulence severity
  if (turb.includes('SEVERE')) return 'red';
  if (['MODERATE', 'MOD', 'CONTINUOUS', 'CONS', 'CON'].some(k => turb.includes(k))) return '#FF6991';
  if (['LIGHT', 'LGT', 'LT', 'OCNL'].some(k => turb.includes(k))) return '#FFC7D5';

  // ❄️ 3. Icing severity
  if (ice === 'SEVERE') return '#0085CA';
  if (ice === 'MODERATE' || ice === 'MOD') return '#00B5E2';
  if (['LIGHT', 'TRACE', 'LGT', 'LT'].includes(ice)) return '#9ADBE8';



  // 🛑 Everything else
  return 'gray';
};



// =====================================
// 📋 SHOW PIREP MODAL
// =====================================

const showPirepData = (data) => {

  const modalContent =
    document.getElementById(
      'pirepModalContent'
    );

  const modalElement =
    document.getElementById(
      'pirepModal'
    );

  if (
    !modalContent ||
    !modalElement
  ) {

    console.error(
      "❌ PIREP modal element not found."
    );

    return;
  }

  // =====================================
  // 🎥 OPTIONAL VIDEO
  // =====================================

  const videoHTML =
    data.video_filename

      ? `

      <div style="
        margin-top:16px;
      ">

        <div style="
          font-size:14px;
          font-weight:700;
          margin-bottom:8px;
          color:#00d5ff;
        ">
          🎥 Attached Video
        </div>

        <video
          width="100%"
          height="240"
          controls
          style="
            border-radius:10px;
            background:black;
          "
        >
          <source
            src="${backendURL}/videos/${data.video_filename}"
            type="video/mp4"
          >

          Your browser does not support video.
        </video>

      </div>
    `

      : '';

  // =====================================
  // 📋 BUILD MODAL HTML
  // =====================================

  modalContent.innerHTML = `

    <div style="
      color:white;
      font-family:system-ui,sans-serif;
    ">

      <div style="
        font-size:22px;
        font-weight:700;
        margin-bottom:16px;
        color:#00d5ff;
      ">
        ✈️ PIREP DETAILS
      </div>

      <div style="margin-bottom:8px;">
        <strong>Source:</strong>
        ${data.source || 'Unknown'}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Aircraft / Identifier:</strong>
        ${data.icao || data.callsign || 'Unknown'}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Time:</strong>
        ${data.time || data.timestamp || 'N/A'}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Flight Level:</strong>
        ${formatAltitude(
          data.altitude ||
          data.flight_level
        )}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Aircraft Type:</strong>
        ${data.aircraft_type || "Unknown"}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Turbulence:</strong>
        ${data.turbulence || "None"}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Icing:</strong>
        ${data.icing_condition || "None"}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Sky Condition:</strong>
        ${data.sky_condition || "N/A"}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Visibility:</strong>
        ${data.visibility || "N/A"}
      </div>

      <div style="margin-bottom:8px;">
        <strong>Temperature:</strong>
        ${data.temperature || "N/A"}
      </div>

      <div style="
        margin-top:14px;
        padding:12px;
        background:rgba(255,255,255,0.06);
        border-radius:10px;
        line-height:1.5;
      ">
        <strong>Remarks:</strong><br/>
        ${data.remarks || "No remarks"}
      </div>

      ${videoHTML}

    </div>
  `;

  // =====================================
  // 🚀 SHOW MODAL
  // =====================================

  modalElement.style.display =
    'block';
};

// =====================================
// 📍 CREATE PIREP MARKER
// =====================================

// =====================================
// 📍 CREATE PIREP MARKER
// =====================================

const createPirepMarker = (
  pirep
) => {

  const lat =
    parseFloat(
      pirep.latitude
    );

  const lon =
    parseFloat(
      pirep.longitude
    );

  const markerColor =
    getMarkerColor(
      pirep.turbulence,
      pirep.icing_condition
    );

  // =====================================
  // 🚫 INVALID COORDS
  // =====================================

  if (
    isNaN(lat) ||
    isNaN(lon)
  ) {

    console.warn(
      "⚠️ Invalid PIREP coords:",
      pirep
    );

    return null;
  }

  // =====================================
  // 📍 CREATE NORMAL MAPBOX FLAG
  // =====================================

  const marker =
    new mapboxgl.Marker({
      color: markerColor
    })

      .setLngLat([
        lon,
        lat
      ])

      .addTo(map);

  // =====================================
  // 📡 IDENTIFY FICON-X PIREP
  // =====================================

// =====================================
// 📡 IDENTIFY FICON-X PIREP
// Temporary rule:
// Our generated reports use a 3-letter ICAO
// =====================================
const icaoValue =
  String(
    pirep.icao || ""
  )
    .trim()
    .toUpperCase();

const isAirportIdentifier =
  /^[A-Z]{3,4}$/.test(
    icaoValue
  );

const isAircraftCallsign =
  /^[A-Z]{2,4}\d{1,4}[A-Z]?$/.test(
    icaoValue
  );

const isFiconXPirep =
  pirep.source === "FICON-X";
  
  // 📡 ADD SIGNAL PULSE
  // =====================================

  if (isFiconXPirep) {

    const markerElement =
      marker.getElement();

    markerElement.classList.add(
      "ficonx-pirep-marker"
    );

    const pulseRingOne =
      document.createElement(
        "span"
      );

    pulseRingOne.className =
      "ficonx-pirep-pulse-ring";

    const pulseRingTwo =
      document.createElement(
        "span"
      );

    pulseRingTwo.className =
      "ficonx-pirep-pulse-ring ficonx-pirep-pulse-ring-delay";

    markerElement.appendChild(
      pulseRingOne
    );

    markerElement.appendChild(
      pulseRingTwo
    );
  }

  // =====================================
  // 🖱 CLICK HANDLER
  // =====================================

  marker
    .getElement()
    .addEventListener(
      "click",
      () => {

        console.log(
          "📍 PIREP CLICKED:",
          pirep
        );

        showPirepData(
          pirep
        );
      }
    );

  return marker;
};
// =====================================
// ❌ CLOSE PIREP MODAL
// =====================================

const closePirepModal = () => {

  const modal =
    document.getElementById(
      'pirepModal'
    );

  if (modal) {

    modal.style.display =
      'none';
  }
};

// =====================================
// 🔥 PDP INIT
// =====================================

useEffect(() => {

  setPdpActive(true);

  setShowPdpBox(true);

}, []);

// =====================================
// 🎨 HAZARD DOT COLORS
// =====================================

function getDotColor(score) {

  if (score >= 7)
    return '#d32f2f'; // 🔴 Severe

  if (score >= 5)
    return '#f9a825'; // 🟡 Moderate

  if (score >= 3)
    return '#0288d1'; // 🔵 Light

  return '#2e7d32';   // 🟢 Clear
}



useEffect(() => {
  async function loadNavData() {
    const [fixData, aptData, vorData] = await Promise.all([
      fetch("/fixesdata.json").then(res => res.json()),
      fetch("/airports.json").then(res => res.json()),
      fetch("/vorData.json").then(res => res.json())
    ]);

    setFixes(fixData);
    setAirports(aptData);
    setVors(vorData);
  }

  loadNavData();
}, []);


function resolveRouteToCoords(routeStr, fixes, airports, vors) {
  const route = routeStr.trim().split(/\s+/);
  const coords = [];

  for (const fix of route) {
    let point = null;

    if (fixes[fix]) {
      point = fixes[fix];
    } else if (airports[fix]) {
      point = airports[fix];
    } else if (vors[fix]) {
      point = vors[fix];
    }

    if (point) {
      coords.push({ lat: point.lat, lon: point.lon });
    } else {
      console.warn(`❌ Couldn't resolve: ${fix}`);
    }
  }

  return coords;
}

useEffect(() => {
  console.log("📌 useEffect triggered for routeSuggestions");

  // 🔒 IMPORTANT:
  // Do nothing if there is already a confirmed/active route
  if (routeConfirmed || routeResult) {
    console.log("🛑 Skipping suggested route plotting — active route exists");
    return;
  }

  if (
    map &&
    routeSuggestions.length > 0 &&
    fixes &&
    airports &&
    vors
  ) {
    console.log(
      "🧠 Calling plotSuggestedRoutes with",
      routeSuggestions.length,
      "suggestions"
    );

    plotSuggestedRoutes(map, routeSuggestions, fixes, airports, vors);
  } else {
    console.log("⛔ Missing data: ", {
      mapExists: !!map,
      suggestions: routeSuggestions.length,
      fixes: !!fixes,
      airports: !!airports,
      vors: !!vors
    });
  }
}, [routeSuggestions, map, fixes, airports, vors, routeConfirmed, routeResult]);



function plotSuggestedRoutes(map, suggestions, fixes, airports, vors) {
  suggestions.forEach((sug, idx) => {
    const coords = resolveRouteToCoords(sug.route, fixes, airports, vors);

    if (coords.length < 2) {
      console.warn(`⚠️ Not enough fixes to plot route: ${sug.route}`);
      return;
    }

    // ✅ Use distinct and matching IDs
    const sourceId = `suggestion-src-${idx}`;
    const layerId = `suggestion-layer-${idx}`;
    const color = sug.hazard_color || "gray";

    // 🧼 Remove if already exists
    if (map.getLayer(layerId)) map.removeLayer(layerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);

    map.addSource(sourceId, {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: coords.map(p => [p.lon, p.lat])
        }
      }
    });

    map.addLayer({
      id: layerId,
      type: "line",
      source: sourceId,
      layout: {
        "line-join": "round",
        "line-cap": "round"
      },
      paint: {
        "line-color": color,
        "line-width": 2,
        "line-dasharray": [2, 2]
      }
    });
  });
}


const [vfrVisible, setVfrVisible] = useState(false);
const [ifrLowVisible, setIfrLowVisible] = useState(false);
const [ifrHighVisible, setIfrHighVisible] = useState(false);

//
// 🧠 DEFINE YOUR IFR HIGH STACK HERE (ORDER = LAYER PRIORITY)
//
const IFR_HIGH_CHARTS = [
  "ifr_high_H11",
  "ifr_high_H02",
    "ifr_high_H01",

  "ifr_high_H10",
  "ifr_high_H05",
    "ifr_high_H03",
      "ifr_high_H12",
        "ifr_high_H06",
      "ifr_high_H09",

    "ifr_high_H04",

  "ifr_high_H08",
    "ifr_high_H07",




];


//
// ✈️ IFR LOW (MULTI-LAYER — NEW SYSTEM)
//
const IFR_LOW_CHARTS = [






"ifr_low_L09",
"ifr_low_L30",
"ifr_low_L27",
"ifr_low_L10",
"ifr_low_L11",
"ifr_low_L12",
"ifr_low_L13",
"ifr_low_L14",
"ifr_low_L15",
"ifr_low_L16",
"ifr_low_L17",
"ifr_low_L18",
"ifr_low_L19",
"ifr_low_L20",
"ifr_low_L21",
"ifr_low_L22",

"ifr_low_L24",
"ifr_low_L23",
"ifr_low_L25",
"ifr_low_L29",
"ifr_low_L26",

"ifr_low_L28",


"ifr_low_L31",
"ifr_low_L32",
"ifr_low_L33",
"ifr_low_L34",
"ifr_low_L35",
"ifr_low_L36",
"ifr_low_L01",


"ifr_low_L03",
"ifr_low_L02",

"ifr_low_L08",
"ifr_low_L07",

"ifr_low_L05",
"ifr_low_L04",
"ifr_low_L06N",
"ifr_low_L06S",



];

const toggleIfrLow = () => {
  const mapInstance = map;
  if (!mapInstance) return;

  console.log("🟢 IFR LOW TOGGLE");

  const next = !ifrLowVisible;

  setIfrLowVisible(next);
  setVfrVisible(false);
  setIfrHighVisible(false);

  IFR_LOW_CHARTS.forEach(chart => {
    const sourceId = `${chart}-source`;
    const layerId = `${chart}-layer`;

    // add source
    if (!mapInstance.getSource(sourceId)) {
      mapInstance.addSource(sourceId, {
        type: "raster",
        tiles: [`https://ficon-x.com/tiles/${chart}/{z}/{x}/{y}.png`],
        tileSize: 256,
        minzoom: 3,
        maxzoom: 12
      });
    }

    // add layer
    if (!mapInstance.getLayer(layerId)) {
      mapInstance.addLayer({
        id: layerId,
        type: "raster",
        source: sourceId,
        paint: {
          "raster-opacity": 1
        }
      });
    }

    // toggle visibility
    mapInstance.setLayoutProperty(
      layerId,
      "visibility",
      next ? "visible" : "none"
    );
  });

  // hide other systems
  ["vfr-layer", ...IFR_HIGH_CHARTS.map(c => `${c}-layer`)]
    .forEach(id => {
      if (mapInstance.getLayer(id)) {
        mapInstance.setLayoutProperty(id, "visibility", "none");
      }
    });

    // 🔥 KEEP ROUTE ABOVE IFR HIGH RASTER
setTimeout(() => {
  bringRouteLayersToFront();
}, 100);  

  console.log("📍 IFR LOW ACTIVE:", next);
};
//
// 🗺️ VFR (UNCHANGED)
//
//
// 🗺️ VFR SECTIONALS (MULTI-LAYER — NEW SYSTEM)
//
const VFR_CHARTS = [

 


  "vfr_Halifax",
    "vfr_Montreal",
     "vfr_Lake_Huron",
       
  "vfr_New_York",

              "vfr_Detroit",
                "vfr_Chicago",
                            "vfr_Green_Bay",


  "vfr_Twin_Cities",
      "vfr_Billings",
        "vfr_Great_Falls",
   "vfr_Seattle",
 "vfr_Point_Barrow",
  "vfr_Anchorage",

  "vfr_Bethel",

  "vfr_Brownsville",
  "vfr_Cape_Lisburne",



 "vfr_Omaha",
  "vfr_Cheyenne",

  "vfr_Cold_Bay",

  "vfr_Washington",
        "vfr_Cincinnati",
        "vfr_St_Louis",
            "vfr_Atlanta",
              "vfr_Charlotte",

      "vfr_Kansas_City",

  "vfr_Memphis",
 
      "vfr_Dallas-Ft_Worth",

    "vfr_Albuquerque",
     
  "vfr_Salt_Lake_City",
               "vfr_Wichita",
        "vfr_Denver",

        
  "vfr_Dawson",




  "vfr_Dutch_Harbor",

  "vfr_Fairbanks",


  "vfr_Hawaiian_Islands",


        "vfr_Miami",

  "vfr_Jacksonville",




        "vfr_Ketchikan",
  "vfr_Juneau",




  "vfr_New_Orleans",
 
  "vfr_McGrath",


      "vfr_Houston",




  
 
  "vfr_Nome",

    "vfr_San_Antonio",

      "vfr_El_Paso",






                         "vfr_Las_Vegas",
                           "vfr_Phoenix",
                    "vfr_Los_Angeles",

  

                "vfr_San_Francisco",

 
         "vfr_Klamath_Falls",


  "vfr_Seward",







      
  "vfr_Western_Aleutian_Islands",
    "vfr_Kodiak",
];

const toggleVfrSectionals = () => {
  const mapInstance = map;
  if (!mapInstance) return;

  console.log("🟢 VFR TOGGLE");

  const next = !vfrVisible;

  setVfrVisible(next);
  setIfrLowVisible(false);
  setIfrHighVisible(false);

  VFR_CHARTS.forEach(chart => {
    const sourceId = `${chart}-source`;
    const layerId = `${chart}-layer`;

    // add source
    if (!mapInstance.getSource(sourceId)) {
      mapInstance.addSource(sourceId, {
        type: "raster",
        tiles: [`https://ficon-x.com/tiles/${chart}/{z}/{x}/{y}.png`],
        tileSize: 256,
        minzoom: 3,
        maxzoom: 12
      });
    }

    // add layer
    if (!mapInstance.getLayer(layerId)) {
      mapInstance.addLayer({
        id: layerId,
        type: "raster",
        source: sourceId,
        paint: {
          "raster-opacity": 1,
          "raster-resampling": "linear",
          "raster-fade-duration": 100
        }
      });
    }

    // toggle visibility
    mapInstance.setLayoutProperty(
      layerId,
      "visibility",
      next ? "visible" : "none"
    );
  });

  // hide IFR layers
  [
    ...IFR_LOW_CHARTS.map(c => `${c}-layer`),
    ...IFR_HIGH_CHARTS.map(c => `${c}-layer`)
  ].forEach(id => {
    if (mapInstance.getLayer(id)) {
      mapInstance.setLayoutProperty(id, "visibility", "none");
    }
  });

    // 🔥 KEEP ROUTE ABOVE IFR HIGH RASTER
setTimeout(() => {
  bringRouteLayersToFront();
}, 100);

  console.log("📍 VFR ACTIVE:", next);
};

//
// 🟣 IFR HIGH (MULTI-LAYER — FULL CONTROL)
//
const toggleIfrHigh = () => {
  const mapInstance = map;
  if (!mapInstance) return;

  console.log("🟣 IFR HIGH TOGGLE");

  const next = !ifrHighVisible;

  setIfrHighVisible(next);
  setIfrLowVisible(false);
  setVfrVisible(false);

  IFR_HIGH_CHARTS.forEach(chart => {
    const sourceId = `${chart}-source`;
    const layerId = `${chart}-layer`;

    // ADD SOURCE
    if (!mapInstance.getSource(sourceId)) {
      mapInstance.addSource(sourceId, {
        type: "raster",
        tiles: [`https://ficon-x.com/tiles/${chart}/{z}/{x}/{y}.png`],
        tileSize: 256,
        minzoom: 3,
        maxzoom: 12
      });
    }

    // ADD LAYER (ORDER MATTERS)
    if (!mapInstance.getLayer(layerId)) {
      mapInstance.addLayer({
        id: layerId,
        type: "raster",
        source: sourceId,
        paint: {
          "raster-opacity": 1
        }
      });
    }

    // TOGGLE VISIBILITY
    mapInstance.setLayoutProperty(
      layerId,
      "visibility",
      next ? "visible" : "none"
    );
  });

  // hide others
  ["ifr-low-layer", "vfr-layer"].forEach(id => {
    if (mapInstance.getLayer(id)) {
      mapInstance.setLayoutProperty(id, "visibility", "none");
    }
  });

  // 🔥 KEEP ROUTE ABOVE IFR HIGH RASTER
setTimeout(() => {
  bringRouteLayersToFront();
}, 100);

  console.log("📍 IFR HIGH ACTIVE:", next);
};

const toggleAirportConditions = async () => {
  const next = !showAirportWeather;
  setShowAirportWeather(next);

  if (!next) {
    clearAirportMarkers();
    return;
  }

  try {
    const res = await axios.get(`${backendURL}/get-weather-data`);
    const locations = res.data?.locations || [];
    if (!locations.length) return;

    if (!showAirportWeatherRef.current) return;
    setWeatherData(locations);
    plotAirportMarkers(locations);
  } catch (err) {
    console.error("❌ Error fetching weather data:", err);
  }
};

const routeColors = [
  "#1E88E5", // blue
  "#43A047", // green
  "#FB8C00", // orange
  "#8E24AA", // purple
  "#E53935", // red
  "#00ACC1", // cyan
  "#FDD835", // yellow
];

const loadRouteSuggestions = async () => {
  // 🔥 EXIT ACTIVE FLIGHT MODE — entering planning
  console.log("🧹 Clearing ACTIVE flight before generating new suggestions");

  localStorage.removeItem("activeFlight");
  localStorage.removeItem("flightStatus");

  // 🧹 Clear previously selected route + fix labels
  if (map && map.getStyle) {
    const style = map.getStyle();

    // Remove selected route
    if (map.getLayer("selected-route")) {
      try { map.removeLayer("selected-route"); } catch {}
    }
    if (map.getSource("selected-route")) {
      try { map.removeSource("selected-route"); } catch {}
    }

    // 🔥 REMOVE FIX LABELS (THIS WAS MISSING)
    if (map.getLayer("route-fix-labels")) {
      try { map.removeLayer("route-fix-labels"); } catch {}
    }
    if (map.getSource("route-fix-labels")) {
      try { map.removeSource("route-fix-labels"); } catch {}
    }
  }

  // ---- existing logic continues ----


  const o = (origin || "").trim().toUpperCase();
  const d = (destination || "").trim().toUpperCase();
  const a = (altitude || "").toString().trim();

  if (!o || !d || !a) {
    console.warn("🛑 Missing required fields");
    setRouteSuggestions([]);
    return;
  }

  setSuggestionsLoading(true);
  setSuggestionsError(null);
  console.log("📡 Starting route suggestion fetch...");

  try {
    // Clear any old layers first
    for (let i = 0; i < 20; i++) {
      const id = `suggested-route-${i}`;
      if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(id)) map.removeSource(id);
    }

const res = await axios.post(
  "https://ficon-x.com/route/route-suggestions",
  {
    origin: o,
    destination: d,
    altitude: a,
    burn_rate: burnRate,
    tas_kts: tasKts,
  }
);



    const suggestions = Array.isArray(res.data)
      ? res.data
      : res.data?.suggestions || [];

    console.log("################################");
    console.log("RAW BACKEND SUGGESTIONS");
    console.log("################################");
    console.log(suggestions);

    if (suggestions.length > 0) {

      console.log(
        "FIRST STAR:",
        suggestions[0].selected_star
      );

      console.log(
        "FIRST TRANSITION:",
        suggestions[0].selected_star_transition
      );

      console.log(
        "FIRST STAR RESULT:",
        suggestions[0].selected_star_result
      );
    }

    console.log("################################");

    console.log(`📦 Received ${suggestions.length} suggestions`);

    if (suggestions.length === 0) {
      console.warn("⚠️ No route suggestions returned from backend");
      setRouteSuggestions([]);
      return;
    }

    const processedSuggestions = [];

    // --- Loop through and plot each suggestion ---
    for (let idx = 0; idx < suggestions.length; idx++) {
      const suggestion = suggestions[idx];
      const routeString = suggestion.route || suggestion.route_code || "";

      try {
        console.log(`🧩 Resolving suggestion ${idx + 1}: ${routeString}`);

        const resolved = await resolveRouteWithCoords(

            routeString,

            o,

            d,

            a,

            null,                  // departureRunway
            null,                  // selectedSid
            null,                  // selectedSidTransition

            null,                  // arrivalRunway

            suggestion.selected_star,

            suggestion.selected_star_transition,

            null,                  // selectedApproach

            null                   // selectedArrivalIAF

        );
        if (!resolved || !resolved.coords || resolved.coords.length < 2) {
          console.warn(`⚠️ Skipping invalid route at index ${idx}`);
          continue;
        }

        console.log(
  "🚨 RESOLVED ROUTE OBJECT",
  resolved
);

        const origin_ll = resolved.coords[0];
        const dest_ll = resolved.coords[resolved.coords.length - 1];

        console.log("######## ORIGINAL SUGGESTION ########");
        console.log(suggestion);
        console.log("selected_star =", suggestion.selected_star);
        console.log("selected_star_transition =", suggestion.selected_star_transition);
        console.log("selected_star_result =", suggestion.selected_star_result);
        console.log("#####################################");
                
        processedSuggestions.push({

          // Preserve everything from backend
          ...suggestion,

          // Replace only the frontend-generated fields
          route: routeString,

          // Full operational coordinates.
          // Keep these for route selection/use.
          coords: resolved.coords,

          // Filled below for comparison-map display.
          previewCoords: null,

          names:
            resolved.names ||
            routeString.split(" ")

        });


        // ==========================================
        // 🗺️ SIMPLIFIED SUGGESTED-ROUTE PREVIEW
        // ==========================================
        //
        // IMPORTANT:
        // resolved.coords contains the fully expanded
        // operational route, including detailed
        // procedure geometry.
        //
        // KEEP resolved.coords in processedSuggestions
        // for the actual selected route.
        //
        // For the 4-route comparison map, draw only
        // the named route fixes from routeString.
        // This prevents detailed SID/STAR geometry
        // from making the preview look like spaghetti.
        // ==========================================

        // ==========================================
        // 🗺️ SUGGESTED-ROUTE PREVIEW
        // ==========================================
        //
        // Use the authoritative resolved geometry.
        //
        // The backend has already expanded and resolved
        // the route in correct flying order, including
        // SID/STAR fixes.
        //
        // Do not independently rebuild the route from
        // routeString here. Procedure names such as
        // JAIKE4 are not geographic waypoints and can
        // create incorrect preview geometry.
        //

        const previewCoords =
          resolved.coords;

          processedSuggestions[
          processedSuggestions.length - 1
        ].previewCoords =
          previewCoords;

        console.log(
          `🗺️ Suggestion ${idx + 1} preview:`,
          previewCoords.length,
          "points vs",
          resolved.coords.length,
          "full resolved points"
        );

        // ==========================================
        // 🎨 DRAW PREVIEW ONLY
        // ==========================================

        const routeColor =
          routeColors[
            idx % routeColors.length
          ];

        const layerId =
          `suggested-route-${idx}`;

        const geojson = {

          type: "Feature",

          geometry: {

            type: "LineString",

            coordinates:
              previewCoords
          }
        };

        if (map.getLayer(layerId)) {

          map.removeLayer(layerId);
        }

        if (map.getSource(layerId)) {

          map.removeSource(layerId);
        }

        map.addSource(
          layerId,
          {
            type: "geojson",
            data: geojson
          }
        );

        map.addLayer({

          id: layerId,

          type: "line",

          source: layerId,

          layout: {

            "line-join": "round",

            "line-cap": "round"
          },

          paint: {

            "line-color":
              routeColor,

            "line-width": 3,

            "line-opacity": 0.6
          }
        });

        console.log(
          `🗺️ Plotted simplified suggestion ${idx + 1} (${routeColor})`
        );
      } catch (err) {
        console.error(`❌ Error resolving suggestion ${idx + 1}:`, err);
      }
    }

    setRouteSuggestions(processedSuggestions);

    // --- Fit map to all suggestions ---
    const allCoords =
  processedSuggestions.flatMap(
    (s) =>
      s.previewCoords ||
      s.coords ||
      []
  );
    if (allCoords.length > 1 && map) {
      const bounds = new mapboxgl.LngLatBounds();
      allCoords.forEach(([lon, lat]) => bounds.extend([lon, lat]));
      map.fitBounds(bounds, {
        padding: { top: 200, bottom: 200, left: 200, right: 200 },
        linear: true,
        duration: 600,
      });
      console.log("📍 Map fit to all plotted suggestions");
    }

    console.log("✅ Finished processing all suggestions");
  } catch (err) {
    console.error("🚨 Route suggestion fetch failed:", err);
    setSuggestionsError(
      err?.response?.data?.error || "Failed to load route suggestions."
    );
    setRouteSuggestions([]);
  } finally {
    console.log("🧹 Finished route suggestion load — hiding loader");
    setSuggestionsLoading(false);
  }
};

function highlightRoute(i) {
  const id = `suggested-route-${i}`;
  if (!map || !map.getLayer(id)) return;

  map.setPaintProperty(id, "line-opacity", 1.0); // already max
  map.setPaintProperty(id, "line-width", 9);     // 🔥 biggest impact
  map.setPaintProperty(id, "line-blur", 0.5);    // 🔥 subtle glow without color change
}

function resetRoute(i) {
  const id = `suggested-route-${i}`;
  if (!map || !map.getLayer(id)) return;

  map.setPaintProperty(id, "line-opacity", 0.5);
  map.setPaintProperty(id, "line-width", 3);
  map.setPaintProperty(id, "line-blur", 0);
}


function plotResolvedRoute(raw) {
  let coords = raw;

  // If the backend wrapped coords inside an object
  if (raw && typeof raw === "object" && !Array.isArray(raw) && raw.coords) {
    coords = raw.coords;
  }

  if (!map) {
    console.error("❌ Map instance not available.");
    return;
  }

  if (!coords || !Array.isArray(coords)) {
    console.error("⚠️ Invalid coordinates array:", coords);
    return;
  }

  if (coords.length < 2) {
    console.warn("⚠️ Not enough points to plot a route:", coords);
    return;
  }

  const normalizedCoords = coords.map((c) => {
    if (Array.isArray(c) && c.length === 2) return c;          // already [lon, lat]
    if (typeof c === "object" && "lon" in c && "lat" in c) return [c.lon, c.lat];
    console.warn("⚠️ Unrecognized coordinate format:", c);
    return null;
  }).filter(Boolean);

  if (normalizedCoords.length < 2) {
    console.error("⚠️ Could not normalize enough coordinates to draw route:", coords);
    return;
  }

  const geojson = {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: normalizedCoords,
    },
  };

  if (map.getSource("route-src")) {
    map.getSource("route-src").setData(geojson);
  } else {
    map.addSource("route-src", { type: "geojson", data: geojson });
    map.addLayer({
      id: "route-layer",
      type: "line",
      source: "route-src",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-width": 3,
        "line-color": "#d33",
      },
    });
  }

  console.log("🟢 Route plotted successfully:", normalizedCoords.length, "points");
}



async function resolveAndPlotRoute(routeString, origin, destination, altitude, onResult, setHazardData) {
  try {
    const res = await fetch("https://ficon-x.com/route/resolve_manual_route", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        route: routeString,
        origin,
        destination,
        altitude: Number(altitude),
      }),
    });

    if (!res.ok) throw new Error("Failed to resolve route");

    const data = await res.json();
    console.log("🟢 Route resolved:", data);

    if (data.hazardAnalysis) {
      setHazardData(data.hazardAnalysis);
    }


    const coords = Array.isArray(data.coordinates)
      ? data.coordinates
      : data.coords || [];

      // =====================================
      // 🧠 SUPPORT STRUCTURED ROUTE SEGMENTS
      // =====================================

      const routeSegments =

        data.routeSegments ||

        [

          {
            type: "ENROUTE",
            coords
          }
        ];

      // =====================================
      // 🔥 FLATTEN SEGMENTS
      // =====================================

      const allCoords =

        routeSegments.flatMap(

          seg => seg.coords || []
        );

    if (
  !allCoords ||
  allCoords.length < 2
) {
      console.warn("⚠️ Not enough coordinates to plot route.");
      return;
    }

    // 🧼 Remove possible old route layers/sources
    const routeIdsToTry = [
      "main-route", "main-route-layer", "route", "route-main"
    ];
    routeIdsToTry.forEach(id => {
      if (map.getLayer(id)) {
        map.removeLayer(id);
        console.log("🗑 Removed old main route layer:", id);
      }
      if (map.getSource(id)) {
        map.removeSource(id);
        console.log("🗑 Removed old main route source:", id);
      }
    });

    // 🧼 Remove all suggestion and label layers/sources
    const style = map.getStyle();
    if (style) {
      Object.keys(style.sources).forEach((id) => {
        if (
          id.startsWith("route-suggestion-") ||
          id.startsWith("suggestion-src-") ||
          id.startsWith("route-fix-labels-")
        ) {
          if (map.getLayer(id)) {
            map.removeLayer(id);
            console.log("🗑 Removed suggestion layer:", id);
          }
          if (map.getSource(id)) {
            map.removeSource(id);
            console.log("🗑 Removed suggestion source:", id);
          }
        }
      });
    }

    // 🟥 Add new red route to the map
    map.addSource("main-route-src", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: allCoords,
        },
      },
    });

    map.addLayer({
      id: "main-route-layer",
      type: "line",
      source: "main-route-src",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "#d33",
        "line-width": 4,
      },
    });

    // 🔁 Callback with full result
    if (typeof onResult === "function") {
      onResult(data);
    }

  } catch (err) {
    console.error("❌ Route resolve error:", err);
  }
}


function clearAllSuggestedRoutes() {
  const style = map.getStyle();

  if (!style || !style.sources) return;

  Object.keys(style.sources).forEach((id) => {
    if (id.startsWith("route-suggestion-") || id.startsWith("route-fix-labels-")) {
      if (map.getLayer(id)) {
        map.removeLayer(id);
        console.log("🗑 Removed layer:", id);
      }
      if (map.getSource(id)) {
        map.removeSource(id);
        console.log("🗑 Removed source:", id);
      }
    }
  });
}


function clearOtherSuggestedRoutes(keepIndex, totalCount) {
  for (let i = 0; i < totalCount; i++) {
    if (i === keepIndex) continue;

    const id = `route-suggestion-${i}`;

    if (map.getLayer(id)) {
      map.removeLayer(id);
      console.log("🗑 Removed layer:", id);
    }

    if (map.getSource(id)) {
      map.removeSource(id);
      console.log("🗑 Removed source:", id);
    }

    const labelId = `route-fix-labels-${i}`;
    if (map.getLayer(labelId)) {
      map.removeLayer(labelId);
      console.log("🗑 Removed label layer:", labelId);
    }

    if (map.getSource(labelId)) {
      map.removeSource(labelId);
      console.log("🗑 Removed label source:", labelId);
    }
  }
}

function clearMainRoute(map) {

  if (!map) return;

  if (
    map.getLayer("main-route-layer")
  ) {
    map.removeLayer(
      "main-route-layer"
    );
  }

  if (
    map.getSource("main-route-src")
  ) {
    map.removeSource(
      "main-route-src"
    );
  }
}

function plotMainRoute(map, coords) {
  const routeSourceId = "main-route-src";
  const routeLayerId = "main-route-layer";

  // 🧼 Remove previous route if it exists
  if (map.getLayer(routeLayerId)) map.removeLayer(routeLayerId);
  if (map.getSource(routeSourceId)) map.removeSource(routeSourceId);

  // 🟥 Add new red route
  map.addSource(routeSourceId, {
    type: "geojson",
    data: {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: coords.map(p => [p.lon, p.lat])
      }
    }
  });

  map.addLayer({
    id: routeLayerId,
    type: "line",
    source: routeSourceId,
    layout: {
      "line-join": "round",
      "line-cap": "round"
    },
    paint: {
      "line-color": "#d33",  // red
      "line-width": 4
    }
  });
}



function buildHazardExplanation(fixDetails) {
  const reasons = [];

  for (const fix of fixDetails) {
    const {
      fix: name,
      hazard_score = 0,
      radar_dbz = 0,
      forecast_dbz = 0,
      echo_tops_ft = 0,
      sigmet = false,
      airmet = false,
      pirep = null,
      hazard_details = [],
      hazard_summary = ""
    } = fix;

    // Use pre-built backend hazard_summary if available
    if (hazard_summary && typeof hazard_summary === "string") {
      const severity =
        hazard_score >= 0.9 ? "🛑 Extreme" :
        hazard_score >= 0.7 ? "⚠️ Severe" :
        hazard_score >= 0.4 ? "⚠ Moderate" :
        "⚠ Light";

      reasons.push(`${severity} activity near ${name}: ${hazard_summary}`);
      continue;
    }

    // Build explanation manually if summary missing
    const parts = new Set();

    // --- Radar Reflectivity ---
    if (radar_dbz >= 60) parts.add(`extreme radar reflectivity (${radar_dbz} dBZ)`);
    else if (radar_dbz >= 50) parts.add(`strong radar returns (${radar_dbz} dBZ)`);
    else if (radar_dbz >= 40) parts.add(`moderate radar returns (${radar_dbz} dBZ)`);
    else if (radar_dbz >= 30) parts.add(`light radar returns (${radar_dbz} dBZ)`);

    // --- Forecast Reflectivity ---
    if (forecast_dbz >= 60) parts.add(`forecast extreme returns (${forecast_dbz} dBZ)`);
    else if (forecast_dbz >= 50) parts.add(`forecast heavy returns (${forecast_dbz} dBZ)`);
    else if (forecast_dbz >= 40) parts.add(`forecast moderate returns (${forecast_dbz} dBZ)`);
    else if (forecast_dbz >= 30) parts.add(`forecast light activity (${forecast_dbz} dBZ)`);

    // --- Echo Tops (capped at FL400) ---
    const capped_tops = Math.min(echo_tops_ft, 40000);

    if (capped_tops >= 40000) {
      parts.add("Extreme echo tops above FL400");
    } else if (capped_tops >= 30000) {
      parts.add(`Moderate echo tops (≥FL${Math.round(capped_tops / 100)})`);
    }  


    // --- SIGMET / AIRMET ---
    if (sigmet) parts.add(`SIGMET in effect`);
    if (airmet) parts.add(`AIRMET active`);

    // --- PIREP Data ---
    if (pirep && typeof pirep === "object") {
      const { turb, icing, wx, flight_level, aircraft_type } = pirep;
      const pirepInfo = [];

      if (turb) pirepInfo.push(`${turb} turbulence`);
      if (icing) pirepInfo.push(`icing`);
      if (wx) pirepInfo.push(`wx: ${wx}`);

      if (pirepInfo.length > 0) {
        let infoStr = pirepInfo.join(", ");
        if (flight_level || aircraft_type) {
          infoStr += ` reported by ${aircraft_type || "aircraft"} at FL${flight_level || "??"}`;
        }
        parts.add(infoStr);
      }
    }

    // --- Backend hazard_details (array of strings) ---
    if (Array.isArray(hazard_details)) {
      for (const h of hazard_details) {
        if (typeof h === "string" && h.trim().length > 0) {
          parts.add(h);
        }
      }
    }

    // --- Skip if nothing useful ---
    if (parts.size === 0 && hazard_score < 0.15) continue;

    // --- Fallback description if needed ---
    if (parts.size === 0) {
      parts.add("elevated hazard signal detected");
    }

    // --- Severity level label ---
    const severity =
      hazard_score >= 0.9 ? "🛑 Extreme" :
      hazard_score >= 0.7 ? "⚠️ Severe" :
      hazard_score >= 0.4 ? "⚠ Moderate" :
      "⚠ Light";
    
     // ❌ Skip anonymous MID points like "MID0", "MID1", etc.
    if (typeof name === "string" && name.startsWith("MID")) continue;
 

    // --- Final formatted explanation line ---
    reasons.push(`${severity} activity near ${name}: ${Array.from(parts).join(", ")}`);
  }

  // --- Route-wide fallback if all fixes are clean ---
  if (reasons.length === 0) {
    const hasMildActivity = fixDetails.some(f => f.hazard_score >= 0.1);
    if (hasMildActivity) {
      reasons.push("⚠ Light weather activity detected along this route");
    } else {
      reasons.push("✅ No significant weather hazards detected along this route");
    }
  }

  return reasons;
}

const handleResetFlightPlan = () => {
  setCallsign("");   
  setOrigin("");
  setDestination("");
  setAltitude("");
  setRoute("");
  setRouteTokens([]);
  setSelectedRouteString("");
  setRouteSuggestions([]);
  setSuggestionsLoading(false);
  setSuggestionsError(null);
  setModalOpen(true);  // optionally reopen the modal
  window.scrollTo({ top: 0, behavior: "smooth" }); // scroll to top
};





// Inside your MapComponent function (or the component holding the drawer)
useEffect(() => {
  const handleKey = (e) => {
    if (e.key === "Escape") {
      setActivePanel(null);
    }
  };

  window.addEventListener("keydown", handleKey);
  return () => window.removeEventListener("keydown", handleKey);
}, []);

const flightPlanContainer = {
  marginTop: 200,
  marginLeft: 260, // To avoid overlapping the sidebar
  fontFamily: "monospace",
  fontSize: "14px",
  color: "#fff",
  textShadow: "0 1px 3px rgba(0,0,0,0.7)",
  maxWidth: "70%",
  wordBreak: "break-word",
  zIndex: 3000,
  position: "relative",
  background: "transparent",
  padding: 0,
  border: "none",
  boxShadow: "none",
};


useEffect(() => {

  console.log(
    "🧪 ACTIVE ROUTE:",
    activeRoute
  );

  console.log(
    "🧪 ACTIVE FLIGHT:",
    activeFlight
  );

  console.log(
    "🧪 SHARED LEG:",
    sharedActiveLegIndex
  );

  console.log(
    "🧪 CURRENT LEG:",
    currentLegIndex
  );

}, [

  activeRoute,
  activeFlight,
  sharedActiveLegIndex,
  currentLegIndex
]);


const flightPlanHeader = {
  fontSize: 15,
  fontWeight: 700,
  color: "#1e88e5",
  marginBottom: 8,
};

const flightPlanText = {
  fontSize: 13,
  lineHeight: 1.5,
  color: "#333",
  wordBreak: "break-word",
};

const confirmButtonStyle = {
  padding: "10px 20px",
  backgroundColor: "#007bff",
  color: "white",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
  transition: "background-color 0.2s ease",
};

const handleConfirmRoute = async () => {
  await resolveAndPlotRoute(
    confirmedRouteString,
    origin,
    destination,
    altitude,
    null,
    setHazardData
  );
  setRouteConfirmed(true);
};


console.log("📦 showChartsPanel =", showChartsPanel);

const authOverlay = (

<div className="op-auth-bar">

  <div className="op-auth-brand"> 

   <div className="op-auth-dot" />

    <span className="op-auth-title">
      FICON-X
    </span>

  </div>

  {!user ? (

    <button
      className="op-auth-btn"
      onClick={() =>
        setShowAuthModal(true)
      }
    >
      🔐 Operational Access
    </button>

  ) : (

      <div style={{

        position: "relative"
      }}>

        <button

          onClick={() =>
            setShowProfileMenu(
              !showProfileMenu
            )
          }

          style={{

            background: "#111",

            color: "white",

            border: "1px solid #444",

            borderRadius: "8px",

            padding: "10px 14px",

            cursor: "pointer"
          }}
        >

          👨‍✈️ {

            user?.username ||

            user?.email
          }

        </button>

        {showProfileMenu && (

<div style={{

  position: "fixed",

  top: "60px",

  left: "50%",

  transform: "translateX(-50%)",

  width: "240px",

background: "#303640",

border: "1px solid #5c6672",

color: "white",

  borderRadius: "10px",

  padding: "12px",

  zIndex: 9999999
}}>

            <div>

              👨‍✈️ {

                user?.username ||

                "Unknown"
              }

            </div>

            <div>

              🏠 {

                user?.home_airport ||

                "No Home Airport"
              }

            </div>

            <div>

              ✈️ {

                user?.default_aircraft ||

                "No Aircraft"
              }

            </div>

            <button
  onClick={() => {

    setShowProfileMenu(false);

    setShowPilotProfile(true);

  }}

  style={{

    width: "100%",

    marginTop: "10px",

    padding: "10px",

    background: "#303640",

    color: "white",

    border: "1px solid #5c6672",

    borderRadius: "8px",

    cursor: "pointer"

  }}
>

  👤 Pilot Profile

</button>

<button
    onClick={() => {
        setShowProfileMenu(false);
        setShowPersonalMinimums(true);
    }}
  style={{

    width: "100%",

    marginTop: "10px",

    padding: "10px",

    background: "#303640",

    color: "white",

    border: "1px solid #5c6672",

    borderRadius: "8px",

    cursor: "pointer"

  }}

>
    Personal Minimums
</button>

            <button
  onClick={() => {

    setShowProfileMenu(false);

    setShowFlightsModal(true);

  }}

  style={{

    width: "100%",

    marginTop: "10px",

    padding: "10px",

    background: "#303640",

    color: "white",

    border: "1px solid #5c6672",

    borderRadius: "8px",

    cursor: "pointer"

  }}
>

  ✈️ My Flights

</button>

            <button

              onClick={logout}

              style={{

                width: "100%",

                marginTop: "12px"
              }}
            >

              Logout

            </button>

          </div>
        )}

      </div>
    )}
    {showAuthModal && (

      <AuthModal

        onClose={() =>
          setShowAuthModal(false)
        }

      />
    )}

    {showFlightsModal && (

  <FlightsModal

    onClose={() =>
      setShowFlightsModal(false)
    }

  />

)}

{showPilotProfile && (
    <PilotProfile
        onClose={() => setShowPilotProfile(false)}
    />
)}

{showPersonalMinimums && (
    <PersonalMinimums
        onClose={() => setShowPersonalMinimums(false)}
    />
)}


  </div>
);

const alt =
  activeFlight?.alternate;

// =====================================
// 🟠 LIVE ALT DISTANCE
// =====================================

const currentAltDistance = (() => {

  // =====================================
  // 🚫 NO ALTERNATE ROUTE
  // =====================================

  if (
    !alt?.route?.coords?.length
  ) {

    return (
      alt?.distanceNm ??
      null
    );
  }

  const coords =
    alt.route.coords;

  // =====================================
  // 📍 ALTERNATE DESTINATION POSITION
  //
  // Last coordinate in the stored
  // alternate route is the alternate
  // airport.
  // =====================================

  const alternatePosition =
    coords[
      coords.length - 1
    ];

  if (!alternatePosition) {

    return (
      alt?.distanceNm ??
      null
    );
  }

  // =====================================
  // ✈️ NO LIVE AIRCRAFT POSITION YET
  //
  // Use original calculated distance
  // until tracking becomes available.
  // =====================================

  if (!aircraftPosition) {

    return (
      alt?.distanceNm ??
      null
    );
  }

  // =====================================
  // 🟠 LIVE DIRECT DIVERSION DISTANCE
  //
  // IMPORTANT:
  //
  // coords[0] is the aircraft position
  // from when the alternate was created.
  //
  // Do NOT route back through coords[0].
  //
  // For direct alternate mode:
  //
  // CURRENT AIRCRAFT
  //       ↓
  // ALTERNATE AIRPORT
  // =====================================

  if (
    !alt.mode ||
    alt.mode === "direct"
  ) {

    return getDistanceNm(

      aircraftPosition,

      alternatePosition
    );
  }

  // =====================================
  // 🔮 FUTURE NON-DIRECT MODE FALLBACK
  //
  // Preserve stored distance until we
  // build active-leg tracking for an
  // airway/procedure alternate route.
  // =====================================

  return (
    alt?.distanceNm ??
    null
  );

})();
// =====================================
// 🟠 LIVE ALT ETE
// =====================================

const currentAltMinutes = (() => {

  const currentGs =
    Number(
      aircraftGroundspeed ||
      sharedAircraftState?.speed ||
      activeRoute?.meta?.tas ||
      tasKts
    );

  console.log(
    "✈️ ALT PANEL GS:",
    currentGs
  );

  // 🚫 NOT MOVING
  if (

    !currentGs ||

    currentGs < 50 ||

    currentGs > 700

  ) {

    return null;
  }

  if (!currentAltDistance) {

    return null;
  }

  return (
    currentAltDistance / currentGs
  ) * 60;

})();

// =====================================
// 🛬 LIVE DESTINATION DISTANCE
// =====================================

const currentDestDistance = (() => {

  if (!aircraftPosition) {
    return null;
  }

  const coords =
    activeRoute?.route?.coords ||
    activeFlight?.route?.coords ||
    [];

  if (!coords.length) {
    return null;
  }

  let totalNm = 0;

  // Start from the aircraft's current position
  let previous =
    aircraftPosition;

  // Only count the remaining route
  const startIndex =
    Math.min(
      currentLegIndex + 1,
      coords.length - 1
    );

  for (
    let i = startIndex;
    i < coords.length;
    i++
  ) {

    totalNm +=
      getDistanceNm(
        previous,
        coords[i]
      );

    previous =
      coords[i];
  }

  return totalNm;

})();


// =====================================
// 🛬 LIVE DESTINATION ETE
// =====================================

const currentDestMinutes = (() => {

  const currentGs =
    Number(
      aircraftGroundspeed ||
      sharedAircraftState?.speed ||
      activeRoute?.meta?.tas ||
      tasKts
    );

  if (
    !currentGs ||
    currentGs < 50 ||
    currentGs > 700
  ) {
    return null;
  }

  if (currentDestDistance == null) {
    return null;
  }

  return (
    currentDestDistance /
    currentGs
  ) * 60;

})();
// =====================================
// 🕒 KEEP LATEST LIVE ETA FOR WEATHER
// =====================================

latestDestMinutesRef.current =
  currentDestMinutes;

latestAltMinutesRef.current =
  currentAltMinutes;



// =====================================
// 🌦️ LIVE IN-FLIGHT ETA WEATHER MONITOR
// =====================================

useEffect(() => {

  if (!isActiveFlight) {
    return;
  }

  let cancelled = false;

  const refreshEtaWeather =
    async () => {

      try {

        // =====================================
        // 🛬 DESTINATION ETA WEATHER
        // =====================================

      const latestDestMinutes =
        latestDestMinutesRef.current;

      if (
        destIcao?.length === 4 &&
        latestDestMinutes != null
      ) {

        const etaHours =
          latestDestMinutes / 60;

          const res =
            await fetch(

              `https://ficon-x.com/route/pilot-briefing?icao=${destIcao}&type=arrival&eta_hours=${etaHours}`

            );

          const data =
            await res.json();

          if (!cancelled) {

            setLiveDestEtaWeather(
              data
            );

            console.log(
              "🌦️ LIVE DEST ETA WEATHER:",
              destIcao,
              etaHours,
              data
            );
          }
        }


        // =====================================
        // 🟠 ALTERNATE ETA WEATHER
        // =====================================

        const latestAltMinutes =
          latestAltMinutesRef.current;

        if (
          altIcao?.length === 4 &&
          latestAltMinutes != null
        ) {

          const etaHours =
            latestAltMinutes / 60;

          const res =
            await fetch(

              `https://ficon-x.com/route/pilot-briefing?icao=${altIcao}&type=arrival&eta_hours=${etaHours}`

            );

          const data =
            await res.json();

          if (!cancelled) {

            setLiveAltEtaWeather(
              data
            );

            console.log(
              "🌦️ LIVE ALT ETA WEATHER:",
              altIcao,
              etaHours,
              data
            );
          }
        }

      } catch (err) {

        console.error(
          "❌ Live ETA weather refresh failed:",
          err
        );
      }
    };


  // 🔥 Fetch immediately
  refreshEtaWeather();


  // 🔥 Then refresh every 5 minutes
  const interval =
    setInterval(
      refreshEtaWeather,
      5 * 60 * 1000
    );


  return () => {

    cancelled = true;

    clearInterval(
      interval
    );
  };

}, [

  isActiveFlight,

  destIcao,

  altIcao

]);

// =====================================
// ⚠️ PILOT PERSONAL MINIMUM WARNING ENGINE
// =====================================

useEffect(() => {

  if (!isActiveFlight || !user) {

    setInflightWeatherWarnings([]);

    return;
  }

  const warnings = [];


  // =====================================
  // 🛬 DESTINATION
  // =====================================

  const destForecast =
    liveDestEtaWeather?.eta_weather;

  if (destForecast) {

const rawCeiling =
  destForecast.ceiling;

const rawVisibility =
  destForecast.visibility_num ??
  destForecast.visibility;

const ceiling =
  rawCeiling == null
    ? null
    : Number(rawCeiling);

const visibility =
  rawVisibility == null
    ? null
    : Number(rawVisibility);

    const minCeiling =
      Number(
        user.ifr_ceiling_min_ft
      );

    const minVisibility =
      Number(
        user.ifr_visibility_min_sm
      );


if (
  ceiling !== null &&
  Number.isFinite(ceiling) &&
  Number.isFinite(minCeiling) &&
  ceiling < minCeiling
) {

      warnings.push({

        id: "dest-ceiling",

        airport: destIcao,

        type: "Destination",

        message:
          `Ceiling ${ceiling} ft — below your ${minCeiling} ft minimum`

      });
    }


    if (
      Number.isFinite(visibility) &&
      Number.isFinite(minVisibility) &&
      visibility < minVisibility
    ) {

      warnings.push({

        id: "dest-visibility",

        airport: destIcao,

        type: "Destination",

        message:
          `Visibility ${visibility} SM — below your ${minVisibility} SM minimum`

      });
    }
  }


  // =====================================
  // 🟠 ALTERNATE
  // =====================================

  const altForecast =
    liveAltEtaWeather?.eta_weather;

  if (altForecast) {

const rawCeiling =
  altForecast.ceiling;

const rawVisibility =
  altForecast.visibility_num ??
  altForecast.visibility;

const ceiling =
  rawCeiling == null
    ? null
    : Number(rawCeiling);

const visibility =
  rawVisibility == null
    ? null
    : Number(rawVisibility);

    const minCeiling =
      Number(
        user.alternate_ceiling_min_ft
      );

    const minVisibility =
      Number(
        user.alternate_visibility_min_sm
      );


if (
  ceiling !== null &&
  Number.isFinite(ceiling) &&
  Number.isFinite(minCeiling) &&
  ceiling < minCeiling
) {

      warnings.push({

        id: "alt-ceiling",

        airport: altIcao,

        type: "Alternate",

        message:
          `Ceiling ${ceiling} ft — below your ${minCeiling} ft alternate minimum`

      });
    }


    if (
      Number.isFinite(visibility) &&
      Number.isFinite(minVisibility) &&
      visibility < minVisibility
    ) {

      warnings.push({

        id: "alt-visibility",

        airport: altIcao,

        type: "Alternate",

        message:
          `Visibility ${visibility} SM — below your ${minVisibility} SM alternate minimum`

      });
    }
  }


  console.log(
    "⚠️ IN-FLIGHT WEATHER WARNINGS:",
    warnings
  );

  setInflightWeatherWarnings(
    warnings
  );

}, [

  isActiveFlight,

  user,

  destIcao,

  altIcao,

  liveDestEtaWeather,

  liveAltEtaWeather

]);


return (

<>
{authOverlay}
  
  
  <div className="flight-plan-page">
    {routeTokens?.length > 0 && (
      <>
        {!routeConfirmed && (
          <>
            <div className="bubble-overlay">
              <div className="bubble-wrap">
                <RouteBubbleBar
                  tokens={routeTokens}
                  activeIndex={activeTokenIndex}
                  onSelect={handleBubbleSelect}
onChange={async (updatedTokens) => {
  console.log("🟢 Updated routeTokens:", updatedTokens);
  setRouteTokens(updatedTokens);

  const joinedRoute = updatedTokens.join(" ");
  setRoute(joinedRoute);
  setSelectedRouteString(joinedRoute);

  try {
const resolved = await resolveRouteWithCoords(
  joinedRoute,
  origin?.trim().toUpperCase(),
  destination?.trim().toUpperCase(),
  altitude
);

if (!resolved || !resolved.coords || resolved.coords.length < 2) {
  console.error("❌ Could not resolve from bubble bar:", resolved);
  return;
}

setRouteTokens(resolved.expanded_tokens || resolved.names || []);
setRouteResult(resolved);

// 🔥 ADD THIS
if (resolved?.hazardAnalysis) {
  console.log("🔥 BubbleBar hazardAnalysis:", resolved.hazardAnalysis);
  setHazardData(resolved.hazardAnalysis);
} else {
  console.warn("⚠️ No hazardAnalysis returned from BubbleBar");
}

plotRoute(resolved);

if (map) {
  map.dragPan.enable();
  map.scrollZoom.enable();
  map.boxZoom.enable();
}
  } catch (err) {
    console.error("❌ BubbleBar route resolution error:", err);
  }
}}



                />
              </div>
            </div>



          </>
        )}


{showConfirmBox &&
 !routeConfirmed &&
 confirmedRouteString && (

  <div
    style={{
      position: "absolute",
      top: 80,
      left: 100,
      right: 20,
      zIndex: 1000,
      background: "rgba(0, 0, 0, 0.75)",
      color: "white",
      padding: "16px 20px",
      borderRadius: 14,
      maxWidth: "calc(100% - 140px)",
      boxShadow:
        "0 6px 18px rgba(0, 0, 0, 0.4)",

      fontSize: 15,

      fontFamily:
        "system-ui, sans-serif",

      lineHeight: 1.5,

      backdropFilter: "blur(6px)",

      overflowWrap: "break-word",
    }}
  >

    {/* =====================================
        TITLE
    ===================================== */}

    <div
      style={{
        fontWeight: 700,
        fontSize: 16,
        marginBottom: 8
      }}
    >
      Flight Plan
    </div>

    {/* =====================================
        ROUTE TEXT
    ===================================== */}

    <div
      style={{
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        lineHeight: 1.6,
        fontSize: 14,
        fontFamily: "monospace",
      }}
    >
      {confirmedRouteString}
    </div>

    {/* =====================================
        EDIT BUTTON
    ===================================== */}

    <div
      style={{
        marginTop: 12,
        display: "flex",
        gap: 10,
        alignItems: "center",
      }}
    >

      <button
        onClick={() => {

          setConfirmRouteDraft(
            confirmedRouteString
          );

          setEditingConfirmRoute(true);

        }}
        style={{
          background:
            "rgba(255,255,255,0.08)",

          color: "white",

          padding: "6px 12px",

          border:
            "1px solid rgba(255,255,255,0.15)",

          borderRadius: 8,

          cursor: "pointer",

          fontSize: 13,

          fontWeight: 600,
        }}
      >
        ✏️ Edit Route
      </button>

    </div>

    {/* =====================================
        EDITOR
    ===================================== */}

    {editingConfirmRoute && (

      <div
        style={{
          marginTop: 14,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >

        <textarea
          value={confirmRouteDraft}

          onChange={(e) =>
            setConfirmRouteDraft(
              e.target.value.toUpperCase()
            )
          }

          style={{
            width: "100%",
            minHeight: 90,

            background:
              "rgba(255,255,255,0.06)",

            color: "white",

            border:
              "1px solid rgba(255,255,255,0.12)",

            borderRadius: 10,

            padding: 12,

            fontSize: 14,

            fontFamily: "monospace",

            resize: "vertical",

            outline: "none",
          }}
        />

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
          }}
        >

          {/* CANCEL */}

          <button
            onClick={() => {

              setEditingConfirmRoute(false);

            }}
            style={{
              background:
                "rgba(255,255,255,0.08)",

              color: "white",

              padding: "8px 14px",

              border:
                "1px solid rgba(255,255,255,0.15)",

              borderRadius: 8,

              cursor: "pointer",

              fontWeight: 600,
            }}
          >
            Cancel
          </button>

          {/* APPLY */}

          <button
            onClick={async () => {

              try {

                console.log(
                  "🛠 Applying edited route:",
                  confirmRouteDraft
                );

                await resolveAndPlotRoute(
                  confirmRouteDraft,
                  origin,
                  destination,
                  altitude,
                  null,
                  setHazardData
                );

                // 🔥 UPDATE ROUTE
                setConfirmedRouteString(
                  confirmRouteDraft
                );

                setSelectedRouteString(
                  confirmRouteDraft
                );

                // 🔥 REBUILD BUBBLE TOKENS
setRouteTokens(
  confirmRouteDraft
    .trim()
    .split(/\s+/)
);

                setEditingConfirmRoute(false);

              } catch (err) {

                console.error(
                  "❌ Route edit failed:",
                  err
                );
              }

            }}
            style={{
              background: "#00b894",

              color: "white",

              padding: "8px 14px",

              border: "none",

              borderRadius: 8,

              cursor: "pointer",

              fontWeight: 700,
            }}
          >
            Apply Changes
          </button>

        </div>

      </div>
    )}

    {/* =====================================
        ACTION BUTTONS
    ===================================== */}

    <div
      style={{
        marginTop: 14,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      {/* =====================================
          ⬅️ BACK TO PLANNING
      ===================================== */}

      <button
onClick={() => {

  console.log(
    "⬅️ Returning to planning mode"
  );

  // 🔥 REOPEN FLIGHT PLANNER
  setActivePanel("flight");

  // 🔥 CLEAR ROUTE WORKFLOW
  setRouteTokens([]);

  setConfirmedRouteString("");

  setSelectedRouteString("");

  setRouteResult(null);

  setHazardData(null);

  // 🔥 RESET CONFIRM
  setRouteConfirmed(false);

}}
        style={{
          background:
            "rgba(255,255,255,0.08)",

          color: "white",

          padding: "8px 16px",

          border:
            "1px solid rgba(255,255,255,0.15)",

          borderRadius: 10,

          cursor: "pointer",

          fontSize: 14,

          fontWeight: 600,
        }}
      >
        ← Back To Flight Planning
      </button>

      {/* =====================================
          ✅ CONFIRM ROUTE
      ===================================== */}

      <button
        onClick={async () => {

          console.log(
            "✅ Confirming route"
          );

          await resolveAndPlotRoute(
            confirmedRouteString,
            origin,
            destination,
            altitude,
            null,
            setHazardData
          );

          // 🔥 HIDE CONFIRM BOX
          setShowConfirmBox(false);

          // 🔥 ENTER BRIEFING
          setRouteConfirmed(true);

          setFlightStage("briefing");

        }}
        style={{
          background: "#00b894",

          color: "white",

          padding: "8px 16px",

          border: "none",

          borderRadius: 10,

          cursor: "pointer",

          fontSize: 14,

          fontWeight: 600,

          boxShadow:
            "0 2px 6px rgba(0,0,0,0.2)",

          transition:
            "background 0.2s",
        }}
      >
        ✅ Confirm Route
      </button>

    </div>

  </div>
)}

        {/* ✅ AFTER confirmation — Preflight briefing screen */}
        {flightStage === "briefing" &&
          originData &&
          destinationData && (

<PreflightBriefing

  filedRoute={

    confirmedRouteString
      ?.split(/\s+/)
      .filter(Boolean)

    || []
  }

    onRequireLogin={() =>
        setShowAuthModal(true)
    }
  callsign={callsign}
  origin={origin}
  destination={destination}

  sidExits={sidExits}

  route={selectedRouteString}

  altitude={altitude}

  tas={tasKts}
  burnRate={burnRate}

  aircraftType={aircraftType}
  cruiseMach={cruiseMach}

  zfw={zfw}
  tow={tow}
  ldw={ldw}

  mzfw={mzfw}
  mtow={mtow}
  mlw={mlw}

  payload={payload}

  originData={originData}
  destinationData={destinationData}

  enrouteHazards={hazardData}

  originPireps={originPireps}
  destinationPireps={destinationPireps}

  onChartSelect={(chart) => {

    console.log(
      "📄 Opening chart from Preflight:",
      chart.name
    );

    openChartWindow(chart);
  }}

  savedCharts={savedCharts}
  setSavedCharts={setSavedCharts}

  taxiOutMinutes={taxiOutMinutes}
  taxiInMinutes={taxiInMinutes}

  contingencyMinutes={contingencyMinutes}
  reserveMinutes={reserveMinutes}

  taxiOutBurn={taxiOutBurn}
  taxiInBurn={taxiInBurn}

  contingencyBurn={contingencyBurn}
  reserveBurn={reserveBurn}

  alternateIcao={alternateInput}

    selectedSid={selectedSid}
  setSelectedSid={setSelectedSid}

  selectedSidTransition={selectedSidTransition}
  setSelectedSidTransition={setSelectedSidTransition}

  selectedStar={selectedStar}
  setSelectedStar={setSelectedStar}

  selectedStarTransition={selectedStarTransition}
  setSelectedStarTransition={setSelectedStarTransition}

  selectedApproach={selectedApproach}
  setSelectedApproach={setSelectedApproach}

  selectedArrivalRunway={selectedArrivalRunway}
  setSelectedArrivalRunway={setSelectedArrivalRunway}

  selectedArrivalIAF={selectedArrivalIAF}
  setSelectedArrivalIAF={setSelectedArrivalIAF}

  availableSids={availableSids}
sidTransitions={sidTransitions}

availableStars={availableStars}
starTransitions={starTransitions}

arrivalRunways={arrivalRunways}

availableApproaches={availableApproaches}

availableIAFs={availableIAFs}

sidRunways={sidRunways}

selectedSidRunway={selectedSidRunway}

setSelectedSidRunway={
  setSelectedSidRunway
}

updateDepartureProcedure={updateDepartureProcedure}
updateArrivalProcedure={updateArrivalProcedure}

onStartFlightComplete={() => {

  console.log(
    "🧭 Start Flight → activating saved flight"
  );

  try {

    const savedFlight =
      JSON.parse(
        localStorage.getItem("activeFlight") || "null"
      );

    if (!savedFlight) {

      console.error(
        "❌ No activeFlight found in localStorage"
      );

      return;
    }

    console.log(
  "🚀 ACTIVATING FLIGHT:",
  savedFlight
);

// =====================================
// 🧹 CLEAR PREFLIGHT RED ROUTE
// =====================================

clearMainRoute(
  mapRef.current
);

// =====================================
// 🔥 ACTIVATE FLIGHT IN REACT STATE
// =====================================

setActiveFlight(
  savedFlight
);

setActiveRoute(
  savedFlight
);

    setConfirmedRouteString(
      savedFlight.routeText || ""
    );

    setRouteConfirmed(
      true
    );

    setFlightStage(
      "active"
    );

    setIsFlightActive(
      true
    );

    setLocalFlightActive(
      true
    );

    // =====================================
    // CLOSE PREFLIGHT
    // =====================================

    setActivePanel(
      null
    );

    console.log(
      "✅ Active flight initialized without page reload"
    );

  } catch (err) {

    console.error(
      "❌ Failed to activate flight:",
      err
    );

  }

}}
/>

        )}

        {/* ✅ Full-screen chart overlay */}
        {/* ===================================== */}

      </>

    )}

<canvas
  ref={canvasRef}
  style={{
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 10,
    pointerEvents: drawingMode ? "auto" : "none"
  }}
/>

{/* Map + overlays */}
<div className="map-shell relative">
<div
  className="location-search-box"
  style={{

    position: "absolute",

    top: "0px",

    left: "10px",

    zIndex: 3000

  }}
>

  <span
    style={{

      position: "absolute",

      left: "12px",

      top: "50%",

      transform:
        "translateY(-50%)",

      pointerEvents:
        "none",

      color:
        "#00bfff",

      textShadow:
        "0 0 8px rgba(0,191,255,0.8)",

      fontSize:
        "16px",

      zIndex: 10

    }}
  >
    🔍
  </span>

  <input
    type="text"

    value={locationSearch}

    placeholder="Search Fix / Airport"

    style={{

      width: "280px",

      paddingLeft: "38px",

      paddingTop: "10px",

      paddingBottom: "10px",

      background:
        "rgba(18,18,22,0.88)",

      color:
        "#ffffff",

      border:
        "1px solid rgba(0,191,255,0.35)",

      borderRadius:
        "999px",

      backdropFilter:
        "blur(12px)",

      boxShadow:
        "0 0 18px rgba(0,191,255,0.15)",

      fontSize:
        "14px",

      fontWeight:
        "600",

      outline:
        "none",

      boxSizing:
        "border-box"

    }}

    onChange={(e) =>
      setLocationSearch(
        e.target.value
      )
    }

    onKeyDown={async (e) => {

      if (
        e.key !== "Enter"
      )
        return;

      try {

        console.log(
          "🔎 Searching:",
          locationSearch
        );

        const res =
          await fetch(

            `https://ficon-x.com/route/find_location/${locationSearch}`

          );

        const data =
          await res.json();

        console.log(
          "🔎 RESULT:",
          data
        );

        if (
          !data.found
        ) {

          alert(
            "Location not found"
          );

          return;

        }

        mapRef.current.flyTo({

          center: [

            data.lon,

            data.lat

          ],

          zoom: 10,

          essential: true,

          duration: 2000

        });

      } catch (err) {

        console.error(
          "❌ SEARCH ERROR:",
          err
        );

      }

    }}
  />

</div>

{/* =====================================
    🧰 BOTTOM UTILITY BAR
===================================== */}

{localFlightActive && (

  <div
    style={{

      position: "absolute",

      bottom: 12,

      left: "50%",

      transform: "translateX(-50%)",

      display: "flex",

      gap: 8,

      zIndex: 9999,

      background: "rgba(20,20,20,0.88)",

      padding: "8px 12px",

      borderRadius: 12,

      border:
        "1px solid rgba(255,255,255,0.12)",

      backdropFilter: "blur(8px)"
    }}
  >

    {/* =====================================
        📋 BRIEF
    ===================================== */}

    <button

      className="action-btn"

      onClick={() =>

        setShowInflightBrief(
          prev => !prev
        )
      }
    >
      📋 BRIEF
    </button>

{/* =====================================
    📄 SAVED CHARTS
===================================== */}

{savedCharts.map(chart => (

  <div

    key={
      chart.uniqueId ||
      chart.id
    }

    style={{

      position: "relative",

      display: "flex",

      alignItems: "center"
    }}
  >

{/* =====================================
    📄 OPEN CHART
===================================== */}

<button

  type="button"

  onClick={() => {

    console.log(
      "📄 Opening chart:",
      chart.name
    );

    openChartWindow(chart);
  }}

  style={{

    background: "transparent",

    border: "none",

    cursor: "pointer",

    flex: 1,

    fontSize: "0.92em",

    color: "#d9ecff",

    fontWeight: 500,

    textAlign: "left",

    padding: 0
  }}
>
  {chart.name}
</button>

    {/* =====================================
        ❌ REMOVE SAVED CHART
    ===================================== */}

    <button

      type="button"

      onClick={(e) => {

        e.stopPropagation();

        const updated =
          savedCharts.filter(

            c => c.id !== chart.id
          );

        setSavedCharts(updated);

        localStorage.setItem(

          "savedCharts",

          JSON.stringify(updated)
        );

        console.log(
          "🗑️ Removed saved chart:",
          chart.name
        );
      }}

      style={{

        position: "absolute",

        top: "-4px",

        right: "-4px",

        width: "18px",

        height: "18px",

        borderRadius: "50%",

        border: "none",

        background: "#d32f2f",

        color: "white",

        fontSize: "10px",

        fontWeight: "bold",

        cursor: "pointer",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        padding: 0,

        zIndex: 2
      }}
    >
      ✕
    </button>

  </div>
))}

</div>

)}

{showSegmentEditor && selectedSegment && (

  <div
    style={{

      position: "absolute",

      top: 200,

      left: "50%",

      transform:
        "translateX(-50%)",

      zIndex: 99999,

      background:
        "rgba(20,20,20,0.95)",

      padding: 12,

      borderRadius: 10,

      border:
        "1px solid #00FFFF"
    }}
  >

    <div>

      🧲 Segment:

      {" "}

      {selectedSegment.start}

      {" → "}

      {selectedSegment.end}

    </div>

    <input

      value={
        segmentInsertFix
      }

      onChange={(e) =>

        setSegmentInsertFix(
          e.target.value
        )

      }

      placeholder="Insert Fix"

    />

<button

  onClick={async () => {

    console.log(
      "🧲 SEGMENT INSERT",
      selectedSegment,
      segmentInsertFix
    );

    if (
      !selectedSegment ||
      !segmentInsertFix
    ) {
      return;
    }

    const currentRoute =

      activeFlight
        ?.routeStructure
        ?.activeTokens || [];

    setPreviousRouteState({

      activeTokens:

        activeFlight
          ?.routeStructure
          ?.activeTokens,

      activeObjects:

        activeFlight
          ?.routeStructure
          ?.activeObjects,

      route:

        activeFlight?.route,

      waypoints:

        activeFlight?.waypoints

    });

    const newTokens = [
      ...currentRoute
    ];

    newTokens.splice(

      selectedSegment.index + 1,

      0,

      segmentInsertFix
        .toUpperCase()

    );

    const insertRoute =

      newTokens.join(" ");

    console.log(
      "🧲 SEGMENT ROUTE",
      insertRoute
    );

const resolved =
  await resolveRouteWithCoords(

    insertRoute,

    activeFlight.meta.origin,

    activeFlight.meta.destination,

    activeFlight.meta.altitude,

    null,
    null,
    null,

    null,
    null,
    null,

    null,
    null
  );

console.log(
  "➕ INSERT RESOLVED",
  resolved
);

console.log(
  "➕ INSERT RESOLVED NAMES",
  resolved?.names?.slice(0,20)
  );
setActiveFlight(prev => {

  console.log(
    "🗑 DELETE RESOLVED NAMES",
    resolved.names?.slice(0,10)
  );

  console.log(
    "🗑 BEFORE ACTIVE TOKENS",
    prev.routeStructure?.activeTokens?.slice(0,10)
  );

  const updatedFlight = {

    ...prev,

    routeStructure: {

      ...prev.routeStructure,

      activeTokens:
        resolved.names || [],

      activeObjects:
        resolved.coordObjects || []

    },

    route: {

      ...prev.route,

      coords:
        resolved.coords || [],

      waypoints:
        resolved.waypoints || [],

      coordObjects:
        resolved.coordObjects || []

    },

    waypoints:
      resolved.waypoints || []

  };

  setActiveRoute(
    updatedFlight
  );

  console.log(
    "🗑 FIRST TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  console.log(
    "🗑 AFTER ACTIVE TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  return updatedFlight;

});

plotRoute({

  coords:
    resolved.coords,

  coordObjects:
    resolved.coordObjects,

  waypoints:
    resolved.waypoints,

  routeText:
    insertRoute,

  origin:
    resolved.origin,

  destination:
    resolved.destination

});
if (
  mapRef.current?.getLayer(
    "selected-segment"
  )
) {

  try {

    mapRef.current.removeLayer(
      "selected-segment"
    );

  } catch {}

}

if (
  mapRef.current?.getSource(
    "selected-segment"
  )
) {

  try {

    mapRef.current.removeSource(
      "selected-segment"
    );

  } catch {}

}
setSelectedSegment(
  null
);

setShowSegmentEditor(
  false
);

setSegmentInsertFix(
  ""
);


  }}

>
  INSERT
</button>

    <button

      onClick={() => {

        setShowSegmentEditor(
          false
        );

      }}

    >

      CANCEL

    </button>

  </div>

)}


{localFlightActive && (

  

<InflightRouteBar

showUndo={
  !!previousRouteState
}

onUndoAmendment={() => {

  if (!previousRouteState) {

    console.warn(
      "⚠️ No undo snapshot"
    );

    return;
  }

  console.log(
    "↶ RESTORING ROUTE"
  );

  console.log(
    previousRouteState
  );

  const updatedFlight = {

    ...activeFlight,

    routeStructure: {

      ...activeFlight.routeStructure,

      activeTokens:
        previousRouteState.activeTokens,

      activeObjects:
        previousRouteState.activeObjects

    },

    route:
      previousRouteState.route,

    waypoints:
      previousRouteState.waypoints

  };

  setActiveFlight(
    updatedFlight
  );

  setActiveRoute(
    updatedFlight
  );

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  plotRoute({

    coords:
      previousRouteState.route
        ?.coords,

    coordObjects:
      previousRouteState.activeObjects,

    waypoints:
      previousRouteState.waypoints,

    routeText:
      previousRouteState
        ?.activeTokens
        ?.join(" "),

    origin:
      updatedFlight.origin,

    destination:
      updatedFlight.destination

  });

  setPreviousRouteState(
    null
  );

}}


onDirectTo={async (
  token,
  index,
  closePanel
) => {

  const currentRoute =

    activeFlight
      ?.routeStructure
      ?.activeTokens || [];

   setPreviousRouteState({

  activeTokens:

    activeFlight
      ?.routeStructure
      ?.activeTokens,

  activeObjects:

    activeFlight
      ?.routeStructure
      ?.activeObjects,

  route:

    activeFlight?.route,

  waypoints:

    activeFlight?.waypoints

});   

const newTokens =

  currentRoute.slice(index);

const directToRoute =

  newTokens.join(" ");

const resolved =
  await resolveRouteWithCoords(

    directToRoute,

    activeFlight.meta.origin,

    activeFlight.meta.destination,

    activeFlight.meta.altitude,

    null,
    null,
    null,

    activeFlight.routeStructure
      ?.arrival?.runway,

    activeFlight.routeStructure
      ?.arrival?.star,

    activeFlight.routeStructure
      ?.arrival?.transition,

    activeFlight.routeStructure
      ?.arrival?.approach,

    activeFlight.routeStructure
      ?.arrival?.iaf
  );

console.log(
  "🛫 DIRECT TO RESOLVED",
  resolved
); 

// =====================================
// ✈️ DIRECT-TO FROM LIVE AIRCRAFT
//
// Resolver returns:
// ORIGINAL AIRPORT → DIRECT-TO FIX → ...
//
// For an airborne Direct-To, replace the
// original airport with current aircraft.
// =====================================

const liveAircraftPosition =

  aircraftPosition ||

  sharedAircraftState?.position ||

  activeFlight?.aircraft?.position;

if (
  !liveAircraftPosition ||
  liveAircraftPosition.length !== 2
) {

  console.error(
    "❌ DIRECT TO: No live aircraft position",
    liveAircraftPosition
  );

  return;
}

// Copy resolver arrays so normal resolver
// behavior remains untouched.

const directToCoords = [
  ...(
    resolved.coords || []
  )
];

const directToNames = [
  ...(
    resolved.names || []
  )
];

const directToCoordObjects = [
  ...(
    resolved.coordObjects || []
  )
];

// =====================================
// Replace ORIGINAL AIRPORT at index 0
// with LIVE AIRCRAFT position.
//
// Example:
// KLRD → PUB → KPUB
//
// becomes:
//
// AIRCRAFT → PUB → KPUB
// =====================================

if (directToCoords.length > 0) {

  directToCoords[0] = [
    liveAircraftPosition[0],
    liveAircraftPosition[1]
  ];
}

if (directToNames.length > 0) {

  directToNames[0] =
    "AIRCRAFT";
}

if (directToCoordObjects.length > 0) {

  directToCoordObjects[0] = {

    ...directToCoordObjects[0],

    name: "AIRCRAFT",

    lon:
      liveAircraftPosition[0],

    lat:
      liveAircraftPosition[1]

  };
}

console.log(
  "✈️ DIRECT TO FINAL:",
  directToNames,
  directToCoords
);

setActiveFlight(prev => {

  console.log(
  "🚨 DIRECT TO RESOLVED NAMES",
  resolved.names?.slice(0,10)
);

console.log(
  "🚨 BEFORE ACTIVE TOKENS",
  prev.routeStructure?.activeTokens?.slice(0,10)
);

  const updatedFlight = {

    

    ...prev,

    routeStructure: {

      ...prev.routeStructure,

activeTokens:
  directToNames,

activeObjects:
  directToCoordObjects

    },

    route: {

      ...prev.route,

coords:
  directToCoords,

waypoints:
  resolved.waypoints || [],

coordObjects:
  directToCoordObjects

    },

    waypoints:
      resolved.waypoints || []

  };

  setActiveRoute(
    updatedFlight
  );

  console.log(
  "🚨 DIRECT TO ACTIVE ROUTE",
  updatedFlight
);

console.log(
  "🚨 DIRECT TO FIRST TOKENS",
  updatedFlight.routeStructure
    ?.activeTokens
    ?.slice(0,10)
);

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  console.log(
  "💾 UNDO SNAPSHOT",
  previousRouteState
);

  console.log(
  "🚨 AFTER ACTIVE TOKENS",
  updatedFlight.routeStructure
    ?.activeTokens
    ?.slice(0,10)
);

  return updatedFlight;

});

plotRoute({

  coords:
    directToCoords,

  coordObjects:
    directToCoordObjects,

  waypoints:
    resolved.waypoints,

  routeText:
    directToRoute,

  origin:
    resolved.origin,

  destination:
    resolved.destination

});

closePanel?.();

  console.log(
  "🛫 DIRECT TO ROUTE STRING",
  directToRoute
);

  console.log(
    "🛫 DIRECT TO TOKEN:",
    token
  );

  console.log(
    "🛫 OLD ROUTE:",
    currentRoute
  );

  console.log(
    "🛫 NEW ROUTE:",
    newTokens
  );

}}

onDeleteFix={async (

  token,

  index,

  closePanel

) => {

  const currentRoute =

    activeFlight
      ?.routeStructure
      ?.activeTokens || [];

  setPreviousRouteState({

  activeTokens:

    activeFlight
      ?.routeStructure
      ?.activeTokens,

  activeObjects:

    activeFlight
      ?.routeStructure
      ?.activeObjects,

  route:

    activeFlight?.route,

  waypoints:

    activeFlight?.waypoints

});  

  const newTokens =

    currentRoute.filter(

      (_, i) => i !== index

    );

  console.log(
    "🗑 DELETE TOKEN:",
    token
  );

  const deleteRoute =

    newTokens.join(" ");

  console.log(
    "🗑 DELETE ROUTE STRING",
    deleteRoute
  );

const resolved =
  await resolveRouteWithCoords(

    deleteRoute,

    activeFlight.meta.origin,

    activeFlight.meta.destination,

    activeFlight.meta.altitude,

    null,
    null,
    null,

    null,
    null,
    null,

    null,
    null
  );

  console.log(
    "🗑 DELETE RESOLVED",
    resolved
  );

  console.log(
    "🗑 DELETE RESOLVED NAMES",
    resolved?.names?.slice(0,20)
  );
setActiveFlight(prev => {

  console.log(
    "🗑 DELETE RESOLVED NAMES",
    resolved.names?.slice(0,10)
  );

  console.log(
    "🗑 BEFORE ACTIVE TOKENS",
    prev.routeStructure?.activeTokens?.slice(0,10)
  );

  const updatedFlight = {

    ...prev,

    routeStructure: {

      ...prev.routeStructure,

      activeTokens:
        resolved.names || [],

      activeObjects:
        resolved.coordObjects || []

    },

    route: {

      ...prev.route,

      coords:
        resolved.coords || [],

      waypoints:
        resolved.waypoints || [],

      coordObjects:
        resolved.coordObjects || []

    },

    waypoints:
      resolved.waypoints || []

  };

  setActiveRoute(
    updatedFlight
  );

  console.log(
    "🗑 FIRST TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  console.log(
    "🗑 AFTER ACTIVE TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  return updatedFlight;

});

plotRoute({

  coords:
    resolved.coords,

  coordObjects:
    resolved.coordObjects,

  waypoints:
    resolved.waypoints,

  routeText:
    deleteRoute,

  origin:
    resolved.origin,

  destination:
    resolved.destination

});

closePanel?.();
}}

onInsertAfter={async (

  token,

  index,

  insertedFix

  

) => {

  const currentRoute =

    activeFlight
      ?.routeStructure
      ?.activeTokens || [];

  setPreviousRouteState({

  activeTokens:

    activeFlight
      ?.routeStructure
      ?.activeTokens,

  activeObjects:

    activeFlight
      ?.routeStructure
      ?.activeObjects,

  route:

    activeFlight?.route,

  waypoints:

    activeFlight?.waypoints

});    

  const newTokens = [
    ...currentRoute
  ];

newTokens.splice(

  index + 1,

  0,

  insertedFix
    .toUpperCase()

);

  console.log(
    "➕ INSERT FIX:",
    insertedFix
  );

const insertRoute =

  newTokens.join(" ");

console.log(
  "➕ INSERT ROUTE STRING",
  insertRoute
);

const resolved =
  await resolveRouteWithCoords(

    insertRoute,

    activeFlight.meta.origin,

    activeFlight.meta.destination,

    activeFlight.meta.altitude,

    null,
    null,
    null,

    null,
    null,
    null,

    null,
    null
  );

console.log(
  "➕ INSERT RESOLVED",
  resolved
);

console.log(
  "➕ INSERT RESOLVED NAMES",
  resolved?.names?.slice(0,20)
  );
setActiveFlight(prev => {

  console.log(
    "🗑 DELETE RESOLVED NAMES",
    resolved.names?.slice(0,10)
  );

  console.log(
    "🗑 BEFORE ACTIVE TOKENS",
    prev.routeStructure?.activeTokens?.slice(0,10)
  );

  const updatedFlight = {

    ...prev,

    routeStructure: {

      ...prev.routeStructure,

      activeTokens:
        resolved.names || [],

      activeObjects:
        resolved.coordObjects || []

    },

    route: {

      ...prev.route,

      coords:
        resolved.coords || [],

      waypoints:
        resolved.waypoints || [],

      coordObjects:
        resolved.coordObjects || []

    },

    waypoints:
      resolved.waypoints || []

  };

  setActiveRoute(
    updatedFlight
  );

  console.log(
    "🗑 FIRST TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  console.log(
    "🗑 AFTER ACTIVE TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  return updatedFlight;

});

plotRoute({

  coords:
    resolved.coords,

  coordObjects:
    resolved.coordObjects,

  waypoints:
    resolved.waypoints,

  routeText:
    insertRoute,

  origin:
    resolved.origin,

  destination:
    resolved.destination

});



}}

onInsertBefore={async (

  token,

  index,

  insertedFix

  

) => {

console.log(
  "➕ BEFORE CLICKED"
);

console.log(
  "TOKEN:",
  token
);

console.log(
  "INDEX:",
  index
);

console.log(
  "INSERTED FIX:",
  insertedFix
);

  const currentRoute =

    activeFlight
      ?.routeStructure
      ?.activeTokens || [];

  setPreviousRouteState({

  activeTokens:

    activeFlight
      ?.routeStructure
      ?.activeTokens,

  activeObjects:

    activeFlight
      ?.routeStructure
      ?.activeObjects,

  route:

    activeFlight?.route,

  waypoints:

    activeFlight?.waypoints

});    

  const newTokens = [
    ...currentRoute
  ];

newTokens.splice(

  index + 1,

  0,

  insertedFix
    .toUpperCase()

);

  console.log(
    "➕ INSERT FIX:",
    insertedFix
  );

const insertRoute =

  newTokens.join(" ");

console.log(
  "➕ INSERT ROUTE STRING",
  insertRoute
);

const resolved =
  await resolveRouteWithCoords(

    insertRoute,

    activeFlight.meta.origin,

    activeFlight.meta.destination,

    activeFlight.meta.altitude,

    null,
    null,
    null,

    null,
    null,
    null,

    null,
    null
  );

console.log(
  "➕ INSERT RESOLVED",
  resolved
);

console.log(
  "➕ INSERT RESOLVED NAMES",
  resolved?.names?.slice(0,20)
  );
setActiveFlight(prev => {

  console.log(
    "🗑 DELETE RESOLVED NAMES",
    resolved.names?.slice(0,10)
  );

  console.log(
    "🗑 BEFORE ACTIVE TOKENS",
    prev.routeStructure?.activeTokens?.slice(0,10)
  );

  const updatedFlight = {

    ...prev,

    routeStructure: {

      ...prev.routeStructure,

      activeTokens:
        resolved.names || [],

      activeObjects:
        resolved.coordObjects || []

    },

    route: {

      ...prev.route,

      coords:
        resolved.coords || [],

      waypoints:
        resolved.waypoints || [],

      coordObjects:
        resolved.coordObjects || []

    },

    waypoints:
      resolved.waypoints || []

  };

  setActiveRoute(
    updatedFlight
  );

  console.log(
    "🗑 FIRST TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  console.log(
    "🗑 AFTER ACTIVE TOKENS",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0,10)
  );

  return updatedFlight;

});

plotRoute({

  coords:
    resolved.coords,

  coordObjects:
    resolved.coordObjects,

  waypoints:
    resolved.waypoints,

  routeText:
    insertRoute,

  origin:
    resolved.origin,

  destination:
    resolved.destination

});



}}

waypointObjects={
  activeFlight?.waypoints || []
}

onExecuteDeparture={async (config) => {

  console.log(
    "🚀 EXECUTE DEPARTURE:",
    config
  );

  const resolved =
    await resolveRouteWithCoords(

      activeFlight.routeStructure
        .filedTokens
        .join(" "),

      activeFlight.meta.origin,

      activeFlight.meta.destination,

      activeFlight.meta.altitude,

      config.runway,
      config.sid,
      config.transition,

      activeFlight.routeStructure
        ?.arrival?.runway,

      activeFlight.routeStructure
        ?.arrival?.star,

      activeFlight.routeStructure
        ?.arrival?.transition,

      activeFlight.routeStructure
        ?.arrival?.approach,

      activeFlight.routeStructure
        ?.arrival?.iaf

    );

      console.log(
    "🚨 NEW ACTIVE TOKENS",
    resolved?.names
  );

  setActiveFlight(prev => {

  console.log(
    "🚨 BEFORE UPDATE",
    prev.routeStructure
      ?.activeTokens
      ?.slice(0, 10)
  );

  const updatedFlight = {

    ...prev,

    routeStructure: {

      ...prev.routeStructure,

      departure: {

        ...prev.routeStructure.departure,

        ...config

      },

      activeTokens:

        resolved.names || [],

      activeObjects:

        resolved.coordObjects || []

    }

  };

  console.log(
    "🚨 AFTER UPDATE",
    updatedFlight.routeStructure
      ?.activeTokens
      ?.slice(0, 10)
  );

  return updatedFlight;

});

  console.log(
    "🚨 FIRST 10 TOKENS",
    resolved?.names?.slice(0, 10)
  );

  console.log(
    "🚨 DEPARTURE REBUILD RESULT",
    resolved
  );

  console.log(
  "🚨 NAMES",
  resolved.names?.slice(0, 15)
);

console.log(
  "🚨 WAYPOINT IDS",
  resolved.waypoints
    ?.slice(0, 15)
    ?.map(w => w.id)
);

  if (!resolved) {

    console.warn(
      "⚠️ Departure rebuild failed"
    );

    return;

  }

setActiveFlight(prev => {

  const updatedFlight = {

    ...prev,

    routeStructure: {

      ...prev.routeStructure,

      departure: {

        ...prev.routeStructure.departure,

        ...config

      },

      activeTokens:
        resolved.names || [],

      activeObjects:
        resolved.coordObjects || []

    },

    route: {

      ...prev.route,

      coords:
        resolved.coords || [],

      waypoints:
        resolved.waypoints || [],

      coordObjects:
        resolved.coordObjects || []

    },

    waypoints:
      resolved.waypoints || []

  };

  // =====================================
  // 🔥 KEEP ACTIVEROUTE IN SYNC
  // =====================================

  setActiveRoute(
    updatedFlight
  );

  localStorage.setItem(
    "activeFlight",
    JSON.stringify(updatedFlight)
  );

  console.log(
    "💾 ACTIVE FLIGHT SAVED"
  );

  return updatedFlight;

});


  console.log(
    "✅ ACTIVE FLIGHT UPDATED"
  );

  console.log(
  "🚨 TRYING REPLOT"
);

console.log(
  "🚨 PLOT COORDOBJECT IDS",
  resolved.coordObjects?.slice(0,10)
);



console.log(
  "🚨 PLOT WAYPOINT IDS",
  resolved.waypoints?.slice(0,10)
    ?.map(w => w.id)
);

console.log(
  "🚨 COORDOBJECT NAMES",
  resolved.coordObjects
    ?.slice(0,15)
    ?.map(o => o.name)
);

plotRoute({

  coords: resolved.coords,

  coordObjects:
    resolved.coordObjects,

  waypoints:
    resolved.waypoints,

routeText:
  resolved.names
    ?.slice(1, -1)
    ?.join(" "),

  origin:
    resolved.origin,

  destination:
    resolved.destination

});

}}

routeStructure={
  activeFlight?.routeStructure
}

  procedureConfig={
    procedureConfig
  }

  updateArrivalProcedure={
  updateArrivalProcedure
}

updateDepartureProcedure={
  updateDepartureProcedure
}

  filedRoute={
    activeFlight?.routeStructure?.filedTokens
    || []
  }

activeRoute={
  activeFlight?.routeStructure?.activeTokens
  || []
}


  currentLegIndex={
    currentLegIndex
  }

  // =====================================
  // 🛬 ARRIVAL MANAGEMENT DATA
  // =====================================

  availableRunways={
    arrivalRunways || []
  }

  availableTransitions={
    arrivalTransitions || []
  }

  availableApproaches={
    availableApproaches || []
  }

  selectedArrivalToken={
  selectedArrivalToken
  }

  onArrivalTokenSelect={
    setSelectedArrivalToken
  }

  selectedArrivalRunway={
  selectedArrivalRunway
  }

  selectedArrivalTransition={
    selectedArrivalTransition
  }

  selectedApproach={
    selectedApproach
  }

onExecuteArrival={(config) => {

  console.log(
    "🚀 EXECUTE ARRIVAL:",
    config
  );

setActiveArrivalConfig(
  config
);
}}

  // =====================================
  // 🛬 RUNWAY SELECT
  // =====================================

onArrivalRunwaySelect={(runway) => {

  console.log(
    "🔥 RUNWAY CALLBACK:",
    runway
  );

  console.log(
    "🛬 Selected runway:",
    runway
  );

  setSelectedArrivalRunway(
    runway
  );

  // =====================================
  // ✈️ CENTRAL PROCEDURE CONFIG
  // =====================================

  updateArrivalProcedure({

    runway
  });

  // =====================================
  // 🛬 UPDATE ACTIVE ARRIVAL CONFIG
  // =====================================

  setActiveArrivalConfig(prev => ({

    ...prev,

    runway
  }));

  // 🔥 FUTURE:
  // fetch transitions for runway
  // fetch approaches for runway
}}

  // =====================================
  // 🛬 STAR TRANSITION SELECT
  // =====================================

onTransitionSelect={(transition) => {

  console.log(
    "🔥 TRANSITION CALLBACK:",
    transition
  );

  console.log(
    "🛬 Selected transition:",
    transition
  );

  // =====================================
  // ✈️ CENTRAL PROCEDURE CONFIG
  // =====================================

  updateArrivalProcedure({

    transition
  });

  // =====================================
  // 🛬 UPDATE ACTIVE ARRIVAL CONFIG
  // =====================================

  setActiveArrivalConfig(prev => ({

    ...prev,

    transition
  }));

  // 🔥 FUTURE:
  // rebuild STAR geometry
}}

  // =====================================
  // 🛬 APPROACH SELECT
  // =====================================

onApproachSelect={(approach) => {

  console.log(
    "🔥 APPROACH CALLBACK:",
    approach
  );

  console.log(
    "🛬 Selected approach:",
    approach
  );

  setSelectedApproach(
    approach
  );

  // =====================================
  // ✈️ CENTRAL PROCEDURE CONFIG
  // =====================================

  updateArrivalProcedure({

    approach
  });

  // =====================================
  // 🛬 UPDATE ACTIVE ARRIVAL CONFIG
  // =====================================

  setActiveArrivalConfig(prev => ({

    ...prev,

    approach
  }));

  // =====================================
  // 🚀 FUTURE LIVE WORKFLOW
  // =====================================

  // 1. Fetch CIFP approach geometry
  // 2. Trim STAR branches
  // 3. Attach transition
  // 4. Append approach fixes
  // 5. Rebuild active geometry
  // 6. Replot magenta route
  // 7. Preserve filed route
}}

// =====================================
// 🛬 GENERAL TOKEN CLICK
// =====================================

onTokenClick={(token, index) => {

  console.log(
    "🟣 Route token clicked:",
    token,
    index
  );

  // 🔥 FUTURE:
  // Direct-To
  // Delete leg
  // ATC amendment
  // Hold at fix
  // Weather details

}}

 />

)}

<div
  ref={mapContainerRef}
  className="map-container"
/>



  {/* 📑 MULTIPLE CHART WINDOWS */}
{/* ===================================== */}

{console.log(
  "🪟 openChartWindows:",
  openChartWindows
)}
{openChartWindows.map((chartWindow, index) => (

  <Rnd

    key={chartWindow.id}

    size={{
      width: chartWindow.width,
      height: chartWindow.height
    }}

    position={{
      x: chartWindow.x,
      y: chartWindow.y
    }}

    bounds={false}

    minWidth={500}
    minHeight={400}

    enableResizing={{
      top: true,
      right: true,
      bottom: true,
      left: true,
      topRight: true,
      bottomRight: true,
      bottomLeft: true,
      topLeft: true
    }}

    dragHandleClassName="chart-drag-handle"

    onDragStop={(e, d) => {

      setOpenChartWindows(prev =>

        prev.map(c =>

          c.id === chartWindow.id

            ? {
                ...c,
                x: d.x,
                y: d.y
              }

            : c
        )
      );

    }}

    onResizeStop={(e, direction, ref, delta, position) => {

      setOpenChartWindows(prev =>

        prev.map(c =>

          c.id === chartWindow.id

            ? {
                ...c,

                width: ref.offsetWidth,

                height: ref.offsetHeight,

                x: position.x,

                y: position.y
              }

            : c
        )
      );

    }}

    style={{
      zIndex: 10000 + index
    }}
  >

    <ChartModal

      chart={chartWindow}

      onClose={() => {

        setOpenChartWindows(prev =>

          prev.filter(
            c => c.id !== chartWindow.id
          )
        );

      }}

      onSave={handleSaveChart}

      onUnsave={handleUnsaveChart}

      savedCharts={savedCharts}

    />

  </Rnd>

))}

  <TopToolbar

    showSquawkPanel={
    showSquawkPanel
  }

  setShowSquawkPanel={
    setShowSquawkPanel
  }


    isFlightActive={
    isFlightActive || localFlightActive
  }

  onZoomToDestination={
    handleZoomToDestination
  }

  drawingMode={drawingMode}
  setDrawingMode={setDrawingMode}

  canvasRef={canvasRef}




  showPirepForm={
    showPirepForm
  }

  setShowPirepForm={
    setShowPirepForm
  }



  fetchFlights={fetchFlights}
  setShowFlightsPanel={
    setShowFlightsPanel
  }

  setShowChartBrowser={
    setShowChartBrowser
  }

  showChartBrowser={
    showChartBrowser
  }

  setShowChartsPanel={
    setShowChartsPanel
  }

  showChartsPanel={
    showChartsPanel
  }
/>

{/* =====================================
    🐞 BETA SQUAWK PANEL
===================================== */}

{showSquawkPanel && (

  <div
    style={{
      position: "absolute",
      top: "90px",
      right: "20px",

      width: "420px",
      maxHeight: "75vh",
      overflowY: "auto",

      zIndex: 10000,

      background:
        "rgba(15, 23, 42, 0.97)",

      color: "#ffffff",

      border:
        "1px solid rgba(255,255,255,0.18)",

      borderRadius: "12px",

      boxShadow:
        "0 12px 35px rgba(0,0,0,0.45)",

      padding: "18px"
    }}
  >

    {/* =====================================
        HEADER
    ===================================== */}

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px"
      }}
    >

      <div>

        <div
          style={{
            fontSize: "20px",
            fontWeight: "800"
          }}
        >
          🐞 FICON-X Squawks
        </div>

        <div
          style={{
            fontSize: "12px",
            opacity: 0.7,
            marginTop: "3px"
          }}
        >
          Beta discrepancy and feedback reporting
        </div>

      </div>

      <button
        onClick={() =>
          setShowSquawkPanel(false)
        }
        style={{
          background: "transparent",
          border: "none",
          color: "#ffffff",
          fontSize: "20px",
          cursor: "pointer"
        }}
      >
        ✕
      </button>

    </div>


    {/* =====================================
        🐞 SQUAWK TABS
    ===================================== */}

    <div
      style={{
        display: "flex",
        gap: "8px",
        marginBottom: "16px",

        borderBottom:
          "1px solid #334155",

        paddingBottom: "10px"
      }}
    >

      {/* REPORT SQUAWK */}

      <button
        onClick={() =>
          setSquawkTab("report")
        }
        style={{
          flex: 1,
          padding: "10px 12px",

          background:
            squawkTab === "report"
              ? "#dc2626"
              : "#1e293b",

          color: "#ffffff",

          border:
            squawkTab === "report"
              ? "1px solid #ef4444"
              : "1px solid #475569",

          borderRadius: "7px",

          fontSize: "12px",
          fontWeight: "800",

          cursor: "pointer"
        }}
      >
        🐞 REPORT SQUAWK
      </button>


      {/* SQUAWK BOARD */}

      <button
onClick={async () => {

  setSquawkTab(
    "board"
  );

  setSquawkBoardLoading(
    true
  );

  try {

    const token =
      localStorage.getItem(
        "token"
      );

    if (!token) {

      throw new Error(
        "You must be logged in to view the Squawk Board."
      );

    }

    const response =
      await fetch(
        "https://ficon-x.com/auth/squawks",
        {
          method: "GET",

          headers: {
            "Authorization":
              `Bearer ${token}`
          }
        }
      );

    const data =
      await response.json();

    if (!response.ok) {

      throw new Error(
        data.error ||
        "Unable to load Squawk Board."
      );

    }

    console.log(
      "🐞 SQUAWK BOARD:",
      data
    );

    setSquawkBoard(
      data
    );

  } catch (err) {

    console.error(
      "❌ SQUAWK BOARD ERROR:",
      err
    );

    alert(
      err.message ||
      "Unable to load Squawk Board."
    );

  } finally {

    setSquawkBoardLoading(
      false
    );

  }

}}
      >
        📋 SQUAWK BOARD
      </button>

    </div>


    {/* =====================================
        🐞 SQUAWK REPORT FORM
    ===================================== */}

    {squawkTab === "report" && (

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px"
        }}
      >

        {/* CATEGORY */}

        <div>

          <label
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: "700",
              marginBottom: "5px"
            }}
          >
            CATEGORY
          </label>

          <select
            value={
              squawkCategory
            }

            onChange={(e) =>
              setSquawkCategory(
                e.target.value
              )
            }

            style={{
              width: "100%",
              padding: "10px",

              background: "#1e293b",
              color: "#ffffff",

              border:
                "1px solid #475569",

              borderRadius: "7px"
            }}
          >

            <option>
              Routing
            </option>

            <option>
              Weather / Hazards
            </option>

            <option>
              Map / Charts
            </option>

            <option>
              Flight Planning
            </option>

            <option>
              UI / Display
            </option>

            <option>
              Performance
            </option>

            <option>
              Suggestion / Feature
            </option>

            <option>
              Other
            </option>

          </select>

        </div>


        {/* =====================================
            DESCRIPTION
        ===================================== */}

        <div>

          <label
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: "700",
              marginBottom: "5px"
            }}
          >
            WHAT HAPPENED?
          </label>

          <textarea
            value={
              squawkDescription
            }

            onChange={(e) =>
              setSquawkDescription(
                e.target.value
              )
            }

            placeholder="Describe the discrepancy, problem, or suggestion..."

            rows={5}

            style={{
              width: "100%",
              boxSizing: "border-box",

              padding: "10px",

              background: "#1e293b",
              color: "#ffffff",

              border:
                "1px solid #475569",

              borderRadius: "7px",

              resize: "vertical"
            }}
          />

        </div>


        {/* =====================================
            EXPECTED RESULT
        ===================================== */}

        <div>

          <label
            style={{
              display: "block",
              fontSize: "12px",
              fontWeight: "700",
              marginBottom: "5px"
            }}
          >
            WHAT DID YOU EXPECT?
          </label>

          <textarea
            value={
              squawkExpected
            }

            onChange={(e) =>
              setSquawkExpected(
                e.target.value
              )
            }

            placeholder="What should FICON-X have done instead?"

            rows={3}

            style={{
              width: "100%",
              boxSizing: "border-box",

              padding: "10px",

              background: "#1e293b",
              color: "#ffffff",

              border:
                "1px solid #475569",

              borderRadius: "7px",

              resize: "vertical"
            }}
          />

        </div>


        {/* =====================================
            SUBMIT
        ===================================== */}

        <button
          onClick={async () => {

            // =====================================
            // 🐞 VALIDATE SQUAWK
            // =====================================

            if (
              !squawkDescription.trim()
            ) {

              alert(
                "Please describe the squawk."
              );

              return;
            }


            try {

              // =====================================
              // 🔐 GET LOGIN TOKEN
              // =====================================

              const token =
                localStorage.getItem(
                  "token"
                );

              if (!token) {

                alert(
                  "You must be logged in to submit a squawk."
                );

                return;
              }


              // =====================================
              // 🐞 SUBMIT TO FICON-X
              // =====================================

              const response =
                await fetch(
                  "https://ficon-x.com/auth/squawks",
                  {
                    method: "POST",

                    headers: {
                      "Content-Type":
                        "application/json",

                      "Authorization":
                        `Bearer ${token}`
                    },

                    body:
                      JSON.stringify({
                        category:
                          squawkCategory,

                        description:
                          squawkDescription.trim(),

                        expected_result:
                          squawkExpected.trim()
                      })
                  }
                );


              const data =
                await response.json();


              if (!response.ok) {

                throw new Error(
                  data.error ||
                  "Squawk submission failed."
                );
              }


              console.log(
                "🐞 SQUAWK SAVED:",
                data
              );


              // =====================================
              // ✅ SUCCESS
              // =====================================

              alert(
                `Squawk #${data.squawk_id} submitted successfully.`
              );


              // Clear form

              setSquawkCategory(
                "Routing"
              );

              setSquawkDescription(
                ""
              );

              setSquawkExpected(
                ""
              );


              // Close panel

              setShowSquawkPanel(
                false
              );


            } catch (err) {

              console.error(
                "❌ SQUAWK SUBMIT ERROR:",
                err
              );

              alert(
                err.message ||
                "Unable to submit squawk."
              );

            }

          }}

          style={{
            width: "100%",

            marginTop: "4px",
            padding: "13px",

            background:
              "linear-gradient(135deg, #dc2626, #991b1b)",

            color: "#ffffff",

            border: "none",
            borderRadius: "8px",

            fontSize: "15px",
            fontWeight: "800",

            cursor: "pointer",

            boxShadow:
              "0 5px 15px rgba(220,38,38,0.25)"
          }}
        >

          🐞 SUBMIT SQUAWK

        </button>

      </div>

    )}


{/* =====================================
    📋 SQUAWK BOARD
===================================== */}

{squawkTab === "board" && (

  <div>

    {/* LOADING */}

    {squawkBoardLoading && (

      <div
        style={{
          padding: "25px 5px",
          textAlign: "center",
          fontSize: "13px",
          opacity: 0.7
        }}
      >
        Loading squawks...
      </div>

    )}


    {/* EMPTY BOARD */}

    {!squawkBoardLoading &&
      squawkBoard.length === 0 && (

        <div
          style={{
            padding: "25px 5px",
            textAlign: "center",
            fontSize: "13px",
            opacity: 0.7
          }}
        >
          No squawks have been reported.
        </div>

      )}


    {/* SQUAWK CARDS */}

    {!squawkBoardLoading &&
      squawkBoard.map(
        (squawk) => (

          <div
            key={
              squawk.id
            }

            style={{
              background:
                "rgba(30, 41, 59, 0.85)",

              border:
                "1px solid #475569",

              borderRadius: "9px",

              padding: "13px",

              marginBottom: "12px"
            }}
          >

            {/* CARD HEADER */}

            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems:
                  "flex-start",
                gap: "10px",
                marginBottom: "10px"
              }}
            >

              <div>

                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "800"
                  }}
                >
                  #{squawk.id}{" "}
                  {squawk.category}
                </div>

                <div
                  style={{
                    fontSize: "11px",
                    opacity: 0.65,
                    marginTop: "3px"
                  }}
                >
                  Reported by{" "}
                  {squawk.username ||
                    "Unknown user"}
                </div>

              </div>


              {/* STATUS */}

              <div
                style={{
                  padding: "4px 8px",

                  borderRadius: "999px",

                  fontSize: "10px",
                  fontWeight: "900",

                  whiteSpace: "nowrap",

                  background:
                    squawk.status ===
                    "FIXED"
                      ? "#166534"

                      : squawk.status ===
                        "INVESTIGATING"
                      ? "#92400e"

                      : squawk.status ===
                        "CLOSED"
                      ? "#475569"

                      : "#991b1b",

                  color: "#ffffff"
                }}
              >
                {squawk.status ||
                  "OPEN"}
              </div>

            </div>


            {/* DESCRIPTION */}

            <div
              style={{
                marginBottom: "10px"
              }}
            >

              <div
                style={{
                  fontSize: "10px",
                  fontWeight: "800",
                  opacity: 0.6,
                  marginBottom: "3px"
                }}
              >
                REPORTED ISSUE
              </div>

              <div
                style={{
                  fontSize: "13px",
                  lineHeight: "1.45",
                  whiteSpace: "pre-wrap"
                }}
              >
                {squawk.description}
              </div>

            </div>


            {/* EXPECTED RESULT */}

            {squawk.expected_result && (

              <div
                style={{
                  marginBottom: "10px"
                }}
              >

                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "800",
                    opacity: 0.6,
                    marginBottom: "3px"
                  }}
                >
                  EXPECTED
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    lineHeight: "1.4",
                    opacity: 0.85,
                    whiteSpace:
                      "pre-wrap"
                  }}
                >
                  {
                    squawk.expected_result
                  }
                </div>

              </div>

            )}


            {/* DEVELOPER RESPONSE */}

            {squawk.developer_response && (

              <div
                style={{
                  marginTop: "10px",
                  padding: "9px",

                  background:
                    "rgba(37, 99, 235, 0.15)",

                  border:
                    "1px solid rgba(59, 130, 246, 0.35)",

                  borderRadius: "6px"
                }}
              >

                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "800",
                    marginBottom: "4px",
                    color: "#93c5fd"
                  }}
                >
                  FICON-X RESPONSE
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    lineHeight: "1.4",
                    whiteSpace:
                      "pre-wrap"
                  }}
                >
                  {
                    squawk.developer_response
                  }
                </div>

              </div>

            )}

                        {/* =====================================
                🔐 ADMIN SQUAWK MANAGEMENT
            ===================================== */}

            {user?.is_admin && (

              <div
                style={{
                  marginTop: "12px",

                  padding: "11px",

                  background:
                    "rgba(0, 0, 0, 0.25)",

                  border:
                    "1px solid rgba(255,255,255,0.12)",

                  borderRadius: "7px"
                }}
              >

                {/* MANAGEMENT HEADER */}

                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center"
                  }}
                >

                  <div
                    style={{
                      fontSize: "10px",
                      fontWeight: "900",
                      color: "#fbbf24"
                    }}
                  >
                    🔐 ADMIN — MANAGE SQUAWK
                  </div>


                  {/* OPEN / CLOSE EDITOR */}

                  <button
                    onClick={() => {

                      if (
                        editingSquawkId ===
                        squawk.id
                      ) {

                        setEditingSquawkId(
                          null
                        );

                        return;

                      }

                      setEditingSquawkId(
                        squawk.id
                      );

                      setSquawkEditStatus(
                        squawk.status ||
                        "OPEN"
                      );

                      setSquawkEditResponse(
                        squawk.developer_response ||
                        ""
                      );

                    }}

                    style={{
                      padding: "5px 9px",

                      background:
                        editingSquawkId ===
                        squawk.id
                          ? "#475569"
                          : "#1d4ed8",

                      color: "#ffffff",

                      border: "none",
                      borderRadius: "5px",

                      fontSize: "10px",
                      fontWeight: "800",

                      cursor: "pointer"
                    }}
                  >

                    {editingSquawkId ===
                    squawk.id
                      ? "CANCEL"
                      : "MANAGE"}

                  </button>

                </div>


                {/* =====================================
                    EDITOR
                ===================================== */}

                {editingSquawkId ===
                  squawk.id && (

                  <div
                    style={{
                      marginTop: "12px",

                      display: "flex",
                      flexDirection: "column",
                      gap: "10px"
                    }}
                  >

                    {/* STATUS */}

                    <div>

                      <label
                        style={{
                          display: "block",

                          fontSize: "10px",
                          fontWeight: "800",

                          marginBottom: "4px",

                          opacity: 0.7
                        }}
                      >
                        STATUS
                      </label>

                      <select
                        value={
                          squawkEditStatus
                        }

                        onChange={(e) =>
                          setSquawkEditStatus(
                            e.target.value
                          )
                        }

                        style={{
                          width: "100%",

                          padding: "8px",

                          boxSizing:
                            "border-box",

                          background:
                            "#1e293b",

                          color: "#ffffff",

                          border:
                            "1px solid #475569",

                          borderRadius:
                            "6px"
                        }}
                      >

                        <option value="OPEN">
                          OPEN
                        </option>

                        <option value="INVESTIGATING">
                          INVESTIGATING
                        </option>

                        <option value="FIXED">
                          FIXED
                        </option>

                        <option value="CLOSED">
                          CLOSED
                        </option>

                      </select>

                    </div>


                    {/* DEVELOPER RESPONSE */}

                    <div>

                      <label
                        style={{
                          display: "block",

                          fontSize: "10px",
                          fontWeight: "800",

                          marginBottom: "4px",

                          opacity: 0.7
                        }}
                      >
                        DEVELOPER RESPONSE
                      </label>

                      <textarea
                        value={
                          squawkEditResponse
                        }

                        onChange={(e) =>
                          setSquawkEditResponse(
                            e.target.value
                          )
                        }

                        placeholder="Enter a response visible to beta testers..."

                        rows={4}

                        style={{
                          width: "100%",

                          boxSizing:
                            "border-box",

                          padding: "8px",

                          background:
                            "#1e293b",

                          color: "#ffffff",

                          border:
                            "1px solid #475569",

                          borderRadius:
                            "6px",

                          resize: "vertical"
                        }}
                      />

                    </div>


                    {/* SAVE UPDATE */}

                    <button
                      disabled={
                        squawkUpdateSaving
                      }

                      onClick={async () => {

                        try {

                          setSquawkUpdateSaving(
                            true
                          );

                          const token =
                            localStorage.getItem(
                              "token"
                            );

                          if (!token) {

                            throw new Error(
                              "You must be logged in."
                            );

                          }


                          const response =
                            await fetch(

                              `https://ficon-x.com/auth/squawks/${squawk.id}`,

                              {

                                method:
                                  "PATCH",

                                headers: {

                                  "Content-Type":
                                    "application/json",

                                  "Authorization":
                                    `Bearer ${token}`

                                },

                                body:
                                  JSON.stringify({

                                    status:
                                      squawkEditStatus,

                                    developer_response:
                                      squawkEditResponse

                                  })

                              }

                            );


                          const data =
                            await response.json();


                          if (!response.ok) {

                            throw new Error(

                              data.error ||

                              "Unable to update squawk."

                            );

                          }


                          console.log(
                            "🐞 SQUAWK UPDATED:",
                            data
                          );


                          // =====================================
                          // UPDATE CARD IMMEDIATELY
                          // =====================================

                          setSquawkBoard(
                            current =>

                              current.map(
                                item =>

                                  item.id ===
                                  squawk.id

                                    ? {
                                        ...item,

                                        status:
                                          data.squawk.status,

                                        developer_response:
                                          data.squawk
                                            .developer_response,

                                        updated_at:
                                          data.squawk
                                            .updated_at
                                      }

                                    : item

                              )

                          );


                          setEditingSquawkId(
                            null
                          );


                        } catch (err) {

                          console.error(
                            "❌ SQUAWK UPDATE ERROR:",
                            err
                          );

                          alert(
                            err.message ||
                            "Unable to update squawk."
                          );

                        } finally {

                          setSquawkUpdateSaving(
                            false
                          );

                        }

                      }}

                      style={{
                        width: "100%",

                        padding: "9px",

                        background:
                          squawkUpdateSaving
                            ? "#475569"
                            : "#2563eb",

                        color: "#ffffff",

                        border: "none",

                        borderRadius:
                          "6px",

                        fontSize: "11px",
                        fontWeight: "900",

                        cursor:
                          squawkUpdateSaving
                            ? "default"
                            : "pointer"
                      }}
                    >

                      {squawkUpdateSaving
                        ? "SAVING..."
                        : "SAVE UPDATE"}

                    </button>

                    {/* =====================================
    🗑️ DELETE SQUAWK
===================================== */}

<button
  disabled={
    squawkUpdateSaving
  }

  onClick={async () => {

    try {

      setSquawkUpdateSaving(
        true
      );

      const token =
        localStorage.getItem(
          "token"
        );

      if (!token) {

        throw new Error(
          "You must be logged in."
        );

      }


      // =====================================
      // 🗑️ DELETE FROM FICON-X
      // =====================================

      const response =
        await fetch(

          `https://ficon-x.com/auth/squawks/${squawk.id}`,

          {

            method:
              "DELETE",

            headers: {

              "Authorization":
                `Bearer ${token}`

            }

          }

        );


      const data =
        await response.json();


      if (!response.ok) {

        throw new Error(

          data.error ||

          "Unable to delete squawk."

        );

      }


      console.log(
        "🗑️ SQUAWK DELETED:",
        data
      );


      // =====================================
      // REMOVE CARD IMMEDIATELY
      // =====================================

      setSquawkBoard(
        current =>

          current.filter(
            item =>
              item.id !==
              squawk.id
          )

      );


      // Close editor

      setEditingSquawkId(
        null
      );


    } catch (err) {

      console.error(
        "❌ SQUAWK DELETE ERROR:",
        err
      );

      alert(
        err.message ||
        "Unable to delete squawk."
      );

    } finally {

      setSquawkUpdateSaving(
        false
      );

    }

  }}

  style={{
    width: "100%",

    padding: "9px",

    background:
      squawkUpdateSaving
        ? "#475569"
        : "#991b1b",

    color: "#ffffff",

    border:
      "1px solid #dc2626",

    borderRadius:
      "6px",

    fontSize: "11px",
    fontWeight: "900",

    cursor:
      squawkUpdateSaving
        ? "default"
        : "pointer"
  }}
>

  🗑 DELETE SQUAWK

</button>

                  </div>

                )}

              </div>

            )}


            {/* DATE */}


            {/* DATE */}

            <div
              style={{
                marginTop: "10px",
                paddingTop: "8px",

                borderTop:
                  "1px solid rgba(255,255,255,0.08)",

                fontSize: "10px",
                opacity: 0.5
              }}
            >
              Submitted{" "}

              {squawk.created_at
                ? new Date(
                    squawk.created_at
                  ).toLocaleString()
                : ""}
            </div>

          </div>

        )
      )}

  </div>

)}

  </div>

)}

  {authOverlay}

  {/* 🔐 AUTH */}

<div style={{

  position: "relative"
}}>

  <button

    onClick={() =>
      setShowProfileMenu(
        !showProfileMenu
      )
    }

    style={{

      background:
        "rgba(0,0,0,0.75)",

      color: "white",

      border:
        "1px solid rgba(255,255,255,0.15)",

      borderRadius: "8px",

      padding: "8px 12px",

      cursor: "pointer"
    }}
  >

    👨‍✈️ {

      user?.username ||

      user?.display_name||

      user?.email
    }

  </button>

  {showProfileMenu && (

    <div style={{

      position: "absolute",

      top: "42px",

      right: 300,

      width: "240px",

      background: "#1b1b1b",

      border:
        "1px solid rgba(255,255,255,0.15)",

      borderRadius: "10px",

      padding: "12px",

      zIndex: 999999
    }}>

      <div style={{

        marginBottom: "12px"
      }}>

        <div>

          👨‍✈️ {

            user?.username ||

            "Unknown Pilot"
          }

        </div>

        <div>

          🏠 {

            user?.home_airport ||

            "No Home Airport"
          }

        </div>

        <div>

          ✈️ {

            user?.default_aircraft||

            "No Aircraft"
          }

        </div>

      </div>

      <hr style={{

        borderColor:
          "rgba(255,255,255,0.1)"
      }}/>

      <button style={{

        width: "100%",

        marginTop: "10px"
      }}>

        Saved Flights

      </button>

      <button style={{

        width: "100%",

        marginTop: "8px"
      }}>

        My Missions

      </button>

      <button

        onClick={logout}

        style={{

          width: "100%",

          marginTop: "12px"
        }}
      >

        Logout

      </button>

    </div>
  )}

</div>



{pendingRouteProposal && (

  <div style={{
    position: "absolute",
    top: 100,
    right: 300,
    zIndex: 999999,
    background: "#111",
    color: "white",
    padding: "14px",
    borderRadius: "10px",
    width: "320px",
    boxShadow: "0 0 20px rgba(0,0,0,0.5)"
  }}>

    <div style={{
      fontWeight: "bold",
      marginBottom: "10px"
    }}>
      📝 Route Change Request
    </div>

    <div style={{
      fontSize: "12px",
      marginBottom: "10px"
    }}>
      Requested By:
      {" "}
      {pendingRouteProposal.requestedBy}
    </div>

    <div style={{
      fontSize: "12px",
      marginBottom: "15px",
      wordBreak: "break-word"
    }}>
      {pendingRouteProposal.proposedRoute}
    </div>

    <div style={{
      display: "flex",
      gap: "10px"
    }}>

      <button
        onClick={approveRouteProposal}
      >
        ✅ Approve
      </button>

      <button
        onClick={denyRouteProposal}
      >
        ❌ Deny
      </button>

    </div>
  </div>
)}

{showFlightsPanel && (
  <DraggablePanel
    title="📂 Saved Flights"
    defaultPosition={{ x: 300, y: 150 }}
    defaultSize={{ width: 350, height: 450 }}
    onClose={() => setShowFlightsPanel(false)}
  >
    {(!Array.isArray(flights) || flights.length === 0) ? (
      <div>No saved flights</div>
    ) : (
      (Array.isArray(flights) ? flights : []).map((f, i) => (
        <div
          key={f.id || i}
          style={{
            marginBottom: "10px",
            background: "#1a1a1a",
            padding: "8px",
            borderRadius: "6px",
            border: "1px solid rgba(255,255,255,0.1)"
          }}
        >
          {/* 🔥 CLICK AREA (LOAD) */}
          <div
            onClick={() => {
              console.log("🛫 Loading selected flight:", f);
              loadFlight(f.data);
              setShowFlightsPanel(false);
            }}
            style={{
              cursor: "pointer",
              marginBottom: "6px"
            }}
          >
            <div style={{ fontWeight: "bold", color: "#00d4ff" }}>
              ✈️ {f.data?.meta?.origin} → {f.data?.meta?.destination}
            </div>

            <div style={{ fontSize: "11px", color: "#aaa" }}>
              {f.data?.meta?.routeTextCurrent || "No route"}
            </div>

            <div style={{ fontSize: "10px", color: "#666" }}>
              {f.created_at
                ? new Date(f.created_at).toLocaleString()
                : ""}
            </div>
          </div>

          {/* 🔥 ACTIONS ROW */}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            
            {/* 🗑 DELETE */}
            <button
              onClick={async (e) => {
                e.stopPropagation(); // 🔥 prevent triggering load

                const token = localStorage.getItem("token");

                try {
                  await fetch(
                    `https://ficon-x.com/auth/delete-flight/${f.id}`,
                    {
                      method: "DELETE",
                      headers: {
                        Authorization: `Bearer ${token}`
                      }
                    }
                  );

                  console.log("🗑 Deleted flight:", f.id);

                  // 🔥 refresh list
                  fetchFlights();

                } catch (err) {
                  console.error("❌ Delete failed:", err);
                }
              }}
              style={{
                background: "#c0392b",
                border: "none",
                color: "white",
                padding: "4px 8px",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "11px"
              }}
            >
              Delete
            </button>

          </div>
        </div>
      ))
    )}
  </DraggablePanel>
)}


{(isFlightActive || localFlightActive) && (
  <button
    onClick={handleEndFlight}
    style={{

      position: "absolute",

      top: "25px",

      right: "14px",

      zIndex: 9999,

      background:
        "linear-gradient(180deg,#ef4444,#b91c1c)",

      color:
        "#ffffff",

      padding:
        "12px 20px",

      border:
        "1px solid rgba(255,120,120,0.35)",

      borderRadius:
        "999px",

      cursor:
        "pointer",

      fontWeight:
        "700",

      fontSize:
        "14px",

      letterSpacing:
        "0.5px",

      backdropFilter:
        "blur(10px)",

      boxShadow:
        "0 0 20px rgba(255,0,0,0.25)",

      transition:
        "all 0.2s ease"

    }}

    onMouseEnter={(e) => {

      e.currentTarget.style.transform =
        "translateY(-1px)";

      e.currentTarget.style.boxShadow =
        "0 0 28px rgba(255,0,0,0.45)";

    }}

    onMouseLeave={(e) => {

      e.currentTarget.style.transform =
        "translateY(0px)";

      e.currentTarget.style.boxShadow =
        "0 0 20px rgba(255,0,0,0.25)";

    }}
  >

    ⏹ END FLIGHT

  </button>
)}


{/* =====================================
    ✈️ RECENTER AIRCRAFT
===================================== */}

{(isFlightActive || localFlightActive) &&
  !followAircraft &&
  aircraftPosition && (

  <button
    onClick={handleRecenterAircraft}

    style={{

      position: "absolute",

      // Directly below END FLIGHT
      top: "82px",

      right: "14px",

      zIndex: 9999,

      background:
        "rgba(15, 23, 42, 0.92)",

      color:
        "#ffffff",

      padding:
        "10px 18px",

      border:
        "1px solid rgba(255,255,255,0.25)",

      borderRadius:
        "999px",

      cursor:
        "pointer",

      fontWeight:
        "700",

      fontSize:
        "13px",

      letterSpacing:
        "0.5px",

      backdropFilter:
        "blur(10px)",

      boxShadow:
        "0 0 18px rgba(0,0,0,0.30)",

      transition:
        "all 0.2s ease"

    }}

    onMouseEnter={(e) => {

      e.currentTarget.style.transform =
        "translateY(-1px)";

      e.currentTarget.style.boxShadow =
        "0 0 24px rgba(255,255,255,0.20)";
    }}

    onMouseLeave={(e) => {

      e.currentTarget.style.transform =
        "translateY(0px)";

      e.currentTarget.style.boxShadow =
        "0 0 18px rgba(0,0,0,0.30)";
    }}
  >

    ✈ RECENTER

  </button>

)}


{/* =====================================
    🌦 WEATHER SEARCH PANEL
===================================== */}

{showWxPanel && (

  <DraggablePanel

    title="🌦 Airport Weather"

    initialPosition={{
      x: 260,
      y: 140
    }}

    onClose={() =>
      setShowWxPanel(false)
    }
  >

    <div
      style={{
        width: 320,
        color: "white"
      }}
    >

      {/* ICAO INPUT */}

      <input

        value={wxSearchIcao}

        onChange={(e) =>
          setWxSearchIcao(
            e.target.value
              .toUpperCase()
          )
        }

        placeholder="Enter ICAO"

        maxLength={4}

        style={{

          width: "100%",

          padding: 10,

          borderRadius: 8,

          border: "1px solid #555",

          background: "#1e1e1e",

          color: "white",

          marginBottom: 14,

          fontSize: 15
        }}
      />

      {/* METAR */}

      <div
        style={{
          marginBottom: 16
        }}
      >

        <div
          style={{
            fontWeight: 700,
            marginBottom: 6
          }}
        >
          METAR
        </div>

        <div
          style={{
            fontSize: 13,
            lineHeight: 1.4
          }}
        >

          {
            searchedWeather?.metar ||
            "Loading METAR..."
          }

        </div>

      </div>

      {/* TAF */}

      <div>

        <div
          style={{
            fontWeight: 700,
            marginBottom: 6
          }}
        >
          TAF
        </div>

        <div
          style={{
            fontSize: 13,
            lineHeight: 1.4,
            maxHeight: 220,
            overflowY: "auto"
          }}
        >

          {
            searchedWeather?.taf ||
            "Loading TAF..."
          }

        </div>

      </div>

    </div>

  </DraggablePanel>
)}

{showChartBrowser && (
  <DraggablePanel
    title="🔍 Browse Charts"
    defaultPosition={{
      x: window.innerWidth - 400,
      y: 180
    }}
    defaultSize={{
      width: 400,
      height: 450
    }}
    onClose={() =>
      setShowChartBrowser(false)
    }
  >

    {/* 🔍 SEARCH */}
    <input
      className="op-input"
      placeholder="Enter ICAO"
      value={chartSearch}
      onChange={(e) =>
        setChartSearch(e.target.value)
      }
      style={{
        marginBottom: 10
      }}
    />

    <button
      className="op-btn"
      onClick={fetchChartsForAirport}
      style={{
        width: "100%"
      }}
    >
      Search Charts
    </button>

    {/* 📜 RESULTS */}
    <div
      style={{
        marginTop: 14,
        maxHeight: "300px",
        overflowY: "auto",
        paddingRight: 4
      }}
    >

      {chartResults.map((chart, i) => (

        <div
          key={i}
          className="op-panel"
          style={{

            display: "flex",

            justifyContent:
              "space-between",

            alignItems: "center",

            padding: "10px 12px",

            marginBottom: "8px",

            background:
              "rgba(255,255,255,0.03)"
          }}
        >

<span

  onClick={() => {

    console.log(
      "📄 Opening chart:",
      chart.name
    );

    openChartWindow(chart);
  }}

  style={{

    cursor: "pointer",

    flex: 1,

    fontSize: "0.92em",

    color: "#d9ecff",

    fontWeight: 500
  }}
>
  {chart.name}
</span>

<button
  type="button"

  onClick={() =>
    saveChart(chart)
  }

  style={{
    width: "32px",

    minWidth: "32px",

    height: "32px",

    padding: 0,

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    flexShrink: 0
  }}
>
  💾
</button>

        </div>

      ))}

    </div>

  </DraggablePanel>
)}

{showChartsPanel && (
  <DraggablePanel
    title="📄 Saved Charts"
    defaultPosition={{ x: window.innerWidth - 380, y: 120 }}
    onClose={() => setShowChartsPanel(false)}
  >

    {/* 🔥 CLEAR ALL BUTTON */}
    <button
      onClick={() => {
        localStorage.removeItem("savedCharts");
        setSavedCharts([]);
      }}
      style={{
        marginBottom: 10,
        background: "#d63031",
        color: "white",
        border: "none",
        padding: "4px 8px",
        borderRadius: 4,
        cursor: "pointer",
        fontSize: 12,
        fontWeight: 600
      }}
    >
      🗑 Clear All
    </button>

{/* 🔥 CHART LIST */}
{savedCharts.map((chart, i) => (
  <div
    key={chart.id || i}

    style={{

      display: "flex",

      justifyContent: "space-between",

      alignItems: "center",

      marginBottom: 6,

      padding: "4px 6px",

      borderRadius: 4,

      background: "transparent"
    }}
  >

{/* CLICK TO OPEN */}
<span

  style={{

    cursor: "pointer",

    flex: 1,

    overflow: "hidden",

    textOverflow: "ellipsis",

    whiteSpace: "nowrap"
  }}

  onClick={() => {

    console.log(
      "🧪 Open chart:",
      chart
    );

    openChartWindow(chart);
  }}
>
  {chart.name}
</span>

{/* 🔥 DELETE BUTTON */}
<button

  onClick={() => {

    const updated =
      savedCharts.filter(

        (c) => c.url !== chart.url
      );

    setSavedCharts(updated);

    localStorage.setItem(

      "savedCharts",

      JSON.stringify(updated)
    );

    console.log(
      "🗑 Removed chart:",
      chart.name
    );
  }}

  style={{

    marginLeft: 8,

    background: "transparent",

    color: "#ff7675",

    border: "none",

    cursor: "pointer",

    fontWeight: 700
  }}
>
  ✖
</button>
      </div>
    ))}

  </DraggablePanel>
)}

import { Rnd } from "react-rnd";

{
  directToPreview && (

    <div
      style={{
        position: "absolute",
        top: 120,
        right: 20,
        zIndex: 99999,

        background:
          "rgba(15,15,20,0.95)",

        border:
          "1px solid rgba(255,255,255,0.15)",

        borderRadius: 14,

        padding: 16,

        width: 280,

        color: "white",

        backdropFilter: "blur(12px)",

        boxShadow:
          "0 0 25px rgba(0,0,0,0.45)"
      }}
    >

      <div
        style={{
          fontSize: 18,
          fontWeight: "bold",
          marginBottom: 10
        }}
      >
        ✈️ Direct To
      </div>

      <div
        style={{
          fontSize: 22,
          fontWeight: "bold",
          color: "#4FC3F7",
          marginBottom: 14
        }}
      >
        {directToPreview.fixName}
      </div>

      <div
        style={{
          marginBottom: 6
        }}
      >
        Δ Distance:
        {" "}
        {directToPreview.distanceDelta?.toFixed(1)} NM
      </div>

      <div
        style={{
          marginBottom: 6
        }}
      >
        Δ ETE:
        {" "}
        {directToPreview.eteDelta?.toFixed(1)} MIN
      </div>

      <div
        style={{
          marginBottom: 16
        }}
      >
        Δ Fuel:
        {" "}
        {directToPreview.fuelDelta?.toFixed(1)}
      </div>

      <div
        style={{
          display: "flex",
          gap: 10
        }}
      >

        <button
          onClick={confirmDirectTo}
          style={{
            flex: 1,

            background: "#00C853",

            color: "white",

            border: "none",

            borderRadius: 8,

            padding: "10px",

            fontWeight: "bold",

            cursor: "pointer"
          }}
        >
          CONFIRM
        </button>

        <button
          onClick={cancelDirectTo}
          style={{
            flex: 1,

            background: "#D50000",

            color: "white",

            border: "none",

            borderRadius: 8,

            padding: "10px",

            fontWeight: "bold",

            cursor: "pointer"
          }}
        >
          CANCEL
        </button>

      </div>

    </div>
  )
}


{/* =====================================
    📋 INFLIGHT BRIEF
===================================== */}

{showInflightBrief && (

  <DraggablePanel

    title="📋 Inflight Brief"

    defaultPosition={{
      x: window.innerWidth - 380,
      y: 120
    }}

    onClose={() =>
      setShowInflightBrief(false)
    }
  >

    <div
      style={{

        width: 320,

        background:
          "rgba(20,20,20,0.95)",

        border:
          "1px solid rgba(255,255,255,0.12)",

        borderRadius: 12,

        padding: 14,

        color: "white",

        backdropFilter:
          "blur(10px)",

        maxHeight: "78vh",

        overflowY: "auto"
      }}
    >

      {/* 🔥 LIVE FLIGHT OBJECT */}
      {(() => {

        const flight =
          activeRoute || activeFlight;

          // ✈️ WEIGHT CALCULATIONS

const totalFuel =
  Number(
    flight?.fuel?.fuelRemaining || 0
  );

const reserveFuel =
  (
    Number(
      flight?.fuel
        ?.reserveRequiredMinutes || 0
    ) / 60
  ) *
  Number(
    flight?.fuel
      ?.burnRatePerHour || 0
  );

const zfw =
  Number(
    flight?.meta?.zfw || 0
  );

const payload =
  Number(
    flight?.meta?.payload || 0
  );

const mtow =
  Number(
    flight?.meta?.mtow || 0
  );

const mlw =
  Number(
    flight?.meta?.mlw || 0
  );

const calculatedTow =
  zfw +
  Math.max(
    0,
    totalFuel -
    Number(
      flight?.fuel
        ?.taxiOutFuel || 0
    )
  );
  
const calculatedLdw =
  calculatedTow -
  Number(
    flight?.fuel
      ?.plannedTripBurn || 0
  );

  console.log(
  "🚨 PREVIOUS ROUTE STATE",
  previousRouteState
);

        return (

          <>

      {/* HEADER */}

      <div
        style={{

          display: "flex",

          justifyContent:
            "space-between",

          alignItems: "center",

          marginBottom: 12
        }}
      >

        <strong>
          ✈️ Inflight Brief
        </strong>

        <div
          style={{
            fontSize: 13,
            opacity: 0.75
          }}
        >

          🕒
          {" "}

          {
            new Date()
              .toUTCString()
              .slice(17, 22)
          }Z

        </div>

      </div>

      {/* ROUTE */}

      <div>

        <div>

          <strong>FROM:</strong>
          {" "}

          {
            flight?.meta?.origin ||
            origin
          }

        </div>

        <div>

          <strong>TO:</strong>
          {" "}

          {
            flight?.meta?.destination ||
            destination
          }

        </div>

      </div>

      {/* CURRENT LEG */}

      <div
        style={{
          marginTop: 12
        }}
      >

        <strong>
          CURRENT LEG
        </strong>

        <div
          style={{
            marginTop: 4,
            lineHeight: 1.5
          }}
        >

{
  currentLegIndex === 0

    ? (
        flight?.meta?.origin ||
        origin ||
        "—"
      )

    : (
        flight?.route
          ?.waypoints?.[
            currentLegIndex - 1
          ]?.id || "—"
      )
}

{" → "}

{
  flight?.route
    ?.waypoints?.[
      currentLegIndex
    ]?.id || "—"
}

        </div>

      </div>

      {/* NEXT FIX */}

      <div
        style={{
          marginTop: 12
        }}
      >

        <strong>
          NEXT FIX
        </strong>

        <div
          style={{
            marginTop: 4,
            fontSize: 16,
            fontWeight: 600
          }}
        >

          {
            flight?.route
              ?.waypoints?.[
                currentLegIndex 
              ]?.id || "N/A"
          }

        </div>

        <div
          style={{
            marginTop: 2,
            fontSize: 13,
            opacity: 0.7
          }}
        >

          After:
          {" "}

          {
            flight?.route
              ?.waypoints?.[
                currentLegIndex + 1
              ]?.id || "—"
          }

        </div>

      </div>


 {/* =====================================
// ⛽ FUEL & WEIGHT
// ===================================== */}

<div
  style={{
    marginTop: 14
  }}
>

  <strong>
    FUEL & WEIGHT
  </strong>

  <div
    style={{
      marginTop: 8,

      display: "grid",

      gridTemplateColumns:
        "1fr 1fr",

      gap: 8,

      fontSize: 13
    }}
  >

    {/* ZFW */}
    <div
      style={{
        background:
          "rgba(255,255,255,0.05)",

        padding: 8,

        borderRadius: 6
      }}
    >

      <div
        style={{
          opacity: 0.7,
          fontSize: 11
        }}
      >
        ZFW
      </div>

      <div
        style={{
          marginTop: 2,
          fontWeight: 600
        }}
      >

        {
          zfw
            ? Number(zfw)
                .toLocaleString()
            : "---"
        }

      </div>

    </div>

    {/* TOW */}
    <div
      style={{
        background:
          "rgba(255,255,255,0.05)",

        padding: 8,

        borderRadius: 6
      }}
    >

      <div
        style={{
          opacity: 0.7,
          fontSize: 11
        }}
      >
        TOW
      </div>

      <div
        style={{
          marginTop: 2,
          fontWeight: 600
        }}
      >

        {
          calculatedTow
            ? Math.round(
                calculatedTow
              ).toLocaleString()
            : "---"
        }

      </div>

    </div>

    {/* LDW */}
    <div
      style={{
        background:
          "rgba(255,255,255,0.05)",

        padding: 8,

        borderRadius: 6
      }}
    >

      <div
        style={{
          opacity: 0.7,
          fontSize: 11
        }}
      >
        LDW
      </div>

      <div
        style={{
          marginTop: 2,
          fontWeight: 600
        }}
      >

        {
          calculatedLdw
            ? Math.round(
                calculatedLdw
              ).toLocaleString()
            : "---"
        }

      </div>

    </div>

    {/* FOB */}
    <div
      style={{
        background:
          "rgba(255,255,255,0.05)",

        padding: 8,

        borderRadius: 6
      }}
    >

      <div
        style={{
          opacity: 0.7,
          fontSize: 11
        }}
      >
        FOB
      </div>

      <div
        style={{
          marginTop: 2,
          fontWeight: 600
        }}
      >

        {
          totalFuel
            ? Math.round(
                totalFuel
              ).toLocaleString()
            : "---"
        }

      </div>

    </div>

    {/* RESERVE */}
    <div
      style={{
        background:
          "rgba(255,255,255,0.05)",

        padding: 8,

        borderRadius: 6
      }}
    >

      <div
        style={{
          opacity: 0.7,
          fontSize: 11
        }}
      >
        RSV
      </div>

      <div
        style={{
          marginTop: 2,
          fontWeight: 600
        }}
      >

        {
          reserveFuel
            ? Math.round(
                reserveFuel
              ).toLocaleString()
            : "---"
        }

      </div>

    </div>

    {/* PAYLOAD */}
    <div
      style={{
        background:
          "rgba(255,255,255,0.05)",

        padding: 8,

        borderRadius: 6
      }}
    >

      <div
        style={{
          opacity: 0.7,
          fontSize: 11
        }}
      >
        PAYLOAD
      </div>

      <div
        style={{
          marginTop: 2,
          fontWeight: 600
        }}
      >

        {
          payload
            ? Number(payload)
                .toLocaleString()
            : "---"
        }

      </div>

    </div>

  </div>

</div>     

// =====================================
// 🛫 ORIGIN WEATHER
// =====================================

<div
  style={{
    marginTop: 14
  }}
>

  <strong>
    ORIGIN WEATHER
  </strong>

  {/* METAR */}

  <div
    style={{
      marginTop: 8
    }}
  >

    <div
      style={{
        fontSize: 12,
        opacity: 0.7,
        marginBottom: 2
      }}
    >
      METAR
    </div>

    <div
      style={{
        fontSize: 13,
        lineHeight: 1.4
      }}
    >

      {

        originWeather
          ?.departure_weather
          ?.metar ||

        originWeather
          ?.metar ||

        "No METAR available"
      }

    </div>

  </div>

  {/* TAF */}

  <div
    style={{
      marginTop: 10
    }}
  >

    <div
      style={{
        fontSize: 12,
        opacity: 0.7,
        marginBottom: 2
      }}
    >
      TAF
    </div>

    <div
      style={{
        fontSize: 13,
        lineHeight: 1.4,
        maxHeight: 120,
        overflowY: "auto",
        paddingRight: 4
      }}
    >

      {

        originWeather
          ?.departure_weather
          ?.taf ||

        originWeather
          ?.taf ||

        "No TAF available"
      }

    </div>

  </div>

</div>

// =====================================
// 🛬 DEST WEATHER
// =====================================

<div
  style={{
    marginTop: 14
  }}
>

  <strong>
    DEST WEATHER
  </strong>

  {/* METAR */}

  <div
    style={{
      marginTop: 8
    }}
  >

    <div
      style={{
        fontSize: 12,
        opacity: 0.7,
        marginBottom: 2
      }}
    >
      METAR
    </div>

    <div
      style={{
        fontSize: 13,
        lineHeight: 1.4
      }}
    >

      {

        destWeather
          ?.arrival_weather
          ?.metar ||

        destWeather
          ?.metar ||

        destinationData
          ?.metar ||

        "No METAR available"
      }

    </div>

  </div>

  {/* TAF */}

  <div
    style={{
      marginTop: 10
    }}
  >

    <div
      style={{
        fontSize: 12,
        opacity: 0.7,
        marginBottom: 2
      }}
    >
      TAF
    </div>

    <div
      style={{
        fontSize: 13,
        lineHeight: 1.4,
        maxHeight: 120,
        overflowY: "auto",
        paddingRight: 4
      }}
    >

      {

        destWeather
          ?.arrival_weather
          ?.taf ||

        destWeather
          ?.taf ||

        destinationData
          ?.taf ||

        "No TAF available"
      }

    </div>

  </div>

</div>

      {/* ACTIVE HAZARDS */}

      <div
        style={{
          marginTop: 14
        }}
      >

        <strong>
          ACTIVE HAZARDS
        </strong>

        <div
          style={{
            marginTop: 6,
            display: "flex",
            flexDirection: "column",
            gap: 6
          }}
        >

          {enrouteHazards?.fixHazards?.length > 0 ? (

            enrouteHazards.fixHazards

              .sort(
                (a, b) =>
                  (b.score || 0) -
                  (a.score || 0)
              )

              .slice(0, 3)

              .map((hazard, i) => (

                <div
                  key={i}
                  style={{

                    padding: 8,

                    borderRadius: 6,

                    background:
                      "rgba(255,120,120,0.12)",

                    border:
                      "1px solid rgba(255,120,120,0.18)"
                  }}
                >

                  <div>

                    ⚠️
                    {" "}

                    <strong>
                      {
                        hazard.fix
                      }
                    </strong>

                  </div>

                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      opacity: 0.85
                    }}
                  >

                    Risk:
                    {" "}

                    {
                      Math.round(
                        hazard.score || 0
                      )
                    }

                  </div>

                </div>
              ))

          ) : (

            <div
              style={{
                opacity: 0.7
              }}
            >
              No major hazards
            </div>

          )}

        </div>

      </div>

          </>

        );

      })()}

    </div>

  </DraggablePanel>
)}



{/* Left Icon Sidebar */}
<LeftSidebar

  onToggleRadar={toggleRadar}

  onToggleSigmets={toggleSigmets}

  onToggleAirmets={toggleAirmets}

  onToggleAirportConditions={
    toggleAirportConditions
  }

  onToggleVfrSectionals={
    toggleVfrSectionals
  }

  onToggleIfrLow={toggleIfrLow}

  onToggleIfrHigh={toggleIfrHigh}

  onToggleEchoTops={
    toggleEchoTops
  }

  // 🛑 TFRS
  onToggleTFRs={() =>
    setShowTFRs(prev => !prev)
  }

  // ✈️ AIRSPACES
  onToggleAirspaces={() =>
    setShowAirspaces(prev => !prev)
  }

  // 📡 VORS
  onToggleVors={() =>
    setShowVors(prev => !prev)
  }

  // ✳️ FIXES
  onToggleFixes={() =>
    setShowFixes(prev => !prev)
  }

  // 🌦 WEATHER SEARCH
  onToggleWeatherSearch={() =>
    setShowWxPanel(prev => !prev)
  }

  onOpenFlightPlan={
    handleOpenFlightPlan
  }

  onTogglePirepFilter={() =>
    setFilterMenuOpen(
      prev => !prev
    )
  }

  onSelect={(id) => {

    // ✈️ FLIGHT PANEL
    if (id === "flight") {

      setActivePanel("flight");

      return;
    }

    // 🔥 MULTI-GLOW OVERLAYS
    setActiveOverlayButtons(prev => {

      if (prev.includes(id)) {

        return prev.filter(
          x => x !== id
        );
      }

      return [
        ...prev,
        id
      ];
    });
  }}

  active={activeOverlayButtons}
/>

{/* =====================================
    ⚠️ IN-FLIGHT WEATHER WARNING BANNER
    ===================================== */}

{isActiveFlight &&
 inflightWeatherWarnings.length > 0 && (

  <div
    style={{

      position: "absolute",

      top: 20,

      left: "50%",

      transform:
        "translateX(-50%)",

      zIndex: 5000,

      width:
        "min(520px, calc(100% - 40px))",

      background:
        "rgba(120, 0, 0, 0.94)",

      border:
        "2px solid #ff4d4d",

      borderRadius: 10,

      padding:
        "10px 14px",

      color: "white",

      boxShadow:
        "0 4px 18px rgba(0,0,0,0.55)"

    }}
  >

    <div
      style={{

        fontWeight: 800,

        fontSize: 14,

        marginBottom: 6

      }}
    >

      ⚠️ WEATHER BELOW PERSONAL MINIMUMS

    </div>


    {inflightWeatherWarnings.map(
      warning => (

        <div
          key={warning.id}

          style={{

            fontSize: 12,

            marginTop: 4

          }}
        >

          <strong>

            {warning.type}

            {" "}

            {warning.airport}

          </strong>

          {" — "}

          {warning.message}

        </div>

      )
    )}

  </div>

)}

{(isActiveFlight || sessionStorage.getItem("missionRole") === "crew") && (() => {

  const missionRole =
    sessionStorage.getItem(
      "missionRole"
    );

const flight =
  activeRoute || activeFlight || {};

  

const destIcao =

  activeRoute?.meta?.destination ||

  activeFlight?.meta?.destination ||

  activeRoute?.destination ||

  activeFlight?.destination ||

  destination ||

  "";

  const alt = flight?.alternate;
  const activeAltIcao = alt?.icao;

  const isBadAlt =
    altWeather?.eta_weather &&
    (
      altWeather.eta_weather.visibility < 3 ||
      altWeather.eta_weather.ceiling < 1000
    );

  return (
    <>
{/* ✈️ ROUTE AMEND BOX */}
<DraggablePanel
  title="✏️ Route Amendment"
  defaultPosition={routePos}

size={
  routeCollapsed
    ? { width: routeSize.width, height: 40 }
    : {
        width: routeSize.width,
        height: Math.max(
          routeSize.height,
          180
        )
      }
}

  collapsed={routeCollapsed}

  onResizeStop={(e, dir, ref) => {

    const newSize = {
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height)
    };

    setRouteSize(newSize);

    localStorage.setItem(
      "routeSize",
      JSON.stringify(newSize)
    );
  }}

  onDragStop={(e, d) => {

    const pos = {
      x: d.x,
      y: d.y
    };

    setRoutePos(pos);

    localStorage.setItem(
      "routePos",
      JSON.stringify(pos)
    );
  }}

  onClose={() =>
    setRouteCollapsed(prev => !prev)
  }
>
  {!routeCollapsed && (
    <>
      <input
        value={routeInput}

        onChange={(e) =>
          setRouteInput(
            e.target.value
          )
        }

        placeholder="Edit route..."

        style={{
          width: "100%",
          padding: "6px",
          marginBottom: "6px"
        }}
      />

      <button onClick={applyRouteAmendment}>
        {
          missionRole === "crew"
            ? "Request Route Change"
            : "Apply"
        }
      </button>

      <div style={{
        marginTop: 8,
        borderTop:
          "1px solid rgba(255,255,255,0.2)",
        paddingTop: 6,
        fontSize: 12
      }}>

        <div style={{
          fontWeight: "bold",
          color: "#00cec9"
        }}>
          🛬 Destination {destIcao}
        </div>

        <div>
          {currentDestDistance?.toFixed(0) ?? "--"} NM
        </div>

        <div>
          {currentDestMinutes?.toFixed(0) ?? "--"} min
        </div>

        {destWeather?.metar && (
          <div style={{
            marginTop: 4,
            fontSize: 11,
            opacity: 0.8
          }}>
            {destWeather.metar}
          </div>
        )}

      </div>

    </>
  )}

</DraggablePanel>

{/* 🟠 ALTERNATE BOX */}
<DraggablePanel
  title="🟠 Change Alternate"
  defaultPosition={altPos}

  size={
    altCollapsed
      ? {
          width: altSize.width,
          height: 40
        }
      : altSize
  }

  collapsed={altCollapsed}

  onResizeStop={(e, dir, ref) => {

    const newSize = {
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height)
    };

    setAltSize(newSize);

    localStorage.setItem(
      "altSize",
      JSON.stringify(newSize)
    );
  }}

  onDragStop={(e, d) => {

    const pos = {
      x: d.x,
      y: d.y
    };

    setAltPos(pos);

    localStorage.setItem(
      "altPos",
      JSON.stringify(pos)
    );
  }}

  onClose={() =>
    setAltCollapsed(prev => !prev)
  }
>
  {!altCollapsed && (
    <>
      <input
        type="text"

        placeholder="NEW ALT ICAO (e.g. KDEN)"

        value={alternateInput}

        onChange={(e) =>
          setAlternateInput(
            e.target.value
              .replace(/[^A-Z]/gi, "")
              .toUpperCase()
          )
        }

        style={{
          width: "100%",
          marginBottom: "6px"
        }}
      />

      <button
        onClick={() =>
          generateAlternateRoute(
            alternateInput
          )
        }
      >
        {
          missionRole === "crew"
            ? "Request Alternate"
            : "Plot Direct To Alternate"
        }
      </button>

      <button
        onClick={toggleAlternate}

        style={{
          marginTop: "6px"
        }}
      >
        Show Alternate
      </button>

      

      {alt && alt.icao && (
        <div style={{
          marginTop: "8px",
          fontSize: "12px",
          borderTop:
            "1px solid rgba(255,255,255,0.2)",
          paddingTop: "6px"
        }}>
          <div style={{
            fontWeight: "bold",
            color:
              isBadAlt
                ? "#ff7675"
                : "#55efc4"
          }}>
            🟠 Current Alternate {alt.icao}
          </div>

          <div>
            {currentAltDistance?.toFixed(0) ?? "--"} NM
          </div>

          <div>
            {currentAltMinutes?.toFixed(0) ?? "--"} min
          </div>

          {altWeather ? (
            <div style={{
              marginTop: "6px",
              fontSize: "11px"
            }}>
              <div style={{
                marginTop: 4,
                opacity: 0.7
              }}>
                {altWeather.metar}
              </div>
            </div>
          ) : (
            <div style={{
              fontSize: "10px",
              opacity: 0.6
            }}>
              Fetching weather...
            </div>
          )}
        </div>
      )}
    </>
  )}
</DraggablePanel>
    </>
  );

})()}

{suggestionsLoading && (
  <div style={styles.loadingOverlay}>
    <div style={styles.loadingBox}>
      <div className="spinner" />
      <p style={{ marginTop: 10 }}>
  🧠 {loadingMessages[messageIndex]}
</p>
    </div>
  </div>
)}

      {/* KissBox */}
     
      <KissBox
        activeInstruction={activeInstruction}
        pdpCallsignSpoken={pdpCallsignSpoken}
      />


      
      <button onClick={() => setShowRadar(prev => !prev)}>
        {showRadar ? "Hide Radar" : "Show Radar"}
      </button>

      <button onClick={() => setShowSigmets(prev => !prev)}>
        {showSigmets ? 'Hide SIGMETs' : 'Show SIGMETs'}
      </button>

      <button onClick={() => setShowAirmets(prev => !prev)}>
        {showAirmets ? "Hide AIRMETs" : "Show AIRMETs"}
      </button>

      <button
        onClick={() =>
          setTrafficOn(prev => !prev)
        }
      >
        {trafficOn
          ? `Hide Traffic (${trafficAircraft.length})`
          : "Show Traffic"}
      </button>
      


      {/* Optional Legend */}
      <div style={{
        position: 'absolute',
        bottom: 20,
        left: 100,
        background: 'white',
        padding: '6px 10px',
        fontSize: '12px',
        borderRadius: '4px',
        zIndex: 1000,
        boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
      }}>
        <div><span style={{ background: '#00FFFF', width: 20, height: 10, display: 'inline-block' }}></span> ~10k ft</div>
        <div><span style={{ background: '#00FF00', width: 20, height: 10, display: 'inline-block' }}></span> ~20k ft</div>
        <div><span style={{ background: '#FFFF00', width: 20, height: 10, display: 'inline-block' }}></span> ~30k ft</div>
        <div><span style={{ background: '#FF0000', width: 20, height: 10, display: 'inline-block' }}></span> ~40k ft</div>
        <div><span style={{ background: '#FF00FF', width: 20, height: 10, display: 'inline-block' }}></span> ~50k+ ft</div>
      </div>


      {/* Start PDP Monitor 
      <button
        onClick={() => setShowPdpBox(true)}
        style={{
          position: 'absolute',
          top: '250px',
          left: '10px',
          zIndex: 1000,
          backgroundColor: '#6c757d',
          color: 'white',
          padding: '10px 20px',
          border: 'none',
          borderRadius: '8px',
          fontWeight: 'bold',
          cursor: 'pointer',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.2)'
        }}
      >
        Start PDP Monitor
      </button>*/}

      {/* Start Auto Turbulence Monitor 
      <button
        onClick={() => setShowTurbMonitor(true)}
        style={{
          position: 'absolute',
          top: '60px',
          right: '10px',
          zIndex: 1000,
          backgroundColor: '#ff9800',
          color: 'white',
          padding: '10px 20px',
          border: 'none',
          borderRadius: '8px',
          fontWeight: 'bold',
          cursor: 'pointer',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.2)'
        }}
      >
        Start Auto Turbulence Monitor
      </button>*/}

      {/* Submit PIREP modal */}
{showPirepForm && (
  <DraggablePanel title="Submit PIREP" onClose={() => setShowPirepForm(false)}>
    <SubmitPIREPForm
      mapClickLocation={clickedPoint}
      onClose={() => setShowPirepForm(false)}
    />
  </DraggablePanel>
)}

      {/* Airport layers menu */}
      {showAirportMenu && (
        <div style={{
          position: 'absolute',
          top: 600,
          right: 200,
          zIndex: 2001,
          background: 'white',
          padding: '8px 10px',
          borderRadius: 8,
          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          fontSize: 14
        }}>
          <strong>Airport Layers</strong>
          <button

  onClick={() => {

    setShowAirportMenu(false);

    setDismissedAirportMenu(true);
  }}

  style={{

    position: "absolute",

    top: 6,

    right: 8,

    background: "transparent",

    border: "none",

    cursor: "pointer",

    fontSize: 16,

    fontWeight: "bold",

    color: "#333"
  }}
>
  ✕
</button>

          <label style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
            <input
              type="checkbox"
              checked={runwayColorsOn}
              onChange={(e) => {
                setRunwayColorsOn(e.target.checked);
                applyRunwayColorsVisibility(e.target.checked);
              }}
            />
            <span>Runway colors</span>
          </label>

          <label style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
            <input
              type="checkbox"
              checked={ficonOn}
              onChange={(e) => {
                setFiconOn(e.target.checked);
                applyFiconVisibility(e.target.checked);
              }}
            />
            <span>FICON numbers</span>
          </label>

          <label style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
            <input
              type="checkbox"
              checked={taxiwayOn}
              onChange={(e) => {
                setTaxiwayOn(e.target.checked);
                applyTaxiwayVisibility(e.target.checked);
              }}
            />
            <span>Taxiway closures</span>
          </label>

          <label style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
            <input
              type="checkbox"
              checked={wingspanOn}
              onChange={(e) => {
                setWingspanOn(e.target.checked);
                applyWingspanVisibility(e.target.checked);
              }}
            />
            <span>Wingspan NOTAMs</span>
          </label>

          <label style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
            <input
              type="checkbox"
              checked={obstructionsOn}
              onChange={(e) => {
                setObstructionsOn(e.target.checked);
                applyObstructionsVisibility(e.target.checked);
              }}
            />
            <span>Obstructions</span>
          </label>

          <label style={{ display:'flex', gap:8, alignItems:'center', marginTop:6 }}>
            <input
              type="checkbox"
              checked={crosswindOn}
              onChange={(e) => {
                setCrosswindOn(e.target.checked);
                applyCrosswindVisibility(e.target.checked);
              }}
            />
            <span>Crosswind labels</span>
          </label>
        </div>
      )}


      {/* PDP quick box 
      {showPdpBox && (
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '35%',
          zIndex: 1000,
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '15px',
          boxShadow: '0px 0px 15px rgba(0,0,0,0.25)',
          width: '400px'
        }}>
          <h3>Pilot Deviation Preventer (PDP)</h3>
          <input
            type="text"
            placeholder="Your Callsign"
            value={pdpCallsign}
            onChange={(e) => setPdpCallsign(e.target.value)}
            style={{ width: '100%', marginBottom: '10px' }}
          />
          <button
            onClick={startPdpMonitoring}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              padding: '10px 16px',
              marginBottom: '10px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              width: '100%'
            }}
          >
            Start PDP Monitoring
          </button>
          <div style={{
            maxHeight: '150px',
            overflowY: 'auto',
            fontSize: '0.85em',
            background: '#f5f5f5',
            padding: '10px',
            borderRadius: '8px'
          }}>
            {pdpLogs.map((log, idx) => (
              <div key={idx}>{log}</div>
            ))}
          </div>
          <button
            onClick={() => setShowPdpBox(false)}
            style={{
              marginTop: '10px',
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              width: '100%'
            }}
          >
            Close
          </button>
        </div>
      )}*/}

      {/* PDP transcript (top of map) 
      <div style={{ backgroundColor: "#222", color: "#0f0", padding: "10px", fontFamily: "monospace", fontSize: "14px", marginTop: "10px" }}>
        <strong>PDP Transcript:</strong> {pdpTranscript || "Waiting..."}
      </div>*/}

      {/* Live camera button */}
      {cameraFeedUrl && (
        <button
          onClick={() => setCameraModalOpen(true)}
          style={{
            padding: '10px',
            cursor: 'pointer',
            position: 'absolute',
            top: '150px',
            left: '10px',
            zIndex: 1000,
            background: 'black',
            color: 'white',
            borderRadius: '5px'
          }}>
          📷 View Live Camera
        </button>
      )}

      {/* PIREP details modal (hidden by default) */}
{/* ✈️ PIREP MODAL */}

<div
  id="pirepModal"
  style={{

    display: 'none',

    position: 'fixed',

    top: '18%',

    left: '50%',

    transform: 'translateX(-50%)',

    zIndex: 99999,

    width: '420px',

    maxHeight: '70vh',

    overflowY: 'auto',

    background: 'rgba(20,20,20,0.96)',

    backdropFilter: 'blur(10px)',

    border: '1px solid rgba(255,255,255,0.15)',

    borderRadius: '16px',

    padding: '20px',

    boxShadow: '0 0 30px rgba(0,0,0,0.55)',

    color: 'white'
  }}
>

  <div id="pirepModalContent"></div>

  <div
    style={{
      marginTop: '18px',
      textAlign: 'right'
    }}
  >

    <button

      onClick={() =>
        document.getElementById(
          'pirepModal'
        ).style.display = 'none'
      }

      style={{

        background: '#d32f2f',

        color: 'white',

        border: 'none',

        borderRadius: '8px',

        padding: '8px 14px',

        cursor: 'pointer',

        fontWeight: '600'
      }}
    >
      Close
    </button>

  </div>

</div>

      {/* PIREP Filter Menu */}
      {filterMenuOpen && (
        <div style={{
          position: 'absolute',
          top: '600px',
          left: '200px',
          zIndex: 1000,
          backgroundColor: 'white',
          padding: '20px',
          border: '1px solid black'
        }}>
          <h4>Filter PIREPs</h4>

          <label>
            <input type="checkbox" checked={allPirepsEnabled} onChange={handleAllPirepsChange} />
            Show All PIREPs
          </label>
          <br />

          <fieldset disabled={allPirepsEnabled}>
            <label>
              <input type="checkbox" value="SEVERE" onChange={(e) => handleCategoryChange(e, 'turbulence')} />
              Severe Turbulence
            </label>
            <br />
            <label>
              <input type="checkbox" value="MODERATE" onChange={(e) => handleCategoryChange(e, 'turbulence')} />
              Moderate Turbulence
            </label>
            <br />
            <label>
              <input type="checkbox" value="LIGHT" onChange={(e) => handleCategoryChange(e, 'turbulence')} />
              Light Turbulence
            </label>
            <br />
            <label>
              <input type="checkbox" value="SEVERE" onChange={(e) => handleCategoryChange(e, 'icing')} />
              Severe Icing
            </label>
            <br />
            <label>
              <input type="checkbox" value="MODERATE" onChange={(e) => handleCategoryChange(e, 'icing')} />
              Moderate Icing
            </label>
            <br />
            <label>
              <input type="checkbox" value="LIGHT" onChange={(e) => handleCategoryChange(e, 'icing')} />
              Light Icing
            </label>
          </fieldset>
        </div>
      )}

      {/* Transcript Debug 
      <div style={{ backgroundColor: "#111", color: "#0f0", padding: "10px", fontFamily: "monospace", marginTop: "20px" }}>
        <h4>Transcript Debug</h4>
        <p>{pdpTranscript || "No transcript yet."}</p>
      </div>*/}



    




      {/* Auto Turbulence Monitor modal */}
      {showTurbMonitor && (
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '35%',
          zIndex: 1000,
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '15px',
          boxShadow: '0px 0px 15px rgba(0,0,0,0.25)',
          width: '400px'
        }}>
          <h3>Auto Turbulence Monitor</h3>
          <input
            type="text"
            placeholder="Callsign"
            value={turbCallsign}
            onChange={(e) => setTurbCallsign(e.target.value)}
            style={{ width: '100%', marginBottom: '10px' }}
          />
          <input
            type="text"
            placeholder="Aircraft Type"
            value={turbAircraft}
            onChange={(e) => setTurbAircraft(e.target.value)}
            style={{ width: '100%', marginBottom: '10px' }}
          />
          <button
            onClick={async () => {
              setMonitoringStatus('Running');
              await axios.post(`${backendURL}/start-turbulence-monitor`, {
                callsign: turbCallsign,
                aircraft_type: turbAircraft
              });
              setTurbLog((prev) => [...prev, '🟢 Monitoring started...']);
            }}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              padding: '8px 16px',
              marginBottom: '10px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              width: '100%'
            }}
          >
            Start Monitoring
          </button>
          <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '0.85em', background: '#f5f5f5', padding: '10px', borderRadius: '8px' }}>
            <strong>Status:</strong> {monitoringStatus}<br />
            {turbLog.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
          <button
            onClick={() => setShowTurbMonitor(false)}
            style={{
              marginTop: '10px',
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              width: '100%'
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* PDP top right side panel 
      {pdpActive && (
        <div style={{
          position: 'absolute',
          top: '20%',
          right: '10px',
          zIndex: 1000,
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '15px',
          boxShadow: '0px 0px 10px rgba(0,0,0,0.25)',
          width: '400px',
          maxHeight: '300px',
          overflowY: 'auto'
        }}>
          <h3>Pilot Deviation Monitor</h3>
          {pdpTranscript && (
            <div
              style={{
                marginTop: "10px",
                padding: "10px",
                backgroundColor: "#f1f1f1",
                borderRadius: "6px",
                border: "1px solid #ccc",
                fontSize: "14px",
              }}
            >
              <strong>Transcript:</strong> {pdpTranscript}
            </div>
          )}

          <p><strong>Monitoring:</strong> {pdpCallsignFiled} / {pdpCallsignSpoken}</p>

          {pdpPairs.length > 0 && (
            <table style={{ fontSize: '0.85em', width: '100%', marginTop: '10px' }}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>ATC</th>
                  <th>Pilot</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {['altitude', 'heading', 'turn', 'speed'].map((field) => {
                  const atc = pdpPairs[0]?.instruction?.[field] || '';
                  const pilot = pdpPairs[0]?.readback?.[field] || '';
                  const match = atc.toUpperCase() === pilot.toUpperCase();
                  return (
                    <tr key={field}>
                      <td>{field}</td>
                      <td>{atc}</td>
                      <td>{pilot}</td>
                      <td style={{ color: match ? 'green' : 'red' }}>
                        {match ? '✅ Match' : '❌ Mismatch'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div style={{
            marginTop: '10px',
            fontFamily: 'monospace',
            maxHeight: '150px',
            overflowY: 'auto',
            border: '1px solid #ccc',
            padding: '5px',
            borderRadius: '5px',
            backgroundColor: '#f9f9f9'
          }}>
            {pdpLogs.map((log, idx) => (
              <div key={idx}>{log}</div>
            ))}
          </div>
        </div>
      )}*/}

      {/* Live Camera Modal */}
      {cameraModalOpen && cameraFeedUrl && (
        <div style={{
          position: 'fixed',
          top: '20%',
          left: '30%',
          zIndex: 1000,
          backgroundColor: 'white',
          padding: '20px',
          border: '1px solid black'
        }}>
          <h3>Live Camera Feed</h3>
          <iframe
            width="1000"
            height="630"
            src={cameraFeedUrl}
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
          ></iframe>
          <br />
          <button onClick={() => setCameraModalOpen(false)}>Close</button>
        </div>
      )}




      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        




</div>

    



{/* Flight Input Drawer */}
{activePanel === "flight" && (
  <div className="drawer-overlay" onClick={() => setActivePanel(null)}>
    <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
      <div className="drawer-header">
        <h2>Enter Flight Plan</h2>
        <button
          className="drawer-close-button"
          onClick={() => setActivePanel(null)}
        >
          ✕
        </button>
      </div>

      {/* Callsign */}
      <div>
        <label>Flight Callsign</label>
        <input
          type="text"
          value={callsign}
          onChange={(e) =>
            setCallsign(e.target.value.toUpperCase())
          }
          placeholder="e.g., UAL1234"
        />
      </div>

      {/* Origin */}
      <div>
        <label>Origin Airport (ICAO)</label>
        <input
          type="text"
          value={origin}
          onChange={(e) => setOrigin(e.target.value)}
          placeholder="e.g., KJFK"
        />
      </div>

      {/* Destination */}
      <div>
        <label>Destination Airport (ICAO)</label>
        <input
          type="text"
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          placeholder="e.g., KORD"
        />
      </div>

      


      {/* 🟠 ALTERNATE AIRPORT (NEW) */}
      <div style={{ marginTop: 5 }}>
        <label>Alternate Airport (ICAO)</label>
        <input
          type="text"
          value={alternateInput}
          onChange={(e) => setAlternateInput(e.target.value.toUpperCase())}
          placeholder="e.g., KDEN"
        />
      </div>


      



{
showProcedureOptions && (

  <div
    style={{

      marginTop: 10,

      padding: 14,

      borderRadius: 12,

      background:
        "rgba(255,255,255,0.05)",

      border:
        "1px solid rgba(255,255,255,0.08)"
    }}
  >

    {/* ========================= */}
    {/* DEPARTURE */}
    {/* ========================= */}

    <div
      style={{
        marginBottom: 16
      }}
    >

      <div
        style={{
          fontWeight: 700,
          marginBottom: 8,
          color: "#00d2ff"
        }}
      >
        DEPARTURE
      </div>

      {/* SID */}

      <select

        value={selectedSid}

        onChange={(e) => {

          const sid =
            e.target.value;

          setSelectedSid(
            sid
          );

          updateDepartureProcedure({

            sid
          });
        }}

        style={{

          width: "100%",
          marginBottom: 8
        }}
      >

        <option value="">
          SID (AUTO)
        </option>

        {
          availableSids.map(
            (sid) => (

              <option
                key={sid}
                value={sid}
              >
                {sid}
              </option>
            )
          )
        }

      </select>

      {/* SID RUNWAY */}

<select

  value={
    selectedSidRunway
  }

  onChange={(e) => {

    const runway =
      e.target.value;

    setSelectedSidRunway(
      runway
    );

    updateDepartureProcedure({

      runway

    });

  }}

  style={{

    width: "100%",

    marginBottom: 8

  }}

>

  <option value="">
    Departure Runway (AUTO)
  </option>

  {
    sidRunways.map(
      (rwy) => (

        <option
          key={rwy}
          value={rwy}
        >
          {rwy}
        </option>

      )
    )
  }

</select>

      {/* SID TRANSITION */}

      <select

        value={
          selectedSidTransition
        }

        onChange={(e) => {

          const transition =
            e.target.value;

          setSelectedSidTransition(
            transition
          );

          updateDepartureProcedure({

            transition
          });
        }}

        style={{
          width: "100%"
        }}
      >

        <option value="">
          SID Transition (AUTO)
        </option>

        {
          sidTransitions.map(
            (t) => (

              <option
                key={t}
                value={t}
              >
                {t}
              </option>
            )
          )
        }

      </select>

    </div>

    {/* ========================= */}
    {/* ARRIVAL */}
    {/* ========================= */}

    <div>

      <div
        style={{
          fontWeight: 700,
          marginBottom: 8,
          color: "#00d2ff"
        }}
      >
        ARRIVAL
      </div>

      {/* STAR */}

      <select

        value={selectedStar}

        onChange={(e) => {

          const star =
            e.target.value;

          setSelectedStar(
            star
          );

          updateArrivalProcedure({

            star
          });
        }}

        style={{

          width: "100%",
          marginBottom: 8
        }}
      >

        <option value="">
          STAR (AUTO)
        </option>

        {
          availableStars.map(
            (star) => (

              <option
                key={star}
                value={star}
              >
                {star}
              </option>
            )
          )
        }

      </select>

      {/* STAR TRANSITION */}

      <select

        value={
          selectedStarTransition
        }

        onChange={(e) => {

          const transition =
            e.target.value;

          setSelectedStarTransition(
            transition
          );

          updateArrivalProcedure({

            transition
          });
        }}

        style={{

          width: "100%",
          marginBottom: 8
        }}
      >

        <option value="">
          STAR Transition (AUTO)
        </option>

        {
          starTransitions.map(
            (t) => (

              <option
                key={t}
                value={t}
              >
                {t}
              </option>
            )
          )
        }

      </select>

      {/* RUNWAY */}

      <select

        value={
          selectedArrivalRunway
        }

        onChange={(e) => {

          const runway =
            e.target.value;

          setSelectedArrivalRunway(
            runway
          );

          updateArrivalProcedure({

            runway
          });
        }}

        style={{

          width: "100%",
          marginBottom: 8
        }}
      >

        <option value="">
          Runway (AUTO)
        </option>

        {
          arrivalRunways.map(
            (rwy) => (

              <option
                key={rwy}
                value={rwy}
              >
                {rwy}
              </option>
            )
          )
        }

      </select>

      {/* APPROACH */}

      <select

        value={
          selectedApproach
        }

        onChange={(e) => {

          const approach =
            e.target.value;

          setSelectedApproach(
            approach
          );

          updateArrivalProcedure({

            approach
          });
        }}

        style={{

          width: "100%",
          marginBottom: 8
        }}
      >

<option value="">
  Approach (AUTO)
</option>

{
  availableApproaches.map(
    (apch) => (

      <option
        key={apch.value}
        value={apch.value}
      >
        {apch.label}
      </option>

    )
  )
}
      </select>

      {/* IAF */}

      <select

        value={
          selectedArrivalIAF
        }

        onChange={(e) => {

          const iaf =
            e.target.value;

          setSelectedArrivalIAF(
            iaf
          );

          updateArrivalProcedure({

            iaf
          });
        }}

        style={{
          width: "100%"
        }}
      >

        <option value="">
          IAF Entry (AUTO)
        </option>

        {
          availableIAFs.map(
            (iaf) => (

              <option
                key={iaf}
                value={iaf}
              >
                {iaf}
              </option>
            )
          )
        }

      </select>

    </div>

  </div>
)
}


      <div style={{ marginTop: 10 }}>
        <h4 style={{ color: "#FFA500" }}>🟠 Suggested Alternates</h4>

        {loadingAlts && <p>Loading...</p>}

        {!loadingAlts && alternates.length === 0 && (
          <p style={{ color: "#888" }}>No alternates found</p>
        )}

        {alternates.map((alt, i) => {
          const isSelected = alternateInput === alt.icao;

          return (
            <div
              key={alt.icao}
              onClick={() => handleSelectAlternate(alt.icao)}
              style={{
                marginBottom: 8,
                padding: "6px 10px",
                borderRadius: "6px",
                cursor: "pointer",
                backgroundColor: isSelected ? "#FFA50022" : "transparent",
                border: isSelected ? "1px solid #FFA500" : "1px solid transparent"
              }}
            >
              <strong>
                {alt.icao} {i === 0 && "⭐ BEST"}
              </strong>

              {/* 🔥 LINE 1: Core airport info */}
              <div style={{ fontSize: "0.8em", color: "#aaa" }}>
                {alt.distance} NM • RWY {alt.runway} ft
              </div>

              {/* 🔥 LINE 2: ETA-based forecast */}
              <div style={{ fontSize: "0.8em", color: "#00d4ff" }}>
                🕒 ETA {alt.eta_display || "--"} → {alt.visibility} SM / {alt.ceiling} ft
              </div>
            </div>
          );
        })}
      </div>

      {/* Altitude */}
      <div style={{ marginTop: 5 }}>
        <label>Cruise Altitude (feet) REQUIRED</label>
        <input
          type="number"
          value={altitude}
          onChange={(e) => setAltitude(e.target.value)}
          placeholder="e.g., 36000"
        />
      </div>

      {/* Burn Rate */}
      <div style={{ marginTop: 5 }}>
        <label>Burn Rate (lbs/hr) REQUIRED</label>
        <input
          type="number"
          value={burnRate}
          onChange={(e) => setBurnRate(e.target.value)}
          placeholder="e.g., 6000"
        />
      </div>

{/* TAS */}
<div style={{ marginTop: 5 }}>
  <label>True Airspeed (TAS) in knots REQUIRED</label>

  <input
    type="number"
    value={tasKts}
    onChange={(e) => setTasKts(e.target.value)}
    placeholder="e.g., 450"
  />
</div>

{/* === AIRCRAFT & WEIGHTS === */}
<div
  style={{
    marginTop: 18,
    padding: "14px",
    borderRadius: 12,
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.08)"
  }}
>

  <h3
    style={{
      marginTop: 0,
      marginBottom: 14
    }}
  >
    ✈️ Aircraft & Weights (Optional)
  </h3>

  <div
    style={{
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit, minmax(180px, 1fr))",
      gap: 12
    }}
  >

    {/* AIRCRAFT TYPE */}
    <div>
      <label>Aircraft Type</label>

      <input
        type="text"
        value={aircraftType}
        onChange={(e) =>
          setAircraftType(
            e.target.value
          )
        }
        placeholder="e.g., B744F"
      />
    </div>

    {/* CRUISE MACH */}
    <div>
      <label>Cruise Mach</label>

      <input
        type="text"
        value={cruiseMach}
        onChange={(e) =>
          setCruiseMach(
            e.target.value
          )
        }
        placeholder="e.g., 0.85"
      />
    </div>

    {/* ZFW */}
    <div>
      <label>ZFW</label>

      <input
        type="number"
        value={zfw}
        onChange={(e) =>
          setZfw(
            e.target.value
          )
        }
        placeholder="e.g., 419574"
      />
    </div>


    {/* MZFW */}
    <div>
      <label>MZFW</label>

      <input
        type="number"
        value={mzfw}
        onChange={(e) =>
          setMzfw(
            e.target.value
          )
        }
        placeholder="e.g., 635000"
      />
    </div>

    {/* MTOW */}
    <div>
      <label>MTOW</label>

      <input
        type="number"
        value={mtow}
        onChange={(e) =>
          setMtow(
            e.target.value
          )
        }
        placeholder="e.g., 875000"
      />
    </div>

    {/* MLW */}
    <div>
      <label>MLW</label>

      <input
        type="number"
        value={mlw}
        onChange={(e) =>
          setMlw(
            e.target.value
          )
        }
        placeholder="e.g., 666000"
      />
    </div>

    {/* PAYLOAD */}
    <div>
      <label>Payload</label>

      <input
        type="number"
        value={payload}
        onChange={(e) =>
          setPayload(
            e.target.value
          )
        }
        placeholder="e.g., 57000"
      />
    </div>

  </div>

</div>

{/* Fuel Additions */}
<div style={{ marginTop: 15 }}>
  <h3 style={{ marginBottom: 6 }}>
    Additional Fuel (Optional)
  </h3>

  {/* Taxi Out */}
  <div style={{ marginBottom: 10 }}>
    <label>Taxi Out Fuel</label>

    <div
      style={{
        display: "flex",
        gap: "8px",
        marginTop: 3
      }}
    >
      <input
        type="number"
        value={taxiOutMinutes}
        onChange={(e) =>
          setTaxiOutMinutes(
            e.target.value
          )
        }
        placeholder="Minutes"
        style={{ flex: 1 }}
      />

      <input
        type="number"
        value={taxiOutBurn}
        onChange={(e) =>
          setTaxiOutBurn(
            e.target.value
          )
        }
        placeholder="Pounds"
        style={{ flex: 1 }}
      />
    </div>
  </div>

  {/* Taxi In */}
  <div style={{ marginBottom: 10 }}>
    <label>Taxi In Fuel</label>

    <div
      style={{
        display: "flex",
        gap: "8px",
        marginTop: 3
      }}
    >
      <input
        type="number"
        value={taxiInMinutes}
        onChange={(e) =>
          setTaxiInMinutes(
            e.target.value
          )
        }
        placeholder="Minutes"
        style={{ flex: 1 }}
      />

      <input
        type="number"
        value={taxiInBurn}
        onChange={(e) =>
          setTaxiInBurn(
            e.target.value
          )
        }
        placeholder="Pounds"
        style={{ flex: 1 }}
      />
    </div>
  </div>

  {/* Contingency */}
  <div style={{ marginBottom: 10 }}>
    <label>Contingency Fuel</label>

    <div
      style={{
        display: "flex",
        gap: "8px",
        marginTop: 3
      }}
    >
      <input
        type="number"
        value={contingencyMinutes}
        onChange={(e) =>
          setContingencyMinutes(
            e.target.value
          )
        }
        placeholder="Minutes"
        style={{ flex: 1 }}
      />

      <input
        type="number"
        value={contingencyBurn}
        onChange={(e) =>
          setContingencyBurn(
            e.target.value
          )
        }
        placeholder="Pounds"
        style={{ flex: 1 }}
      />
    </div>
  </div>

  {/* Final Reserve */}
  <div style={{ marginBottom: 5 }}>
    <label>Final Reserve Fuel</label>

    <div
      style={{
        fontSize: "11px",
        opacity: 0.7,
        marginTop: 2,
        marginBottom: 4
      }}
    >
      Protected fuel planned to remain at the end of the flight.
    </div>

    <div
      style={{
        display: "flex",
        gap: "8px",
        marginTop: 3
      }}
    >
<input
  type="number"
  value={reserveMinutes}
  onChange={(e) => {

    const value =
      e.target.value;

    setReserveMinutes(value);

    // Minutes → Pounds
    const minutes =
      Number(value);

    const rate =
      Number(burnRate);

    if (
      value !== "" &&
      Number.isFinite(minutes) &&
      Number.isFinite(rate) &&
      rate > 0
    ) {

      setReserveBurn(
        Math.round(
          (minutes / 60) * rate
        ).toString()
      );

    } else {

      setReserveBurn("");
    }
  }}
  placeholder="Minutes"
  min="0"
  style={{ flex: 1 }}
/>

<input
  type="number"
  value={reserveBurn}
  onChange={(e) => {

    const value =
      e.target.value;

    setReserveBurn(value);

    // Pounds → Minutes
    const pounds =
      Number(value);

    const rate =
      Number(burnRate);

    if (
      value !== "" &&
      Number.isFinite(pounds) &&
      Number.isFinite(rate) &&
      rate > 0
    ) {

      setReserveMinutes(
        (
          (pounds / rate) * 60
        ).toFixed(0)
      );

    } else {

      setReserveMinutes("");
    }
  }}
  placeholder="Pounds"
  min="0"
  style={{ flex: 1 }}
/>
    </div>
  </div>
</div>

      {/* Route Suggestion Button */}
<button
  onClick={loadRouteSuggestions}
  style={{
    marginTop: "18px",
    width: "100%",
    padding: "16px 22px",

    background:
      "linear-gradient(135deg, #0066ff, #0047cc)",

    color: "#ffffff",

    border: "2px solid rgba(255,255,255,0.25)",
    borderRadius: "10px",

    fontSize: "17px",
    fontWeight: "800",
    letterSpacing: "0.5px",

    cursor: "pointer",

    boxShadow:
      "0 6px 18px rgba(0, 82, 204, 0.45)",

    textTransform: "uppercase"
  }}
>
  ✈ GENERATE ROUTE SUGGESTIONS
</button>

{/* Manual Route Input */}
<div style={{ marginTop: 10 }}>
  <label>Manual Route</label>
  <input
    type="text"
    value={route}
    onChange={(e) => setRoute(e.target.value)}
    placeholder="e.g., KLGA COATE Q436 KAYYS WYNDE3 KORD"
    className="manual-route-input"
  />

{/* Plot Route Button */}
<button
  className="btn btn-success"
  onClick={async () => {

    if (!route.trim()) {

      console.warn(
        "🛑 No manual route entered"
      );

      return;
    }

    try {

      const cleanedRoute =
        buildSidRouteString();

      const cleanedOrigin =
        origin.trim().toUpperCase();

      const cleanedDest =
        destination.trim().toUpperCase();

      console.log(
        "🛫 Resolving manual route..."
      );

      // =====================================
      // 🔥 RESOLVE ROUTE HERE
      // =====================================

const resolved =
  await resolveRouteWithCoords(

    cleanedRoute,

    cleanedOrigin,

    cleanedDest,

    altitude,

    selectedSidRunway,

    selectedSid,
    selectedSidTransition,

    selectedArrivalRunway,

    selectedStar,
    selectedStarTransition,

    selectedApproach,
    selectedArrivalIAF
  );

console.log(
  "🧪 START FLIGHT PROCEDURES",
  {

    selectedSidRunway,
    selectedSid,
    selectedSidTransition,

    selectedArrivalRunway,
    selectedStar,
    selectedStarTransition,

    selectedApproach,
    selectedArrivalIAF
  }
);

      console.log(
        "🧪 RESOLVED:",
        resolved
      );

      console.log(
  "################################"
);

console.log(
  "PROCEDURES RETURNED"
);

console.log(
  "################################"
);

console.log(
  resolved.procedures
);

console.log(
  "################################"
);

      if (
        !resolved?.coords ||
        resolved.coords.length < 2
      ) {

        console.warn(
          "❌ Route resolution failed"
        );

        return;
      }

      // =====================================
      // 🔥 SAFE STATE UPDATES
      // =====================================

      setRouteTokens(
        resolved.names || []
      );

      console.log(
  "🚨 START FLIGHT RESOLVED",
  resolved
);

console.log(
  "🚨 START FLIGHT COORDOBJECT COUNT:",
  resolved?.coordObjects?.length
);

console.log(
  "🚨 START FLIGHT FIRST COORDOBJECT:",
  resolved?.coordObjects?.[0]
);

      setRouteResult(
        resolved
      );

      // =====================================
      // 🔥 HAZARD DATA
      // =====================================

      if (
        resolved?.hazardAnalysis
      ) {

        console.log(
          "🔥 Hazard analysis:",
          resolved.hazardAnalysis
        );

        setHazardData(
          resolved.hazardAnalysis
        );
      }


      // =====================================
// 🎯 STORE ACTIVE FLIGHT DESTINATION
// =====================================

destinationRef.current =
  resolved.coords[
    resolved.coords.length - 1
  ];
// =====================================
// 🔥 BUILD ROUTE OBJECT
// =====================================
const routeData = {

  origin:
    resolved.coords[0],

  destination:
    resolved.coords[
      resolved.coords.length - 1
    ],

  routeText:
    cleanedRoute,

   coordObjects:
  resolved.coordObjects || [], 

  // =====================================
  // 🧠 STRUCTURED ROUTE MODEL
  // =====================================

  routeStructure: {

    // =====================================
    // ✈️ ORIGINAL FILED ROUTE TOKENS
    // =====================================

    filedRoute:

      cleanedRoute
        .split(/\s+/)
        .filter(Boolean),

    // =====================================
    // 🛫 DEPARTURE
    // =====================================

    departure: {

      runway:
        departureRunway || null,

      sid:
        selectedSid || null,

      transition:
        selectedSidTransition || null
    },

    // =====================================
    // 🛣 ENROUTE
    // =====================================

    enroute: {

      routeText:
        cleanedRoute || "",

      tokens:
        resolved.names || []
    },

    // =====================================
    // 🛬 ARRIVAL
    // =====================================

arrival: {

  runway:
    arrivalRunway || null,

  star:
    selectedStar || null,

  transition:
    selectedStarTransition || null,

  approach:
    selectedApproach || null,

  iaf:
    selectedArrivalIAF || null,

  // =====================================
  // FAA STAR OBJECT
  // =====================================

  starObject:

    resolved.selected_star_result || null
}
  },

  // =====================================
  // 🧠 ROUTE SEGMENTS
  // =====================================

  routeSegments: [

    {
      type: "ENROUTE",

      name: "MAIN",

      coords:
        resolved.coords,

      waypoints:
        resolved.waypoints || []
    }
  ],

  // =====================================
  // 🔥 FULL ROUTE
  // =====================================

route: {

  coords:
    resolved.coords,

  waypoints:
    resolved.waypoints || [],

  coordObjects:
    resolved.coordObjects || []
},

  // =====================================
  // 🔥 WAYPOINTS
  // =====================================

  waypoints:
    resolved.waypoints || [],

  // =====================================
  // 🔥 META
  // =====================================

  meta: {

    origin:
      cleanedOrigin,

    destination:
      cleanedDest,

    altitude,

    routeTextOriginal:
      cleanedRoute,

    routeTextCurrent:
      cleanedRoute
  }
};

console.log(
  "🧪 FINAL PREFLIGHT ROUTE:",
  routeData
);

console.log(
  "🚨 PREFLIGHT COORDOBJECT COUNT:",
  routeData.route?.coordObjects?.length
);

console.log(
  "🚨 PREFLIGHT COORDOBJECTS:",
  routeData.route?.coordObjects
);

console.log(
  "🚨 PREFLIGHT ARRIVAL SAVED:",
  routeData.routeStructure?.arrival
);

console.log(
  "🚨 STAR OBJECT:",
  routeData.routeStructure.arrival.starObject
);
// =====================================
// 🔥 ACTIVATE PREFLIGHT
// =====================================

setActiveRoute(
  routeData
);

// =====================================
// 💾 PERSIST ACTIVE FLIGHT
// =====================================

setActiveFlight(
  routeData
);

localStorage.setItem(

  "activeFlight",

  JSON.stringify(routeData)
);

setConfirmedRouteString(
  cleanedRoute
);

setRouteConfirmed(
  true
);

setFlightStage(
  "briefing"
);
      // =====================================
      // 🔥 CLEANUP
      // =====================================

      setActivePanel(null);

      if (map) {

        map.dragPan.enable();

        map.scrollZoom.enable();

        map.boxZoom.enable();
      }

      console.log(
        "✅ Manual route flow complete"
      );

    } catch (err) {

      console.error(
        "❌ Failed to process manual route:",
        err
      );
    }
  }}

  style={{ marginTop: 8 }}
>
  Plot Manual Route
</button>

{/* Reset Button */}
<div style={{ marginTop: 20, textAlign: "right" }}>
  <button
    className="btn btn-secondary"
    onClick={handleResetFlightPlan}
  >
    🔁 Reset Form
  </button>
</div>

      </div>
      
{/* Suggested routes from backend */}
{(!suggestionsLoading && routeSuggestions.length > 0) && (

<div
  style={{
    position: 'absolute',
    top: 60,
    right: 20,
    bottom: 20,
    width: 420,
    background: '#fafafa',
    borderRadius: 12,
    border: '1px solid #ccc',
    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
    overflowY: 'auto',
    padding: 14,
    zIndex: 2000,
  }}
>

<h4 style={{ marginTop: 0 }}>
  Suggested Routes ({routeSuggestions.length})
</h4>

{suggestionsLoading && (
  <div style={{ color: "#666", fontStyle: "italic" }}>
    Loading suggestions…
  </div>
)}

{!suggestionsLoading && suggestionsError && (
  <div style={{ color: "crimson" }}>{suggestionsError}</div>
)}

{!suggestionsLoading &&
  !suggestionsError &&
  routeSuggestions.length === 0 && (
    <div style={{ color: "#666" }}>No suggestions available.</div>
)}

{/* 🔥 ONLY CHANGE IS HERE */}
{[...routeSuggestions]
  .sort((a, b) => (a.hazard_score ?? 999) - (b.hazard_score ?? 999))
  .map((s, i) => {

  const reasons =
    Array.isArray(s.fix_details) && s.fix_details.length > 0
      ? buildHazardExplanation(s.fix_details)
      : s.hazard_summary
      ? [s.hazard_summary]
      : [];

return (
  <div
    key={`${s.label}-${i}`}
    style={{
      padding: '12px 10px',
      borderBottom: '1px solid #eee',
      borderRadius: 6,
      background: '#fff',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      marginBottom: 10,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      cursor: 'pointer',
      transition: 'background 0.2s ease',
    }}
    onMouseEnter={() => highlightRoute(i)}
    onMouseLeave={() => resetRoute(i)}
  >
    <div style={{ flex: 1, minWidth: 0 }}>
      {/* Top row: hazard dot + colored route label */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {(typeof s.hazard_score === "number" || typeof s.score === "number" || typeof s.risk === "number") && (
          <>
            {/* Hazard color dot */}
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: getDotColor(s.hazard_score || s.score || s.risk || 0),
              }}
              title={`Hazard Level ${s.hazard_score?.toFixed(1)}`}
            ></div>
          {/* Hazard score number */}
          <span
            style={{
              fontWeight: 600,
              fontSize: '0.85em',
              color:
                s.hazard_score >= 85
                  ? '#c62828'      // 🔴 Red
                  : s.hazard_score >= 75
                  ? '#ef6c00'      // 🟠 Orange
                  : s.hazard_score >= 45
                  ? '#f9a825'      // 🟡 Yellow
                  : s.hazard_score >= 20
                  ? '#42a5f5'      // 🔵 Blue
                  : '#2e7d32',     // 🟢 Green
              marginRight: 6,
            }}
            title="Total Hazard Score"
          >
            ⚠ {s.hazard_score.toFixed(1)}
          </span>

{/* Max / Avg / Σ hazard breakdown */}
<span style={{ fontSize: '0.75em', color: '#555' }}>
  <span title="Max score at any fix" style={{ color: '#c62828', fontWeight: 600 }}>
    Max {((s.hazard_max ?? 0) * 100).toFixed(1)}
  </span>{" / "}
  <span title="Avg score along route" style={{ color: '#f9a825', fontWeight: 600 }}>
    Avg {((s.hazard_avg ?? 0) * 100).toFixed(1)}
  </span>{" / "}
</span>

          </>
        )}
      </div>

        {/* Route string */}
        <div
          style={{
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            marginTop: 4
          }}
        >
          {s.route}
        </div>

        {/* Meta info */}
        <div style={{ fontSize: '0.8em', color: '#555', marginTop: 6 }}>
          {typeof s.distance_nm === 'number' || typeof s.mileage === 'number' ? (
            <strong style={{ color: '#222' }}>
              {Math.round(
                typeof s.distance_nm === 'number'
                  ? s.distance_nm
                  : s.mileage || 0
              )}{" "}
              NM
            </strong>
          ) : (
            <span style={{ color: '#999' }}>Distance unknown</span>
          )}
          {s.type || s.source ? ` · ${s.type || s.source}` : ""}
          {s.hazards?.length > 0 && (
            <span style={{ color: 'red', marginLeft: 6 }}>
              ⚠ {s.hazards.join(", ")}
            </span>
          )}
        </div>

        {/* Fuel burn and estimated time */}
        {(s.fuel_burn !== undefined || s.flight_time_hr !== undefined) && (
          <div style={{ fontSize: '0.75em', color: '#444', marginTop: 6 }}>
            {s.fuel_burn !== undefined && (
              <span>⛽ Burn: <strong>{s.fuel_burn}</strong>{' '}</span>
            )}
            {s.flight_time_hr !== undefined && (
              <span>⏱ Time: <strong>{s.flight_time_hr}</strong> hr</span>
            )}
          </div>
        )}

        {/* Hazard detail list */}
        {Array.isArray(s.hazard_details) && s.hazard_details.length > 0 && (
          <ul style={{ fontSize: '0.75em', color: '#a94442', marginTop: 6, marginLeft: 16 }}>
            {s.hazard_details.map((detail, idx) => (
              <li key={idx}>
                {detail.fix}: {detail.hazard}
              </li>
            ))}
          </ul>
        )}

        {/* Explanation Reasoning Summary */}
        {reasons.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <b style={{ fontSize: '0.75em', color: '#444' }}>Why this route?</b>
            <ul style={{ fontSize: '0.75em', color: '#333', marginTop: 2, marginLeft: 16 }}>
              {reasons.map((r, ri) => (
                <li key={ri}>– {r}</li>
              ))}
            </ul>
          </div>
        )}
      </div>


{/* "Use" button */}
<button
  style={{ flexShrink: 0, padding: "6px 10px", marginLeft: 10 }}
  onClick={() => {
    // 🧹 Remove ALL previously plotted suggestion routes (regardless of state)
    if (map && map.getStyle) {
      const style = map.getStyle();

      // Remove suggestion layers
      style.layers.forEach((layer) => {
        if (layer.id.startsWith("route-suggestion-")) {
          map.removeLayer(layer.id);
        }
      });

      // Remove suggestion sources
      Object.keys(style.sources).forEach((sourceId) => {
        if (sourceId.startsWith("route-suggestion-")) {
          map.removeSource(sourceId);
        }
      });
    }

    const orig = (origin || "").trim().toUpperCase();
    const dest = (destination || "").trim().toUpperCase();

    if (!s.coords || s.coords.length < 2) {
      console.error("❌ Invalid route data, cannot plot.");
      return;
    }

    setActivePanel(null); // Close drawer

    const origin_ll = s.coords[0];
    const dest_ll = s.coords[s.coords.length - 1];
    const waypoints = s.coords.slice(1, -1).map((coord, i) => ({
      name: s.names?.[i + 1] || `WP${i + 1}`,  // ✅ FIXED
      coordinates: coord,
    }));

    const dataForPlot = {
      origin: origin_ll,
      destination: dest_ll,
      origin_icao: orig,
      destination_icao: dest,
      waypoints: waypoints,
      altitude: altitude,
      debug: { expanded_tokens: s.names || [] },
    };

    const expandedTokens =
      s.names && s.coords && s.names.length === s.coords.length
        ? s.names
        : (s.coords || []).map((_, i) => `WP${i + 1}`);

    // 💡 Clear previously plotted "selected" route if it exists
    if (map) {
      if (map.getLayer("selected-route")) map.removeLayer("selected-route");
      if (map.getSource("selected-route")) map.removeSource("selected-route");
    }

    console.log(
  "🧪 SELECTED SUGGESTION:",
  s
);
console.log(
  "################################"
);

console.log(
  "STAR OBJECT FROM BACKEND"
);

console.log(
  s.selected_star
);

console.log(
  s.selected_star_transition
);

console.log(
  s.selected_star_result
);

console.log(
  "################################"
);
    // ✅ Set state + plot
    setRouteTokens(expandedTokens);
    setRouteResult(dataForPlot);
    console.log(
  "🚨 USE BUTTON DATA",
  dataForPlot
);
    plotRoute(dataForPlot); // this must plot with ID "selected-route"
    setSelectedRouteString(s.route);
    setConfirmedRouteString(s.route);

console.log("🔥 BEFORE AUTO INIT");
    // =====================================
// AUTO-INITIALIZE ARRIVAL PACKAGE
// =====================================

console.log(
  "🚨 RAW S.ROUTE",
  s.route
);

const tokens =
  (s.route || "")
    .trim()
    .split(/\s+/);

 console.log(
  "🔥 ROUTE STRING",
  s.route
);

console.log(
  "🔥 TOKENS",
  tokens
);

console.log(
  "🔥 TOKEN COUNT",
  tokens.length
);   

 console.log(
  "🚨 ROUTE TOKENS",
  tokens
); 

// =====================================
// SID AUTO-DETECTION DISABLED
// SIDs are selected in Preflight
// =====================================

console.log(
  "🚫 SID AUTO-DETECTION DISABLED"
);


if (tokens.length >= 3) {

  // =====================================
  // Prefer backend STAR information.
  // Fall back to legacy route parsing.
  // =====================================

  const detectedStar =
    s.selected_star ||
    tokens[tokens.length - 2];

  const detectedTransition =
    s.selected_star_transition ||
    tokens[tokens.length - 3];

console.log(
  "🛬 AUTO STAR:",
  detectedStar
);

console.log(
  "🛬 AUTO TRANSITION:",
  detectedTransition
);

console.log(
  "🛬 STAR DROPDOWN OPTIONS:",
  availableStars
);

const detectedStarBase =
  detectedStar.replace(
    /[0-9]+[A-Z]?$/,
    ""
  );

  console.log(
  "🚨 AVAILABLE STARS LENGTH",
  availableStars?.length
);

console.log(
  "🚨 AVAILABLE STARS",
  availableStars
);

console.log(
  "🚨 AVAILABLE SIDS",
  availableSids
);

console.log(
  "🚨 DETECTED STAR RAW",
  detectedStar
);

console.log(
  "🚨 DETECTED STAR BASE",
  detectedStarBase
);

console.log(
  "🚨 STAR MATCH TEST",
  availableStars?.includes(
    detectedStarBase
  )
);

if (
  availableStars?.includes(
    detectedStarBase
  )
) {

  console.log(
    "✅ AUTO STAR ACCEPTED:",
    detectedStarBase
  );

  console.log(
    "🚨 SETTING STAR:",
    detectedStarBase
  );

  console.log(
    "🚨 SETTING TRANSITION:",
    detectedTransition
  );

  setSelectedStar(
    detectedStarBase
  );

  setSelectedStarTransition(
    detectedTransition
  );

  updateArrivalProcedure({

    star:
      detectedStarBase,

    transition:
      detectedTransition

  });

  setTimeout(() => {

    console.log(
      "🚨 selectedStar after AUTO set:",
      detectedStarBase
    );

  }, 1000);

} else {

  console.log(
    "🚫 AUTO STAR REJECTED:",
    detectedStarBase
  );

  setSelectedStar("");

  setSelectedStarTransition("");

}
}
    setRouteSuggestions([]); // clear sidebar
    setModalOpen(false);

    // ✅ Re-enable interaction
    if (map) {
      map.dragPan.enable();
      map.scrollZoom.enable();
      map.boxZoom.enable();
    }
  }}
>
  Use
</button>

    </div>
  );
})}
  </div>
)}

    
    </div>
  </div>
)}

      {/* FICON NOTAMs Modal */}
      {ficonNotams.length > 0 && (
        <div className="modal">
          <div className="modal-content">
            <h2>FICON NOTAMs</h2>
            <ul>
              {ficonNotams.map((notam, index) => (
                <li key={index}>
                  <strong>{notam.properties.coreNOTAMData.notam.icaoLocation}</strong>: {notam.properties.coreNOTAMData.notam.text}
                </li>
              ))}
            </ul>
            <button onClick={() => setFiconNotams([])}>Close</button>
          </div>
        </div>
      )}

      {/* Bottom-right PDP Live Log */}
      {pdpLogs.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          width: '400px',
          backgroundColor: '#ffffff',
          padding: '15px',
          borderRadius: '10px',
          boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
          fontFamily: 'monospace',
          fontSize: '0.9em',
          zIndex: 1000,
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          <h4 style={{ marginTop: 0 }}>🎧 PDP Transcripts</h4>
          {pdpLogs.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      )}
    </div> {/* end .map-shell */}

  
    
  </div>
  
   </> 
);

}


export default MapComponent;
